import { Component, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getFirebaseAuth,
  getFirebaseDb,
  getFirebaseApp,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updatePassword,
  signOut,
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  setDoc,
  writeBatch,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  storage,
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "../alphaFirebase.js";
import { alphaNectaResults, enabledNectaResults, findNectaResult, availableNectaYears } from "../nectaResults.js";
import { useTeacherClasses, classTeacherUids } from "../alphaRbac.js";

const SCHOOL_ID = "alpha";
const SUPER_ADMIN_EMAIL = "stea.africa@gmail.com";
const BLUE = "#0B4EA2";
const GOLD = "#F7C948";
const DARK = "#062A58";
const LIGHT = "#F5F9FF";
// Fix #1 — Origin-aware API base.
// Vite build runs with `VITE_ALPHA_API_BASE=` (empty) → relative URLs bypass CORS entirely
// because every Firebase Hosting site (including alpha.stea.africa custom domain +
// alphaportal-3e1d8.web.app) has the /api/** rewrite → Cloud Function proxy.
// Dev/emulator set the full absolute base explicitly via env var instead.
const ALPHA_API_BASE = (() => {
  const fromEnv = String(import.meta.env.VITE_ALPHA_API_BASE || "").trim();
  if (fromEnv) return fromEnv;
  // Browser fallback: if already served from a page whose origin is known, go relative.
  if (typeof window !== "undefined") return "";
  return "https://alphaportal-3e1d8.web.app";
})();

const alphaAssets = {
  logo: "/alpha/logo/alpha-logo-square.png",
  lockup: "/alpha/hero/alpha-classroom-lockup.png",
  instantHero: "https://firebasestorage.googleapis.com/v0/b/alphaportal-3e1d8.firebasestorage.app/o/alpha%2Fwebsite%2Fhero-carousel%2F1783746365064-Generated-Image-July-11-2026-11_48AM.jpg?alt=media&token=3ec52511-07e5-4515-a41a-36fc76085147",
  icon192: "/alpha/icons/alpha-icon-192.png",
  appleTouchIcon: "/alpha/apple-touch-icon.png",
  favicon: "/alpha/favicon-32x32.png?v=alpha-logo-20260713-1",
};

const alphaInfo = {
  schoolName: "Alpha Schools",
  campuses: "Mikocheni · Kunduchi",
  location: "Dar es Salaam, Tanzania",
  website: "alphaschools.ac.tz",
  phone: "+255 734 036 010",
  phone2: "+255 653 312 438",
  phone3: "+255 784 848 686",
  email: "alphaschoolsdsm@gmail.com",
  instagram: "@alphaschoolstz",
  motto: "Your Education Is Our Priority",
  schools: [
    {
      id: "alpha-high-school",
      name: "Alpha High School",
      type: "Co-Educational",
      level: "O-Level \u2022 A-Level",
      location: "Mikocheni",
      description: "A co-educational secondary school providing O-Level and A-Level education, with a focus on academic excellence, practical learning and holistic student development.",
    },
    {
      id: "alpha-girls-high-school",
      name: "Alpha Girls High School",
      type: "Girls School",
      level: "A-Level (Form V\u2013VI)",
      location: "Kunduchi",
      description: "A girls' A-Level institution providing Form Five and Form Six education in a supportive environment designed to develop confident, capable and well-rounded young women.",
    },
    {
      id: "alpha-nursery-primary",
      name: "Alpha Nursery & Primary School",
      type: "Nursery & Primary",
      level: "Nursery \u2022 Primary",
      location: "Kunduchi",
      description: "An early-years and primary learning environment focused on building strong academic foundations, confidence, character and essential skills.",
    },
  ],
};

const roleHome = {
  superAdmin: "/portal/super-admin",
  schoolAdmin: "/portal/admin",
  teacher: "/portal/teacher",
  student: "/portal/student",
};

const moduleMap = {
  superAdmin: ["Schools", "Alpha Homepage", "Create School", "Create School Admin", "Manage Users", "Analytics", "Logs", "Notifications", "Settings"],
  schoolAdmin: ["Teachers", "Students", "Website Manager", "Academic Years", "Terms", "Forms", "Streams", "Subjects", "Subject Offerings", "Classes", "Attendance", "Assignments", "Notes", "Announcements", "Quizzes", "Reports", "Branding", "Settings"],
  teacher: ["My Classes", "Attendance", "Assignments", "Quizzes", "Notes", "Announcements", "Resources", "Students", "Gradebook", "Analytics"],
  student: ["My Classes", "Notifications", "Assignments", "Attendance", "Quizzes", "Announcements", "Resources", "Notes", "Results", "Analytics", "Calendar", "Profile"],
};

const defaultWebsite = {
  heroTitle: "Alpha Schools",
  heroSubtitle: "Form Five 2026/2027 Admission Open",
  heroImageUrl: alphaAssets.instantHero,
  heroBadges: "NECTA Results,Form I - VI,Science | Business | Arts,Alpha High School - Mikocheni,Alpha Girls High School - Kunduchi,Alpha Nursery & Primary - Kunduchi",
  aboutText: "Founded on 19th March 2007, Alpha Schools develops academic excellence through intellectual and physical challenge, preparing responsible citizens for Tanzania and beyond.",
  founderName: "Prof. Aloyce Mayo",
  founderTitle: "Founder of Alpha High School",
  founderMessage: "Alpha High School was established on 19th March 2007 with a vision of enabling students to achieve academic excellence through intellectual and physical challenges, while becoming responsible citizens of a dynamic society.",
  directorName: "Ms Fatina Said",
  directorTitle: "Managing Director",
  directorMessage: "Alpha Schools provides a safe, supportive and engaging environment that helps every learner discover their potential, become an effective communicator, an independent thinker and a technologically skilled member of the community.",
  whyChooseText: "Alpha Schools combines excellent academic facilities, highly skilled teachers, libraries and laboratories with practical opportunities that prepare students for further study, careers and responsible citizenship.",
  missionText: "To provide education that supports intellectual, spiritual, cultural and social growth.",
  visionText: "To nurture confident, responsible and innovative learners prepared to contribute locally and globally.",
  coreValues: "Excellence,Integrity,Discipline,Innovation,Responsibility,Community",
  schoolsText: "Alpha High School, Alpha Girls High School, and Alpha Nursery & Primary School provide focused learning communities for every stage of a learner's journey.",
  academicsText: "Alpha offers Science, Business and Arts pathways supported by practical learning, coding, aviation exposure, laboratories and extracurricular activities.",
  admissionsRequirements: "Completed application form, recent academic records, identification documents and successful completion of the school's admission assessment.",
  admissionsProcess: "Contact the admissions office, collect or request an application form, submit the required documents, complete the assessment and receive admission confirmation.",
  scholarshipText: "Scholarship opportunities, when available, are communicated by the Alpha Schools admissions office. Contact the school for current eligibility and application details.",
  facebookUrl: "",
  instagramUrl: "https://instagram.com/alphaschoolstz",
  youtubeUrl: "",
  admissionsText: "Application forms are available at Mikocheni (Alpha High School), Kunduchi (Alpha Girls High School & Alpha Nursery & Primary), and Msimbazi Centre Room No.19.",
  admissionPhone: alphaInfo.phone,
  contactPhone: alphaInfo.phone,
  contactEmail: alphaInfo.email,
  contactLocation: "Mikocheni · Kunduchi, Dar es Salaam, Tanzania",
  mapEmbedUrl: "https://www.google.com/maps?q=Alpha%20Schools%20Dar%20es%20Salaam%20Tanzania&output=embed",
};

const defaultNews = [
  { id: "admissions-open", title: "Form Five 2026/2027 Admission Open", body: "Applications are open at Alpha High School (Mikocheni), Alpha Girls High School (Kunduchi), and Msimbazi Centre Room No.19.", tag: "Admissions" },
  { id: "results-2026", title: "ACSEE 2026 Examination Results", body: "Alpha Girls High School and Alpha High School celebrate strong Division I and II performance.", tag: "Results" },
  { id: "school-events", title: "Campus Events and Student Life", body: "Follow Alpha Schools for academic, practical, sports, and community updates.", tag: "Events" },
];

const defaultGallery = [
  { id: "campus", title: "Campus", imageUrl: alphaAssets.lockup },
  { id: "students", title: "Students", imageUrl: alphaAssets.logo },
  { id: "practicals", title: "Practicals", imageUrl: alphaAssets.lockup },
  { id: "sports", title: "Sports", imageUrl: alphaAssets.logo },
  { id: "admissions", title: "Admissions", imageUrl: alphaAssets.lockup },
  { id: "events", title: "Events", imageUrl: alphaAssets.logo },
];

function normalizeRole(role) {
  if (role === "admin") return "schoolAdmin";
  if (role === "super_admin") return "superAdmin";
  return role || "student";
}

function normalizeRoles(profile) {
  const roles = Array.isArray(profile?.roles) ? profile.roles : [profile?.role];
  return Array.from(new Set(roles.map(normalizeRole).filter((role) => ["superAdmin", "schoolAdmin", "teacher", "student"].includes(role))));
}

function isSuperAdminEmail(email) {
  return String(email || "").trim().toLowerCase() === SUPER_ADMIN_EMAIL;
}

function emailDocId(email) {
  return String(email || "").trim().toLowerCase();
}

function cleanStudentId(value) {
  return String(value || "").trim().toLowerCase().replace(/[^a-z0-9._-]/g, "-");
}

function syntheticStudentEmail(studentId) {
  return `${cleanStudentId(studentId)}@alpha.stea.africa`;
}

function isSyntheticStudentEmail(value) {
  return String(value || "").trim().toLowerCase().endsWith("@alpha.stea.africa");
}

function visibleLoginIdentifier(value) {
  const raw = String(value || "").trim().toLowerCase();
  return isSyntheticStudentEmail(raw) ? raw.replace("@alpha.stea.africa", "") : raw;
}

function profileDisplayName(profile) {
  if (profile?.fullName) return profile.fullName;
  if (normalizeRole(profile?.role) === "student" && profile?.studentId) return `Student ID: ${profile.studentId}`;
  return profile?.email || "Alpha user";
}

function sanitizeStudentLoginText(value) {
  return String(value || "").replace(/\b([a-z0-9._-]+)@alpha\.stea\.africa\b/gi, "$1");
}

function isPortalPath(pathname) {
  if (!pathname || pathname === "/") return false;
  return pathname === "/portal" || pathname.startsWith("/portal/") || pathname === "/alpha" || pathname.startsWith("/alpha/");
}

function legacyAlphaRedirect(pathname) {
  if (pathname === "/alpha" || pathname === "/alpha/") return "/portal";
  if (pathname.startsWith("/alpha/super-admin")) return pathname.replace("/alpha/super-admin", "/portal/super-admin");
  if (pathname.startsWith("/alpha/admin")) return pathname.replace("/alpha/admin", "/portal/admin");
  if (pathname.startsWith("/alpha/teacher")) return pathname.replace("/alpha/teacher", "/portal/teacher");
  if (pathname.startsWith("/alpha/student")) return pathname.replace("/alpha/student", "/portal/student");
  if (pathname.startsWith("/alpha/auth")) return "/portal";
  return "";
}

function routeRole(pathname) {
  const path = pathname.startsWith("/alpha") ? legacyAlphaRedirect(pathname) || pathname : pathname;
  if (path.startsWith("/portal/super-admin") || path.startsWith("/super-admin")) return "superAdmin";
  if (path.startsWith("/portal/admin") || path.startsWith("/admin")) return "schoolAdmin";
  if (path.startsWith("/portal/teacher") || path.startsWith("/teacher")) return "teacher";
  if (path.startsWith("/portal/student") || path.startsWith("/student")) return "student";
  return null;
}

function roleLabel(role) {
  return ({ superAdmin: "Super Admin", schoolAdmin: "School Admin", teacher: "Teacher", student: "Student" }[role] || "User");
}

function slugify(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function makeCode(prefix = "ALPHA") {
  return `${prefix}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
}

function formatDate(value) {
  if (!value) return "Not recorded";
  const seconds = Number(value?.seconds ?? value?._seconds);
  const date = value?.toDate ? value.toDate() : Number.isFinite(seconds) ? new Date(seconds * 1000) : new Date(value);
  return Number.isNaN(date.getTime()) ? "Not recorded" : date.toLocaleString();
}

function recordTime(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  const seconds = Number(value?.seconds ?? value?._seconds);
  if (Number.isFinite(seconds)) return (seconds * 1000) + Math.floor(Number(value?.nanoseconds ?? value?._nanoseconds ?? 0) / 1e6);
  const date = value?.toDate ? value.toDate() : new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? 0 : time;
}

function itemCreatedTime(item) {
  return Math.max(recordTime(item?.createdAt), recordTime(item?.openedAt), recordTime(item?.updatedAt));
}

function safeFileName(value) {
  return String(value || "file")
    .trim()
    .replace(/[^a-zA-Z0-9._-]/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 120);
}

function formatFileSize(bytes) {
  const size = Number(bytes || 0);
  if (!size) return "";
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

function isDueSoon(dateValue) {
  if (!dateValue) return false;
  const due = new Date(`${dateValue}T23:59:59`);
  const time = due.getTime();
  if (Number.isNaN(time)) return false;
  const now = Date.now();
  return time >= now && time <= now + 7 * 24 * 60 * 60 * 1000;
}

function assignmentStats(assignments = [], submissions = []) {
  const assignmentIds = new Set(assignments.map((item) => item.id).filter(Boolean));
  const relevantSubmissions = submissions.filter((item) => assignmentIds.has(item.assignmentId));
  return [
    { label: "Total Assignments", value: assignments.length },
    { label: "Active Assignments", value: assignments.filter((item) => (item.status || "active") === "active").length },
    { label: "Draft Assignments", value: assignments.filter((item) => (item.status || "active") === "draft").length },
    { label: "Due Soon", value: assignments.filter((item) => isDueSoon(item.dueDate)).length },
    { label: "Total Submissions", value: relevantSubmissions.length },
    { label: "Pending Marking", value: relevantSubmissions.filter((item) => !["graded", "marked", "returned"].includes(String(item.status || "").toLowerCase())).length },
    { label: "Average Score", value: relevantSubmissions.filter((item) => Number.isFinite(Number(item.score))).length ? Math.round((relevantSubmissions.reduce((sum, item) => sum + (Number(item.score) || 0), 0) / relevantSubmissions.filter((item) => Number.isFinite(Number(item.score))).length) * 10) / 10 : 0 },
  ];
}

function classStudentIds(classItem = {}) {
  const raw = [
    ...(classItem.studentUids || []),
    ...(classItem.studentIds || []),
    ...(classItem.assignedStudentIds || []),
    ...(classItem.students || []),
    ...(classItem.classStudents || []),
  ];
  return Array.from(new Set(raw.map((student) => (
    typeof student === "string"
      ? student
      : student?.uid || student?.id || student?.studentUid || student?.studentId || ""
  )).filter(Boolean)));
}

function assignmentAnalytics(assignments = [], submissions = [], classes = []) {
  const assignmentIds = new Set(assignments.map((item) => item.id).filter(Boolean));
  const relevantSubmissions = submissions.filter((item) => assignmentIds.has(item.assignmentId));
  const classById = new Map(classes.map((item) => [item.id, item]));
  const assignedKeys = new Set();
  assignments.forEach((assignment) => {
    const classItem = classById.get(assignment.classId);
    const students = classStudentIds(classItem);
    students.forEach((uid) => assignedKeys.add(`${assignment.id}:${uid}`));
  });
  const graded = relevantSubmissions.filter((item) => String(item.status || "").toLowerCase() === "graded");
  const scores = graded.map((item) => Number(item.score ?? item.grade)).filter((value) => Number.isFinite(value));
  return {
    totalAssignedStudents: assignedKeys.size,
    submitted: relevantSubmissions.length,
    missing: Math.max(0, assignedKeys.size - relevantSubmissions.length),
    notSubmitted: Math.max(0, assignedKeys.size - relevantSubmissions.length),
    late: relevantSubmissions.filter((item) => String(item.status || "").toLowerCase() === "late" || item.isLate).length,
    graded: graded.length,
    pending: relevantSubmissions.filter((item) => String(item.status || "submitted").toLowerCase() !== "graded").length,
    averageScore: scores.length ? Math.round((scores.reduce((sum, score) => sum + score, 0) / scores.length) * 10) / 10 : 0,
    highestScore: scores.length ? Math.max(...scores) : 0,
    lowestScore: scores.length ? Math.min(...scores) : 0,
  };
}

function readableFirebaseError(error) {
  const code = String(error?.code || "");
  const friendly = {
    "storage/unauthorized": "You do not have permission to upload this file. Refresh the portal and sign in again.",
    "storage/canceled": "The upload was cancelled.",
    "storage/retry-limit-exceeded": "The upload took too long. Check your connection and try again.",
    "storage/invalid-format": "This file type is not supported.",
    "permission-denied": "You do not have permission to complete this action.",
    "firestore/permission-denied": "You do not have permission to complete this action.",
    "unavailable": "The service is temporarily unavailable. Check your connection and try again.",
  }[code];
  return friendly || error?.message || "Something went wrong. Please try again.";
}

async function uploadAlphaLearningFile(file, { classId, collectionName, userUid, prefix = "", onProgress } = {}) {
  if (!file) return {};
  const fileName = `${prefix}${Date.now()}-${safeFileName(file.name)}`;
  const path = collectionName === "submissions"
    ? `alpha/${classId || "general"}/submissions/${userUid || "student"}/${fileName}`
    : `alpha/${collectionName}/${classId || "general"}/${fileName}`;
  const storageRef = ref(storage, path);
  const snapshot = await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, { contentType: file.type || "application/octet-stream" });
    task.on("state_changed", (state) => {
      if (onProgress) onProgress(Math.round((state.bytesTransferred / state.totalBytes) * 100));
    }, reject, () => resolve(task.snapshot));
  });
  const fileUrl = await getDownloadURL(snapshot.ref);
  return {
    fileUrl,
    fileName: file.name,
    fileType: file.type || "application/octet-stream",
    fileSize: file.size || 0,
    storagePath: path,
  };
}

async function uploadAlphaWebsiteImage(file, { section = "general", onProgress } = {}) {
  if (!file) throw new Error("Choose an image from your device.");
  if (!file.type?.startsWith("image/")) throw new Error("Only image files are allowed here.");
  if (file.size > 15 * 1024 * 1024) throw new Error("Image must be smaller than 15 MB.");
  const safeName = safeFileName(file.name || "image");
  const path = `alpha/website/${section}/${Date.now()}-${safeName}`;
  const storageRef = ref(storage, path);
  const task = uploadBytesResumable(storageRef, file, { contentType: file.type });
  await new Promise((resolve, reject) => task.on("state_changed", (snapshot) => {
    onProgress?.(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
  }, reject, resolve));
  return { imageUrl: await getDownloadURL(task.snapshot.ref), storagePath: path };
}

async function createAlphaNotification(payload) {
  try {
    const db = getFirebaseDb();
    const { classId, recipientUids, actorUid, type, title, message, module: notifModule, itemId, actionUrl, studentUid: singleStudentUid, className } = payload;
    let uids = [];
    if (Array.isArray(recipientUids) && recipientUids.length) {
      uids = recipientUids;
    } else if (singleStudentUid) {
      uids = [singleStudentUid];
    } else if (classId) {
      try {
        const classSnap = await getDoc(doc(db, "schools", SCHOOL_ID, "classes", classId));
        if (classSnap.exists()) uids = classSnap.data().studentUids || [];
      } catch (e) { /* class lookup failed */ }
    }
    if (!uids.length) {
      console.warn("[AlphaClassroom] notification skipped: no recipients", { type, classId });
      return { ok: false, reason: "no-recipients" };
    }
    const basePayload = {
      schoolId: SCHOOL_ID,
      actorUid: actorUid || "system",
      type: type || "generic",
      title: title || "Notification",
      message: message || "",
      read: false,
      status: "unread",
      classId: classId || null,
      className: className || null,
      module: notifModule || null,
      itemId: itemId || null,
      actionUrl: actionUrl || null,
      createdAt: serverTimestamp(),
    };
    const batch = writeBatch(db);
    uids.forEach((uid) => {
      const notifRef = doc(collection(db, "schools", SCHOOL_ID, "notifications"));
      batch.set(notifRef, { ...basePayload, recipientUid: uid });
    });
    await batch.commit();
    return { ok: true, count: uids.length };
  } catch (error) {
    console.error("[AlphaClassroom] notification failed", error.code || error.message);
    return { ok: false, reason: error.code || error.message };
  }
}

async function markAlphaNotificationRead(notificationId) {
  try {
    await updateDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "notifications", notificationId), {
      read: true,
      status: "read",
      readAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error("[AlphaClassroom] mark-read failed", error.code || error.message);
    return false;
  }
}

function useAlphaStudentNotifications(studentUid) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!studentUid) { setLoading(false); return undefined; }
    setLoading(true);
    const q = query(
      collection(getFirebaseDb(), "schools", SCHOOL_ID, "notifications"),
      where("recipientUid", "==", studentUid),
      orderBy("createdAt", "desc")
    );
    return onSnapshot(q, (snap) => {
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (error) => {
      console.error("[AlphaClassroom] notifications listener failed", error.code || error.message);
      setLoading(false);
    });
  }, [studentUid]);
  const unreadCount = items.filter((item) => !item.read && item.status !== "read").length;
  return { items, loading, unreadCount };
}

function readStateKey(studentUid, collectionName) {
  return `alpha_read_${studentUid}_${collectionName}`;
}

function getReadState(studentUid, collectionName) {
  if (!studentUid || typeof localStorage === "undefined") return 0;
  return Number(localStorage.getItem(readStateKey(studentUid, collectionName)) || 0) || 0;
}

function setReadState(studentUid, collectionName, value) {
  if (!studentUid || typeof localStorage === "undefined") return;
  localStorage.setItem(readStateKey(studentUid, collectionName), String(value || Date.now()));
  setDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "readStates", studentUid), {
    userId: studentUid,
    schoolId: SCHOOL_ID,
    modules: { [collectionName]: Number(value || Date.now()) },
    updatedAt: serverTimestamp(),
  }, { merge: true }).catch((error) => console.warn("[AlphaClassroom] read-state sync failed", error.code || error.message));
  if (typeof window !== "undefined") window.dispatchEvent(new Event("alpha-read-state-change"));
}

function isUnreadForStudent(studentUid, collectionName, item) {
  const created = itemCreatedTime(item);
  return Boolean(created && created > getReadState(studentUid, collectionName));
}

function useAlphaReadVersion() {
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((current) => current + 1), []);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.addEventListener("storage", bump);
    window.addEventListener("alpha-read-state-change", bump);
    return () => {
      window.removeEventListener("storage", bump);
      window.removeEventListener("alpha-read-state-change", bump);
    };
  }, [bump]);
  return [version, bump];
}

function useFirebaseReadState(studentUid) {
  useEffect(() => {
    if (!studentUid) return undefined;
    return onSnapshot(doc(getFirebaseDb(), "schools", SCHOOL_ID, "readStates", studentUid), (snapshot) => {
      const modules = snapshot.data()?.modules || {};
      Object.entries(modules).forEach(([name, value]) => localStorage.setItem(readStateKey(studentUid, name), String(Number(value || 0))));
      window.dispatchEvent(new Event("alpha-read-state-change"));
    }, (error) => console.warn("[AlphaClassroom] read-state load failed", error.code || error.message));
  }, [studentUid]);
}

function useAlphaStudentFeed(authUser, enabled = true) {
  const [feed, setFeed] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!enabled || !authUser) return undefined;
    let active = true;
    async function load() {
      try {
        const token = await authUser.getIdToken();
        const response = await fetch(`${ALPHA_API_BASE}/api/alpha/student-feed`, { headers: { Authorization: `Bearer ${token}` } });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.message || "Student content could not be loaded.");
        if (active) { setFeed(result.feed || {}); setError(""); }
      } catch (loadError) {
        if (active) setError(loadError.message || "Student content could not be loaded.");
      }
    }
    load();
    const timer = window.setInterval(load, 15000);
    window.addEventListener("focus", load);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener("focus", load); };
  }, [authUser, enabled]);
  return { feed, error };
}

function latestRecordTime(record) {
  return Math.max(recordTime(record?.updatedAt), recordTime(record?.createdAt), recordTime(record?.attachedAt));
}

function isAuthUidRecord(record) {
  const id = String(record?.uid || record?.id || "");
  return Boolean(id && !id.startsWith("student-") && !id.startsWith("teacher-") && !id.includes("@"));
}

function studentDedupeKey(record) {
  const explicitId = cleanStudentId(record?.studentId);
  if (explicitId) return explicitId;
  const email = String(record?.email || "").toLowerCase();
  return email.endsWith("@alpha.stea.africa") ? cleanStudentId(email.split("@")[0]) : "";
}

function userDedupeKey(record, role) {
  if (role === "student") return studentDedupeKey(record);
  return String(record?.email || "").trim().toLowerCase();
}

function preferredAlphaUser(current, next, role) {
  if (!current) return next;
  if (role === "student") {
    if (Boolean(next.passwordCreated) !== Boolean(current.passwordCreated)) return next.passwordCreated ? next : current;
    if (isAuthUidRecord(next) !== isAuthUidRecord(current)) return isAuthUidRecord(next) ? next : current;
    return latestRecordTime(next) >= latestRecordTime(current) ? next : current;
  }
  if (isAuthUidRecord(next) !== isAuthUidRecord(current)) return isAuthUidRecord(next) ? next : current;
  if (Boolean(next.passwordCreated) !== Boolean(current.passwordCreated)) return next.passwordCreated ? next : current;
  return latestRecordTime(next) >= latestRecordTime(current) ? next : current;
}

function dedupeAlphaUsers(rows, role, options = {}) {
  const visibleClassIds = options.visibleClassIds || [];
  const groups = new Map();
  const loose = [];

  rows.forEach((row) => {
    const normalized = { ...row, role: normalizeRole(row.role), roles: normalizeRoles(row), status: row.status || "active" };
    if (!normalized.roles.includes(role) || ["claimed", "duplicate", "deleted", "replaced"].includes(normalized.status)) return;
    if (role === "student" && visibleClassIds.length) {
      const studentClassIds = [normalized.classId, ...(normalized.classIds || [])].filter(Boolean);
      if (!studentClassIds.some((classId) => visibleClassIds.includes(classId))) return;
    }
    const key = userDedupeKey(normalized, role);
    if (!key) {
      loose.push(normalized);
      return;
    }
    groups.set(key, preferredAlphaUser(groups.get(key), normalized, role));
  });

  return [...groups.values(), ...loose].sort((a, b) => latestRecordTime(b) - latestRecordTime(a));
}

async function apiRequest(authUser, path, options = {}) {
  const token = await authUser.getIdToken();
  const response = await fetch(path, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok === false) {
    throw new Error(data.message || data.error || data.detail || `Request failed: ${response.status}`);
  }
  return data;
}

function useTenantCollection(name, enabled = true) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      return undefined;
    }
    const db = getFirebaseDb();
    const q = query(collection(db, "schools", SCHOOL_ID, name), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      setItems(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
      setError("");
    }, (err) => {
      console.error("[AlphaClassroom] collection read failed", { name, code: err.code, message: err.message });
      setError(err.message);
    });
  }, [name, enabled]);

  return { items, error };
}

function useTenantClassCollection(name, classIds, enabled = true) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const key = (classIds || []).filter(Boolean).sort().join("|");

  useEffect(() => {
    const ids = key ? key.split("|") : [];
    if (!enabled || !ids.length) {
      setItems([]);
      return undefined;
    }
    const db = getFirebaseDb();
    const byId = new Map();
    const unsubscribers = ids.map((classId) => {
      const q = query(collection(db, "schools", SCHOOL_ID, name), where("classId", "==", classId));
      return onSnapshot(q, (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "removed") byId.delete(change.doc.id);
          else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
        });
        setItems(Array.from(byId.values()).sort((a, b) => recordTime(b.createdAt || b.updatedAt) - recordTime(a.createdAt || a.updatedAt)));
        setError("");
      }, (err) => {
        console.error("[AlphaClassroom] class collection read failed", { name, classId, code: err.code, message: err.message });
        setError(err.message);
      });
    });
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [name, key, enabled]);

  return { items, error };
}

function useAlphaTeacherAssignments(teacherUid, classIds, enabled = true) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const key = (classIds || []).filter(Boolean).sort().join("|");

  useEffect(() => {
    const ids = key ? key.split("|") : [];
    if (!enabled || !teacherUid) {
      setItems([]);
      return undefined;
    }
    const db = getFirebaseDb();
    const byId = new Map();
    const publish = () => {
      setItems(Array.from(byId.values()).sort((a, b) => recordTime(b.createdAt || b.updatedAt) - recordTime(a.createdAt || a.updatedAt)));
      setError("");
    };
    const assignCol = collection(db, "schools", SCHOOL_ID, "assignments");
    const unsubscribers = [
      onSnapshot(query(assignCol, where("teacherId", "==", teacherUid)), (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "removed") byId.delete(change.doc.id);
          else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
        });
        publish();
      }, (err) => {
        console.error("[AlphaClassroom] teacher assignments read failed", { code: err.code, message: err.message });
        setError(err.message);
      }),
      onSnapshot(query(assignCol, where("createdByUid", "==", teacherUid)), (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "removed") byId.delete(change.doc.id);
          else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
        });
        publish();
      }, (err) => {
        console.error("[AlphaClassroom] teacher assignments createdByUid read failed", { code: err.code, message: err.message });
        setError(err.message);
      }),
      onSnapshot(query(assignCol, where("createdBy", "==", teacherUid)), (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "removed") byId.delete(change.doc.id);
          else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
        });
        publish();
      }, (err) => {
        console.error("[AlphaClassroom] teacher assignments createdBy read failed", { code: err.code, message: err.message });
        setError(err.message);
      }),
      onSnapshot(query(assignCol, where("ownerId", "==", teacherUid)), (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "removed") byId.delete(change.doc.id);
          else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
        });
        publish();
      }, (err) => {
        console.error("[AlphaClassroom] teacher assignments ownerId read failed", { code: err.code, message: err.message });
        setError(err.message);
      }),
      onSnapshot(query(assignCol, where("assignedTeacherUid", "==", teacherUid)), (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "removed") byId.delete(change.doc.id);
          else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
        });
        publish();
      }, (err) => {
        console.error("[AlphaClassroom] teacher assignments assignedTeacherUid read failed", { code: err.code, message: err.message });
        setError(err.message);
      }),
      ...ids.map((classId) => onSnapshot(query(collection(db, "schools", SCHOOL_ID, "assignments"), where("classId", "==", classId)), (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "removed") byId.delete(change.doc.id);
          else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
        });
        publish();
      }, (err) => {
        console.error("[AlphaClassroom] teacher class assignments read failed", { classId, code: err.code, message: err.message });
        setError(err.message);
      })),
    ];
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [teacherUid, key, enabled]);

  return { items, error };
}

function useAlphaTeacherQuizzes(teacherUid, classIds, enabled = true) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const key = (classIds || []).filter(Boolean).sort().join("|");

  useEffect(() => {
    const ids = key ? key.split("|") : [];
    if (!enabled || (!teacherUid && !ids.length)) {
      setItems([]);
      return undefined;
    }
    const db = getFirebaseDb();
    const byId = new Map();
    const publish = () => {
      setItems(Array.from(byId.values()).sort((a, b) => recordTime(b.createdAt || b.updatedAt) - recordTime(a.createdAt || a.updatedAt)));
      setError("");
    };
    const quizCol = collection(db, "schools", SCHOOL_ID, "quizzes");
    const unsubscribers = [
      teacherUid ? onSnapshot(query(quizCol, where("teacherId", "==", teacherUid)), (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "removed") byId.delete(change.doc.id);
          else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
        });
        publish();
      }, (err) => {
        console.error("[AlphaClassroom] teacher quizzes read failed", { code: err.code, message: err.message });
        setError(err.message);
      }) : null,
      teacherUid ? onSnapshot(query(quizCol, where("createdByUid", "==", teacherUid)), (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "removed") byId.delete(change.doc.id);
          else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
        });
        publish();
      }, (err) => {
        console.error("[AlphaClassroom] teacher quizzes createdByUid read failed", { code: err.code, message: err.message });
        setError(err.message);
      }) : null,
      teacherUid ? onSnapshot(query(quizCol, where("createdBy", "==", teacherUid)), (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "removed") byId.delete(change.doc.id);
          else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
        });
        publish();
      }, (err) => {
        console.error("[AlphaClassroom] teacher quizzes createdBy read failed", { code: err.code, message: err.message });
        setError(err.message);
      }) : null,
      teacherUid ? onSnapshot(query(quizCol, where("ownerId", "==", teacherUid)), (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "removed") byId.delete(change.doc.id);
          else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
        });
        publish();
      }, (err) => {
        console.error("[AlphaClassroom] teacher quizzes ownerId read failed", { code: err.code, message: err.message });
        setError(err.message);
      }) : null,
      teacherUid ? onSnapshot(query(quizCol, where("assignedTeacherUid", "==", teacherUid)), (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "removed") byId.delete(change.doc.id);
          else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
        });
        publish();
      }, (err) => {
        console.error("[AlphaClassroom] teacher quizzes assignedTeacherUid read failed", { code: err.code, message: err.message });
        setError(err.message);
      }) : null,
      ...ids.map((classId) => onSnapshot(query(collection(db, "schools", SCHOOL_ID, "quizzes"), where("classId", "==", classId)), (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "removed") byId.delete(change.doc.id);
          else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
        });
        publish();
      }, (err) => {
        console.error("[AlphaClassroom] teacher class quizzes read failed", { classId, code: err.code, message: err.message });
        setError(err.message);
      })),
    ].filter(Boolean);
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [teacherUid, key, enabled]);

  return { items, error };
}

function useAlphaTeacherCollection(name, teacherUid, classIds, enabled = true) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const key = (classIds || []).filter(Boolean).sort().join("|");

  useEffect(() => {
    const ids = key ? key.split("|") : [];
    if (!enabled || (!teacherUid && !ids.length)) {
      setItems([]);
      return undefined;
    }
    const db = getFirebaseDb();
    const byId = new Map();
    const publish = () => {
      setItems(Array.from(byId.values()).sort((a, b) => recordTime(b.createdAt || b.updatedAt || b.openedAt) - recordTime(a.createdAt || a.updatedAt || a.openedAt)));
      setError("");
    };
    const mergeSnap = (snap) => {
      snap.docChanges().forEach((change) => {
        if (change.type === "removed") byId.delete(change.doc.id);
        else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
      });
      publish();
    };
    const collectionRef = collection(db, "schools", SCHOOL_ID, name);
    const unsubscribers = [
      teacherUid ? onSnapshot(query(collectionRef, where("teacherId", "==", teacherUid)), mergeSnap, (err) => {
        console.error("[AlphaClassroom] teacher collection read failed", { module: name, operation: "listen-teacherId", collection: name, userUid: teacherUid, role: "teacher", schoolId: SCHOOL_ID, code: err.code, message: err.message });
        setError(err.message);
      }) : null,
      teacherUid ? onSnapshot(query(collectionRef, where("createdBy", "==", teacherUid)), mergeSnap, (err) => {
        console.error("[AlphaClassroom] teacher collection read failed", { module: name, operation: "listen-createdBy", collection: name, userUid: teacherUid, role: "teacher", schoolId: SCHOOL_ID, code: err.code, message: err.message });
        setError(err.message);
      }) : null,
      teacherUid ? onSnapshot(query(collectionRef, where("ownerId", "==", teacherUid)), mergeSnap, (err) => {
        console.error("[AlphaClassroom] teacher collection read failed", { module: name, operation: "listen-ownerId", collection: name, userUid: teacherUid, role: "teacher", schoolId: SCHOOL_ID, code: err.code, message: err.message });
        setError(err.message);
      }) : null,
      ...ids.map((classId) => onSnapshot(query(collectionRef, where("classId", "==", classId)), mergeSnap, (err) => {
        console.error("[AlphaClassroom] teacher class collection read failed", { module: name, operation: "listen-classId", collection: name, classId, userUid: teacherUid, role: "teacher", schoolId: SCHOOL_ID, code: err.code, message: err.message });
        setError(err.message);
      })),
    ].filter(Boolean);
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [name, teacherUid, key, enabled]);

  return { items, error };
}

function useAlphaStudentSubmissions(studentUid, enabled = true) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!enabled || !studentUid) {
      setItems([]);
      return undefined;
    }
    const db = getFirebaseDb();
    const q = query(collection(db, "schools", SCHOOL_ID, "assignmentSubmissions"), where("studentUid", "==", studentUid));
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      rows.sort((a, b) => recordTime(b.submittedAt || b.createdAt) - recordTime(a.submittedAt || a.createdAt));
      setItems(rows);
      setError("");
    }, (err) => {
      console.error("[AlphaClassroom] student assignment submissions read failed", { code: err.code, message: err.message });
      setError(err.message);
    });
  }, [studentUid, enabled]);

  return { items, error };
}

function useTenantStudentAttempts(studentUid, enabled = true) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!enabled || !studentUid) {
      setItems([]);
      return undefined;
    }
    const db = getFirebaseDb();
    const q = query(collection(db, "schools", SCHOOL_ID, "quizAttempts"), where("studentUid", "==", studentUid));
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      rows.sort((a, b) => recordTime(b.submittedAt || b.createdAt) - recordTime(a.submittedAt || a.createdAt));
      setItems(rows);
      setError("");
    }, (err) => {
      console.error("[AlphaClassroom] student quiz attempts read failed", { code: err.code, message: err.message });
      setError(err.message);
    });
  }, [studentUid, enabled]);

  return { items, error };
}

function useAlphaStudentQuizzes(classIds, enabled = true) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const key = (classIds || []).filter(Boolean).sort().join("|");

  useEffect(() => {
    const ids = key ? key.split("|") : [];
    if (!enabled || !ids.length) {
      setItems([]);
      return undefined;
    }
    const db = getFirebaseDb();
    const byId = new Map();
    const unsubscribers = ids.map((classId) => {
      const q = query(collection(db, "schools", SCHOOL_ID, "quizzes"), where("status", "==", "published"), where("classId", "==", classId));
      return onSnapshot(q, (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "removed") byId.delete(change.doc.id);
          else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
        });
        const rows = Array.from(byId.values()).sort((a, b) => recordTime(b.createdAt || b.updatedAt) - recordTime(a.createdAt || a.updatedAt));
        setItems(rows);
        setError("");
      }, (err) => {
        console.error("[AlphaClassroom] student quizzes read failed", { code: err.code, message: err.message });
        setError(err.message);
      });
    });
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [key, enabled]);

  return { items, error };
}

function useAlphaClassQuizAttempts(classIds, enabled = true) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const key = (classIds || []).filter(Boolean).sort().join("|");

  useEffect(() => {
    const ids = key ? key.split("|") : [];
    if (!enabled || !ids.length) {
      setItems([]);
      return undefined;
    }
    const db = getFirebaseDb();
    const byId = new Map();
    const unsubscribers = ids.map((classId) => {
      const q = query(collection(db, "schools", SCHOOL_ID, "quizAttempts"), where("classId", "==", classId));
      return onSnapshot(q, (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === "removed") byId.delete(change.doc.id);
          else byId.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
        });
        const rows = Array.from(byId.values()).sort((a, b) => recordTime(b.submittedAt || b.createdAt) - recordTime(a.submittedAt || a.createdAt));
        setItems(rows);
        setError("");
      }, (err) => {
        console.error("[AlphaClassroom] class quiz attempts read failed", { code: err.code, message: err.message });
        setError(err.message);
      });
    });
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [key, enabled]);

  return { items, error };
}

function AlphaMark({ size = 48 }) {
  return (
    <span style={{ width: size, height: size, display: "grid", placeItems: "center", flex: "0 0 auto", filter: "drop-shadow(0 6px 14px rgba(6,42,88,.18))" }}>
      <img src={alphaAssets.logo} alt="Alpha Schools" width={size} height={size} style={{ width: size, height: size, objectFit: "contain" }} />
    </span>
  );
}

function Button({ children, onClick, type = "button", variant = "primary", disabled = false, busy = false }) {
  const styles = {
    primary: { background: `linear-gradient(135deg,${DARK},${BLUE})`, color: "#fff", border: `1px solid ${BLUE}`, boxShadow: "0 8px 20px rgba(11,78,162,.18)" },
    gold: { background: `linear-gradient(135deg,#FFE17A,${GOLD})`, color: DARK, border: `1px solid ${GOLD}`, boxShadow: "0 8px 20px rgba(247,201,72,.2)" },
    light: { background: "linear-gradient(180deg,#fff,#F8FAFD)", color: DARK, border: "1px solid #D5E1EE", boxShadow: "0 5px 14px rgba(15,45,85,.06)" },
    danger: { background: "linear-gradient(135deg,#B91C1C,#DC2626)", color: "#fff", border: "1px solid #B91C1C", boxShadow: "0 8px 20px rgba(185,28,28,.16)" },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled || busy} style={{ ...styles[variant], minHeight: 42, borderRadius: 11, padding: "9px 14px", fontWeight: 950, cursor: (disabled || busy) ? "not-allowed" : "pointer", opacity: (disabled || busy) ? 0.65 : 1 }}>
      {busy ? "Working..." : children}
    </button>
  );
}

function Field({ label, value, onChange, type = "text", required = false, placeholder = "" }) {
  return (
    <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>
      {label}
      <input type={type} value={value} onChange={(event) => onChange(event.target.value)} required={required} placeholder={placeholder} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", font: "inherit", fontWeight: 700, boxSizing: "border-box", width: "100%" }} />
    </label>
  );
}

function Select({ label, value, onChange, children }) {
  return (
    <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", font: "inherit", fontWeight: 700, background: "#fff" }}>
        {children}
      </select>
    </label>
  );
}

function Panel({ title, children, action }) {
  return (
    <section className="alpha-panel" style={{ background: "linear-gradient(180deg,#fff 0%,#FCFDFE 100%)", border: "1px solid #DCE6F1", borderRadius: 18, padding: 20, display: "grid", gap: 16, boxShadow: "0 16px 42px rgba(15,45,85,.07)" }}>
      <div className="alpha-panel-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, color: DARK, fontSize: 21, fontWeight: 950, display: "flex", alignItems: "center", gap: 10 }}><span aria-hidden="true" style={{ width: 5, height: 24, borderRadius: 999, background: `linear-gradient(${GOLD},#F59E0B)`, boxShadow: "0 0 0 4px rgba(247,201,72,.14)" }} />{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Table({ columns, rows, empty, rowStyle }) {
  if (!rows.length) return <AlphaEmptyState title={empty || "No records yet"} />;
  return (
    <div className="alpha-student-table alpha-table-wrap" style={{ overflowX: "auto", border: "1px solid #E2E8F0", borderRadius: 8 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720, fontSize: 13 }}>
        <thead style={{ background: LIGHT, color: DARK }}>
          <tr>{columns.map((col) => <th key={col.key} style={{ textAlign: "left", padding: 12, borderBottom: "1px solid #E2E8F0" }}>{col.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id || row.uid || row.code} style={{ borderBottom: "1px solid #EEF2F7", ...(rowStyle ? rowStyle(row) : {}) }}>
              {columns.map((col) => <td key={col.key} data-label={col.label} style={{ padding: 12, verticalAlign: "top", whiteSpace: col.wrap ? "normal" : undefined, overflowWrap: "anywhere", lineHeight: 1.45 }}>{col.render ? col.render(row) : row[col.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AlphaEmptyState({ title = "No records yet", body = "Create the first record and it will appear here immediately." }) {
  return (
    <div style={{ border: "1px dashed #AFC4DD", borderRadius: 16, padding: "clamp(24px,5vw,42px)", background: "radial-gradient(circle at 50% 0,#EFF6FF,#fff 70%)", textAlign: "center", display: "grid", justifyItems: "center", gap: 9 }}>
      <span style={{ width: 72, height: 72, borderRadius: 22, display: "grid", placeItems: "center", background: "#fff", boxShadow: "0 14px 32px rgba(11,78,162,.12)" }}><img src={alphaAssets.logo} alt="" width="58" height="58" loading="lazy" style={{ objectFit: "contain" }} /></span>
      <strong style={{ color: DARK, fontSize: 17 }}>{title}</strong>
      <span style={{ color: "#64748B", lineHeight: 1.45 }}>{body}</span>
    </div>
  );
}

function usePublicWebsiteContent() {
  const [settings, setSettings] = useState(() => {
    try { return { ...defaultWebsite, ...JSON.parse(localStorage.getItem("alpha_website_settings") || "{}") }; } catch { return defaultWebsite; }
  });
  const [gallery, setGallery] = useState(defaultGallery);
  const [news, setNews] = useState(defaultNews);
  const [heroImages, setHeroImages] = useState(() => {
    try { return JSON.parse(localStorage.getItem("alpha_hero_images") || "[]"); } catch { return []; }
  });

  useEffect(() => {
    const db = getFirebaseDb();
    let galleryRows = [];
    let galleryInitialized = false;
    const refreshGallery = () => setGallery(galleryRows.length ? galleryRows : (galleryInitialized ? [] : defaultGallery));
    const settingsUnsub = onSnapshot(doc(db, "schools", SCHOOL_ID, "publicWebsite", "settings"), (snap) => {
      const nextSettings = { ...defaultWebsite, ...(snap.exists() ? snap.data() : {}) };
      galleryInitialized = Boolean(nextSettings.galleryInitialized);
      setSettings(nextSettings);
      try { localStorage.setItem("alpha_website_settings", JSON.stringify(nextSettings)); } catch {}
      refreshGallery();
    }, (error) => console.warn("[AlphaWebsite] settings unavailable", error.message));
    const galleryUnsub = onSnapshot(collection(db, "schools", SCHOOL_ID, "publicWebsite", "gallery", "items"), (snap) => {
      galleryRows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      refreshGallery();
    }, (error) => console.warn("[AlphaWebsite] gallery unavailable", error.message));
    const newsUnsub = onSnapshot(collection(db, "schools", SCHOOL_ID, "publicWebsite", "news", "items"), (snap) => {
      const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      setNews(rows.length ? rows : defaultNews);
    }, (error) => console.warn("[AlphaWebsite] news unavailable", error.message));
    const heroUnsub = onSnapshot(collection(db, "schools", SCHOOL_ID, "publicWebsite", "hero", "items"), (snap) => {
      const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() })).filter((item) => item.imageUrl).slice(0, 5);
      setHeroImages(rows);
      try { localStorage.setItem("alpha_hero_images", JSON.stringify(rows.map(({ id, imageUrl, title }) => ({ id, imageUrl, title })))); } catch {}
    }, (error) => console.warn("[AlphaWebsite] hero images unavailable", error.message));
    return () => {
      settingsUnsub();
      galleryUnsub();
      newsUnsub();
      heroUnsub();
    };
  }, []);

  return { settings, gallery, news, heroImages };
}

function PublicAlphaWebsite() {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings, gallery, news, heroImages } = usePublicWebsiteContent();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [installHidden, setInstallHidden] = useState(() => Number(localStorage.getItem("alpha_install_dismissed_at") || 0) > Date.now() - 3 * 24 * 60 * 60 * 1000);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installMessage, setInstallMessage] = useState("");
  const heroBadges = String(settings.heroBadges || defaultWebsite.heroBadges).split(",").map((item) => item.trim()).filter(Boolean);
  const path = location.pathname;


  const go = (target) => {
    setMenuOpen(false);
    setOpenMenu(null);
    setMobileExpanded(null);
    navigate(target);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (!openMenu) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest("[data-dropdown]")) setOpenMenu(null);
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [openMenu]);

  useEffect(() => { setOpenMenu(null); }, [path]);

  const navConfig = [
    { label: "Home", target: "/" },
    { label: "About", target: "/about"},
    { label: "Academics", target: "/academics", menu: "academics", items: [
      { label: "Academic Programmes", target: "/academics" },
      { label: "Curriculum", target: "/academics" },
      { label: "Departments", target: "/academics" },
      { label: "Examinations", target: "/academics" },
      { label: "NECTA Results", target: "/necta-results" },
    ]},
    { label: "Schools", target: "/our-schools", menu: "schools", mega: true },
    { label: "Admissions", target: "/admissions", menu: "admissions", items: [
      { label: "Admissions Overview", target: "/admissions" },
      { label: "Admission Process", target: "/admissions" },
      { label: "Requirements", target: "/admissions" },
      { label: "Online Application", target: "/admissions" },
      { label: "Contact Admissions", target: "/contact" },
    ]},
    { label: "Student Life", target: "/student-life", menu: "student-life", items: [
      { label: "Student Life", target: "/student-life" },
      { label: "Sports & Athletics", target: "/student-life" },
      { label: "Clubs & Societies", target: "/student-life" },
      { label: "Arts & Culture", target: "/student-life" },
      { label: "Gallery", target: "/gallery" },
    ]},
    { label: "More", menu: "more", items: [
      { label: "News", target: "/news" },
      { label: "Contact", target: "/contact" },
    ]},
  ];

  const schoolPageIds = alphaInfo.schools.map(s => s.id);
  const pageOnly = path !== "/" && !schoolPageIds.includes(path.slice(1));

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  useEffect(() => {
    localStorage.removeItem("alpha_install_hidden");
    const capturePrompt = (event) => { event.preventDefault(); setInstallPrompt(event); setInstallHidden(false); };
    const installed = () => { setInstallHidden(true); setInstallPrompt(null); };
    window.addEventListener("beforeinstallprompt", capturePrompt);
    window.addEventListener("appinstalled", installed);
    return () => { window.removeEventListener("beforeinstallprompt", capturePrompt); window.removeEventListener("appinstalled", installed); };
  }, []);

  async function installAlphaApp() {
    if (installPrompt) {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (choice.outcome === "accepted") setInstallHidden(true);
      setInstallPrompt(null);
      return;
    }
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setInstallMessage(isIos ? "On iPhone: tap Share, then Add to Home Screen." : "Open your browser menu and choose Install app or Add to Home screen.");
  }

  return (
    <div style={{ background: "#F8FAFC", color: "#0F172A", minHeight: "100vh", paddingBottom: 74 }}>
      <style>{`
        .alpha-public-shell a{color:inherit;text-decoration:none}
        .alpha-public-wrap{width:min(1180px,100%);margin:0 auto;padding:0 16px}
        .alpha-public-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:16px}
        .alpha-public-card{background:#fff;border:1px solid #E2E8F0;border-radius:12px;padding:18px;box-shadow:0 14px 36px rgba(15,23,42,.06)}
        .alpha-public-section{padding:56px 0}
        .alpha-gallery-track{scrollbar-width:none}.alpha-gallery-track::-webkit-scrollbar{display:none}
        .alpha-why-alpha-track{scrollbar-width:none}.alpha-why-alpha-track::-webkit-scrollbar{display:none}
        @media(max-width:760px){
          .alpha-public-grid:has(img[width="1080"]),.alpha-public-grid:has(.alpha-public-card>button){grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:9px!important}
          .alpha-public-card:has(img[width="1080"]),.alpha-public-card:has(>button){padding:10px!important;border-radius:10px!important;gap:8px!important;align-content:start!important}
          .alpha-public-card:has(img[width="1080"]) h3{font-size:16px!important;margin:3px 0 5px!important}
          .alpha-public-card:has(img[width="1080"]) span{font-size:10px!important}
          .alpha-public-card:has(img[width="1080"]) p{font-size:11px!important;line-height:1.45!important;display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden}
          .alpha-public-card:has(>button) img{width:42px!important;height:42px!important}
          .alpha-public-card:has(>button) h3{font-size:14px!important;margin:6px 0!important}
          .alpha-public-card:has(>button) p{font-size:11px!important;line-height:1.4!important;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
          .alpha-public-card:has(>button) button{font-size:10px!important;padding:7px!important}
          div[style*="repeat(auto-fit,minmax(230px,1fr))"]{grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:9px!important}
          div[style*="repeat(auto-fit,minmax(230px,1fr))"] img{border-radius:9px!important}
          .alpha-why-alpha-track{display:flex!important;flex-wrap:nowrap!important;overflow-x:auto!important;overflow-y:hidden!important;grid-template-columns:none!important;gap:10px!important;padding-bottom:8px!important;scroll-snap-type:x mandatory;-webkit-overflow-scrolling:touch}
          .alpha-why-alpha-track>.alpha-public-card{flex:0 0 78%!important;min-width:78%!important;max-width:78%!important;scroll-snap-align:start}
          .alpha-why-alpha-track>.alpha-public-card>*{min-width:0}
          div[style*="repeat(auto-fit,minmax(230px,1fr))"] strong{font-size:12px!important;margin-top:5px!important}
          div[style*="repeat(auto-fit,minmax(230px,1fr))"] span{font-size:10px!important}
          .alpha-public-wrap>div:has(>button>img+strong+span){grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:9px!important}
          .alpha-public-wrap>div:has(>button>img+strong+span)>button{min-width:0!important;width:100%!important}
          .alpha-public-wrap>div:has(>button>img+strong+span)>button img{width:100%!important;aspect-ratio:1/1!important;object-fit:cover!important;border-radius:9px!important}
          .alpha-public-wrap>div:has(>button>img+strong+span)>button strong{font-size:12px!important;margin-top:5px!important;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
          .alpha-public-wrap>div:has(>button>img+strong+span)>button span{font-size:10px!important;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        }
        @media(max-width:1199px){.alpha-public-hide-mobile{display:none!important}.alpha-public-mobile-menu{display:grid!important}.alpha-public-top{display:none!important}}
        @media(max-width:760px){.alpha-public-hero{min-height:calc(100svh - 74px)!important;align-items:end!important;background:#051426!important}.alpha-public-hero>img{object-fit:cover!important;object-position:center center!important}.alpha-public-hero .alpha-public-wrap{padding:70px 12px 30px!important;background:transparent!important}.alpha-hero-copy{padding:18px!important;border:0!important;border-radius:14px!important;background:transparent!important;color:#fff!important;box-shadow:none!important;text-shadow:0 2px 7px rgba(0,0,0,.92)}.alpha-hero-copy h1{font-size:clamp(36px,12vw,58px)!important;color:#fff!important}.alpha-hero-copy .alpha-hero-subtitle{color:#fff!important}.alpha-hero-copy .alpha-hero-badge{color:#fff!important;background:rgba(6,42,88,.34)!important;border-color:rgba(255,255,255,.72)!important;text-shadow:0 1px 4px rgba(0,0,0,.9)}.alpha-gallery-track{grid-auto-columns:86%!important}.alpha-public-section{padding:38px 0}}
        @keyframes alphaDropdownIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
        .alpha-premium-dropdown{animation:alphaDropdownIn .2s ease-out}
        @keyframes alphaFadeIn{from{opacity:0}to{opacity:1}}
        .alpha-nav-item{position:relative;padding:6px 0;transition:color .15s ease}
        .alpha-nav-item::after{content:'';position:absolute;bottom:0;left:50%;width:0;height:2px;background:#E8A838;border-radius:2px;transform:translateX(-50%);transition:width .2s ease}
        .alpha-nav-item:hover::after,.alpha-nav-item.active::after{width:60%}
        .alpha-nav-item.active{color:#1A3A5C}
        .alpha-dropdown-arrow{display:inline-block;transition:transform .2s ease}
        .alpha-dropdown-arrow.open{transform:rotate(180deg)}
        .alpha-school-mega{display:grid;grid-template-columns:repeat(3,minmax(200px,1fr));gap:12px;padding:8px}
        .alpha-school-mega-card{border:1px solid rgba(26,58,92,.08);border-radius:14px;padding:16px;background:#fff;transition:transform .15s ease,box-shadow .15s ease}
        .alpha-school-mega-card:hover{transform:translateY(-2px);box-shadow:0 12px 30px rgba(15,23,42,.10)}
        .alpha-mobile-accordion{overflow:hidden;transition:max-height .2s ease}
      `}</style>
      <div className="alpha-public-shell">
        <header style={{ position: "sticky", top: 0, zIndex: 30, background: "rgba(255,255,255,.97)", borderBottom: "1px solid rgba(26,58,92,.08)", backdropFilter: "blur(12px)", boxShadow: "0 8px 30px rgba(0,0,0,.04)" }}>
          <div className="alpha-public-wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, minHeight: 80 }}>
            <button onClick={() => go("/")} style={{ border: 0, background: "transparent", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", textAlign: "left" }}>
              <AlphaMark size={42} />
              <span><strong style={{ display: "block", color: DARK, fontSize: 18 }}>Alpha Schools</strong><span style={{ color: "#64748B", fontWeight: 800, fontSize: 11 }}>{alphaInfo.motto}</span></span>
            </button>
            <nav className="alpha-public-hide-mobile" style={{ display: "flex", gap: 20, alignItems: "center", fontWeight: 900, fontSize: 13 }}>
              {navConfig.map((item) => {
                const isActive = item.target && path === item.target;
                if (!item.menu) {
                  return <button key={item.label} onClick={() => go(item.target)} className={`alpha-nav-item${isActive ? " active" : ""}`} style={linkButtonStyle(isActive ? BLUE : DARK)}>{item.label}</button>;
                }
                const menuKey = item.menu;
                const isOpen = openMenu === menuKey;
                return (
                  <div key={item.label} data-dropdown style={{ position: "relative" }}>
                    <button
                      onClick={() => setOpenMenu(isOpen ? null : menuKey)}
                      onMouseEnter={() => setOpenMenu(menuKey)}
                      className={`alpha-nav-item${isActive ? " active" : ""}`}
                      style={linkButtonStyle(isActive ? BLUE : DARK)}
                      aria-expanded={isOpen}
                      aria-haspopup="true"
                    >
                      {item.label}<span className={`alpha-dropdown-arrow${isOpen ? " open" : ""}`} style={{ marginLeft: 4, fontSize: 10 }}>▾</span>
                    </button>
                    {isOpen && item.mega && (
                      <div data-dropdown className="alpha-premium-dropdown alpha-school-mega" style={{ position: "absolute", top: "calc(100% + 10px)", left: "50%", transform: "translateX(-50%)", background: "#fff", border: "1px solid rgba(26,58,92,.08)", borderRadius: 16, boxShadow: "0 20px 50px rgba(0,0,0,.12)", zIndex: 1000, width: "max-content", maxWidth: "90vw" }} onMouseLeave={() => setOpenMenu(null)}>
                        {alphaInfo.schools.map((school) => (
                          <div key={school.id} className="alpha-school-mega-card" style={{ display: "grid", gap: 6, alignContent: "start" }}>
                            <div style={{ width: 40, height: 40, borderRadius: 10, background: `linear-gradient(135deg,${DARK},${BLUE})`, display: "grid", placeItems: "center", color: "#fff", fontWeight: 950, fontSize: 16 }}>{school.name.replace(/^Alpha\s+/, "").charAt(0)}</div>
                            <div style={{ fontWeight: 950, color: DARK, fontSize: 14 }}>{school.name}</div>
                            <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700 }}>{school.location} · {school.type}</div>
                            <div style={{ fontSize: 11, color: "#64748B" }}>{school.level}</div>
                            <div style={{ display: "grid", gap: 2, marginTop: 4 }}>
                              {[["Overview", `/${school.id}`], ["Academic Programmes", "/academics"], ["Student Life", "/student-life"], ["Gallery", "/gallery"]].map(([label, target]) => (
                                <button key={label} onClick={() => go(target)} style={{ border: 0, background: "transparent", color: DARK, fontWeight: 700, fontSize: 12, cursor: "pointer", textAlign: "left", padding: "2px 0" }} onMouseEnter={(e) => { e.currentTarget.style.color = BLUE; }} onMouseLeave={(e) => { e.currentTarget.style.color = DARK; }}>· {label}</button>
                              ))}
                            </div>
                            <button onClick={() => go(`/${school.id}`)} style={{ marginTop: 6, border: 0, background: "transparent", color: BLUE, fontWeight: 900, fontSize: 12, cursor: "pointer", textAlign: "left", padding: 0 }}>Explore School →</button>
                          </div>
                        ))}
                      </div>
                    )}
                    {isOpen && !item.mega && item.items && (
                      <div data-dropdown className="alpha-premium-dropdown" style={{ position: "absolute", top: "calc(100% + 10px)", left: "50%", transform: "translateX(-50%)", minWidth: 220, background: "#fff", border: "1px solid rgba(26,58,92,.08)", borderRadius: 14, boxShadow: "0 20px 50px rgba(0,0,0,.12)", padding: 8, zIndex: 1000 }} onMouseLeave={() => setOpenMenu(null)}>
                        {item.items.map((sub) => (
                          <button key={sub.label} onClick={() => go(sub.target)} style={{ display: "block", width: "100%", textAlign: "left", border: 0, background: "transparent", padding: "10px 12px", borderRadius: 8, color: DARK, fontWeight: 800, fontSize: 13, cursor: "pointer", transition: "background .15s ease" }} onMouseEnter={(e) => { e.currentTarget.style.background = "#F5F6FA"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>{sub.label}</button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
            <div className="alpha-public-hide-mobile" style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Button variant="light" onClick={() => go("/portal")}>Portal Login</Button>
              <Button variant="gold" onClick={() => go("/admissions")}>Apply Now</Button>
            </div>
            <button onClick={() => setMenuOpen(!menuOpen)} style={{ border: "1px solid #D8E2F0", background: "#fff", borderRadius: 8, padding: "9px 11px", color: DARK, fontWeight: 950, display: "none" }} className="alpha-public-mobile-menu">Menu</button>
          </div>
          {menuOpen && (
            <>
              <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.4)", zIndex: 99, animation: "alphaFadeIn .2s ease" }} />
              <div style={{ borderTop: "1px solid rgba(26,58,92,.08)", background: "#fff", padding: 12, display: "grid", gap: 6, maxHeight: "calc(100vh - 80px)", overflowY: "auto", position: "relative", zIndex: 100 }}>
                {navConfig.map((item) => {
                  if (!item.menu) {
                    return <button key={item.label} onClick={() => go(item.target)} style={{ border: "1px solid #E2E8F0", background: "#fff", borderRadius: 8, padding: 12, color: DARK, fontWeight: 900, textAlign: "left" }}>{item.label}</button>;
                  }
                  const menuKey = item.menu;
                  const expanded = mobileExpanded === menuKey;
                  return (
                    <div key={item.label}>
                      <button onClick={() => setMobileExpanded(expanded ? null : menuKey)} style={{ border: "1px solid #E2E8F0", background: "#fff", borderRadius: 8, padding: 12, color: DARK, fontWeight: 900, textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                        {item.label} <span style={{ fontSize: 11, color: "#64748B" }}>{expanded ? "▲" : "▼"}</span>
                      </button>
                      {expanded && item.mega && (
                        <div style={{ paddingLeft: 8, display: "grid", gap: 6, marginTop: 4 }}>
                          {alphaInfo.schools.map((school) => (
                            <button key={school.id} onClick={() => go(`/${school.id}`)} style={{ border: "1px solid #E2E8F0", background: "#F8FAFC", borderRadius: 8, padding: 10, color: DARK, fontWeight: 800, textAlign: "left", fontSize: 13 }}>
                              {school.name}
                              <span style={{ display: "block", fontSize: 11, color: "#64748B", fontWeight: 700 }}>{school.location} · {school.type}</span>
                            </button>
                          ))}
                          <button onClick={() => go("/our-schools")} style={{ border: "1px solid #E2E8F0", background: "#fff", borderRadius: 8, padding: 10, color: BLUE, fontWeight: 900, textAlign: "left", fontSize: 13 }}>All Schools →</button>
                        </div>
                      )}
                      {expanded && item.items && (
                        <div style={{ paddingLeft: 8, display: "grid", gap: 6, marginTop: 4 }}>
                          {item.items.map((sub) => (
                            <button key={sub.label} onClick={() => go(sub.target)} style={{ border: "1px solid #E2E8F0", background: "#F8FAFC", borderRadius: 8, padding: 10, color: DARK, fontWeight: 800, textAlign: "left", fontSize: 13 }}>{sub.label}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <button onClick={() => go("/portal")} style={{ border: "1px solid #E2E8F0", background: "#EFF6FF", borderRadius: 8, padding: 12, color: BLUE, fontWeight: 900, textAlign: "left" }}>Portal Login</button>
                <button onClick={() => go("/admissions")} style={{ border: "1px solid #E2E8F0", background: GOLD, borderRadius: 8, padding: 12, color: DARK, fontWeight: 900, textAlign: "left" }}>Apply Now</button>
              </div>
            </>
          )}
        </header>

        {(!pageOnly || path === "/") && <PublicHero settings={settings} heroImages={heroImages} badges={heroBadges} go={go} />}
        {(path === "/" || path === "/about") && <AboutSection settings={settings} />}
        {(path === "/" || path === "/academics") && <AcademicsSection settings={settings} />}
        {(path === "/" || path === "/our-schools") && <OurSchoolsSection go={go} settings={settings} />}
        {schoolPageIds.includes(path.slice(1)) && <SchoolDetailPage school={alphaInfo.schools.find(s => s.id === path.slice(1))} go={go} settings={settings} />}
        {(path === "/" || path === "/admissions") && <AdmissionsSection settings={settings} />}
        {(path === "/" || path === "/student-life") && <StudentLifeSection settings={settings} />}
        {(path === "/" || path === "/gallery") && <GallerySection gallery={gallery} />}
        {(path === "/" || path === "/news") && <NewsSection news={news} />}
        {(path === "/" || path === "/necta-results") && <NectaResultsSection go={go} />}
        {(path === "/" || path === "/contact") && <ContactSection settings={settings} />}
        {!["/", "/about", "/academics", "/admissions", "/our-schools", "/student-life", "/gallery", "/news", "/necta-results", "/contact", ...schoolPageIds.map(id => `/${id}`)].includes(path) && <PublicHero settings={settings} badges={heroBadges} go={go} />}
        <PublicFooter go={go} />
        {!installHidden && <div style={{ position: "fixed", left: 14, right: 14, bottom: 76, zIndex: 40, background: "#fff", border: "1px solid #D8E2F0", borderRadius: 12, padding: 12, display: "flex", alignItems: "center", gap: 12, boxShadow: "0 20px 50px rgba(15,23,42,.18)" }}>
          <AlphaMark size={42} />
          <div style={{ flex: 1 }}><strong style={{ color: DARK }}>Install Alpha Schools App</strong><div style={{ color: "#64748B", fontSize: 12 }}>{installMessage || "Access admissions, news, gallery, and portal faster."}</div></div>
          <Button variant="gold" onClick={installAlphaApp}>Install</Button>
          <button onClick={() => { localStorage.setItem("alpha_install_dismissed_at", String(Date.now())); setInstallHidden(true); }} aria-label="Dismiss install app banner" style={{ border: 0, background: "transparent", color: DARK, fontWeight: 950, cursor: "pointer" }}>X</button>
        </div>}
        <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 39, background: "#fff", borderTop: "1px solid #D8E2F0", display: "grid", gridTemplateColumns: "repeat(3,1fr)" }}>
          <a href={`tel:${alphaInfo.phone}`} style={mobileActionStyle}>Call</a>
          <a href={`https://wa.me/255734036010`} style={mobileActionStyle}>WhatsApp</a>
          <button onClick={() => go("/admissions")} style={{ ...mobileActionStyle, border: 0, color: DARK, background: GOLD }}>Apply</button>
        </div>
      </div>
    </div>
  );
}

function linkButtonStyle(color) {
  return { border: 0, background: "transparent", color, fontWeight: 950, cursor: "pointer", padding: 0 };
}

const mobileActionStyle = { minHeight: 58, display: "grid", placeItems: "center", color: BLUE, fontWeight: 950, textDecoration: "none", borderRight: "1px solid #E2E8F0", background: "#fff" };

function PublicHero({ settings, heroImages = [], badges, go }) {
  const configuredHero = settings.heroImageUrl && !settings.heroImageUrl.includes("alpha-classroom-lockup") ? settings.heroImageUrl : "";
  const slides = heroImages.length ? heroImages : (configuredHero ? [{ id: "primary", imageUrl: configuredHero, title: settings.heroTitle }] : []);
  const [activeSlide, setActiveSlide] = useState(0);
  const [imgErrors, setImgErrors] = useState({});
  useEffect(() => {
    setActiveSlide((current) => Math.min(current, Math.max(0, slides.length - 1)));
    if (slides.length < 2) return undefined;
    const timer = window.setInterval(() => setActiveSlide((current) => (current + 1) % slides.length), 5000);
    return () => window.clearInterval(timer);
  }, [slides.length]);
  useEffect(() => {
    if (slides.length < 2) return;
    const nextIdx = (activeSlide + 1) % slides.length;
    const nextUrl = slides[nextIdx]?.imageUrl;
    if (nextUrl && !imgErrors[nextUrl]) { const img = new Image(); img.src = nextUrl; }
  }, [activeSlide, slides, imgErrors]);
  const firstUrl = slides[0]?.imageUrl;
  useEffect(() => {
    if (firstUrl && !imgErrors[firstUrl]) { const pre = new Image(); pre.src = firstUrl; }
    if (slides.length > 1) { const second = new Image(); second.src = slides[1].imageUrl; }
  }, []);
  return (
    <section className="alpha-public-hero" style={{ minHeight: 660, display: "grid", alignItems: "center", position: "relative", overflow: "hidden", background: "#051426", color: "#fff" }}>
      {slides.map((slide, index) => <img key={slide.id || slide.imageUrl} src={slide.imageUrl} alt={slide.title || "Alpha Schools students"} width="1920" height="1080" fetchPriority={index === 0 ? "high" : "low"} loading={index === 0 ? "eager" : "lazy"} decoding={index === 0 ? "sync" : "async"} onError={() => setImgErrors((prev) => ({ ...prev, [slide.imageUrl]: true }))} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", opacity: imgErrors[slide.imageUrl] ? 0 : (index === activeSlide ? 1 : 0), transform: index === activeSlide ? "scale(1.06)" : "scale(1.02)", transition: "opacity 1.2s ease, transform 8s ease-out", filter: "none", WebkitFilter: "none" }} />)}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 1, background: "linear-gradient(90deg, rgba(5,20,38,0.82) 0%, rgba(5,20,38,0.68) 35%, rgba(5,20,38,0.38) 65%, rgba(5,20,38,0.18) 100%)" }} />
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 1, background: "linear-gradient(180deg, rgba(5,20,38,0.22) 0%, transparent 30%, transparent 55%, rgba(5,20,38,0.42) 100%)" }} />
      <div className="alpha-public-wrap" style={{ position: "relative", zIndex: 2, paddingTop: 80, paddingBottom: 80 }}>
        <div className="alpha-hero-copy" style={{ width: "min(680px,100%)", color: "#fff", padding: "clamp(20px,4vw,32px)", textShadow: "0 2px 7px rgba(0,0,0,.84)" }}>
          <p style={{ margin: "0 0 8px", color: GOLD, fontWeight: 950, textTransform: "uppercase", fontSize: 12, letterSpacing: 2 }}>Welcome to Alpha Schools</p>
          <span aria-hidden="true" style={{ display: "block", width: 44, height: 3, background: GOLD, borderRadius: 2, marginBottom: 14 }} />
          <h1 style={{ margin: 0, fontSize: "clamp(40px,8vw,72px)", lineHeight: .96, fontWeight: 950, letterSpacing: "-.02em" }}>{settings.heroTitle || "Alpha Schools"}</h1>
          <p style={{ margin: "14px 0 0", fontSize: "clamp(18px,4vw,26px)", fontWeight: 900, color: "#fff" }}>{alphaInfo.motto}</p>
          <p className="alpha-hero-subtitle" style={{ margin: "10px 0 0", color: "#E2E8F0", fontSize: 16, fontWeight: 600, maxWidth: 520 }}>A quality learning environment shaping confident, responsible and innovative learners.</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 26, alignItems: "center" }}>
            <Button variant="gold" onClick={() => go("/admissions")}>Apply Now</Button>
            <Button variant="light" onClick={() => go("/our-schools")}>Explore Our Schools</Button>
            <button onClick={() => go("/portal")} style={{ border: 0, background: "transparent", color: "#fff", fontWeight: 900, fontSize: 13, cursor: "pointer", padding: "10px 4px", textShadow: "0 1px 4px rgba(0,0,0,.6)" }}>View School Portal →</button>
          </div>
          <div style={{ marginTop: 20 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {alphaInfo.schools.map((s) => (
                <button key={s.id} onClick={() => go(`/${s.id}`)} style={{ border: "1px solid rgba(255,255,255,.35)", background: "rgba(255,255,255,.08)", color: "#fff", borderRadius: 999, padding: "7px 13px", fontWeight: 800, fontSize: 12, cursor: "pointer", transition: "background .15s ease" }} onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,.18)"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,.08)"; }}>{s.name}</button>
              ))}
            </div>
          </div>
          {slides.length > 1 && <div style={{ display: "flex", gap: 7, marginTop: 20 }}>{slides.map((slide, index) => <button key={slide.id || index} type="button" aria-label={`Show hero image ${index + 1}`} onClick={() => setActiveSlide(index)} style={{ width: index === activeSlide ? 30 : 9, height: 9, borderRadius: 999, border: 0, background: index === activeSlide ? GOLD : "rgba(255,255,255,.35)", cursor: "pointer", transition: "width .2s ease" }} />)}</div>}
        </div>
      </div>
    </section>
  );
}

function sectionBackground(imageUrl, fallback = "#fff") {
  return imageUrl ? `linear-gradient(rgba(255,255,255,.9),rgba(255,255,255,.9)),url(${imageUrl}) center/cover no-repeat` : fallback;
}

function AboutSection({ settings }) {
  const leaders = [
    { name: settings.founderName || defaultWebsite.founderName, title: settings.founderTitle || defaultWebsite.founderTitle, message: settings.founderMessage || defaultWebsite.founderMessage, image: settings.founderImageUrl },
    { name: settings.directorName || defaultWebsite.directorName, title: settings.directorTitle || defaultWebsite.directorTitle, message: settings.directorMessage || defaultWebsite.directorMessage, image: settings.directorImageUrl },
  ];
  const values = String(settings.coreValues || defaultWebsite.coreValues).split(",").map((item) => item.trim()).filter(Boolean);
  return <section className="alpha-public-section" style={{ background: sectionBackground(settings.aboutBackgroundUrl, "#F8FAFC") }}><div className="alpha-public-wrap" style={{ display: "grid", gap: 34 }}><div className="alpha-public-grid"><div><p style={eyebrow}>Our Story</p><h2 style={sectionTitle}>Founded 19th March 2007</h2><p style={sectionText}>{settings.aboutText || defaultWebsite.aboutText}</p></div><div className="alpha-public-card"><img src={settings.aboutImageUrl || alphaAssets.lockup} alt="Alpha Schools" loading="eager" decoding="async" style={{ width: "100%", height: "auto", objectFit: "contain" }} /></div></div><div className="alpha-public-grid"><div className="alpha-public-card"><p style={eyebrow}>Mission</p><p style={sectionText}>{settings.missionText || defaultWebsite.missionText}</p></div><div className="alpha-public-card"><p style={eyebrow}>Vision</p><p style={sectionText}>{settings.visionText || defaultWebsite.visionText}</p></div><div className="alpha-public-card"><p style={eyebrow}>Core Values</p><div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{values.map((value) => <span key={value} style={{ background: "#EFF6FF", color: BLUE, borderRadius: 999, padding: "8px 11px", fontWeight: 900 }}>{value}</span>)}</div></div></div><div><p style={eyebrow}>Leadership</p><h2 style={sectionTitle}>The People Behind Alpha Schools</h2><div className="alpha-public-grid">{leaders.map((leader) => <article className="alpha-public-card" key={leader.title} style={{ display: "grid", gap: 16, alignContent: "start" }}><img src={leader.image || alphaAssets.logo} alt={leader.name} loading="eager" decoding="async" width="1080" height="1080" style={{ display: "block", width: "100%", height: "auto", objectFit: "contain", borderRadius: 12, background: LIGHT }} /><div><span style={{ color: BLUE, fontWeight: 950 }}>{leader.title}</span><h3 style={{ color: DARK, fontSize: 24, margin: "5px 0 10px" }}>{leader.name}</h3><p style={sectionText}>{leader.message}</p></div></article>)}</div></div><div><p style={eyebrow}>Why Alpha</p><h2 style={sectionTitle}>Why Choose Our Institution?</h2><p style={{ ...sectionText, maxWidth: 850 }}>{settings.whyChooseText || defaultWebsite.whyChooseText}</p><div className="alpha-public-grid alpha-why-alpha-track" style={{ marginTop: 18 }}>{[["🎓", "Excellent Education", "Strong academic facilities and a disciplined learning environment."],["👩🏾‍🏫", "Highly Skilled Teachers", "Qualified educators committed to every learner's progress."],["🔬", "Libraries & Laboratories", "Resources for research, science, technology and practical learning."],["✈️", "Practical Opportunities", "Aviation, coding and extracurricular exposure that develops confidence."]].map(([icon,title,body]) => <div className="alpha-public-card" key={title}><span style={{ fontSize: 34 }}>{icon}</span><h3 style={{ color: DARK }}>{title}</h3><p style={sectionText}>{body}</p></div>)}</div></div></div></section>;
}

function AcademicsSection({ settings }) {
  const groups = [
    ["Science", "PCB, PMCs, CBG, PCM, PGM"],
    ["Business", "EBUAc, EGM, ECsM, HGE, BuACCs, BuACM, MEBu"],
    ["Arts", "HGL, HKL"],
  ];
  return <section className="alpha-public-section" style={{ background: sectionBackground(settings.academicsBackgroundUrl) }}><div className="alpha-public-wrap"><p style={eyebrow}>Academics</p><h2 style={sectionTitle}>Combinations Designed For Progress</h2><p style={{ ...sectionText, maxWidth: 850 }}>{settings.academicsText || defaultWebsite.academicsText}</p><div className="alpha-public-grid" style={{ marginTop: 18 }}>{groups.map(([title, body]) => <div className="alpha-public-card" key={title}><h3 style={{ color: DARK, marginTop: 0 }}>{title}</h3><p style={sectionText}>{body}</p></div>)}</div><div className="alpha-public-grid" style={{ marginTop: 16 }}>{["Awards & Achievement","School Policies","Extracurricular Activities","Aviation Exposure","Coding Classes"].map((title) => <div className="alpha-public-card" key={title}><h3 style={{ color: DARK }}>{title}</h3><p style={sectionText}>Structured opportunities that strengthen knowledge, discipline, confidence and future-ready skills.</p></div>)}</div></div></section>;
}

function SchoolCard({ school }) {
  return (
    <div className="alpha-public-card" style={{
      padding: 0,
      overflow: "hidden",
      borderRadius: 16,
      border: "1px solid #E2E8F0",
      boxShadow: "0 14px 36px rgba(15,23,42,.08)",
      display: "grid",
      gap: 0,
      alignContent: "start",
    }}>
      <div style={{
        position: "relative",
        width: "100%",
        height: 160,
        background: `linear-gradient(135deg, ${BLUE}, ${DARK})`,
        overflow: "hidden",
        display: "grid",
        placeItems: "center",
        color: "#fff",
      }}>
        <div style={{
          fontSize: 48,
          fontWeight: 950,
          letterSpacing: "-2px",
          textAlign: "center",
          padding: "0 20px",
        }}>
          {school.name}
        </div>
        <div style={{
          position: "absolute",
          bottom: 12,
          left: 14,
          right: 14,
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
        }}>
          <span style={{
            background: GOLD,
            color: DARK,
            borderRadius: 999,
            padding: "4px 10px",
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: ".04em",
          }}>{school.location}</span>
          <span style={{
            background: "rgba(255,255,255,.9)",
            color: DARK,
            borderRadius: 999,
            padding: "4px 10px",
            fontSize: 11,
            fontWeight: 900,
          }}>{school.type}</span>
        </div>
      </div>
      <div style={{ padding: 22, display: "grid", gap: 12 }}>
        <p style={{ color: "#64748B", fontSize: 14, lineHeight: 1.6, margin: 0 }}>{school.description}</p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
          <span style={{
            background: "#EFF6FF",
            color: BLUE,
            borderRadius: 6,
            padding: "5px 10px",
            fontSize: 12,
            fontWeight: 800,
          }}>{school.level}</span>
        </div>
      </div>
    </div>
  );
}

function SchoolDetailPage({ school, go, settings }) {
  if (!school) return null;
  const otherSchools = alphaInfo.schools.filter(s => s.id !== school.id);
  return (
    <section className="alpha-public-section" style={{ background: "#fff" }}>
      <div className="alpha-public-wrap">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <button onClick={() => go("/our-schools")} style={{ border: 0, background: "transparent", color: BLUE, fontWeight: 900, cursor: "pointer", fontSize: 14 }}>← All Schools</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 24 }}>
          <div style={{
            borderRadius: 20,
            border: "1px solid #E2E8F0",
            overflow: "hidden",
            boxShadow: "0 20px 50px rgba(15,23,42,.08)",
          }}>
            <div style={{
              height: 220,
              background: `linear-gradient(135deg, ${BLUE}, ${DARK})`,
              display: "grid",
              placeItems: "center",
              color: "#fff",
              position: "relative",
              overflow: "hidden",
            }}>
              <div style={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${alphaAssets.lockup})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: 0.15,
                mixBlendMode: "screen",
              }} />
              <div style={{ textAlign: "center", zIndex: 1 }}>
                <div style={{ fontSize: 64, fontWeight: 950, letterSpacing: "-3px" }}>{school.name.split(" ").slice(-1)[0]}</div>
              </div>
              <div style={{ position: "absolute", bottom: 16, left: 20, right: 20, display: "flex", gap: 8 }}>
                <span style={{ background: GOLD, color: DARK, borderRadius: 999, padding: "5px 12px", fontSize: 12, fontWeight: 900 }}>{school.location}</span>
                <span style={{ background: "rgba(255,255,255,.9)", color: DARK, borderRadius: 999, padding: "5px 12px", fontSize: 12, fontWeight: 900 }}>{school.type}</span>
              </div>
            </div>
            <div style={{ padding: 28, display: "grid", gap: 16 }}>
              <p style={eyebrow}>{school.type}</p>
              <h1 style={{ ...sectionTitle, fontSize: "clamp(28px,4vw,40px)" }}>{school.name}</h1>
              <p style={{ ...sectionText, fontSize: 16 }}>{school.description}</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, marginTop: 8 }}>
                <div style={{ padding: 14, background: "#F8FAFC", borderRadius: 12, border: "1px solid #E2E8F0" }}>
                  <div style={{ fontSize: 11, fontWeight: 900, color: "#64748B", textTransform: "uppercase" }}>Location</div>
                  <div style={{ fontSize: 18, fontWeight: 950, color: DARK, marginTop: 4 }}>{school.location}</div>
                </div>
                <div style={{ padding: 14, background: "#F8FAFC", borderRadius: 12, border: "1px solid #E2E8F0" }}>
                  <div style={{ fontSize: 11, fontWeight: 900, color: "#64748B", textTransform: "uppercase" }}>Level</div>
                  <div style={{ fontSize: 18, fontWeight: 950, color: DARK, marginTop: 4 }}>{school.level}</div>
                </div>
                <div style={{ padding: 14, background: "#F8FAFC", borderRadius: 12, border: "1px solid #E2E8F0" }}>
                  <div style={{ fontSize: 11, fontWeight: 900, color: "#64748B", textTransform: "uppercase" }}>Type</div>
                  <div style={{ fontSize: 18, fontWeight: 950, color: DARK, marginTop: 4 }}>{school.type}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 12 }}>
                <Button variant="gold" onClick={() => go("/admissions")}>Apply for Admission</Button>
                <Button variant="light" onClick={() => go("/contact")}>Contact School</Button>
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 16 }}>
            <div className="alpha-public-card">
              <p style={eyebrow}>Academic Pathways</p>
              <h3 style={{ color: DARK, marginTop: 0 }}>Science, Business & Arts</h3>
              <p style={sectionText}>Students can pursue Science, Business, or Arts combinations supported by practical learning, laboratories and extracurricular activities.</p>
            </div>
            <div className="alpha-public-card">
              <p style={eyebrow}>Facilities</p>
              <h3 style={{ color: DARK, marginTop: 0 }}>Libraries, Labs & Technology</h3>
              <p style={sectionText}>Access to well-resourced libraries, science laboratories, and technology-enabled learning environments.</p>
            </div>
            <div className="alpha-public-card">
              <p style={eyebrow}>Beyond Academics</p>
              <h3 style={{ color: DARK, marginTop: 0 }}>Aviation, Coding & Sports</h3>
              <p style={sectionText}>Extracurricular opportunities including aviation exposure, coding classes, sports and community engagement.</p>
            </div>
          </div>

          <div>
            <p style={eyebrow}>.Explore Other Schools</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 16, marginTop: 12 }}>
              {otherSchools.map((other) => (
                <div key={other.id} className="alpha-public-card" style={{ cursor: "pointer" }} onClick={() => go(`/${other.id}`)}>
                  <h3 style={{ color: DARK, marginTop: 0 }}>{other.name}</h3>
                  <p style={sectionText}>{other.description}</p>
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <span style={{ background: "#EFF6FF", color: BLUE, borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 800 }}>{other.location}</span>
                    <span style={{ background: "#FEF9E7", color: "#B7791F", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 800 }}>{other.type}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function OurSchoolsSection({ go, settings = defaultWebsite }) {
  const schools = alphaInfo.schools;
  return (
    <section className="alpha-public-section" style={{ background: "#fff" }}>
      <div className="alpha-public-wrap">
        <p style={eyebrow}>Alpha Schools</p>
        <h2 style={sectionTitle}>Three Schools. One Standard of Excellence.</h2>
        <p style={{ ...sectionText, maxWidth: 850 }}>{settings.schoolsText || defaultWebsite.schoolsText}</p>
        <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 20 }}>
          {schools.map((school) => (
            <SchoolCard key={school.id} school={school} go={go} />
          ))}
        </div>
        <div style={{ marginTop: 32, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, padding: 20, background: "#F8FAFC", borderRadius: 16, border: "1px solid #E2E8F0" }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ width: 42, height: 42, borderRadius: 12, background: "#EFF6FF", color: BLUE, display: "grid", placeItems: "center", fontWeight: 950 }}>M</span>
            <div>
              <strong style={{ color: DARK, fontSize: 14 }}>Mikocheni</strong>
              <p style={{ color: "#64748B", fontSize: 12, margin: 0 }}>Alpha High School</p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ width: 42, height: 42, borderRadius: 12, background: "#FEF9E7", color: "#B7791F", display: "grid", placeItems: "center", fontWeight: 950 }}>K</span>
            <div>
              <strong style={{ color: DARK, fontSize: 14 }}>Kunduchi</strong>
              <p style={{ color: "#64748B", fontSize: 12, margin: 0 }}>Alpha Girls High School · Alpha Nursery & Primary</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AdmissionsSection({ settings }) {
  return <section className="alpha-public-section" style={{ background: BLUE, color: "#fff" }}><div className="alpha-public-wrap"><p style={{ ...eyebrow, color: GOLD }}>Admissions</p><h2 style={{ ...sectionTitle, color: "#fff" }}>Start Your Alpha Journey</h2><p style={{ ...sectionText, color: "rgba(255,255,255,.86)", maxWidth: 850 }}>{settings.admissionsText}</p><div className="alpha-public-grid" style={{ marginTop: 20 }}>{[["Admission Requirements",settings.admissionsRequirements || defaultWebsite.admissionsRequirements],["Application Process",settings.admissionsProcess || defaultWebsite.admissionsProcess],["Scholarships",settings.scholarshipText || defaultWebsite.scholarshipText]].map(([title,body]) => <div className="alpha-public-card" key={title}><h3 style={{ color: DARK }}>{title}</h3><p style={sectionText}>{body}</p></div>)}</div><div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 22 }}><a href="https://wa.me/255734036010" style={{ background: GOLD, color: DARK, borderRadius: 8, padding: "12px 15px", fontWeight: 950 }}>Apply via WhatsApp</a><a href={`tel:${settings.admissionPhone || alphaInfo.phone}`} style={{ background: "#fff", color: DARK, borderRadius: 8, padding: "12px 15px", fontWeight: 950 }}>Call Admissions</a></div></div></section>;
}

function StudentLifeSection({ settings }) {
  return <section className="alpha-public-section" style={{ background: sectionBackground(settings.studentLifeBackgroundUrl, "#F8FAFC") }}><div className="alpha-public-wrap"><p style={eyebrow}>Student Life</p><h2 style={sectionTitle}>Safe Learning, Practical Growth, Real Support</h2><div className="alpha-public-grid">{["Academic Excellence", "Supportive Culture", "Admissions Access", "Safe Learning Environment"].map((title) => <div className="alpha-public-card" key={title}><h3 style={{ color: DARK }}>{title}</h3><p style={sectionText}>A premium Alpha experience built around student growth and disciplined care.</p></div>)}</div></div></section>;
}

function GallerySection({ gallery }) {
  const categories = Array.from(new Set(gallery.map((item) => item.category || item.title || "Campus")));
  const [activeCategory, setActiveCategory] = useState("All Photos");
  const [viewerIndex, setViewerIndex] = useState(-1);
  const visible = (activeCategory === "All Photos" ? gallery : gallery.filter((item) => (item.category || item.title || "Campus") === activeCategory)).slice(0, activeCategory === "All Photos" ? 80 : 20);
  useEffect(() => {
    if (activeCategory !== "All Photos" && !categories.includes(activeCategory)) setActiveCategory("All Photos");
  }, [categories.join("|"), activeCategory]);
  return <section className="alpha-public-section" style={{ background: "#fff", overflow: "hidden" }}><div className="alpha-public-wrap"><p style={eyebrow}>Gallery</p><h2 style={sectionTitle}>Campus Moments</h2><div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 14 }}>{["All Photos", ...categories].map((category) => <button key={category} type="button" onClick={() => { setActiveCategory(category); setViewerIndex(-1); }} style={{ flex: "0 0 auto", border: `1px solid ${activeCategory === category ? BLUE : "#D8E2F0"}`, background: activeCategory === category ? BLUE : "#fff", color: activeCategory === category ? "#fff" : DARK, borderRadius: 999, padding: "9px 13px", fontWeight: 900 }}>{category}</button>)}</div><div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 16 }}>{visible.map((item, index) => <button type="button" onClick={() => setViewerIndex(index)} key={item.id} style={{ padding: 0, border: 0, background: "transparent", textAlign: "left", cursor: "zoom-in" }}><img src={item.imageUrl || alphaAssets.logo} alt={item.title || "Alpha gallery"} loading="eager" decoding="async" style={{ width: "100%", aspectRatio: "16/10", objectFit: "cover", background: LIGHT, borderRadius: 14, boxShadow: "0 14px 34px rgba(15,45,85,.12)" }} /><strong style={{ display: "block", marginTop: 9, color: DARK }}>{item.title}</strong><span style={{ display: "block", marginTop: 3, color: "#64748B", fontSize: 12, fontWeight: 800 }}>{item.category || item.title}</span></button>)}</div></div>{viewerIndex >= 0 && visible[viewerIndex] && <div role="dialog" aria-modal="true" aria-label="Gallery image viewer" onClick={() => setViewerIndex(-1)} style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,.94)", display: "grid", placeItems: "center", padding: 18 }}><button type="button" onClick={() => setViewerIndex(-1)} style={{ position: "absolute", right: 18, top: 18, width: 46, height: 46, borderRadius: "50%", border: 0, background: "#fff", fontSize: 22, fontWeight: 950 }}>×</button><button type="button" disabled={viewerIndex === 0} onClick={(event) => { event.stopPropagation(); setViewerIndex((index) => Math.max(0, index - 1)); }} style={{ position: "absolute", left: 12, border: 0, borderRadius: "50%", width: 46, height: 46, fontSize: 24 }}>‹</button><img onClick={(event) => event.stopPropagation()} src={visible[viewerIndex].imageUrl} alt={visible[viewerIndex].title || "Alpha gallery"} style={{ maxWidth: "96vw", maxHeight: "88vh", objectFit: "contain" }} /><button type="button" disabled={viewerIndex === visible.length - 1} onClick={(event) => { event.stopPropagation(); setViewerIndex((index) => Math.min(visible.length - 1, index + 1)); }} style={{ position: "absolute", right: 12, border: 0, borderRadius: "50%", width: 46, height: 46, fontSize: 24 }}>›</button></div>}</section>;
}

function NewsSection({ news }) {
  return <section className="alpha-public-section"><div className="alpha-public-wrap"><p style={eyebrow}>News</p><h2 style={sectionTitle}>Latest Alpha Updates</h2><div className="alpha-public-grid">{news.map((item) => <article className="alpha-public-card" key={item.id}><span style={{ color: BLUE, fontWeight: 950 }}>{item.tag || "News"}</span><h3 style={{ color: DARK }}>{item.title}</h3><p style={sectionText}>{item.body}</p></article>)}</div></div></section>;
}

function ContactSection({ settings }) {
  const [status, setStatus] = useState("");
  async function submitInquiry(event) { event.preventDefault(); const form = event.currentTarget; setStatus("Sending…"); try { await addDoc(collection(getFirebaseDb(), "schools", SCHOOL_ID, "publicWebsite", "inquiries", "items"), { ...Object.fromEntries(new FormData(form).entries()), schoolId: SCHOOL_ID, status: "new", createdAt: serverTimestamp() }); form.reset(); setStatus("✅ Message sent. Alpha Schools will contact you."); } catch (error) { setStatus(`❌ Message could not be sent: ${readableFirebaseError(error)}`); } }
  return <section className="alpha-public-section" style={{ background: "#fff" }}><div className="alpha-public-wrap"><div className="alpha-public-grid"><div><p style={eyebrow}>Contact</p><h2 style={sectionTitle}>Talk To Alpha Schools</h2><p style={sectionText}>{settings.contactLocation}</p><p><strong>Phone:</strong> {settings.contactPhone || alphaInfo.phone}</p><p><strong>Email:</strong> {settings.contactEmail || alphaInfo.email}</p><a href="https://wa.me/255734036010" style={{ display: "inline-block", background: GOLD, color: DARK, borderRadius: 8, padding: "11px 14px", fontWeight: 950 }}>WhatsApp Admissions</a></div><form onSubmit={submitInquiry} className="alpha-public-card" style={{ display: "grid", gap: 12 }}><h3 style={{ margin: 0, color: DARK }}>Send an Enquiry</h3><RawInput name="name" label="Full Name" required /><RawInput name="email" label="Email" type="email" required /><RawInput name="phone" label="Phone" required /><RawInput name="subject" label="Subject" required /><RawInput name="message" label="Message" multiline required style={{ minHeight: 140 }} /><Button type="submit" variant="gold">Send Message</Button>{status && <strong style={{ color: status.startsWith("❌") ? "#B91C1C" : "#047857" }}>{status}</strong>}</form></div><iframe title="Alpha Schools map" src={settings.mapEmbedUrl || defaultWebsite.mapEmbedUrl} loading="eager" style={{ width: "100%", minHeight: 360, border: 0, borderRadius: 12, marginTop: 18 }} /></div></section>;
}

function NectaResultsSection({ go }) {
  const [examType, setExamType] = useState("acsee");
  const [year, setYear] = useState(null);
  const [schoolCode, setSchoolCode] = useState("S2549");
  const [loading, setLoading] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [error, setError] = useState(null);

  const examTypes = [
    { id: "acsee", name: "ACSEE", fullName: "Advanced Certificate of Secondary Education (Form Six)" },
    { id: "csee", name: "CSEE", fullName: "Certificate of Secondary Education (Form Four)" },
    { id: "ftna", name: "FTNA", fullName: "Form Two National Assessment" },
  ];

  const schools = [
    { code: "S2549", name: "Alpha High School", campus: "Mikocheni" },
    { code: "S5889", name: "Alpha Girls High School", campus: "Kunduchi" },
  ];

  const years = availableNectaYears(examType, schoolCode);
  const selectedResult = year ? findNectaResult(examType, year, schoolCode) : null;

  useEffect(() => {
    const yrs = availableNectaYears(examType, schoolCode);
    if (yrs.length && !yrs.includes(year)) setYear(yrs[0]);
    setResultData(null);
    setError(null);
  }, [examType, schoolCode, year]);

  const fetchResults = async () => {
    if (!selectedResult) return;
    setLoading(true);
    setError(null);
    setResultData(null);
    try {
      const res = await fetch(`${ALPHA_API_BASE}/api/necta/results/${examType}/${year}/${schoolCode}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (res.status === 404) setError("not_configured");
        else if (res.status === 503) setError("temporarily_unavailable");
        else setError("unknown");
        return;
      }
      const data = await res.json();
      setResultData(data);
    } catch (e) {
      if (!navigator.onLine) setError("offline");
      else setError("network");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (selectedResult) fetchResults(); }, [examType, year, schoolCode]);

  const errorMessages = {
    not_configured: "These results have not yet been published by NECTA for this school.",
    temporarily_unavailable: "NECTA\u2019s result service is temporarily unavailable. Please try again shortly.",
    network: "Unable to connect to the results service. Please check your connection and try again.",
    offline: "Your connection appears to be offline. Please check your internet connection and try again.",
    unknown: "An unexpected error occurred while fetching results. Please try again later.",
  };

  return (
    <section className="alpha-public-section" style={{ background: "#fff" }}>
      <div className="alpha-public-wrap">
        <p style={eyebrow}>Official NECTA Results</p>
        <h2 style={sectionTitle}>NECTA Examination Results</h2>
        <p style={{ ...sectionText, maxWidth: 850 }}>Results are provided through the official National Examinations Council of Tanzania (NECTA) result system. Select an examination, year and school to view results.</p>

        <div style={{ display: "grid", gap: 16, marginTop: 24 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {examTypes.map((et) => (
              <button key={et.id} onClick={() => { setExamType(et.id); setYear(null); }} style={{ padding: "10px 16px", borderRadius: 10, border: examType === et.id ? "2px solid " + BLUE : "2px solid #E2E8F0", background: examType === et.id ? "#EFF6FF" : "#fff", color: examType === et.id ? BLUE : DARK, fontWeight: 900, fontSize: 13, cursor: "pointer" }}>{et.name}</button>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 900, color: "#64748B", textTransform: "uppercase", marginBottom: 6 }}>Year</label>
              <select value={year || ""} onChange={(e) => setYear(Number(e.target.value))} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E2E8F0", fontWeight: 800, fontSize: 14, color: DARK, background: "#fff" }}>
                {years.length === 0 && <option value="">No results available</option>}
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 900, color: "#64748B", textTransform: "uppercase", marginBottom: 6 }}>School</label>
              <select value={schoolCode} onChange={(e) => setSchoolCode(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E2E8F0", fontWeight: 800, fontSize: 14, color: DARK, background: "#fff" }}>
                {schools.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.campus})</option>)}
              </select>
            </div>
          </div>

          <div style={{ padding: 20, borderRadius: 14, border: "1px solid #E2E8F0", background: "#F8FAFC" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <div>
                <strong style={{ color: DARK, fontSize: 18 }}>{examTypes.find((e) => e.id === examType)?.name} {year || "—"}</strong>
                <p style={{ margin: "4px 0 0", color: "#64748B", fontSize: 13 }}>{schools.find((s) => s.code === schoolCode)?.name} · Centre {schoolCode}</p>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 11, fontWeight: 900, color: "#64748B", textTransform: "uppercase" }}>Official Source: NECTA</span>
              </div>
            </div>

            {loading && <p style={{ color: BLUE, fontWeight: 800, marginTop: 16, fontSize: 14 }}>Loading official NECTA results…</p>}

            {!loading && error && (
              <div style={{ marginTop: 16, padding: 14, borderRadius: 10, background: "#FEF2F2", border: "1px solid #FECACA" }}>
                <p style={{ color: "#991B1B", fontWeight: 700, fontSize: 14, margin: "0 0 10px" }}>{errorMessages[error]}</p>
                {selectedResult && (
                  <a href={selectedResult.officialSourceUrl} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, background: BLUE, color: "#fff", fontWeight: 900, fontSize: 12, textDecoration: "none" }}>View Official NECTA Result ↗</a>
                )}
              </div>
            )}

            {!loading && !error && resultData && (
              <div style={{ marginTop: 16 }}>
                {resultData.summary && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(80px,1fr))", gap: 8, marginBottom: 16 }}>
                    {Object.entries(resultData.summary).map(([key, val]) => (
                      <div key={key} style={{ textAlign: "center", padding: "10px 6px", borderRadius: 10, background: "#fff", border: "1px solid #E2E8F0" }}>
                        <div style={{ fontSize: 22, fontWeight: 950, color: DARK }}>{val}</div>
                        <div style={{ fontSize: 11, color: "#64748B", fontWeight: 800 }}>{key}</div>
                      </div>
                    ))}
                  </div>
                )}
                {resultData.candidates && resultData.candidates.length > 0 && (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead><tr>{["Candidate No.", "Sex", "Aggregate", "Division", "Subjects"].map((h) => <th key={h} style={{ textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #E2E8F0", color: DARK, fontWeight: 900 }}>{h}</th>)}</tr></thead>
                      <tbody>{resultData.candidates.slice(0, 50).map((c, i) => <tr key={i}>{[c.candidateNo, c.sex, c.aggregate, c.division, c.subjects].map((v, j) => <td key={j} style={{ padding: "8px 10px", borderBottom: "1px solid #F1F5F9", color: "#334155" }}>{v || "—"}</td>)}</tr>)}</tbody>
                    </table>
                    {resultData.candidates.length > 50 && <p style={{ fontSize: 12, color: "#64748B", marginTop: 8 }}>Showing 50 of {resultData.candidates.length} candidates.</p>}
                  </div>
                )}
              </div>
            )}

            {!loading && !error && !resultData && !selectedResult && (
              <div style={{ marginTop: 16, padding: 14, borderRadius: 10, background: "#FEF3C7", border: "1px solid #FDE68A" }}>
                <p style={{ color: "#92400E", fontWeight: 700, fontSize: 14, margin: 0 }}>These results have not yet been published by NECTA for this examination year and school.</p>
              </div>
            )}

            {selectedResult && (
              <a href={selectedResult.officialSourceUrl} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 16, padding: "10px 16px", borderRadius: 10, background: BLUE, color: "#fff", fontWeight: 900, fontSize: 13, textDecoration: "none" }}>View Official NECTA Result ↗</a>
            )}
          </div>
        </div>

        <p style={{ ...sectionText, marginTop: 24, fontSize: 12 }}>Results are provided through the official National Examinations Council of Tanzania (NECTA) result system. Alpha Schools is not the source of examination results.</p>
      </div>
    </section>
  );
}

function PublicFooter({ go }) {
  return (
    <footer style={{ background: DARK, color: "#fff", padding: "38px 0 92px" }}>
      <div className="alpha-public-wrap" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 32 }}>
        <div>
          <AlphaMark />
          <h3 style={{ marginTop: 12 }}>Alpha Schools</h3>
          <p style={{ color: "rgba(255,255,255,.78)" }}>{alphaInfo.motto}</p>
        </div>
        <div>
          <h4 style={{ color: GOLD, marginBottom: 12 }}>Schools</h4>
          {alphaInfo.schools.map((school) => (
            <button key={school.id} onClick={() => go(`/${school.id}`)} style={{ ...linkButtonStyle("#fff"), display: "block", margin: "6px 0", fontSize: 13 }}>
              {school.name}
            </button>
          ))}
        </div>
        <div>
          <h4 style={{ color: GOLD, marginBottom: 12 }}>Quick Links</h4>
          {["About", "Academics", "Admissions", "Our Schools", "NECTA Results", "Gallery", "Contact"].map((item) => (
            <button key={item} onClick={() => go(item === "Our Schools" ? "/our-schools" : item === "NECTA Results" ? "/necta-results" : `/${item.toLowerCase()}`)} style={{ ...linkButtonStyle("#fff"), display: "block", margin: "6px 0", fontSize: 13 }}>
              {item}
            </button>
          ))}
        </div>
        <div>
          <h4 style={{ color: GOLD, marginBottom: 12 }}>Contact</h4>
          <p style={{ margin: "4px 0" }}>{alphaInfo.phone}</p>
          <p style={{ margin: "4px 0" }}>{alphaInfo.email}</p>
          <p style={{ margin: "4px 0" }}>{alphaInfo.instagram}</p>
        </div>
        <div>
          <h4 style={{ color: GOLD, marginBottom: 12 }}>Portal</h4>
          <Button variant="gold" onClick={() => go("/portal")}>Portal Login</Button>
        </div>
      </div>
    </footer>
  );
}

const eyebrow = { margin: "0 0 8px", color: BLUE, fontWeight: 950, textTransform: "uppercase", fontSize: 12 };
const sectionTitle = { margin: "0 0 14px", color: DARK, fontSize: "clamp(28px,5vw,44px)", lineHeight: 1.05, fontWeight: 950 };
const sectionText = { color: "#64748B", lineHeight: 1.7, fontSize: 15 };

function AuthPage({ onSignedIn }) {
  const { settings } = usePublicWebsiteContent();
  const [identifier, setIdentifier] = useState(() => visibleLoginIdentifier(localStorage.getItem("alpha_last_login") || ""));
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("signin");

  useEffect(() => {
    const stored = localStorage.getItem("alpha_last_login") || "";
    const visible = visibleLoginIdentifier(stored);
    if (stored && stored !== visible) localStorage.setItem("alpha_last_login", visible);
  }, []);

  function updateIdentifier(value) {
    setIdentifier(visibleLoginIdentifier(value));
  }

  async function resolveEmail(raw) {
    if (!raw) throw new Error("Enter your email or student ID.");
    const response = await fetch(`${ALPHA_API_BASE}/api/alpha/resolve-login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ identifier: raw.trim() }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) throw new Error(data.message || data.error || "Account is not approved for Alpha Schools.");
    return data;
  }

  async function attachApprovedProfile(user, resolved) {
    if (!user || isSuperAdminEmail(user.email)) return;
    const db = getFirebaseDb();
    const email = String(user.email || resolved.email || "").toLowerCase();
    let approvedProfile = resolved.profile || null;

    if (!approvedProfile && email) {
      const accessSnap = await getDoc(doc(db, "schools", SCHOOL_ID, "emailAccess", emailDocId(email)));
      if (accessSnap.exists()) approvedProfile = accessSnap.data();
    }

    if (!approvedProfile && email.endsWith("@alpha.stea.africa")) {
      const studentId = email.replace("@alpha.stea.africa", "");
      const usersSnap = await getDocs(collection(db, "schools", SCHOOL_ID, "users"));
      const roster = usersSnap.docs.find((item) => String(item.data().studentId || "").toLowerCase() === studentId && item.data().role === "student");
      if (roster) approvedProfile = { ...roster.data(), rosterDocId: roster.id };
    }

    if (!approvedProfile) return;
    const status = approvedProfile.status || "active";
    const roles = normalizeRoles(approvedProfile);
    const role = normalizeRole(approvedProfile.role || roles[0]);
    if (!["schoolAdmin", "teacher", "student"].includes(role) || status !== "active") return;

    const profileData = {
      ...approvedProfile,
      uid: user.uid,
      email,
      role,
      roles: roles.length ? roles : [role],
      schoolId: SCHOOL_ID,
      status: "active",
      passwordCreated: true,
      attachedFrom: approvedProfile.rosterDocId || approvedProfile.sourceDocId || (role !== "student" ? `emailAccess/${emailDocId(email)}` : ""),
      attachedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    await setDoc(doc(db, "schools", SCHOOL_ID, "users", user.uid), profileData, { merge: true });
    await setDoc(doc(db, "users", user.uid), profileData, { merge: true });
    if (approvedProfile.rosterDocId && approvedProfile.rosterDocId !== user.uid) {
      setDoc(doc(db, "schools", SCHOOL_ID, "users", approvedProfile.rosterDocId), {
        claimedUid: user.uid,
        claimedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true }).catch((error) => {
        console.warn("[AlphaClassroom] roster claim marker skipped", error.code || error.message);
      });
    }
  }

  async function login(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const raw = identifier.trim();
      const resolved = await resolveEmail(raw);
      const email = resolved.email;
      const credential = await signInWithEmailAndPassword(getFirebaseAuth(), email, password);

      await attachApprovedProfile(credential.user, resolved);
      localStorage.setItem("alpha_last_login", visibleLoginIdentifier(raw));
      setMessage("✅ Login successful.");
      onSignedIn();
    } catch (error) {
      console.error("[AlphaClassroom] login failed", { code: error.code, message: error.message });
      const code = error?.code || "";
      if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
        setMessage("❌ Wrong password. Use Forgot Password or Create Password if this is your first time.");
      } else if (code === "auth/user-not-found") {
        setMessage("❌ Account not found. Use Create Password if this is your first time.");
      } else {
        setMessage(`❌ ${error.message || "Could not sign in."}`);
      }
    } finally {
      setBusy(false);
    }
  }

  async function createPassword(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const raw = identifier.trim();
      const resolved = await resolveEmail(raw);
      const email = resolved.email;
      const role = normalizeRole(resolved.role || resolved.profile?.role);
      const isApprovedSuperAdmin = role === "superAdmin" && isSuperAdminEmail(email);
      if (!isApprovedSuperAdmin && !["schoolAdmin", "teacher", "student"].includes(role)) {
        throw new Error("This account is not approved for password creation.");
      }
      let credential;
      try {
        credential = await createUserWithEmailAndPassword(getFirebaseAuth(), email, password);
      } catch (createError) {
        if (createError?.code !== "auth/email-already-in-use") throw createError;

        try {
          credential = await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
        } catch (signInError) {
          if (!["auth/wrong-password", "auth/invalid-credential"].includes(signInError?.code)) throw signInError;
          await sendPasswordResetEmail(getFirebaseAuth(), email);
          localStorage.setItem("alpha_last_login", visibleLoginIdentifier(raw));
          setMessage(`✅ ${roleLabel(role)} access is approved. This email already has a STEA account, so a secure password setup link has been sent to ${email}. Open the email, choose your new password, then return and Sign in.`);
          return;
        }
      }
      await attachApprovedProfile(credential.user, resolved);
      localStorage.setItem("alpha_last_login", visibleLoginIdentifier(raw));
      setMessage("✅ Password created. Opening dashboard...");
      onSignedIn();
    } catch (error) {
      console.error("[AlphaClassroom] create password failed", { code: error.code, message: error.message });
      const code = error?.code || "";
      if (code === "auth/email-already-in-use") {
        setMessage("❌ This email already has a STEA account. Use Forgot Password to securely choose a new password.");
      } else {
        setMessage(`❌ ${error.message || "Could not create password."}`);
      }
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    try {
      const resolved = await resolveEmail(identifier);
      await sendPasswordResetEmail(getFirebaseAuth(), resolved.email);
      setMessage("✅ Password reset link sent if account exists.");
    } catch (error) {
      setMessage(`❌ ${error.message || "Enter your email or student ID first."}`);
    }
  }

  return (
    <main className="alpha-auth-page" style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: `linear-gradient(145deg, ${BLUE}, ${DARK})`, padding: 16 }}>
      <section className="alpha-auth-shell" style={{ width: "min(1080px,100%)", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,320px),1fr))", background: "#fff", borderRadius: 10, overflow: "hidden", boxShadow: "0 28px 80px rgba(0,0,0,.28)" }}>
        <div className="alpha-auth-intro" style={{ padding: "34px clamp(22px,4vw,42px)", minHeight: 520, color: "#fff", background: `linear-gradient(160deg, ${BLUE}, ${DARK})`, display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 26 }}>
          <div>
            <AlphaMark size={58} />
            <h1 style={{ margin: "24px 0 8px", fontSize: "clamp(32px,7vw,50px)", lineHeight: 1, fontWeight: 950 }}>Alpha School Portal</h1>
            <p style={{ margin: 0, color: "rgba(255,255,255,.86)", lineHeight: 1.6 }}>{alphaInfo.motto}</p>
          </div>
          <div className="alpha-auth-promo"><h2 style={{ margin: "0 0 14px", fontSize: 24 }}>Welcome back</h2><a href="/" aria-label="Explore the Alpha Schools website" style={{ display: "grid", gridTemplateColumns: "minmax(0,1.05fr) minmax(150px,.95fr)", minHeight: 150, background: "#fff", borderRadius: 14, overflow: "hidden", color: DARK, textDecoration: "none", boxShadow: "0 16px 40px rgba(0,0,0,.16)" }}><img src={settings.portalPromoImageUrl || alphaAssets.instantHero} alt="Explore Alpha Schools" loading="eager" decoding="async" style={{ width: "100%", height: "100%", minHeight: 150, objectFit: "contain", objectPosition: "center", background: "#EAF2FC" }} /><span style={{ padding: 16, display: "flex", flexDirection: "column", justifyContent: "center", gap: 7 }}><strong style={{ fontSize: 20, lineHeight: 1.15 }}>Explore Alpha Schools</strong><small style={{ color: "#64748B", lineHeight: 1.45 }}>Schools, admissions, news and campus life</small><b style={{ marginTop: 4, width: 36, height: 36, borderRadius: "50%", display: "grid", placeItems: "center", alignSelf: "flex-end", background: GOLD, color: DARK, fontSize: 20 }}>→</b></span></a></div>
          <div className="alpha-auth-contact" style={{ display: "grid", gap: 6, color: "rgba(255,255,255,.9)", fontSize: 13, fontWeight: 800 }}>
            <span>{alphaInfo.campuses}</span>
            <span>{alphaInfo.location}</span>
            <span>{alphaInfo.phone}</span>
          </div>
        </div>
        <form className="alpha-auth-form" onSubmit={mode === "signin" ? login : createPassword} style={{ padding: "clamp(26px,5vw,44px)", display: "grid", gap: 15, alignContent: "center" }}>
          <div>
            <h2 style={{ margin: 0, color: DARK, fontSize: 24, fontWeight: 950 }}>{mode === "signin" ? "Sign in" : "Create Password"}</h2>
            <p style={{ margin: "7px 0 0", color: "#64748B", lineHeight: 1.5 }}>{mode === "signin" ? "Use your approved Alpha Schools account." : "Create your first password after teacher or admin approval."}</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, background: LIGHT, border: "1px solid #D8E2F0", borderRadius: 10, padding: 5 }}>
            <button type="button" onClick={() => { setMode("signin"); setMessage(""); }} style={{ border: 0, borderRadius: 8, padding: "10px 8px", background: mode === "signin" ? BLUE : "transparent", color: mode === "signin" ? "#fff" : DARK, fontWeight: 950, cursor: "pointer" }}>Sign in</button>
            <button type="button" onClick={() => { setMode("create"); setMessage(""); }} style={{ border: 0, borderRadius: 8, padding: "10px 8px", background: mode === "create" ? BLUE : "transparent", color: mode === "create" ? "#fff" : DARK, fontWeight: 950, cursor: "pointer" }}>Create Password</button>
          </div>
          {message && <div style={{ padding: 12, borderRadius: 8, background: "#EFF6FF", color: DARK, fontWeight: 800 }}>{message}</div>}
          <Field label="Email or Student ID" value={identifier} onChange={updateIdentifier} required />
          <Field label={mode === "signin" ? "Password" : "New Password"} type="password" value={password} onChange={setPassword} required />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <label style={{ display: "inline-flex", gap: 8, alignItems: "center", color: DARK, fontWeight: 900, fontSize: 13 }}><input type="checkbox" defaultChecked /> Remember Me</label>
            <button type="button" onClick={resetPassword} style={{ border: 0, background: "transparent", color: BLUE, fontWeight: 950, cursor: "pointer" }}>Forgot Password</button>
          </div>
          <Button type="submit" variant="gold" disabled={busy}>{busy ? "Continuing..." : (mode === "signin" ? "Sign in" : "Create Password")}</Button>
        </form>
      </section>
      <style>{`@media(max-width:680px){.alpha-auth-page{padding:14px!important;display:block!important;background:${DARK}!important;min-height:100svh!important}.alpha-auth-shell{width:100%!important;margin:0 auto!important;border-radius:24px!important;overflow:hidden!important;box-shadow:0 20px 55px rgba(0,0,0,.28)!important;display:block!important;border:1px solid rgba(255,255,255,.28)!important}.alpha-auth-intro{min-height:auto!important;padding:20px 18px!important;gap:18px!important;justify-content:flex-start!important;border-radius:23px 23px 0 0!important}.alpha-auth-intro>div:first-child{display:grid;grid-template-columns:52px 1fr;column-gap:12px;align-items:center}.alpha-auth-intro>div:first-child>span{grid-row:1/3}.alpha-auth-intro>div:first-child h1{font-size:28px!important;margin:0 0 4px!important}.alpha-auth-intro>div:first-child p{font-size:14px!important}.alpha-auth-promo h2{font-size:20px!important;margin-bottom:10px!important}.alpha-auth-promo a{min-height:118px!important;grid-template-columns:minmax(0,1.15fr) minmax(135px,.85fr)!important}.alpha-auth-promo img{min-height:118px!important;max-height:132px!important;object-fit:contain!important}.alpha-auth-promo a>span{padding:12px!important;gap:4px!important}.alpha-auth-promo strong{font-size:16px!important}.alpha-auth-promo small{font-size:11px!important}.alpha-auth-promo b{width:30px!important;height:30px!important;font-size:17px!important}.alpha-auth-contact{display:none!important}.alpha-auth-form{padding:22px 18px 28px!important;gap:13px!important;align-content:start!important;border-radius:0 0 23px 23px!important}.alpha-auth-form h2{font-size:22px!important}.alpha-auth-form p{font-size:13px!important}.alpha-auth-form input{min-height:44px!important}}@media(max-width:360px){.alpha-auth-page{padding:9px!important}.alpha-auth-intro,.alpha-auth-form{padding-left:14px!important;padding-right:14px!important}.alpha-auth-intro>div:first-child h1{font-size:24px!important}.alpha-auth-promo a{grid-template-columns:1fr 125px!important}}`}</style>
    </main>
  );
}

function ChangeTemporaryPassword({ authUser, profile, onComplete }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event) {
    event.preventDefault();
    const data = Object.fromEntries(new FormData(event.currentTarget).entries());
    if (String(data.password || "").length < 8) return setMessage("❌ New password must contain at least 8 characters.");
    if (data.password !== data.confirmPassword) return setMessage("❌ Password confirmation does not match.");
    setBusy(true);
    try {
      await updatePassword(authUser, data.password);
      const db = getFirebaseDb();
      await setDoc(doc(db, "schools", SCHOOL_ID, "users", authUser.uid), { mustChangePassword: false, passwordChangedAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
      setMessage("✅ Password changed securely.");
      onComplete();
    } catch (error) {
      setMessage(`❌ Password change failed: ${error.message}`);
    } finally {
      setBusy(false);
    }
  }
  return <main style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 18, background: `linear-gradient(145deg,${BLUE},${DARK})` }}><form onSubmit={submit} style={{ width: "min(460px,100%)", display: "grid", gap: 14, padding: 28, borderRadius: 16, background: "#fff", boxShadow: "0 24px 70px rgba(0,0,0,.3)" }}><AlphaMark /><h1 style={{ margin: 0, color: DARK }}>Create your private password</h1><p style={{ margin: 0, color: "#64748B", lineHeight: 1.6 }}>You signed in using a temporary password set by an administrator. Replace it now. Administrators cannot see this new password.</p>{message && <strong style={{ color: message.startsWith("✅") ? "#047857" : "#B91C1C" }}>{message}</strong>}<RawInput name="password" label="New Password" type="password" required /><RawInput name="confirmPassword" label="Confirm New Password" type="password" required /><Button type="submit" variant="gold" busy={busy}>Save Private Password</Button></form></main>;
}

function Shell({ profile, module, setModule, children, unreadCounts = {} }) {
  const nav = moduleMap[profile.role] || [];
  const availableRoles = normalizeRoles(profile).filter((role) => role !== "student" || normalizeRoles(profile).length === 1);
  const [moreOpen, setMoreOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const mobileNavByRole = {
    teacher: [["Home", "Home", "🏠"], ["My Classes", "Classes", "🎓"], ["Assignments", "Assignments", "📝"], ["Attendance", "Attendance", "✅"]],
    student: [["Home", "Home", "🏠"], ["Assignments", "Assignments", "📝"], ["Attendance", "Attendance", "✅"], ["Results", "Results", "📊"]],
    schoolAdmin: [["Home", "Home", "🏠"], ["Students", "Students", "🎓"], ["Teachers", "Teachers", "👩🏽‍🏫"], ["Classes", "Classes", "🏫"]],
    superAdmin: [["Schools", "Home", "🏠"], ["Schools", "Schools", "🏫"], ["Manage Users", "Users", "👥"], ["Analytics", "Analytics", "📊"]],
  };
  const mobilePrimary = mobileNavByRole[profile.role] || [];
  const primaryModules = new Set(mobilePrimary.map(([target]) => target));
  const moreModules = nav.filter((item) => !primaryModules.has(item));
  const unreadTotal = Object.values(unreadCounts).reduce((total, count) => total + Number(count || 0), 0);

  useEffect(() => {
    if (!moreOpen && !profileOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [moreOpen, profileOpen]);

  function openModule(item) {
    setModule(item);
    setMoreOpen(false);
    setProfileOpen(false);
  }

  return (
    <div className="alpha-portal-shell" style={{ minHeight: "100vh", background: "#F8FAFC", color: "#111827" }}>
      <header className="alpha-portal-header" style={{ position: "sticky", top: 0, zIndex: 30, background: "rgba(255,255,255,.96)", borderBottom: "1px solid #E2E8F0", backdropFilter: "blur(14px)" }}>
        <div className="alpha-portal-header-inner" style={{ width: "min(1240px,100%)", margin: "0 auto", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div className="alpha-portal-brand" style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <span className="alpha-portal-logo"><AlphaMark /></span>
            <div>
              <strong className="alpha-portal-title" style={{ color: DARK, display: "block" }}>Alpha School Portal</strong>
              <span className="alpha-portal-user" style={{ color: "#64748B", fontSize: 12, fontWeight: 800 }}>{roleLabel(profile.role)} · {profileDisplayName(profile)}</span>
            </div>
          </div>
          <div className="alpha-mobile-header-actions">
            <button className="alpha-icon-button" type="button" aria-label={`${unreadTotal} unread notifications`} onClick={() => profile.role === "student" ? setModule("Notifications") : setMoreOpen(true)}>🔔{unreadTotal > 0 && <span>{unreadTotal > 99 ? "99+" : unreadTotal}</span>}</button>
            <button className="alpha-icon-button" type="button" aria-label="Open profile menu" onClick={() => setProfileOpen(true)}>👤</button>
          </div>
          <div className="alpha-desktop-header-actions" style={{ display: "flex", alignItems: "center", gap: 8 }}>{availableRoles.length > 1 && <label className="alpha-role-switcher"><span>View as</span><select value={profile.role} onChange={(event) => profile.onSwitchRole?.(event.target.value)}>{availableRoles.map((role) => <option key={role} value={role}>{roleLabel(role)}</option>)}</select></label>}<a className="alpha-home-link" href="/" aria-label="Open Alpha Schools website">↗ School Website</a><button className="alpha-signout" type="button" onClick={() => signOut(getFirebaseAuth())} aria-label="Sign out">Sign out</button></div>
        </div>
      </header>
      <div className="alpha-portal-layout" style={{ width: "min(1240px,100%)", margin: "0 auto", padding: "18px 16px 52px", display: "grid", gridTemplateColumns: "minmax(180px,230px) minmax(0,1fr)", gap: 18 }}>
        <aside className="alpha-portal-nav" style={{ display: "grid", alignContent: "start", gap: 8 }}>
          {nav.map((item) => (
            <button className="alpha-nav-pill" key={item} type="button" onClick={() => setModule(item)} style={{ textAlign: "left", border: `1px solid ${module === item ? BLUE : "#D8E2F0"}`, background: module === item ? BLUE : "#fff", color: module === item ? "#fff" : DARK, borderRadius: 8, padding: "11px 12px", fontWeight: 900, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minWidth: 0 }}>
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{item}</span>
              {unreadCounts[item] > 0 && <span className="alpha-new-badge" style={{ flex: "0 0 auto", background: module === item ? GOLD : "#FEF3C7", color: DARK, border: `1px solid ${GOLD}`, borderRadius: 999, padding: "2px 7px", fontSize: 10, lineHeight: 1.2, fontWeight: 950 }}>{unreadCounts[item] > 99 ? "99+" : unreadCounts[item]}</span>}
            </button>
          ))}
        </aside>
        <main className="alpha-portal-main" style={{ minWidth: 0, display: "grid", gap: 16 }}>{children}</main>
      </div>
      <nav className="alpha-mobile-bottom-nav" aria-label="Primary navigation">
        {mobilePrimary.map(([target, label, icon]) => (
          <button key={`${target}-${label}`} type="button" className={module === target ? "is-active" : ""} onClick={() => openModule(target)} aria-label={label}>
            <span className="alpha-bottom-icon" aria-hidden="true">{icon}</span><span>{label}</span>
            {unreadCounts[target] > 0 && <b>{unreadCounts[target]}</b>}
          </button>
        ))}
        <button type="button" className={moreOpen || (!primaryModules.has(module) && module !== "") ? "is-active" : ""} onClick={() => setMoreOpen(true)} aria-label="More modules"><span className="alpha-bottom-icon" aria-hidden="true">☰</span><span>More</span>{unreadTotal > 0 && <b>{unreadTotal}</b>}</button>
      </nav>
      {(moreOpen || profileOpen) && <div className="alpha-drawer-backdrop" role="presentation" onClick={() => { setMoreOpen(false); setProfileOpen(false); }} />}
      {moreOpen && <section className="alpha-mobile-drawer" role="dialog" aria-modal="true" aria-label="More modules">
        <div className="alpha-drawer-heading"><div><strong>More</strong><span>All portal modules</span></div><button type="button" onClick={() => setMoreOpen(false)} aria-label="Close more menu">✕</button></div>
        <div className="alpha-more-grid">{moreModules.map((item) => <button key={item} type="button" onClick={() => openModule(item)} className={module === item ? "is-active" : ""}><span aria-hidden="true">{item === "Profile" ? "👤" : item === "Settings" ? "⚙️" : item === "Announcements" ? "📣" : item === "Resources" ? "📚" : item === "Quizzes" ? "❓" : "▦"}</span><strong>{item}</strong>{unreadCounts[item] > 0 && <b>{unreadCounts[item] > 99 ? "99+" : unreadCounts[item]}</b>}</button>)}</div>
        <button className="alpha-drawer-signout" type="button" onClick={() => signOut(getFirebaseAuth())}>Sign Out</button>
      </section>}
      {profileOpen && <section className="alpha-mobile-drawer" role="dialog" aria-modal="true" aria-label="Profile menu">
        <div className="alpha-drawer-heading"><div><strong>{profileDisplayName(profile)}</strong><span>{roleLabel(profile.role)} · {profile.email || profile.studentId || "Alpha School Portal"}</span></div><button type="button" onClick={() => setProfileOpen(false)} aria-label="Close profile menu">✕</button></div>
        <div className="alpha-profile-actions">{availableRoles.length > 1 && <div className="alpha-mobile-role-switch"><strong>Switch role</strong>{availableRoles.map((role) => <button key={role} type="button" className={profile.role === role ? "is-active" : ""} onClick={() => { profile.onSwitchRole?.(role); setProfileOpen(false); }}>{profile.role === role ? "✓ " : ""}{roleLabel(role)}</button>)}</div>}<a href="/" style={{ textDecoration: "none" }}>🏫 Alpha Schools Website</a><button type="button" onClick={() => openModule(profile.role === "student" ? "Profile" : "Settings")}>👤 Profile & settings</button><button type="button" onClick={() => signOut(getFirebaseAuth())}>↪ Sign Out</button></div>
      </section>}
      <style>{`
        .alpha-portal-shell{--alpha-blue:${BLUE};--alpha-dark:${DARK};--alpha-gold:${GOLD};--alpha-border:#DCE5F0;--alpha-muted:#64748B;--alpha-surface:#fff;font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;background:radial-gradient(circle at 85% 0,rgba(219,234,254,.72),transparent 28%),linear-gradient(180deg,#F8FAFD 0%,#F1F6FC 100%)!important}
        .alpha-portal-header{border-bottom-color:rgba(220,229,240,.82)!important;box-shadow:0 8px 28px rgba(15,45,85,.07)!important;background:rgba(255,255,255,.9)!important}
        .alpha-portal-header-inner{min-height:68px}.alpha-portal-title{font-size:16px;letter-spacing:-.015em}
        .alpha-signout,.alpha-home-link{border:1px solid #D8E2F0;background:#fff;color:${DARK};border-radius:8px;min-height:40px;padding:9px 12px;font-weight:950;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center}
        .alpha-role-switcher{display:flex;align-items:center;gap:7px;color:#64748B;font-size:11px;font-weight:900}.alpha-role-switcher select{min-height:40px;border:1px solid #D8E2F0;border-radius:8px;background:#F8FAFC;color:${DARK};padding:8px 30px 8px 10px;font-weight:950}
        .alpha-signout:hover{border-color:${BLUE};color:${BLUE};background:#F5F9FF}
        .alpha-portal-layout{gap:24px!important;padding-top:24px!important}
        .alpha-portal-nav{position:sticky;top:92px;border:1px solid rgba(147,197,253,.38);border-radius:20px;padding:11px!important;background:linear-gradient(180deg,rgba(255,255,255,.94),rgba(247,250,253,.9));box-shadow:0 18px 44px rgba(15,45,85,.08);backdrop-filter:blur(14px)}
        .alpha-nav-pill{border:0!important;border-radius:10px!important;padding:12px 13px!important;transition:background .18s ease,color .18s ease,transform .18s ease,box-shadow .18s ease}
        .alpha-nav-pill:hover{transform:translateX(3px);box-shadow:0 8px 20px rgba(15,45,85,.09)}.alpha-nav-pill[style*="background: rgb(11, 78, 162)"],.alpha-nav-pill[style*="background: #0B4EA2"]{box-shadow:0 10px 24px rgba(11,78,162,.22)}
        .alpha-portal-main{gap:20px!important;align-content:start!important;grid-auto-rows:max-content}
        .alpha-panel{border-color:rgba(204,219,235,.9)!important;border-radius:18px!important;padding:20px!important;box-shadow:0 16px 42px rgba(15,45,85,.07)!important}
        .alpha-panel-header h2{letter-spacing:-.025em}
        .alpha-stat-card{border-color:var(--alpha-border)!important;border-radius:14px!important;padding:16px!important;box-shadow:0 8px 24px rgba(15,45,85,.045)!important}
        .alpha-panel label{letter-spacing:.005em}.alpha-panel input,.alpha-panel select,.alpha-panel textarea{border-color:#C9D6E5!important;border-radius:11px!important;transition:border-color .18s ease,box-shadow .18s ease,background .18s ease;background:#FBFDFF!important}
        .alpha-panel input:focus,.alpha-panel select:focus,.alpha-panel textarea:focus{outline:none;border-color:${BLUE}!important;box-shadow:0 0 0 3px rgba(11,78,162,.12)}
        .alpha-panel textarea{line-height:1.55}.alpha-panel button,.alpha-signout{transition:transform .16s ease,box-shadow .16s ease,background .16s ease}
        .alpha-panel button:hover:not(:disabled),.alpha-signout:hover{transform:translateY(-1px)}
        .alpha-panel button:focus-visible,.alpha-signout:focus-visible,.alpha-nav-pill:focus-visible{outline:3px solid rgba(247,201,72,.65);outline-offset:2px}
        .alpha-table-wrap{border-color:var(--alpha-border)!important;border-radius:15px!important;overflow:hidden;box-shadow:inset 0 1px 0 #fff}.alpha-table-wrap thead{background:linear-gradient(180deg,#F4F8FC,#EDF3F9)!important}.alpha-table-wrap th{font-size:11px;text-transform:uppercase;letter-spacing:.055em;color:#52657D;padding-top:14px!important;padding-bottom:14px!important}.alpha-table-wrap tbody tr:nth-child(even){background:#FAFCFF}.alpha-table-wrap tbody tr:hover{background:#F1F7FF}.alpha-table-wrap td{color:#26384F}
        .alpha-compact-page-title h1{font-size:32px!important;letter-spacing:-.035em}
        .alpha-mobile-header-actions,.alpha-mobile-bottom-nav,.alpha-mobile-drawer,.alpha-drawer-backdrop{display:none}
        @media(max-width:767px){
          .alpha-portal-shell{background:#F3F7FC!important}
          .alpha-portal-header{box-shadow:0 8px 24px rgba(15,23,42,.07)}
          .alpha-portal-header-inner{padding:8px 12px!important;min-height:64px!important}
          .alpha-portal-logo > span{width:38px!important;height:38px!important;border-radius:10px!important}
          .alpha-portal-logo img{width:30px!important;height:30px!important}
          .alpha-portal-title{font-size:14px!important;line-height:1.05}
          .alpha-portal-user{display:block;max-width:190px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-size:11px!important}
          .alpha-desktop-header-actions,.alpha-portal-nav{display:none!important}
          .alpha-mobile-header-actions{display:flex;align-items:center;gap:6px}
          .alpha-icon-button{position:relative;width:44px;height:44px;border:0;border-radius:12px;background:#F1F5F9;font-size:19px;cursor:pointer}
          .alpha-icon-button>span{position:absolute;right:1px;top:1px;min-width:17px;height:17px;padding:0 4px;border-radius:999px;background:#DC2626;color:#fff;font-size:9px;line-height:17px;font-weight:950}
          .alpha-portal-layout{grid-template-columns:1fr!important;width:100%!important;padding:10px 12px calc(92px + env(safe-area-inset-bottom))!important;gap:10px!important}
          .alpha-new-badge{font-size:9px!important;padding:2px 5px!important}
          .alpha-portal-main{gap:10px!important}
          .alpha-hero{grid-template-columns:1fr auto!important;padding:14px!important;border-radius:12px!important;min-height:112px}
          .alpha-hero h1{font-size:22px!important;line-height:1.05}
          .alpha-hero p{font-size:12px!important;line-height:1.35}
          .alpha-hero-logo{width:48px!important;height:48px!important;padding:5px!important;border-radius:12px!important}
          .alpha-stat-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:8px!important}
          .alpha-stat-card{border-radius:12px!important;padding:10px!important;min-height:82px}
          .alpha-stat-card span{font-size:10px!important;line-height:1.2}
          .alpha-stat-card strong{font-size:20px!important;margin-top:4px!important}
          .alpha-panel{border-radius:12px!important;padding:12px!important;gap:10px!important}
          .alpha-panel-header h2{font-size:17px!important}
          .alpha-table-wrap{border:0!important;background:transparent!important;overflow:visible!important}
          .alpha-student-table table,.alpha-student-table thead,.alpha-student-table tbody,.alpha-student-table tr,.alpha-student-table th,.alpha-student-table td{display:block}
          .alpha-student-table table{min-width:0!important;border-spacing:0!important}
          .alpha-student-table thead{display:none}
          .alpha-student-table tr{background:#fff;border:1px solid #E2E8F0;border-radius:12px;padding:10px;margin-bottom:10px;box-shadow:0 10px 24px rgba(15,23,42,.05)}
          .alpha-student-table td{padding:7px 0!important;border:0!important;white-space:normal!important;overflow-wrap:anywhere}
          .alpha-student-table td::before{content:attr(data-label);display:block;color:#64748B;font-size:10px;font-weight:950;text-transform:uppercase;margin-bottom:3px}
          .alpha-submissions-desktop{display:none!important}
          .alpha-submissions-cards{display:grid!important}
          .alpha-panel form{grid-template-columns:1fr!important}
          .alpha-panel input,.alpha-panel select,.alpha-panel textarea{width:100%!important;box-sizing:border-box;min-height:48px!important}
          .alpha-panel button{max-width:100%;min-height:44px}
          .alpha-announcement-body{min-height:150px!important}
          .alpha-mobile-bottom-nav{position:fixed;display:grid;grid-template-columns:repeat(5,1fr);left:0;right:0;bottom:0;z-index:50;background:#fff;border-top:1px solid #E2E8F0;box-shadow:0 -10px 28px rgba(15,23,42,.09);padding:6px 4px calc(6px + env(safe-area-inset-bottom))}
          .alpha-mobile-bottom-nav button{position:relative;min-width:0;min-height:54px;border:0;border-radius:10px;background:transparent;color:#64748B;font-size:10px;font-weight:900;display:grid;place-items:center;align-content:center;gap:2px;cursor:pointer}
          .alpha-mobile-bottom-nav button.is-active{color:${BLUE};background:#EFF6FF}
          .alpha-bottom-icon{font-size:19px;line-height:1}
          .alpha-mobile-bottom-nav b{position:absolute;top:3px;right:calc(50% - 21px);min-width:16px;height:16px;padding:0 3px;border-radius:999px;background:#DC2626;color:#fff;font-size:9px;line-height:16px}
          .alpha-drawer-backdrop{display:block;position:fixed;inset:0;z-index:60;background:rgba(15,23,42,.48)}
          .alpha-mobile-drawer{display:grid;position:fixed;left:0;right:0;bottom:0;z-index:70;max-height:min(78vh,680px);overflow-y:auto;background:#fff;border-radius:22px 22px 0 0;padding:18px 16px calc(20px + env(safe-area-inset-bottom));box-shadow:0 -20px 60px rgba(15,23,42,.24);gap:16px}
          .alpha-drawer-heading{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}.alpha-drawer-heading>div{display:grid;gap:3px;min-width:0}.alpha-drawer-heading strong{font-size:20px;color:${DARK}}.alpha-drawer-heading span{font-size:12px;color:#64748B;overflow-wrap:anywhere}.alpha-drawer-heading button{width:44px;height:44px;border:0;border-radius:12px;background:#F1F5F9;font-size:18px}
          .alpha-more-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}.alpha-more-grid button{position:relative;min-height:72px;border:1px solid #E2E8F0;border-radius:14px;background:#F8FAFC;color:${DARK};padding:12px;text-align:left;display:flex;align-items:center;gap:9px}.alpha-more-grid button.is-active{border-color:${BLUE};background:#EFF6FF;color:${BLUE}}.alpha-more-grid button>span{font-size:20px}.alpha-more-grid button>b{position:absolute;right:7px;top:7px;color:#92400E;background:#FEF3C7;border-radius:999px;padding:2px 5px;font-size:8px}
          .alpha-drawer-signout,.alpha-profile-actions button,.alpha-profile-actions a{min-height:48px;border:1px solid #FECACA;border-radius:12px;background:#FEF2F2;color:#991B1B;font-weight:950;display:flex;align-items:center;padding:0 14px}.alpha-profile-actions{display:grid;gap:10px}.alpha-profile-actions a,.alpha-profile-actions button:first-of-type{border-color:#D8E2F0;background:#F8FAFC;color:${DARK}}
          .alpha-mobile-role-switch{display:grid;grid-template-columns:1fr 1fr;gap:8px;padding:10px;border:1px solid #D8E2F0;border-radius:12px;background:#F8FAFC}.alpha-mobile-role-switch>strong{grid-column:1/-1;color:${DARK};font-size:12px}.alpha-mobile-role-switch button{min-height:42px!important;border-color:#D8E2F0!important;background:#fff!important;color:${DARK}!important;padding:8px!important;justify-content:center!important}.alpha-mobile-role-switch button.is-active{background:${BLUE}!important;color:#fff!important;border-color:${BLUE}!important}
          .alpha-module-tabs{display:flex!important;flex-wrap:nowrap!important;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;margin:0 -12px;padding:2px 12px 8px}.alpha-module-tabs::-webkit-scrollbar{display:none}.alpha-module-tabs button{flex:0 0 auto;width:auto!important;min-height:40px!important;border-radius:999px!important}
          .alpha-compact-page-title{padding:2px 2px 4px}.alpha-compact-page-title h1{font-size:26px!important}.alpha-compact-page-title p{font-size:13px!important}
        }
        @media(min-width:768px){
          .alpha-submissions-desktop{display:block}
          .alpha-submissions-cards{display:none!important}
        }
        @media(min-width:768px) and (max-width:1023px){
          .alpha-portal-layout{grid-template-columns:minmax(160px,190px) minmax(0,1fr)!important;padding-left:14px!important;padding-right:14px!important;gap:14px!important}
          .alpha-portal-nav{padding:8px!important}.alpha-nav-pill{font-size:12px;padding:10px!important}.alpha-panel{padding:16px!important}
          .alpha-stat-grid{grid-template-columns:repeat(3,minmax(0,1fr))!important}
        }
        @media(prefers-reduced-motion:reduce){.alpha-portal-shell *{scroll-behavior:auto!important;transition:none!important;animation-duration:.01ms!important}}
      `}</style>
    </div>
  );
}

function Hero({ title, subtitle }) {
  return (
    <section className="alpha-hero" style={{ background: `radial-gradient(circle at 88% 10%,#1C6BC1 0,${BLUE} 28%,${DARK} 78%)`, color: "#fff", borderRadius: 20, padding: "26px clamp(20px,4vw,38px)", display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 16, alignItems: "center", border: "1px solid rgba(147,197,253,.45)", boxShadow: "0 20px 48px rgba(6,42,88,.2)", overflow: "hidden" }}>
      <div>
        <p style={{ margin: "0 0 6px", color: GOLD, fontSize: 12, fontWeight: 950, textTransform: "uppercase" }}>{alphaInfo.schoolName}</p>
        <h1 style={{ margin: 0, fontSize: "clamp(26px,5vw,40px)", fontWeight: 950 }}>{title}</h1>
        <p style={{ margin: "8px 0 0", color: "rgba(255,255,255,.86)", lineHeight: 1.55 }}>{subtitle}</p>
      </div>
      <img className="alpha-hero-logo" src={alphaAssets.logo} alt="Alpha Schools" loading="eager" style={{ width: 92, height: 92, objectFit: "contain", filter: "drop-shadow(0 16px 26px rgba(0,0,0,.28))" }} />
    </section>
  );
}

function StatGrid({ stats }) {
  return (
    <section className="alpha-stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }}>
      {stats.map((stat) => (
        <div className="alpha-stat-card" key={stat.label} style={{ background: stat.glow ? "linear-gradient(145deg,#FFFBEB,#FFF7D6)" : "linear-gradient(145deg,#fff,#F7FAFD)", border: `1px solid ${stat.glow ? GOLD : "#DCE6F1"}`, borderRadius: 16, padding: 17, boxShadow: stat.glow ? "0 0 0 3px rgba(247,201,72,.16),0 16px 34px rgba(247,201,72,.14)" : "0 12px 28px rgba(15,45,85,.06)" }}>
          <span style={{ color: "#64748B", fontWeight: 900, fontSize: 12 }}>{stat.label}</span>
          <strong style={{ display: "block", color: DARK, fontSize: 24, marginTop: 6 }}>{stat.value}</strong>
          {stat.glow && <span style={{ display: "inline-block", marginTop: 6, color: "#92400E", fontSize: 11, fontWeight: 950 }}>New update</span>}
        </div>
      ))}
    </section>
  );
}

function SuperAdminDashboard({ authUser, profile }) {
  const [module, setModule] = useState("Schools");
  const [schools, setSchools] = useState([]);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [message, setMessage] = useState("");
  const [selectedSchool, setSelectedSchool] = useState("alpha");
  const [search, setSearch] = useState("");
  const [actionBusy, setActionBusy] = useState(false);

  const load = useCallback(async () => {
    const db = getFirebaseDb();

    const schoolSnap = await getDocs(collection(db, "schools"));
    const schoolList = schoolSnap.docs.map((item) => ({ id: item.id, schoolId: item.id, ...item.data() }));
    if (!schoolList.some((item) => (item.schoolId || item.id) === SCHOOL_ID)) {
      schoolList.unshift({
        id: SCHOOL_ID,
        schoolId: SCHOOL_ID,
        schoolName: alphaInfo.schoolName,
        domain: "alpha.stea.africa",
        subdomain: "alpha",
        status: "active",
      });
    }

    const userList = [];
    for (const schoolDoc of schoolSnap.docs) {
      const schoolId = schoolDoc.id;
      const userSnap = await getDocs(collection(db, "schools", schoolId, "users"));
      userSnap.docs.forEach((item) => {
        userList.push({ id: item.id, uid: item.id, schoolId, ...item.data() });
      });
    }

    let logList = [];
    try {
      const logSnap = await getDocs(collection(db, "systemLogs"));
      logList = logSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
    } catch (error) {
      console.warn("[AlphaClassroom] logs unavailable", error.message);
    }

    setSchools(schoolList);
    setUsers(userList);
    setLogs(logList);
  }, []);

  useEffect(() => {
    load().catch((error) => setMessage(error.message));
  }, [load]);

  const filteredUsers = users.filter((user) => {
    const normalizedRole = normalizeRole(user.role);
    const text = `${user.fullName || ""} ${user.email || ""} ${user.role || ""} ${user.schoolId || ""}`.toLowerCase();
    return text.includes(search.toLowerCase()) && ["schoolAdmin", "teacher", "student"].includes(normalizedRole);
  });
  const dedupedUsers = useMemo(() => {
    return ["schoolAdmin", "teacher", "student"].flatMap((userRole) => dedupeAlphaUsers(filteredUsers, userRole));
  }, [filteredUsers]);

  async function saveSchool(event) {
    event.preventDefault();
    const formEl = event.currentTarget;
    if (actionBusy) return;
    setActionBusy(true);
    setMessage("⏳ Saving school...");
    try {
      const form = new FormData(formEl);
      const payload = Object.fromEntries(form.entries());
      const schoolId = slugify(payload.subdomain || payload.schoolName || SCHOOL_ID);
      if (!schoolId) throw new Error("School ID/subdomain is required.");

      const db = getFirebaseDb();
      const cleanPayload = {
        ...payload,
        schoolId,
        subdomain: slugify(payload.subdomain || schoolId),
        status: payload.status || "active",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: authUser.email || authUser.uid,
      };

      await setDoc(doc(db, "schools", schoolId), cleanPayload, { merge: true });
      if (formEl) formEl.reset();
      setSelectedSchool(schoolId);
      setMessage(`School saved successfully: ${payload.schoolName || schoolId}`);
      await load();
      setModule("Schools");
    } catch (error) {
      console.error("[AlphaClassroom] save school failed", error);
      setMessage(`❌ Save school failed: ${error.message}`);
    } finally {
      setActionBusy(false);
    }
  }

  async function createSchoolAdmin(event) {
    event.preventDefault();
    const formEl = event.currentTarget;
    if (actionBusy) return;
    setActionBusy(true);
    setMessage("⏳ Creating school admin...");
    try {
      const form = new FormData(formEl);
      const payload = Object.fromEntries(form.entries());
      const schoolId = payload.schoolId || selectedSchool || SCHOOL_ID;
      const email = String(payload.email || "").trim().toLowerCase();
      const password = String(payload.temporaryPassword || "").trim();

      if (!schoolId) throw new Error("Choose a school.");
      if (!email) throw new Error("Admin email is required.");
      if (!password || password.length < 6) throw new Error("Temporary password must be at least 6 characters.");

      const app = getFirebaseApp();
      const apiKey = app.options.apiKey;
      const signupUrl = `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${apiKey}`;

      const response = await fetch(signupUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, returnSecureToken: true }),
      });

      const result = await response.json();
      if (!response.ok) {
        const message = result?.error?.message || "Could not create Firebase Auth user.";
        if (message === "EMAIL_EXISTS") {
          const db = getFirebaseDb();
          const pendingProfile = {
            fullName: payload.fullName || "School Admin",
            email,
            phone: payload.phone || "",
            role: "schoolAdmin",
            schoolId,
            status: "active",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: authUser.email || authUser.uid,
            note: "Existing Firebase Auth user. Profile will attach automatically on next login.",
          };

          await setDoc(doc(db, "schools", schoolId, "emailAccess", emailDocId(email)), pendingProfile, { merge: true });

          if (formEl) formEl.reset();
          setMessage(`✅ Existing STEA account detected. Alpha school admin access approved for ${email}. Tell them to log out and log in again with their existing password.`);
          await load();
          setModule("Manage Users");
          return;
        }
        throw new Error(message.replaceAll("_", " "));
      }

      const uid = result.localId;
      const db = getFirebaseDb();
      const profileData = {
        uid,
        fullName: payload.fullName || "School Admin",
        email,
        phone: payload.phone || "",
        role: "schoolAdmin",
        schoolId,
        status: "active",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: authUser.email || authUser.uid,
      };

      await setDoc(doc(db, "schools", schoolId, "users", uid), profileData, { merge: true });
      await setDoc(doc(db, "users", uid), profileData, { merge: true });
      await setDoc(doc(db, "schools", schoolId, "emailAccess", emailDocId(email)), profileData, { merge: true });

      if (formEl) formEl.reset();
      setMessage(`✅ School admin created successfully. Email: ${email} | Temporary password: ${password}`);
      await load();
      setModule("Manage Users");
    } catch (error) {
      console.error("[AlphaClassroom] create school admin failed", error);
      setMessage(`❌ Create school admin failed: ${error.message}`);
    } finally {
      setActionBusy(false);
    }
  }

  async function updateSchool(schoolId, updates) {
    setMessage("");
    try {
      if (!schoolId) throw new Error("Missing school ID.");
      const db = getFirebaseDb();
      await setDoc(doc(db, "schools", schoolId), {
        ...updates,
        schoolId,
        updatedAt: serverTimestamp(),
        updatedBy: authUser.email || authUser.uid,
      }, { merge: true });
      setMessage("✅ School updated successfully.");
      await load();
    } catch (error) {
      console.error("[AlphaClassroom] update school failed", error);
      setMessage(`❌ Update school failed: ${error.message}`);
    }
  }

  async function deleteSchool(schoolId) {
    setMessage("");
    try {
      if (schoolId === "alpha") {
        setMessage("Alpha school cannot be deleted from the pilot system.");
        return;
      }
      if (!window.confirm(`Delete ${schoolId}? This removes the school document.`)) return;
      await deleteDoc(doc(getFirebaseDb(), "schools", schoolId));
      setMessage("✅ School deleted successfully.");
      await load();
    } catch (error) {
      console.error("[AlphaClassroom] delete school failed", error);
      setMessage(`❌ Delete school failed: ${error.message}`);
    }
  }

  async function updateUser(user, updates) {
    setMessage("");
    try {
      const schoolId = user.schoolId || SCHOOL_ID;
      const uid = user.uid || user.id;
      const data = {
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy: authUser.email || authUser.uid,
      };
      await setDoc(doc(getFirebaseDb(), "schools", schoolId, "users", uid), data, { merge: true });
      await setDoc(doc(getFirebaseDb(), "users", uid), data, { merge: true });
      setMessage("✅ User updated successfully.");
      await load();
    } catch (error) {
      console.error("[AlphaClassroom] update user failed", error);
      setMessage(`❌ Update user failed: ${error.message}`);
    }
  }

  async function resetUser(user) {
    try {
      const temporaryPassword = window.prompt(`Set a temporary password for ${user.fullName || user.email}. Minimum 8 characters:`);
      if (temporaryPassword === null) return;
      if (temporaryPassword.length < 8) throw new Error("Temporary password must contain at least 8 characters.");
      const result = await apiRequest(authUser, `${ALPHA_API_BASE}/api/alpha/users/set-temporary-password`, { method: "POST", body: JSON.stringify({ targetId: user.id || user.uid, temporaryPassword }) });
      setMessage(`✅ Temporary password set for ${result.email}. Give it privately to the user; it is shown only now and must be changed after login.`);
    } catch (error) {
      console.error("[AlphaClassroom] reset user failed", error);
      setMessage(`❌ Reset failed: ${error.message}`);
    }
  }

  async function deleteUser(user) {
    setMessage("");
    try {
      if (!window.confirm(`Remove ${user.email || user.fullName} from this school?`)) return;
      const schoolId = user.schoolId || SCHOOL_ID;
      const uid = user.uid || user.id;
      await deleteDoc(doc(getFirebaseDb(), "schools", schoolId, "users", uid));
      setMessage("✅ User removed from this school successfully.");
      await load();
    } catch (error) {
      console.error("[AlphaClassroom] delete user failed", error);
      setMessage(`❌ Delete user failed: ${error.message}`);
    }
  }

  async function cleanDuplicateAlphaUsers() {
    if (actionBusy) return;
    setActionBusy(true);
    setMessage("⏳ Cleaning duplicate Alpha users...");
    try {
      const db = getFirebaseDb();
      const alphaUsers = users.filter((user) => (user.schoolId || SCHOOL_ID) === SCHOOL_ID);
      const duplicateUpdates = [];

      ["student", "teacher", "schoolAdmin"].forEach((userRole) => {
        const groups = new Map();
        alphaUsers.forEach((user) => {
          const normalized = { ...user, role: normalizeRole(user.role), status: user.status || "active" };
          if (normalized.role !== userRole || normalized.status === "duplicate") return;
          const key = userDedupeKey(normalized, userRole);
          if (!key) return;
          const group = groups.get(key) || [];
          group.push(normalized);
          groups.set(key, group);
        });

        groups.forEach((group) => {
          if (group.length < 2) return;
          const keep = group.reduce((best, item) => preferredAlphaUser(best, item, userRole), null);
          group.forEach((item) => {
            const itemId = item.uid || item.id;
            const keepId = keep?.uid || keep?.id;
            if (!itemId || itemId === keepId) return;
            duplicateUpdates.push(setDoc(doc(db, "schools", SCHOOL_ID, "users", itemId), {
              status: "duplicate",
              duplicateOf: keepId,
              duplicateKey: userDedupeKey(item, userRole),
              updatedAt: serverTimestamp(),
              updatedBy: authUser.email || authUser.uid,
            }, { merge: true }));
          });
        });
      });

      await Promise.all(duplicateUpdates);
      setMessage(`✅ Cleaned ${duplicateUpdates.length} duplicate Alpha user record${duplicateUpdates.length === 1 ? "" : "s"}.`);
      await load();
    } catch (error) {
      console.error("[AlphaClassroom] clean duplicates failed", error);
      setMessage(`❌ Clean duplicates failed: ${error.message}`);
    } finally {
      setActionBusy(false);
    }
  }

  return (
    <Shell profile={profile} module={module} setModule={setModule}>
      <Hero title="Super Admin Dashboard" subtitle="Manage every STEA Classroom school, admin, tenant user, and platform setting from one production console." />
      {message && <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: DARK, borderRadius: 8, padding: 12, fontWeight: 800, overflowWrap: "anywhere" }}>{message}</div>}
      <StatGrid stats={[
        { label: "Schools", value: schools.length },
        { label: "Active Teachers", value: users.filter((u) => u.role === "teacher" && u.status !== "suspended").length },
        { label: "Active Students", value: users.filter((u) => u.role === "student" && u.status !== "suspended").length },
        { label: "Storage Usage", value: `${Math.round(schools.reduce((sum, item) => sum + Number(item.storageBytes || 0), 0) / 1024 / 1024)} MB` },
      ]} />
      {module === "Schools" && (
        <Panel title="Schools" action={<Button variant="gold" onClick={() => setModule("Create School")}>Create School</Button>}>
          <Table empty="No schools found" columns={[
            { key: "schoolName", label: "School" },
            { key: "domain", label: "Domain", render: (row) => row.domain || `${row.subdomain || row.id}.stea.africa` },
            { key: "status", label: "Status" },
            { key: "storageBytes", label: "Storage", render: (row) => `${Math.round(Number(row.storageBytes || 0) / 1024 / 1024)} MB` },
            { key: "actions", label: "Actions", render: (row) => <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><Button variant="light" onClick={() => setSelectedSchool(row.schoolId || row.id)}>Open</Button><Button variant="light" onClick={() => updateSchool(row.schoolId || row.id, { status: row.status === "suspended" ? "active" : "suspended" })}>{row.status === "suspended" ? "Activate" : "Suspend"}</Button><Button variant="danger" disabled={(row.schoolId || row.id) === "alpha"} onClick={() => deleteSchool(row.schoolId || row.id)}>Delete</Button></div> },
          ]} rows={schools} />
          {selectedSchool && <SchoolDetail school={schools.find((item) => (item.schoolId || item.id) === selectedSchool)} onSave={updateSchool} />}
        </Panel>
      )}
      {module === "Alpha Homepage" && <WebsiteManager profile={profile} />}
      {module === "Create School" && (
        <Panel title="Create School">
          <SchoolForm onSubmit={saveSchool} busy={actionBusy} />
        </Panel>
      )}
      {module === "Create School Admin" && (
        <Panel title="Create School Admin">
          <form onSubmit={createSchoolAdmin} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>School<select name="schoolId" required defaultValue={selectedSchool || SCHOOL_ID} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700 }}>{schools.map((school) => <option key={school.schoolId || school.id} value={school.schoolId || school.id}>{school.schoolName || (school.schoolId === SCHOOL_ID ? alphaInfo.schoolName : school.schoolId || school.id)}</option>)}</select></label>
            <RawInput name="fullName" label="Full Name" required />
            <RawInput name="email" label="Email" type="email" required />
            <RawInput name="phone" label="Phone" />
            <RawInput name="temporaryPassword" label="Temporary Password" type="password" required />
            <div style={{ alignSelf: "end" }}><Button type="submit" variant="gold">Create Account</Button></div>
          </form>
        </Panel>
      )}
      {module === "Manage Users" && (
        <Panel title="Manage Users" action={<div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}><Button variant="light" busy={actionBusy} onClick={cleanDuplicateAlphaUsers}>Clean Duplicate Alpha Users</Button><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search users" style={{ minHeight: 40, border: "1px solid #CBD5E1", borderRadius: 8, padding: "8px 10px" }} /></div>}>
          <Table empty="No tenant users found" columns={[
            { key: "fullName", label: "Name" },
            { key: "email", label: "Email" },
            { key: "role", label: "Role" },
            { key: "schoolId", label: "School" },
            { key: "status", label: "Status" },
            { key: "actions", label: "Actions", render: (row) => <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><Button variant="light" disabled={row.email === SUPER_ADMIN_EMAIL} onClick={() => updateUser(row, { status: row.status === "suspended" ? "active" : "suspended" })}>{row.status === "suspended" ? "Activate" : "Suspend"}</Button><Button variant="light" disabled={row.email === SUPER_ADMIN_EMAIL} onClick={() => resetUser(row)}>Reset Password</Button><Button variant="danger" disabled={row.email === SUPER_ADMIN_EMAIL} onClick={() => deleteUser(row)}>Delete</Button></div> },
          ]} rows={dedupedUsers} />
        </Panel>
      )}
      {module === "Analytics" && <Analytics schools={schools} users={users} />}
      {module === "Logs" && <Panel title="Audit Logs"><Table rows={logs} columns={[{ key: "action", label: "Action" }, { key: "actorEmail", label: "Actor" }, { key: "schoolId", label: "School" }, { key: "message", label: "Message" }, { key: "createdAt", label: "Date", render: (row) => formatDate(row.createdAt) }]} /></Panel>}
      {module === "Notifications" && <TenantCollectionModule collectionName="notifications" role={profile.role} title="Notifications" profile={profile} />}
      {module === "Settings" && <TenantCollectionModule collectionName="settings" role={profile.role} title="System Settings" profile={profile} />}
    </Shell>
  );
}

function RawInput({ label, name, type = "text", required = false, defaultValue = "", placeholder = "", multiline = false, className = "", style = {}, wrapperStyle = {} }) {
  const controlStyle = { minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", font: "inherit", fontWeight: 700, boxSizing: "border-box", width: "100%", ...style };
  return (
    <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900, ...wrapperStyle }}>
      {label}
      {multiline
        ? <textarea className={className} name={name} required={required} defaultValue={defaultValue} placeholder={placeholder} style={{ ...controlStyle, resize: "vertical", whiteSpace: "pre-wrap" }} />
        : <input className={className} name={name} type={type} required={required} defaultValue={defaultValue} placeholder={placeholder} style={controlStyle} />}
    </label>
  );
}

function SchoolForm({ onSubmit, busy = false }) {
  return (
    <form onSubmit={onSubmit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
      <RawInput name="schoolName" label="School Name" required />
      <RawInput name="subdomain" label="Subdomain" required />
      <RawInput name="logoUrl" label="Logo URL" />
      <RawInput name="primaryColor" label="Primary Color" defaultValue={BLUE} />
      <RawInput name="secondaryColor" label="Secondary Color" defaultValue={GOLD} />
      <RawInput name="address" label="Address" />
      <RawInput name="phone" label="Phone" />
      <RawInput name="email" label="Email" type="email" />
      <RawInput name="website" label="Website" />
      <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Status<select name="status" defaultValue="active" style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700 }}><option value="active">Active</option><option value="pending">Pending</option><option value="suspended">Suspended</option></select></label>
      <div style={{ alignSelf: "end" }}><Button type="submit" variant="gold" busy={busy}>Create School</Button></div>
    </form>
  );
}

function SchoolDetail({ school, onSave }) {
  if (!school) return null;
  async function submit(event) {
    event.preventDefault();
    const formEl = event.currentTarget;
    await onSave(school.schoolId || school.id, Object.fromEntries(new FormData(formEl).entries()));
  }
  return (
    <form onSubmit={submit} style={{ borderTop: "1px solid #E2E8F0", paddingTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
      <RawInput name="schoolName" label="School Name" defaultValue={school.schoolName || ""} required />
      <RawInput name="logoUrl" label="Logo URL" defaultValue={school.logoUrl || ""} />
      <RawInput name="primaryColor" label="Primary Color" defaultValue={school.primaryColor || BLUE} />
      <RawInput name="secondaryColor" label="Secondary Color" defaultValue={school.secondaryColor || GOLD} />
      <RawInput name="address" label="Address" defaultValue={school.address || ""} />
      <RawInput name="phone" label="Phone" defaultValue={school.phone || ""} />
      <RawInput name="email" label="Email" type="email" defaultValue={school.email || ""} />
      <RawInput name="website" label="Website" defaultValue={school.website || ""} />
      <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Status<select name="status" defaultValue={school.status || "active"} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700 }}><option value="active">Active</option><option value="pending">Pending</option><option value="suspended">Suspended</option></select></label>
      <div style={{ alignSelf: "end" }}><Button type="submit">Save School</Button></div>
    </form>
  );
}

function Analytics({ schools, users }) {
  return (
    <Panel title="Analytics">
      <StatGrid stats={[
        { label: "Total Schools", value: schools.length },
        { label: "School Admins", value: users.filter((user) => user.role === "schoolAdmin").length },
        { label: "Teachers", value: users.filter((user) => user.role === "teacher").length },
        { label: "Students", value: users.filter((user) => user.role === "student").length },
      ]} />
    </Panel>
  );
}

function TenantDashboard({ authUser, profile, school }) {
  const [module, setModule] = useState(moduleMap[profile.role]?.[0] || "Classes");
  const [readVersion] = useAlphaReadVersion();
  useFirebaseReadState(profile.role === "student" ? profile.uid : "");
  const studentFeed = useAlphaStudentFeed(authUser, profile.role === "student");
  const users = useTenantCollection("users", profile.role !== "student");
  const classes = useTenantCollection("classes");
  const subjects = useTenantCollection("subjects");
  const studentBaseClassIds = Array.from(new Set([...(profile.assignedClassIds || []), ...(profile.classIds || []), profile.classId].filter(Boolean)));
  const studentAugmentedClassIds = useMemo(() => {
    const ids = [...studentBaseClassIds];
    if (profile.role === "student") {
      classes.items.forEach((item) => {
        const uids = Array.isArray(item.studentUids) ? item.studentUids.filter(Boolean) : [];
        if (uids.includes(profile.uid)) {
          ids.push(item.id);
        }
      });
    }
    return Array.from(new Set(ids));
  }, [classes.items, profile.role, profile.uid, studentBaseClassIds.join(",")]);
  const studentProfileClassIds = studentAugmentedClassIds;
  const schoolWideLearning = profile.role === "schoolAdmin" || profile.role === "superAdmin";
  const allAttendance = useTenantCollection("attendance", schoolWideLearning);
  const allQuizzes = useTenantCollection("quizzes", schoolWideLearning);
  const studentQuizzes = useAlphaStudentQuizzes(studentProfileClassIds, profile.role === "student");
  const isTeacherOwnerOfClass = (classItem) => classTeacherUids(classItem).includes(profile.uid);
  const teacherClassIds = classes.items
    .filter(isTeacherOwnerOfClass)
    .map((item) => item.id);
  const scopedLearningClassIds = profile.role === "teacher" ? teacherClassIds : profile.role === "student" ? studentProfileClassIds : [];
  const allAssignments = useTenantCollection("assignments", schoolWideLearning);
  const teacherAssignments = useAlphaTeacherAssignments(profile.uid, teacherClassIds, profile.role === "teacher");
  const studentAssignments = useTenantClassCollection("assignments", studentProfileClassIds, profile.role === "student");
  const assignments = schoolWideLearning ? allAssignments : profile.role === "teacher" ? teacherAssignments : studentAssignments;
  const allNotes = useTenantCollection("notes", schoolWideLearning);
  const teacherNotes = useAlphaTeacherCollection("notes", profile.uid, teacherClassIds, profile.role === "teacher");
  const studentNotes = useTenantClassCollection("notes", studentProfileClassIds, profile.role === "student");
  const notes = schoolWideLearning ? allNotes : profile.role === "teacher" ? teacherNotes : studentNotes;
  const allAnnouncements = useTenantCollection("announcements", schoolWideLearning);
  const teacherAnnouncements = useAlphaTeacherCollection("announcements", profile.uid, teacherClassIds, profile.role === "teacher");
  const studentAnnouncements = useTenantClassCollection("announcements", studentProfileClassIds, profile.role === "student");
  const announcements = schoolWideLearning ? allAnnouncements : profile.role === "teacher" ? teacherAnnouncements : studentAnnouncements;
  const allResources = useTenantCollection("resources", schoolWideLearning);
  const teacherResources = useAlphaTeacherCollection("resources", profile.uid, teacherClassIds, profile.role === "teacher");
  const studentResources = useTenantClassCollection("resources", studentProfileClassIds, profile.role === "student");
  const resources = schoolWideLearning ? allResources : profile.role === "teacher" ? teacherResources : studentResources;
  const adminQuizAttempts = useTenantCollection("quizAttempts", profile.role === "schoolAdmin" || profile.role === "superAdmin");
  const teacherQuizAttempts = useAlphaClassQuizAttempts(teacherClassIds, profile.role === "teacher");
  const ownQuizAttempts = useTenantStudentAttempts(profile.uid, profile.role === "student");
  const allSubmissions = useTenantCollection("assignmentSubmissions", schoolWideLearning);
  const teacherSubmissions = useTenantClassCollection("assignmentSubmissions", teacherClassIds, profile.role === "teacher");
  const ownSubmissions = useAlphaStudentSubmissions(profile.uid, profile.role === "student");
  const submissions = schoolWideLearning ? allSubmissions : profile.role === "teacher" ? teacherSubmissions : ownSubmissions;
  const terms = useTenantCollection("terms");
  const studentNotifications = useAlphaStudentNotifications(profile.role === "student" ? profile.uid : "");
  const studentResultsItems = submissions.items.filter((item) => item.studentUid === profile.uid);
  const teacherQuizzes = useAlphaTeacherQuizzes(profile.uid, teacherClassIds, profile.role === "teacher");
  const quizzes = profile.role === "student" ? studentQuizzes : profile.role === "teacher" ? teacherQuizzes : allQuizzes;
  const quizAttempts = profile.role === "student" ? ownQuizAttempts : profile.role === "teacher" ? teacherQuizAttempts : adminQuizAttempts;
  const teacherAttendance = useAlphaTeacherCollection("attendance", profile.uid, teacherClassIds, profile.role === "teacher");
  const studentAttendance = useTenantClassCollection("attendance", studentProfileClassIds, profile.role === "student");
  const attendance = schoolWideLearning ? allAttendance : profile.role === "teacher" ? teacherAttendance : studentAttendance;
  const serverFeed = studentFeed.feed || {};
  const withFeed = (current, name) => profile.role === "student" && Array.isArray(serverFeed[name]) ? { ...current, items: serverFeed[name], error: studentFeed.error } : current;
  const effectiveAttendance = withFeed(attendance, "attendance");
  const effectiveAssignments = withFeed(assignments, "assignments");
  const effectiveQuizzes = withFeed(quizzes, "quizzes");
  const effectiveNotes = withFeed(notes, "notes");
  const effectiveAnnouncements = withFeed(announcements, "announcements");
  const effectiveResources = withFeed(resources, "resources");

  // Fix #2 — Mark-module-as-read effect.
  // MUST be declared AFTER effective* vars above, because its dependency array
  // references them directly. Declaring it before → TDZ crash ("Cannot access
  // before initialization") on the first successful dashboard mount.
  useEffect(() => {
    if (profile.role !== "student" || !profile.uid) return undefined;
    const moduleToCollection = {
      Assignments: ["assignments", effectiveAssignments.items],
      Attendance: ["attendance", effectiveAttendance.items],
      Quizzes: ["quizzes", effectiveQuizzes.items],
      Announcements: ["announcements", effectiveAnnouncements.items],
      Resources: ["resources", effectiveResources.items],
      Notes: ["notes", effectiveNotes.items],
      Results: ["results", studentResultsItems],
    };
    const pair = moduleToCollection[module];
    if (!pair) return undefined;
    const [collectionName, items] = pair;
    if (!Array.isArray(items) || !items.length) {
      setReadState(profile.uid, collectionName, Date.now());
      return undefined;
    }
    const newest = Math.max(Date.now(), ...items.map(itemCreatedTime));
    let cancelled = false;
    const timer = setTimeout(() => {
      if (!cancelled) setReadState(profile.uid, collectionName, newest);
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [module, profile.role, profile.uid, effectiveAssignments.items, effectiveAttendance.items, effectiveQuizzes.items, effectiveAnnouncements.items, effectiveResources.items, effectiveNotes.items, studentResultsItems]);
  const collections = { users, classes, subjects, attendance: effectiveAttendance, assignments: effectiveAssignments, quizzes: effectiveQuizzes, notes: effectiveNotes, announcements: effectiveAnnouncements, resources: effectiveResources, submissions, quizAttempts, terms };
  const studentClassIds = studentProfileClassIds;
  const visibleClasses = profile.role === "student"
    ? classes.items.filter((item) => studentClassIds.includes(item.id) || (item.studentUids || []).includes(profile.uid))
    : profile.role === "teacher"
      ? classes.items.filter(isTeacherOwnerOfClass)
      : classes.items;
  const visibleClassIds = visibleClasses.map((item) => item.id);
  const studentInClass = (item) => !item.classId || visibleClassIds.includes(item.classId);
  const studentCanSee = (item) => studentInClass(item) && !["draft", "archived"].includes(item.status || "active") && !["teachers", "draft"].includes(item.visibility || "students");
  const studentUnreadCounts = profile.role === "student" ? {
    Notifications: Number(studentNotifications.unreadCount || 0),
    Assignments: effectiveAssignments.items.filter(studentCanSee).filter((item) => isUnreadForStudent(profile.uid, "assignments", item)).length,
    Attendance: effectiveAttendance.items.filter(studentInClass).filter((item) => isUnreadForStudent(profile.uid, "attendance", item)).length,
    Quizzes: effectiveQuizzes.items.filter(studentCanSee).filter((item) => isUnreadForStudent(profile.uid, "quizzes", item)).length,
    Announcements: effectiveAnnouncements.items.filter(studentCanSee).filter((item) => isUnreadForStudent(profile.uid, "announcements", item)).length,
    Resources: effectiveResources.items.filter(studentCanSee).filter((item) => isUnreadForStudent(profile.uid, "resources", item)).length,
    Notes: effectiveNotes.items.filter(studentCanSee).filter((item) => isUnreadForStudent(profile.uid, "notes", item)).length,
    Results: studentResultsItems.filter((item) => isUnreadForStudent(profile.uid, "results", item)).length,
  } : {};
  const pageStats = module === "Assignments"
    ? assignmentStats(assignments.items, submissions.items)
    : module === "Quizzes"
      ? quizOverviewStats(quizzes.items, quizAttempts.items, users.items)
    : profile.role === "student" ? [
      { label: "Assignments", value: studentUnreadCounts.Assignments || effectiveAssignments.items.length, glow: studentUnreadCounts.Assignments > 0 },
      { label: "Quizzes", value: studentUnreadCounts.Quizzes || effectiveQuizzes.items.length, glow: studentUnreadCounts.Quizzes > 0 },
      { label: "Resources", value: studentUnreadCounts.Resources || effectiveResources.items.length, glow: studentUnreadCounts.Resources > 0 },
      { label: "Notes", value: studentUnreadCounts.Notes || effectiveNotes.items.length, glow: studentUnreadCounts.Notes > 0 },
      { label: "Announcements", value: studentUnreadCounts.Announcements || effectiveAnnouncements.items.length, glow: studentUnreadCounts.Announcements > 0 },
      { label: "Attendance", value: studentUnreadCounts.Attendance || effectiveAttendance.items.length, glow: studentUnreadCounts.Attendance > 0 },
    ] : [
      { label: "Classes", value: visibleClasses.length },
      { label: "Assignments", value: assignments.items.length, glow: studentUnreadCounts.Assignments > 0 },
      { label: "Attendance Sessions", value: attendance.items.length, glow: studentUnreadCounts.Attendance > 0 },
      { label: "Announcements", value: announcements.items.length, glow: studentUnreadCounts.Announcements > 0 },
    ];
  const isDashboardHome = module === "Home" || module === "My Classes";
  void readVersion;

  return (
    <Shell profile={profile} module={module} setModule={setModule} unreadCounts={studentUnreadCounts}>
      {isDashboardHome ? <><Hero title={`${roleLabel(profile.role)} Dashboard`} subtitle={`${school?.schoolName || alphaInfo.schoolName} · ${alphaInfo.campuses} · ${alphaInfo.location}`} /><StatGrid stats={pageStats} /></> : <section className="alpha-compact-page-title"><h1 style={{ margin: 0, color: DARK, fontSize: 30, fontWeight: 950 }}>{module}</h1><p style={{ margin: "5px 0 0", color: "#64748B" }}>Manage {module.toLowerCase()} for {school?.schoolName || alphaInfo.schoolName}.</p></section>}
      {module !== "Home" && profile.role === "schoolAdmin" && <SchoolAdminModule module={module} authUser={authUser} profile={profile} collections={collections} />}
      {module !== "Home" && profile.role === "teacher" && <TeacherModule module={module} profile={profile} collections={collections} visibleClasses={visibleClasses} />}
      {module !== "Home" && profile.role === "student" && <StudentModule module={module} profile={{...profile, onOpenModule: setModule}} collections={collections} visibleClasses={visibleClasses} />}
    </Shell>
  );
}

function SchoolAdminModule({ module, authUser, profile, collections }) {
  if (module === "Teachers") return <PeopleModule title="Teachers" role="teacher" authUser={authUser} profile={profile} users={collections.users.items} />;
  if (module === "Students") return <PeopleModule title="Students" role="student" authUser={authUser} profile={profile} users={collections.users.items} classes={collections.classes.items} />;
  if (module === "Website Manager") return <WebsiteManager profile={profile} />;
  if (module === "Academic Years") return <TenantCollectionModule collectionName="academicYears" title="Academic Years" profile={profile} fields={["name", "startDate", "endDate", "status"]} />;
  if (module === "Terms") return <TenantCollectionModule collectionName="terms" title="Terms" profile={profile} fields={["name", "academicYear", "startDate", "endDate", "status"]} />;
  if (module === "Forms") return <TenantCollectionModule collectionName="forms" title="Forms" profile={profile} fields={["name", "level", "status"]} />;
  if (module === "Streams") return <TenantCollectionModule collectionName="streams" title="Streams" profile={profile} fields={["name", "combination", "status"]} />;
  if (module === "Subjects") return <TenantCollectionModule collectionName="subjects" title="Subjects" profile={profile} fields={["name", "code", "department"]} />;
  if (module === "Subject Offerings") return <TenantCollectionModule collectionName="subjectOfferings" title="Subject Offerings" profile={profile} fields={["subject", "formLevel", "stream", "teacherId", "status"]} />;
  if (module === "Classes") return <ClassModule profile={profile} classes={collections.classes.items} users={collections.users.items} canAssign />;
  if (module === "Attendance") return <AttendanceModule profile={profile} items={collections.attendance.items} classes={collections.classes.items} />;
  if (module === "Assignments") return <LearningModule collectionName="assignments" title="Assignments" profile={profile} items={collections.assignments.items} classes={collections.classes.items} terms={collections.terms.items} submissions={collections.submissions.items} />;
  if (module === "Notes") return <LearningModule collectionName="notes" title="Notes" profile={profile} items={collections.notes.items} classes={collections.classes.items} terms={collections.terms.items} />;
  if (module === "Announcements") return <LearningModule collectionName="announcements" title="Announcements" profile={profile} items={collections.announcements.items} classes={collections.classes.items} terms={collections.terms.items} />;
  if (module === "Quizzes") return <QuizBuilderModule profile={profile} quizzes={collections.quizzes.items} classes={collections.classes.items} attempts={(collections.quizAttempts?.items || [])} users={collections.users.items} />;
  if (module === "Reports") return <Reports collections={collections} />;
  if (module === "Academic Year") return <TenantCollectionModule collectionName="terms" title="Academic Year and Terms" profile={profile} fields={["name", "startDate", "endDate", "status"]} />;
  if (module === "Branding") return <TenantCollectionModule collectionName="branding" title="School Branding" profile={profile} fields={["logoUrl", "primaryColor", "secondaryColor", "motto"]} />;
  return <TenantCollectionModule collectionName="settings" title="Settings" profile={profile} fields={["name", "value"]} />;
}

function WebsiteManager({ profile }) {
  const { settings, gallery, news, heroImages } = usePublicWebsiteContent();
  const [message, setMessage] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const [heroFile, setHeroFile] = useState(null);
  const [galleryFile, setGalleryFile] = useState(null);
  const [heroCarouselFile, setHeroCarouselFile] = useState(null);
  const [sectionFiles, setSectionFiles] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [inquiries, setInquiries] = useState([]);

  useEffect(() => onSnapshot(collection(getFirebaseDb(), "schools", SCHOOL_ID, "publicWebsite", "inquiries", "items"), (snap) => setInquiries(snap.docs.map((row) => ({ id: row.id, ...row.data() })).sort((a, b) => String(b.createdAt?.seconds || 0).localeCompare(String(a.createdAt?.seconds || 0)))), (error) => console.warn("[AlphaWebsite] inquiries unavailable", error.message)), []);

  async function saveSettings(event) {
    event.preventDefault();
    const formEl = event.currentTarget;
    if (busyKey) return;
    setBusyKey("settings");
    setMessage("⏳ Saving website settings...");
    try {
      const heroUpload = heroFile ? await uploadAlphaWebsiteImage(heroFile, { section: "hero", onProgress: setUploadProgress }) : null;
      const sectionUploads = {};
      for (const [key, file] of Object.entries(sectionFiles)) {
        if (!file) continue;
        const uploaded = await uploadAlphaWebsiteImage(file, { section: key, onProgress: setUploadProgress });
        sectionUploads[`${key}Url`] = uploaded.imageUrl;
        sectionUploads[`${key}StoragePath`] = uploaded.storagePath;
      }
      await setDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "publicWebsite", "settings"), {
        ...Object.fromEntries(new FormData(formEl).entries()),
        ...(heroUpload ? { heroImageUrl: heroUpload.imageUrl, heroImageStoragePath: heroUpload.storagePath } : {}),
        ...sectionUploads,
        updatedAt: serverTimestamp(),
        updatedBy: profile.uid,
      }, { merge: true });
      setHeroFile(null);
      setSectionFiles({});
      setUploadProgress(0);
      setMessage("✅ Website settings saved.");
    } catch (error) {
      setMessage(`❌ Website settings failed: ${error.message}`);
    } finally {
      setBusyKey("");
    }
  }

  async function removeSectionImage(key, label) {
    if (!window.confirm(`Remove the current ${label} image from the live homepage?`)) return;
    setBusyKey(`remove-${key}`);
    try {
      const storagePath = settings[`${key}StoragePath`];
      await setDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "publicWebsite", "settings"), {
        [`${key}Url`]: "",
        [`${key}StoragePath`]: "",
        updatedAt: serverTimestamp(),
        updatedBy: profile.uid,
      }, { merge: true });
      if (storagePath) await deleteObject(ref(storage, storagePath)).catch(() => {});
      if (key === "heroImage") setHeroFile(null);
      setMessage(`✅ ${label} image removed.`);
    } catch (error) {
      setMessage(`❌ Remove failed: ${error.message}`);
    } finally {
      setBusyKey("");
    }
  }

  async function addGallery(event) {
    event.preventDefault();
    const formEl = event.currentTarget;
    if (busyKey) return;
    setBusyKey("gallery");
    setMessage("⏳ Adding gallery item...");
    try {
      const formData = Object.fromEntries(new FormData(formEl).entries());
      const category = String(formData.category || "Campus").trim();
      if (gallery.filter((item) => String(item.category || item.title || "Campus").trim().toLowerCase() === category.toLowerCase()).length >= 20) throw new Error(`${category} already has 20 images. Delete one before adding another.`);
      const upload = galleryFile ? await uploadAlphaWebsiteImage(galleryFile, { section: `gallery-${slugify(category)}`, onProgress: setUploadProgress }) : null;
      if (!upload && !formData.imageUrl) throw new Error("Choose an image from your device or provide an image URL.");
      await addDoc(collection(getFirebaseDb(), "schools", SCHOOL_ID, "publicWebsite", "gallery", "items"), {
        ...formData,
        ...(upload || {}),
        schoolId: SCHOOL_ID,
        createdAt: serverTimestamp(),
        createdBy: profile.uid,
      });
      await setDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "publicWebsite", "settings"), { galleryInitialized: true, updatedAt: serverTimestamp() }, { merge: true });
      if (formEl) formEl.reset();
      setGalleryFile(null);
      setUploadProgress(0);
      setMessage("✅ Gallery item saved.");
    } catch (error) {
      setMessage(`❌ Gallery save failed: ${error.message}`);
    } finally {
      setBusyKey("");
    }
  }

  async function addHeroSlide(event, selectedFile = null) {
    event?.preventDefault?.();
    if (heroImages.length >= 5) return setMessage("❌ Hero carousel supports up to five images. Delete one before adding another.");
    const fileToUpload = selectedFile || heroCarouselFile;
    if (!fileToUpload) return setMessage("❌ Choose a hero image from your device.");
    setBusyKey("hero-carousel");
    setMessage("⏳ Uploading hero image...");
    try {
      const upload = await uploadAlphaWebsiteImage(fileToUpload, { section: "hero-carousel", onProgress: setUploadProgress });
      await addDoc(collection(getFirebaseDb(), "schools", SCHOOL_ID, "publicWebsite", "hero", "items"), { ...upload, title: `Hero ${heroImages.length + 1}`, schoolId: SCHOOL_ID, createdAt: serverTimestamp(), createdBy: profile.uid });
      if (heroImages.length === 0) await setDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "publicWebsite", "settings"), { heroImageUrl: upload.imageUrl, heroImageStoragePath: upload.storagePath, updatedAt: serverTimestamp(), updatedBy: profile.uid }, { merge: true });
      setHeroCarouselFile(null);
      setUploadProgress(0);
      setMessage("✅ Hero carousel image added.");
    } catch (error) {
      setMessage(`❌ Hero upload failed: ${readableFirebaseError(error)}`);
    } finally {
      setBusyKey("");
    }
  }

  async function removeWebsiteItem(section, item) {
    if (!window.confirm(`Delete ${item.title || "this item"}?`)) return;
    setBusyKey(`${section}-${item.id}`);
    try {
      await deleteDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "publicWebsite", section, "items", item.id));
      if (section === "gallery") await setDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "publicWebsite", "settings"), { galleryInitialized: true, updatedAt: serverTimestamp() }, { merge: true });
      if (item.storagePath) await deleteObject(ref(storage, item.storagePath)).catch(() => {});
      setMessage("✅ Item deleted.");
    } catch (error) {
      setMessage(`❌ Delete failed: ${error.message}`);
    } finally {
      setBusyKey("");
    }
  }

  async function addNews(event) {
    event.preventDefault();
    const formEl = event.currentTarget;
    if (busyKey) return;
    setBusyKey("news");
    setMessage("⏳ Adding news post...");
    try {
      await addDoc(collection(getFirebaseDb(), "schools", SCHOOL_ID, "publicWebsite", "news", "items"), {
        ...Object.fromEntries(new FormData(formEl).entries()),
        schoolId: SCHOOL_ID,
        createdAt: serverTimestamp(),
        createdBy: profile.uid,
      });
      if (formEl) formEl.reset();
      setMessage("✅ News post saved.");
    } catch (error) {
      setMessage(`❌ News save failed: ${error.message}`);
    } finally {
      setBusyKey("");
    }
  }

  return (
    <Panel title="Alpha Homepage Studio" action={<a href="/" target="_blank" rel="noopener noreferrer" style={{ background: BLUE, color: "#fff", borderRadius: 8, padding: "10px 13px", fontWeight: 950, textDecoration: "none" }}>Preview Live Homepage</a>}>
      {message && <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: DARK, borderRadius: 8, padding: 12, fontWeight: 800 }}>{message}</div>}
      {uploadProgress > 0 && <div style={{ background: "#FFFBEB", border: `1px solid ${GOLD}`, borderRadius: 8, padding: 12, fontWeight: 900 }}>Uploading to Alpha Firebase Storage… {uploadProgress}%</div>}
      <section style={{ border: "1px solid #D8E2F0", borderRadius: 12, padding: 18, display: "grid", gap: 14, background: "#F8FAFC" }}>
      <div><h2 style={{ margin: 0, color: DARK, fontSize: 20 }}>Hero & School Identity</h2><p style={{ margin: "5px 0 0", color: "#64748B" }}>Control the first section visitors see. Upload a clear landscape photo from your device.</p></div>
      <form onSubmit={saveSettings} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
        <RawInput name="heroTitle" label="Hero Title" defaultValue={settings.heroTitle || ""} />
        <RawInput name="heroSubtitle" label="Hero Subtitle" defaultValue={settings.heroSubtitle || ""} />
        <RawInput name="heroBadges" label="Hero Badges" defaultValue={settings.heroBadges || ""} />
        <RawInput name="heroImageUrl" label="Hero Image URL" defaultValue={settings.heroImageUrl || ""} />
        <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Upload Hero Background<input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(event) => setHeroFile(event.target.files?.[0] || null)} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: 8, background: "#fff" }} /></label>
        {(heroFile || settings.heroImageUrl) && <div style={{ gridColumn: "1 / -1", display: "grid", gap: 10, justifyItems: "start" }}><img src={heroFile ? URL.createObjectURL(heroFile) : settings.heroImageUrl} alt="Hero preview" style={{ width: "min(100%,620px)", maxHeight: 280, objectFit: "cover", borderRadius: 10, border: "1px solid #CBD5E1" }} />{settings.heroImageUrl && <Button variant="danger" busy={busyKey === "remove-heroImage"} onClick={() => removeSectionImage("heroImage", "hero background")}>Delete Current Hero Background</Button>}</div>}
        <RawInput name="aboutText" label="About Text" multiline defaultValue={settings.aboutText || ""} style={{ minHeight: 130 }} />
        <RawInput name="founderName" label="Founder Name" defaultValue={settings.founderName || defaultWebsite.founderName} />
        <RawInput name="founderTitle" label="Founder Title" defaultValue={settings.founderTitle || defaultWebsite.founderTitle} />
        <RawInput name="founderMessage" label="Founder Story / Message" multiline defaultValue={settings.founderMessage || defaultWebsite.founderMessage} style={{ minHeight: 180 }} wrapperStyle={{ gridColumn: "1 / -1" }} />
        <RawInput name="directorName" label="Managing Director Name" defaultValue={settings.directorName || defaultWebsite.directorName} />
        <RawInput name="directorTitle" label="Managing Director Title" defaultValue={settings.directorTitle || defaultWebsite.directorTitle} />
        <RawInput name="directorMessage" label="Managing Director Message" multiline defaultValue={settings.directorMessage || defaultWebsite.directorMessage} style={{ minHeight: 180 }} wrapperStyle={{ gridColumn: "1 / -1" }} />
        <RawInput name="whyChooseText" label="Why Choose Alpha Introduction" multiline defaultValue={settings.whyChooseText || defaultWebsite.whyChooseText} style={{ minHeight: 150 }} wrapperStyle={{ gridColumn: "1 / -1" }} />
        <RawInput name="missionText" label="Mission" multiline defaultValue={settings.missionText || defaultWebsite.missionText} style={{ minHeight: 130 }} />
        <RawInput name="visionText" label="Vision" multiline defaultValue={settings.visionText || defaultWebsite.visionText} style={{ minHeight: 130 }} />
        <RawInput name="coreValues" label="Core Values (comma separated)" defaultValue={settings.coreValues || defaultWebsite.coreValues} wrapperStyle={{ gridColumn: "1 / -1" }} />
        <RawInput name="schoolsText" label="Our Schools Introduction" multiline defaultValue={settings.schoolsText || defaultWebsite.schoolsText} style={{ minHeight: 130 }} wrapperStyle={{ gridColumn: "1 / -1" }} />
        <RawInput name="academicsText" label="Academics Introduction" multiline defaultValue={settings.academicsText || defaultWebsite.academicsText} style={{ minHeight: 130 }} wrapperStyle={{ gridColumn: "1 / -1" }} />
        <RawInput name="admissionsRequirements" label="Admission Requirements" multiline defaultValue={settings.admissionsRequirements || defaultWebsite.admissionsRequirements} style={{ minHeight: 150 }} />
        <RawInput name="admissionsProcess" label="Application Process" multiline defaultValue={settings.admissionsProcess || defaultWebsite.admissionsProcess} style={{ minHeight: 150 }} />
        <RawInput name="scholarshipText" label="Scholarship Information" multiline defaultValue={settings.scholarshipText || defaultWebsite.scholarshipText} style={{ minHeight: 150 }} wrapperStyle={{ gridColumn: "1 / -1" }} />
        <RawInput name="facebookUrl" label="Facebook URL" defaultValue={settings.facebookUrl || ""} />
        <RawInput name="instagramUrl" label="Instagram URL" defaultValue={settings.instagramUrl || defaultWebsite.instagramUrl} />
        <RawInput name="youtubeUrl" label="YouTube URL" defaultValue={settings.youtubeUrl || ""} />
        <RawInput name="admissionsText" label="Admissions Text" multiline defaultValue={settings.admissionsText || ""} style={{ minHeight: 130 }} />
        <RawInput name="admissionPhone" label="Admission Phone" defaultValue={settings.admissionPhone || ""} />
        <RawInput name="contactPhone" label="Contact Phone" defaultValue={settings.contactPhone || ""} />
        <RawInput name="contactEmail" label="Contact Email" defaultValue={settings.contactEmail || ""} />
        <RawInput name="contactLocation" label="Contact Location" defaultValue={settings.contactLocation || ""} />
        <RawInput name="mapEmbedUrl" label="Google Map Embed URL" defaultValue={settings.mapEmbedUrl || ""} />
        <div style={{ gridColumn: "1 / -1", display: "grid", gap: 12 }}><h3 style={{ margin: "8px 0 0", color: DARK }}>Section Backgrounds</h3><div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 12 }}>{[
          ["aboutBackground", "About Background"],
          ["aboutImage", "About Feature Image"],
          ["founderImage", "Founder Portrait"],
          ["directorImage", "Managing Director Portrait"],
          ["portalPromoImage", "Portal Login Promo Thumbnail"],
          ["academicsBackground", "Academics Background"],
          ["studentLifeBackground", "Student Life Background"],
        ].map(([key, label]) => { const isPortrait = key === "founderImage" || key === "directorImage"; return <div key={key} style={{ border: "1px solid #D8E2F0", borderRadius: 10, padding: 12, background: "#fff", display: "grid", gap: 9, alignContent: "start" }}><strong style={{ color: DARK }}>{label}</strong>{settings[`${key}Url`] && <img src={settings[`${key}Url`]} alt={label} style={{ display: "block", width: "100%", height: "auto", aspectRatio: isPortrait ? "auto" : "16/9", objectFit: isPortrait ? "contain" : "cover", borderRadius: 8, background: LIGHT }} />}<input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(event) => setSectionFiles((current) => ({ ...current, [key]: event.target.files?.[0] || null }))} />{settings[`${key}Url`] && <Button variant="danger" busy={busyKey === `remove-${key}`} onClick={() => removeSectionImage(key, label)}>Delete Current Image</Button>}</div>; })}</div></div>
        <div style={{ alignSelf: "end" }}><Button type="submit" variant="gold" busy={busyKey === "settings"}>Save Website</Button></div>
      </form>
      </section>
      <section style={{ border: "1px solid #D8E2F0", borderRadius: 12, padding: 18, display: "grid", gap: 14, background: "#F8FAFC" }}>
        <div><h2 style={{ margin: 0, color: DARK, fontSize: 20 }}>Hero Image Rotation</h2><p style={{ margin: "5px 0 0", color: "#64748B" }}>Upload up to five high-quality landscape images. The live homepage rotates them automatically and visitors can choose a slide.</p></div>
        <form onSubmit={addHeroSlide} style={{ display: "grid", gridTemplateColumns: "minmax(220px,1fr) auto", gap: 12, alignItems: "end" }}><DeviceUploadField id="alpha-hero-carousel-upload" label="Upload hero carousel image" help="Selecting a file uploads it immediately. Recommended: WebP or JPEG, at least 1600 × 900." file={heroCarouselFile} onFileChange={(file) => { setHeroCarouselFile(file); if (file) addHeroSlide(null, file); }} accept="image/png,image/jpeg,image/webp" disabled={Boolean(busyKey) || heroImages.length >= 5} /><Button type="submit" variant="gold" busy={busyKey === "hero-carousel"} disabled={heroImages.length >= 5 || !heroCarouselFile}>Upload Again ({heroImages.length}/5)</Button></form>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12 }}>{heroImages.map((item, index) => <article key={item.id} style={{ border: "1px solid #D8E2F0", borderRadius: 10, overflow: "hidden", background: "#fff" }}><img src={item.imageUrl} alt={`Hero ${index + 1}`} style={{ width: "100%", aspectRatio: "16/9", objectFit: "cover" }} /><div style={{ padding: 10, display: "grid", gap: 8 }}><strong>Hero image {index + 1}</strong><Button variant="danger" busy={busyKey === `hero-${item.id}`} onClick={() => removeWebsiteItem("hero", item)}>Delete</Button></div></article>)}</div>
      </section>
      <section style={{ border: "1px solid #D8E2F0", borderRadius: 12, padding: 18, display: "grid", gap: 14 }}>
      <div><h2 style={{ margin: 0, color: DARK, fontSize: 20 }}>Photo Gallery</h2><p style={{ margin: "5px 0 0", color: "#64748B" }}>Upload campus, classroom, practical, sports, admissions, and event photos directly.</p></div>
      <form onSubmit={addGallery} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
        <RawInput name="title" label="Gallery Title" required />
        <RawInput name="category" label="Gallery Category" placeholder="Campus, Students, Sports..." required />
        <RawInput name="imageUrl" label="Image URL (optional)" />
        <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Upload From Device<input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(event) => setGalleryFile(event.target.files?.[0] || null)} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: 8, background: "#fff" }} /></label>
        <div style={{ alignSelf: "end" }}><Button type="submit" busy={busyKey === "gallery"}>Add Gallery Image</Button></div>
      </form>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 12 }}>{gallery.map((item) => <article key={item.id} style={{ border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden", background: "#fff" }}><img src={item.imageUrl} alt={item.title || "Gallery"} style={{ width: "100%", aspectRatio: "16/10", objectFit: "cover", background: LIGHT }} /><div style={{ padding: 12, display: "grid", gap: 9 }}><strong style={{ color: DARK }}>{item.title}</strong><Button variant="danger" busy={busyKey === `gallery-${item.id}`} onClick={() => removeWebsiteItem("gallery", item)}>Delete</Button></div></article>)}</div>
      </section>
      <section style={{ border: "1px solid #D8E2F0", borderRadius: 12, padding: 18, display: "grid", gap: 14, background: "#F8FAFC" }}>
      <div><h2 style={{ margin: 0, color: DARK, fontSize: 20 }}>News & Updates</h2><p style={{ margin: "5px 0 0", color: "#64748B" }}>Publish school announcements and homepage news.</p></div>
      <form onSubmit={addNews} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
        <RawInput name="title" label="News Title" required />
        <RawInput name="tag" label="Tag" />
        <RawInput name="body" label="Body" required multiline style={{ minHeight: 180 }} wrapperStyle={{ gridColumn: "1 / -1" }} />
        <div style={{ alignSelf: "end" }}><Button type="submit" busy={busyKey === "news"}>Add News Post</Button></div>
      </form>
      <Table rows={news} columns={[{ key: "title", label: "Title" }, { key: "tag", label: "Tag" }, { key: "body", label: "Body" }, { key: "actions", label: "Actions", render: (row) => <Button variant="danger" busy={busyKey === `news-${row.id}`} onClick={() => removeWebsiteItem("news", row)}>Delete</Button> }]} />
      </section>
      <section style={{ border: "1px solid #D8E2F0", borderRadius: 12, padding: 18, display: "grid", gap: 14 }}><div><h2 style={{ margin: 0, color: DARK, fontSize: 20 }}>Contact Enquiries</h2><p style={{ margin: "5px 0 0", color: "#64748B" }}>Messages submitted from the public Alpha website. Visitor details are visible only to authorized admins.</p></div><Table rows={inquiries} columns={[{ key: "name", label: "Name" },{ key: "phone", label: "Phone" },{ key: "email", label: "Email" },{ key: "subject", label: "Subject" },{ key: "message", label: "Message" },{ key: "status", label: "Status" }]} /></section>
    </Panel>
  );
}

function TeacherModule({ module, profile, collections, visibleClasses }) {
  if (module === "My Classes") return <ClassModule profile={profile} classes={visibleClasses} users={collections.users.items} />;
  if (module === "Attendance") return <AttendanceModule profile={profile} items={collections.attendance.items} classes={visibleClasses} />;
  if (module === "Assignments") return <LearningModule collectionName="assignments" title="Assignments" profile={profile} items={collections.assignments.items} classes={visibleClasses} terms={collections.terms.items} submissions={collections.submissions.items} />;
  if (module === "Quizzes") return <QuizBuilderModule profile={profile} quizzes={collections.quizzes.items} classes={visibleClasses} attempts={(collections.quizAttempts?.items || [])} users={collections.users.items} />;
  if (module === "Notes") return <LearningModule collectionName="notes" title="Notes" profile={profile} items={collections.notes.items} classes={visibleClasses} terms={collections.terms.items} />;
  if (module === "Announcements") return <LearningModule collectionName="announcements" title="Announcements" profile={profile} items={collections.announcements.items} classes={visibleClasses} terms={collections.terms.items} />;
  if (module === "Resources") return <LearningModule collectionName="resources" title="Resources" profile={profile} items={collections.resources.items} classes={visibleClasses} terms={collections.terms.items} />;
  if (module === "Students") return <PeopleModule title="Students" role="student" profile={profile} users={collections.users.items} classes={visibleClasses} />;
  if (module === "Gradebook") return <Gradebook submissions={collections.submissions.items} assignments={collections.assignments.items} quizAttempts={collections.quizAttempts.items} quizzes={collections.quizzes.items} profile={profile} />;
  return <Reports collections={collections} />;
}

function StudentNotifications({ profile, onOpenModule }) {
  const { items, loading, unreadCount } = useAlphaStudentNotifications(profile.uid);
  const [busy, setBusy] = useState("");
  const typeIcons = {
    new_assignment: "📝", new_note: "📚", new_quiz: "❓", new_announcement: "📣",
    new_resource: "📎", new_exam: "📊", assignment_marked: "✅", feedback_received: "💬",
    assignment_submitted: "📤", quiz_submitted: "📤", attendance_marked: "✅",
    account_approved: "🎉", request_approved: "🎉", request_rejected: "❌",
    account_rejected: "❌", teacher_message: "✉️", class_announcement: "📣",
  };
  async function handleOpen(n) {
    if (!n.read && n.status !== "read") {
      setBusy(n.id);
      await markAlphaNotificationRead(n.id);
      setBusy("");
    }
    if (n.module && onOpenModule) onOpenModule(n.module);
  }
  if (loading) return <Panel title="Notifications"><div style={{ padding: 24, textAlign: "center", color: "#64748B" }}>Loading notifications…</div></Panel>;
  if (!items.length) return <Panel title="Notifications"><AlphaEmptyState title="No notifications" subtitle="You are all caught up." /></Panel>;
  return (
    <Panel title={`Notifications${unreadCount > 0 ? ` · ${unreadCount} unread` : ""}`}>
      <div style={{ display: "grid", gap: 8 }}>
        {items.map((n) => {
          const isUnread = !n.read && n.status !== "read";
          return (
            <button key={n.id} type="button" onClick={() => handleOpen(n)} disabled={busy === n.id} style={{ display: "grid", gridTemplateColumns: "32px 1fr auto", gap: 10, alignItems: "start", textAlign: "left", border: `1px solid ${isUnread ? GOLD : "#E2E8F0"}`, background: isUnread ? "#FFFBEB" : "#fff", borderRadius: 10, padding: "12px 14px", cursor: "pointer", opacity: busy === n.id ? 0.6 : 1 }}>
              <span style={{ fontSize: 20 }} aria-hidden="true">{typeIcons[n.type] || "🔔"}</span>
              <span style={{ minWidth: 0 }}>
                <strong style={{ display: "block", color: DARK, fontSize: 14, fontWeight: 900 }}>{n.title || "Notification"}</strong>
                <span style={{ display: "block", color: "#475569", fontSize: 13, marginTop: 2 }}>{n.message || ""}</span>
                <span style={{ display: "block", color: "#94A3B8", fontSize: 11, marginTop: 4 }}>{formatDate(n.createdAt)}</span>
              </span>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: isUnread ? GOLD : "transparent", marginTop: 6 }} aria-hidden="true" />
            </button>
          );
        })}
      </div>
    </Panel>
  );
}

function StudentModule({ module, profile, collections, visibleClasses }) {
  const classIds = Array.from(new Set([...visibleClasses.map((item) => item.id), profile.classId, ...(profile.classIds || []), ...(profile.assignedClassIds || [])].filter(Boolean)));
  const classNames = new Set([profile.className, profile.class, ...visibleClasses.map((item) => item.name || item.className)].filter(Boolean).map((value) => String(value).trim().toLowerCase()));
  const inClass = (item) => !item.classId && !item.className || classIds.includes(item.classId) || classNames.has(String(item.className || "").trim().toLowerCase());
  const studentVisible = (item) => inClass(item) && !["draft", "archived"].includes(item.status || "active") && !["teachers", "draft"].includes(item.visibility || "students");
  if (module === "My Classes") return <Panel title="My Classes"><Table rows={visibleClasses} columns={[{ key: "name", label: "Class" }, { key: "subject", label: "Subject" }, { key: "classCode", label: "Code" }, { key: "teacherName", label: "Teacher" }]} /></Panel>;
  if (module === "Assignments") return <StudentAssignments profile={profile} assignments={collections.assignments.items.filter(studentVisible)} submissions={collections.submissions.items} />;
  if (module === "Attendance") return <StudentAttendance profile={profile} sessions={collections.attendance.items.filter(inClass)} />;
  if (module === "Quizzes") return <StudentQuizModule profile={profile} quizzes={collections.quizzes.items.filter((item) => inClass(item) && item.status === "published")} attempts={(collections.quizAttempts?.items || [])} />;
  if (module === "Announcements") return <StudentReadOnly profile={profile} collectionName="announcements" title="Announcements" items={collections.announcements.items.filter(studentVisible)} columns={[{ key: "title", label: "Title" }, { key: "body", label: "Message", wrap: true, render: (row) => <span style={{ whiteSpace: "pre-wrap" }}>{row.body || row.message || row.content}</span> }, { key: "className", label: "Class" }, { key: "createdAt", label: "Date", render: (row) => formatDate(row.createdAt) }, { key: "status", label: "Status/New", render: (row) => <StudentNewBadge unread={isUnreadForStudent(profile.uid, "announcements", row)} status={row.status} /> }]} />;
  if (module === "Resources") return <StudentReadOnly profile={profile} collectionName="resources" title="Resources" items={collections.resources.items.filter(studentVisible)} columns={[{ key: "title", label: "Resource" }, { key: "body", label: "Details", wrap: true, render: (row) => row.body || row.url }, { key: "className", label: "Class" }, { key: "createdAt", label: "Date", render: (row) => formatDate(row.createdAt) }]} />;
  if (module === "Notes") return <StudentReadOnly profile={profile} collectionName="notes" title="Notes" items={collections.notes.items.filter(studentVisible)} columns={[{ key: "title", label: "Note" }, { key: "body", label: "Content", wrap: true, render: (row) => <span style={{ whiteSpace: "pre-wrap" }}>{row.body}</span> }, { key: "className", label: "Class" }, { key: "createdAt", label: "Date", render: (row) => formatDate(row.createdAt) }]} />;
  if (module === "Results") return <StudentReadOnly title="Results" items={collections.submissions.items.filter((item) => item.studentUid === profile.uid)} columns={[{ key: "assignmentTitle", label: "Item" }, { key: "grade", label: "Grade" }, { key: "feedback", label: "Feedback" }]} />;
  if (module === "Notifications") return <StudentNotifications profile={profile} onOpenModule={profile.onOpenModule} />;
  if (module === "Analytics") return <StudentAnalytics profile={profile} collections={collections} />;
  if (module === "Calendar") return <StudentReadOnly title="Calendar" items={[...collections.assignments.items, ...collections.quizzes.items, ...collections.notes.items, ...collections.resources.items].filter(studentVisible)} columns={[{ key: "title", label: "Item" }, { key: "dueDate", label: "Date", render: (row) => row.dueDate || row.lessonDate || row.resourceDate || formatDate(row.createdAt) }, { key: "className", label: "Class" }]} />;
  return <Panel title="Profile"><Table rows={[profile]} columns={[{ key: "fullName", label: "Name" }, { key: "studentId", label: "Student ID" }, { key: "status", label: "Status" }]} /></Panel>;
}

function PeopleModule({ title, role, authUser, profile, users, classes = [] }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event.preventDefault();
    const formEl = event.currentTarget;
    if (busy) return;
    setBusy(true);
    setMessage(`⏳ Saving ${roleLabel(role)}...`);

    try {
      const data = Object.fromEntries(new FormData(formEl).entries());
      const db = getFirebaseDb();

      if (role === "student") {
        const studentId = String(data.studentId || "").trim();
        if (!studentId) throw new Error("Student ID is required.");
        const normalizedStudentId = cleanStudentId(studentId);
        if (!normalizedStudentId) throw new Error("Student ID is invalid.");
        const classId = String(data.classId || "").trim();
        const classItem = classes.find((item) => item.id === classId);
        if (!classId || !classItem) throw new Error("Choose a class for this student.");

        const syntheticEmail = syntheticStudentEmail(studentId);
        const rosterId = `student-${normalizedStudentId}`;
        const rosterRef = doc(db, "schools", SCHOOL_ID, "users", rosterId);
        const existingRoster = await getDoc(rosterRef);
        const existingData = existingRoster.exists() ? existingRoster.data() : null;
        const incomingName = String(data.fullName || "").trim();
        const existingName = String(existingData?.fullName || "").trim();
        const seriousConflict = existingData
          && normalizeRole(existingData.role) === "student"
          && !["duplicate", "claimed", "suspended"].includes(existingData.status || "active")
          && existingName
          && incomingName
          && existingName.toLowerCase() !== incomingName.toLowerCase()
          && (existingData.passwordCreated || existingData.uid || existingData.claimedUid);
        if (seriousConflict) throw new Error("Student ID already belongs to another active student.");

        const studentProfile = {
          fullName: incomingName || existingData?.fullName || "Student",
          studentId,
          email: syntheticEmail,
          phone: data.phone || "",
          role: "student",
          schoolId: SCHOOL_ID,
          classId,
          classIds: [classId],
          className: classItem.name || classItem.className || "",
          form: data.form || classItem.formLevel || "",
          stream: data.stream || classItem.stream || "",
          status: "active",
          passwordCreated: Boolean(existingData?.passwordCreated),
          createdAt: existingData?.createdAt || serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: existingData?.createdBy || profile.uid,
          updatedBy: profile.uid,
          note: "Roster created. Student must create their own password on first login using Student ID.",
        };

        if (existingData?.uid) studentProfile.uid = existingData.uid;
        if (existingData?.claimedUid) studentProfile.claimedUid = existingData.claimedUid;
        if (existingData?.claimedAt) studentProfile.claimedAt = existingData.claimedAt;
        if (existingData?.attachedAt) studentProfile.attachedAt = existingData.attachedAt;
        if (existingData?.attachedFrom) studentProfile.attachedFrom = existingData.attachedFrom;

        await setDoc(rosterRef, studentProfile, { merge: true });
        setMessage(`${existingRoster.exists() ? "✅ Student roster updated" : "✅ Student roster saved"}. Student ID: ${studentId}. Student should login with Student ID and create their own password.`);
        if (formEl) formEl.reset();
        return;
      }

      const email = String(data.email || "").trim().toLowerCase();
      if (!email) throw new Error("Teacher email is required.");

      const teacherAccess = {
        fullName: data.fullName || "Teacher",
        email,
        phone: data.phone || "",
        role,
        schoolId: SCHOOL_ID,
        status: "active",
        passwordCreated: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: profile.uid,
        note: "Teacher approved. Teacher creates their own password on first login.",
      };

      const accessRef = doc(db, "schools", SCHOOL_ID, "emailAccess", emailDocId(email));
      const existingAccess = await getDoc(accessRef);
      const existingAccessData = existingAccess.exists() ? existingAccess.data() : {};
      const mergedRoles = Array.from(new Set([...normalizeRoles(existingAccessData), role]));
      teacherAccess.roles = mergedRoles;
      teacherAccess.role = normalizeRole(existingAccessData.role || role);
      await setDoc(accessRef, teacherAccess, { merge: true });

      const existingUser = users.find((item) => String(item.email || "").trim().toLowerCase() === email && item.uid);
      if (existingUser) {
        await setDoc(doc(db, "schools", SCHOOL_ID, "users", existingUser.uid), { roles: Array.from(new Set([...normalizeRoles(existingUser), role])), updatedAt: serverTimestamp(), updatedBy: profile.uid }, { merge: true });
      }

      setMessage(`✅ ${roleLabel(role)} access ${existingAccess.exists() ? "updated" : "approved"} for ${email}. They should open Alpha School Portal and create their own password using this email.`);
      if (formEl) formEl.reset();
    } catch (error) {
      console.error("[AlphaClassroom] save roster/access failed", error);
      setMessage(`❌ Save ${roleLabel(role)} failed: ${error.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(user, status) {
    if (profile.role !== "schoolAdmin") return;
    await updateDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "users", user.uid || user.id), {
      status,
      updatedAt: serverTimestamp(),
    });
    setMessage(`✅ ${roleLabel(role)} status updated.`);
  }

  async function editStudent(user) {
    const fullName = window.prompt("Student full name", user.fullName || "");
    if (fullName === null) return;
    const phone = window.prompt("Phone / parent phone", user.phone || "");
    if (phone === null) return;
    const studentId = window.prompt("Student ID", user.studentId || "");
    if (studentId === null) return;
    const normalizedStudentId = cleanStudentId(studentId);
    if (!fullName.trim() || !normalizedStudentId) return setMessage("❌ Student name and valid Student ID are required.");
    const currentId = user.id || user.uid;
    const nextId = `student-${normalizedStudentId}`;
    try {
      const db = getFirebaseDb();
      if (nextId !== currentId) {
        const existing = await getDoc(doc(db, "schools", SCHOOL_ID, "users", nextId));
        if (existing.exists()) throw new Error("That Student ID is already assigned to another student.");
        await setDoc(doc(db, "schools", SCHOOL_ID, "users", nextId), {
          ...user,
          id: undefined,
          fullName: fullName.trim(),
          phone: phone.trim(),
          studentId: studentId.trim(),
          email: syntheticStudentEmail(studentId),
          status: "active",
          updatedAt: serverTimestamp(),
          updatedBy: profile.uid,
        });
        await updateDoc(doc(db, "schools", SCHOOL_ID, "users", currentId), { status: "replaced", replacedBy: nextId, updatedAt: serverTimestamp(), updatedBy: profile.uid });
      } else {
        await updateDoc(doc(db, "schools", SCHOOL_ID, "users", currentId), { fullName: fullName.trim(), phone: phone.trim(), studentId: studentId.trim(), email: syntheticStudentEmail(studentId), updatedAt: serverTimestamp(), updatedBy: profile.uid });
      }
      setMessage(`✅ Student updated. Login ID: ${studentId.trim()}`);
    } catch (error) {
      setMessage(`❌ Student update failed: ${readableFirebaseError(error)}`);
    }
  }

  async function removeStudent(user) {
    if (!window.confirm(`Remove ${user.fullName || "this student"} from the active roster?`)) return;
    try {
      await updateDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "users", user.id || user.uid), { status: "deleted", deletedAt: serverTimestamp(), deletedBy: profile.uid, updatedAt: serverTimestamp() });
      setMessage("✅ Student removed from the active roster. Their Student ID can no longer create a portal account.");
    } catch (error) {
      setMessage(`❌ Student removal failed: ${readableFirebaseError(error)}`);
    }
  }

  async function setTemporaryPassword(user) {
    const temporaryPassword = window.prompt(`Set a temporary password for ${user.fullName || user.email || user.studentId}. Minimum 8 characters:`);
    if (temporaryPassword === null) return;
    if (temporaryPassword.length < 8) return setMessage("❌ Temporary password must contain at least 8 characters.");
    try {
      const result = await apiRequest(authUser, `${ALPHA_API_BASE}/api/alpha/users/set-temporary-password`, { method: "POST", body: JSON.stringify({ targetId: user.id || user.uid, temporaryPassword }) });
      setMessage(`✅ Temporary password set for ${result.email}. Share it privately. For security, it will not be displayed again.`);
    } catch (error) {
      setMessage(`❌ Password update failed: ${error.message}`);
    }
  }

  const visibleClassIds = classes.map((item) => item.id).filter(Boolean);
  const activeRows = dedupeAlphaUsers(users, role, {
    visibleClassIds: role === "student" && profile.role === "teacher" ? visibleClassIds : [],
  });

  const columns = [
    { key: "fullName", label: "Name" },
    { key: "phone", label: "Phone" },
    { key: "studentId", label: "Student ID" },
    { key: "className", label: "Class" },
    { key: "form", label: "Form" },
    { key: "stream", label: "Stream" },
    { key: "status", label: "Status" },
    { key: "passwordCreated", label: "Password", render: (row) => row.passwordCreated ? "Created" : "Not created" },
  ];
  if (role !== "student") {
    columns.splice(1, 0, { key: "email", label: "Email" });
  }
  if (role === "student" && ["schoolAdmin", "teacher"].includes(profile.role)) {
    columns.push({ key: "actions", label: "Actions", render: (row) => <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><Button variant="light" onClick={() => editStudent(row)}>Edit</Button>{profile.role === "schoolAdmin" && <Button variant="gold" onClick={() => setTemporaryPassword(row)}>Set Password</Button>}<Button variant="danger" onClick={() => removeStudent(row)}>Delete</Button></div> });
  } else if (profile.role === "schoolAdmin") {
    columns.push({ key: "actions", label: "Actions", render: (row) => <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><Button variant="light" onClick={() => setStatus(row, row.status === "suspended" ? "active" : "suspended")}>{row.status === "suspended" ? "Activate" : "Suspend"}</Button><Button variant="gold" onClick={() => setTemporaryPassword(row)}>Set Password</Button></div> });
  }

  return (
    <Panel title={title}>
      {message && <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: DARK, borderRadius: 8, padding: 12, fontWeight: 800 }}>{message}</div>}

      <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 12 }}>
        <RawInput name="fullName" label="Full Name" required />

        {role === "student" ? (
          <>
            <RawInput name="studentId" label="Student ID" required />
            <RawInput name="phone" label="Phone / Parent Phone" />
            <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>
              Class
              <select name="classId" required disabled={!classes.length} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700, background: "#fff" }}>
                <option value="">{classes.length ? "Choose class" : "No classes available"}</option>
                {classes.map((item) => <option key={item.id} value={item.id}>{item.name || item.className || item.id}</option>)}
              </select>
            </label>
            <RawInput name="form" label="Form" />
            <RawInput name="stream" label="Stream" />
          </>
        ) : (
          <>
            <RawInput name="email" label="Email" type="email" required />
            <RawInput name="phone" label="Phone" />
          </>
        )}

        <div style={{ alignSelf: "end" }}>
          <Button type="submit" variant="gold" busy={busy} disabled={role === "student" && !classes.length}>
            {role === "student" ? "Add Student Roster" : `Approve ${roleLabel(role)}`}
          </Button>
        </div>
      </form>

      <Table rows={activeRows} columns={columns} />
    </Panel>
  );
}

function ClassModule({ profile, classes, users, canAssign = false }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [openClassId, setOpenClassId] = useState("");

  async function copyText(value, label = "Copied") {
    try {
      await navigator.clipboard.writeText(value || "");
      setMessage(`✅ ${label} copied.`);
    } catch {
      setMessage("❌ Copy failed. Please copy manually.");
    }
  }

  async function submit(event) {
    event.preventDefault();
    const formEl = event.currentTarget;
    if (busy) return;
    setBusy(true);
    setMessage("⏳ Creating class...");
    try {
      const db = getFirebaseDb();
      const data = Object.fromEntries(new FormData(formEl).entries());
      const teacher = users.find((user) => user.uid === data.teacherId);
      const classCode = makeCode("CLASS");

      await addDoc(collection(db, "schools", SCHOOL_ID, "classes"), {
        ...data,
        schoolId: SCHOOL_ID,
        classCode,
        joinLink: "",
        teacherName: teacher?.fullName || profile.fullName || "",
        teacherId: data.teacherId || profile.uid,
        teacherIds: [data.teacherId || profile.uid],
        studentUids: [],
        status: data.status || "active",
        createdBy: profile.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (formEl) formEl.reset();
      setMessage(`✅ Class created successfully. Code: ${classCode}`);
    } catch (error) {
      console.error("[AlphaClassroom] create class failed", error);
      setMessage(`❌ Create class failed: ${error.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit(event, classId) {
    event.preventDefault();
    const formEl = event.currentTarget;
    setMessage("⏳ Saving class...");
    try {
      const data = Object.fromEntries(new FormData(formEl).entries());
      await setDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "classes", classId), {
        ...data,
        updatedAt: serverTimestamp(),
        updatedBy: profile.uid,
      }, { merge: true });
      setEditingId("");
      setMessage("✅ Class updated successfully.");
    } catch (error) {
      console.error("[AlphaClassroom] update class failed", error);
      setMessage(`❌ Update class failed: ${error.message}`);
    }
  }

  const teacherOptions = users.filter((user) => user.role === "teacher");

  return (
    <Panel title="Classes">
      {message && <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: DARK, borderRadius: 8, padding: 12, fontWeight: 800 }}>{message}</div>}

      <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 12 }}>
        <RawInput name="name" label="Class Name" required />
        <RawInput name="subject" label="Subject" required />

        <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>
          Form / Level
          <select name="formLevel" required style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700 }}>
            <option value="">Choose form</option>
            {["Form 1","Form 2","Form 3","Form 4","Form 5","Form 6"].map((form) => <option key={form} value={form}>{form}</option>)}
          </select>
        </label>

        <RawInput name="stream" label="Stream / Combination" placeholder="Arts, Science, PCM, PCB..." />
        <RawInput name="room" label="Room" />
        <RawInput name="academicYear" label="Academic Year" defaultValue="2026" />
        <RawInput name="term" label="Term" defaultValue="Term 1" />

        {canAssign && (
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>
            Teacher
            <select name="teacherId" style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700 }}>
              {teacherOptions.map((user) => <option key={user.uid} value={user.uid}>{user.fullName || user.email}</option>)}
            </select>
          </label>
        )}

        <div style={{ alignSelf: "end" }}>
          <Button type="submit" variant="gold" busy={busy}>Create Class</Button>
        </div>
      </form>

      <div style={{ display: "grid", gap: 12 }}>
        {classes.length ? classes.map((item) => (
          <div key={item.id} style={{ border: "1px solid #D8E2F0", borderRadius: 14, background: "#fff", padding: 16, display: "grid", gap: 12 }}>
            {editingId === item.id ? (
              <form onSubmit={(event) => saveEdit(event, item.id)} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
                <RawInput name="name" label="Class Name" defaultValue={item.name || ""} required />
                <RawInput name="subject" label="Subject" defaultValue={item.subject || ""} required />
                <RawInput name="formLevel" label="Form / Level" defaultValue={item.formLevel || ""} />
                <RawInput name="stream" label="Stream / Combination" defaultValue={item.stream || ""} />
                <RawInput name="room" label="Room" defaultValue={item.room || ""} />
                <RawInput name="academicYear" label="Academic Year" defaultValue={item.academicYear || ""} />
                <RawInput name="term" label="Term" defaultValue={item.term || ""} />
                <RawInput name="capacity" label="Capacity" type="number" defaultValue={item.capacity || ""} />
                <div style={{ display: "flex", gap: 8, alignSelf: "end", flexWrap: "wrap" }}>
                  <Button type="submit" variant="gold">Save</Button>
                  <Button variant="light" onClick={() => setEditingId("")}>Cancel</Button>
                </div>
              </form>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <h3 style={{ margin: 0, color: DARK, fontSize: 22 }}>{item.name}</h3>
                    <p style={{ margin: "6px 0 0", color: "#64748B", fontWeight: 800 }}>
                      {item.subject || "No subject"} · {item.formLevel || "Form missing"} · {item.stream || "Stream missing"}
                    </p>
                    <p style={{ margin: "4px 0 0", color: "#64748B" }}>
                      Room: {item.room || "Missing"} · Year: {item.academicYear || "Missing"} · Term: {item.term || "Missing"} · Students: {(item.studentUids || []).length}/{item.capacity || "∞"}
                    </p>
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <Button variant="gold" onClick={() => setOpenClassId(openClassId === item.id ? "" : item.id)}>
                      {openClassId === item.id ? "Close Class" : "Open Class"}
                    </Button>
                    <Button variant="light" onClick={() => setEditingId(item.id)}>Edit</Button>
                  </div>
                </div>

                {openClassId === item.id && (
                  <div style={{ marginTop: 14, borderTop: "1px solid #E2E8F0", paddingTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10 }}>
                    <div><strong>Class Code</strong><br />{item.classCode || "Missing"}</div>
                    <div><strong>Subject</strong><br />{item.subject || "Missing"}</div>
                    <div><strong>Form</strong><br />{item.formLevel || "Missing"}</div>
                    <div><strong>Stream</strong><br />{item.stream || "Missing"}</div>
                    <div><strong>Room</strong><br />{item.room || "Missing"}</div>
                    <div><strong>Academic Year</strong><br />{item.academicYear || "Missing"}</div>
                    <div><strong>Term</strong><br />{item.term || "Missing"}</div>
                    <div><strong>Students</strong><br />{(item.studentUids || []).length}</div>
                    <div><strong>Status</strong><br />{item.status || "active"}</div>
                  </div>
                )}
              </>
            )}
          </div>
        )) : <AlphaEmptyState title="No classes yet" body="Create your first class. It will appear here immediately." />}
      </div>
    </Panel>
  );
}

function AttendanceModule({ profile, items, classes }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [copiedCode, setCopiedCode] = useState("");
  const displayItems = Array.isArray(items) ? items : [];

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  function readPreciseLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error("GPS is not supported on this device."));
      navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 });
    });
  }

  async function copyText(value, label = "Copied") {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopiedCode(value || "");
      setMessage(`✅ ${label} copied.`);
      window.setTimeout(() => setCopiedCode(""), 1800);
    } catch {
      setMessage("❌ Copy failed. Please copy manually.");
    }
  }

  async function submit(event) {
    event.preventDefault();
    const formEl = event.currentTarget;
    if (busy) return;
    setBusy(true);
    setMessage("⏳ Opening attendance session...");
    try {
      const db = getFirebaseDb();
      const data = Object.fromEntries(new FormData(formEl).entries());
      const classItem = classes.find((item) => item.id === data.classId);
      if (!classItem) throw new Error("Choose a class first.");
      const radiusMeters = Math.min(500, Math.max(10, Number(data.radiusMeters || 30)));
      setMessage("📍 Allow location access so this classroom position can be secured...");
      const position = await readPreciseLocation();
      const code = makeCode("ATT");
      await addDoc(collection(db, "schools", SCHOOL_ID, "attendance"), {
        ...data,
        durationMinutes: Number(data.durationMinutes || 15),
        radiusMeters,
        location: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyMeters: Math.round(position.coords.accuracy || 0),
        },
        locationRequired: true,
        className: classItem?.name || "",
        subject: classItem?.subject || "",
        code,
        schoolId: SCHOOL_ID,
        teacherId: profile.uid,
        teacherName: profile.fullName || "",
        status: "open",
        presentUids: [],
        lateUids: [],
        absentUids: [],
        openedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      if (formEl) formEl.reset();
      setMessage(`✅ GPS attendance opened within ${radiusMeters}m. Code: ${code}`);
    } catch (error) {
      console.error("[AlphaClassroom] open attendance failed", error);
      setMessage(`❌ Open attendance failed: ${error.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function endSession(row) {
    if (!window.confirm(`End attendance for ${row.className || "this class"}?`)) return;
    try {
      await updateDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "attendance", row.id), {
        status: "closed",
        closedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setMessage("✅ Attendance session closed.");
    } catch (error) {
      console.error("[AlphaClassroom] close attendance failed", { module: "Attendance", operation: "close-session", collection: "attendance", documentId: row.id, userUid: profile.uid, role: profile.role, schoolId: SCHOOL_ID, code: error.code, message: error.message });
      setMessage(`❌ Close attendance failed: ${readableFirebaseError(error)}`);
    }
  }

  async function extendSession(row, minutes) {
    const raw = minutes ?? window.prompt("Extend session by how many minutes?", "10");
    if (raw === null) return;
    const extra = Math.max(1, Number(raw || 0));
    if (!Number.isFinite(extra)) {
      setMessage("❌ Extension minutes must be a number.");
      return;
    }
    try {
      await updateDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "attendance", row.id), {
        durationMinutes: Number(row.durationMinutes || 0) + extra,
        status: "open",
        updatedAt: serverTimestamp(),
      });
      setMessage(`✅ Attendance session extended by ${extra} minutes.`);
    } catch (error) {
      console.error("[AlphaClassroom] extend attendance failed", { module: "Attendance", operation: "extend-session", collection: "attendance", documentId: row.id, userUid: profile.uid, role: profile.role, schoolId: SCHOOL_ID, code: error.code, message: error.message });
      setMessage(`❌ Extend attendance failed: ${readableFirebaseError(error)}`);
    }
  }

  const openItems = displayItems.filter((row) => ["open", "expired"].includes(row.status));
  const latestOpen = openItems[0];
  const openedAtMs = latestOpen ? recordTime(latestOpen.openedAt || latestOpen.createdAt) : 0;
  const totalMs = latestOpen ? Math.max(1, Number(latestOpen.durationMinutes || 15)) * 60000 : 0;
  const remainingMs = latestOpen ? Math.max(0, (openedAtMs + totalMs) - now) : 0;
  const sessionExpired = Boolean(latestOpen && openedAtMs && remainingMs <= 0);
  const remainingMinutes = Math.floor(remainingMs / 60000);
  const remainingSeconds = Math.floor((remainingMs % 60000) / 1000);
  const timerText = `${String(remainingMinutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  const elapsedPercent = totalMs ? Math.min(100, Math.max(0, ((totalMs - remainingMs) / totalMs) * 100)) : 0;

  useEffect(() => {
    if (!latestOpen?.id || !sessionExpired) return;
    updateDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "attendance", latestOpen.id), {
      status: "expired",
      expiredAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }).catch((error) => console.error("[AlphaClassroom] expire attendance failed", error));
  }, [latestOpen?.id, sessionExpired]);

  return (
    <Panel title="Attendance">
      {message && <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: DARK, borderRadius: 8, padding: 12, fontWeight: 800 }}>{message}</div>}

      {profile.role !== "student" && <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 12 }}>
        <ClassSelect classes={classes} />
        <RawInput name="durationMinutes" label="Countdown Minutes" type="number" defaultValue="15" />
        <RawInput name="radiusMeters" label="Allowed Distance (metres)" type="number" defaultValue="30" min="10" max="500" />
        <div style={{ alignSelf: "end" }}><Button type="submit" variant="gold" busy={busy}>Open Session</Button></div>
      </form>}
      {profile.role !== "student" && <p style={{ margin: 0, color: "#64748B", fontWeight: 750, fontSize: 13 }}>📍 Opening a session asks for your GPS location. Students must enter the unique code and be inside the selected radius.</p>}

      {latestOpen && (
        <div style={{ border: `1px solid ${sessionExpired ? "#F59E0B" : "#93C5FD"}`, borderRadius: 22, padding: "clamp(16px,3vw,26px)", background: sessionExpired ? "linear-gradient(145deg,#FFFBEB,#FFF7ED)" : `linear-gradient(145deg,${DARK},${BLUE})`, color: "#fff", display: "grid", gap: 20, boxShadow: "0 22px 55px rgba(6,42,88,.18)", overflow: "hidden", position: "relative" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 7, borderRadius: 999, padding: "6px 10px", background: sessionExpired ? "#FEF3C7" : "rgba(255,255,255,.12)", color: sessionExpired ? "#92400E" : "#fff", fontSize: 11, fontWeight: 950, letterSpacing: ".08em", textTransform: "uppercase" }}>{sessionExpired ? "Time finished" : "● Live attendance"}</span>
              <strong style={{ display: "block", color: sessionExpired ? DARK : "#fff", fontSize: "clamp(22px,4vw,30px)", marginTop: 9 }}>{latestOpen.className}</strong>
              <p style={{ margin: "4px 0 0", color: sessionExpired ? "#64748B" : "rgba(255,255,255,.72)", fontWeight: 800 }}>{latestOpen.subject || "Attendance"} · GPS radius {latestOpen.radiusMeters || 30}m</p>
            </div>
            <div style={{ minWidth: 170, textAlign: "right" }}>
              <span style={{ display: "block", color: sessionExpired ? "#92400E" : "rgba(255,255,255,.68)", fontSize: 11, fontWeight: 900, letterSpacing: ".12em", textTransform: "uppercase" }}>{sessionExpired ? "Session expired" : "Time remaining"}</span>
              <strong style={{ color: sessionExpired ? "#B45309" : GOLD, fontSize: "clamp(34px,7vw,52px)", lineHeight: 1, fontVariantNumeric: "tabular-nums", letterSpacing: ".02em" }}>{timerText}</strong>
            </div>
          </div>

          <div style={{ height: 8, borderRadius: 999, background: sessionExpired ? "#FDE68A" : "rgba(255,255,255,.14)", overflow: "hidden" }}><div style={{ width: `${elapsedPercent}%`, height: "100%", background: sessionExpired ? "#F59E0B" : `linear-gradient(90deg,${GOLD},#fff)`, borderRadius: 999, transition: "width 1s linear" }} /></div>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(240px,1.5fr) repeat(2,minmax(110px,.55fr))", gap: 12 }} className="alpha-attendance-metrics">
            <button type="button" onClick={() => copyText(latestOpen.code, "Attendance code")} style={{ border: `1px solid ${sessionExpired ? "#FDE68A" : "rgba(255,255,255,.2)"}`, borderRadius: 16, padding: 16, background: sessionExpired ? "#fff" : "rgba(255,255,255,.1)", color: sessionExpired ? DARK : "#fff", textAlign: "left", cursor: "pointer" }}><span style={{ display: "block", fontSize: 11, fontWeight: 900, opacity: .68, textTransform: "uppercase", letterSpacing: ".1em" }}>Student session code</span><span style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginTop: 5 }}><strong style={{ fontSize: "clamp(25px,5vw,36px)", letterSpacing: ".08em" }}>{latestOpen.code}</strong><b style={{ background: GOLD, color: DARK, borderRadius: 10, padding: "8px 10px", fontSize: 12 }}>{copiedCode === latestOpen.code ? "✓ Copied" : "Copy"}</b></span></button>
            <div style={{ background: sessionExpired ? "#fff" : "rgba(255,255,255,.1)", border: `1px solid ${sessionExpired ? "#FDE68A" : "rgba(255,255,255,.2)"}`, borderRadius: 16, padding: 16 }}><span style={{ color: sessionExpired ? "#64748B" : "rgba(255,255,255,.7)", fontSize: 11, fontWeight: 900, textTransform: "uppercase" }}>Present</span><strong style={{ display: "block", color: sessionExpired ? "#047857" : "#6EE7B7", fontSize: 36 }}>{(latestOpen.presentUids || []).length}</strong></div>
            <div style={{ background: sessionExpired ? "#fff" : "rgba(255,255,255,.1)", border: `1px solid ${sessionExpired ? "#FDE68A" : "rgba(255,255,255,.2)"}`, borderRadius: 16, padding: 16 }}><span style={{ color: sessionExpired ? "#64748B" : "rgba(255,255,255,.7)", fontSize: 11, fontWeight: 900, textTransform: "uppercase" }}>Late</span><strong style={{ display: "block", color: sessionExpired ? "#B45309" : "#FCD34D", fontSize: 36 }}>{(latestOpen.lateUids || []).length}</strong></div>
          </div>

          <div style={{ display: "flex", gap: 9, flexWrap: "wrap", alignItems: "center" }}><span style={{ color: sessionExpired ? "#64748B" : "rgba(255,255,255,.7)", fontSize: 12, fontWeight: 850, marginRight: 3 }}>{sessionExpired ? "Continue this session:" : "Quick controls:"}</span><Button variant="light" onClick={() => extendSession(latestOpen, 5)}>+5 min</Button><Button variant="light" onClick={() => extendSession(latestOpen, 10)}>+10 min</Button><Button variant="light" onClick={() => extendSession(latestOpen)}>Custom time</Button><Button variant="danger" onClick={() => endSession(latestOpen)}>End Session</Button></div>
          <style>{`@media(max-width:720px){.alpha-attendance-metrics{grid-template-columns:1fr 1fr!important}.alpha-attendance-metrics>button{grid-column:1/-1}}`}</style>
        </div>
      )}

      <Table rows={displayItems} empty="No attendance sessions yet" columns={[
        { key: "className", label: "Class" },
        { key: "subject", label: "Subject" },
        { key: "status", label: "Status", render: (row) => <strong style={{ color: row.status === "open" ? "#047857" : row.status === "expired" ? "#B45309" : "#64748B" }}>{row.status === "open" ? "Live" : row.status === "expired" ? "Time finished" : "Closed"}</strong> },
        { key: "durationMinutes", label: "Duration", render: (row) => `${row.durationMinutes || 15} min` },
        { key: "present", label: "Present", render: (row) => (row.presentUids || []).length },
        { key: "createdAt", label: "Created", render: (row) => formatDate(row.createdAt) },
        { key: "actions", label: "Actions", render: (row) => ["open", "expired"].includes(row.status) ? <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><Button variant="light" onClick={() => extendSession(row, 10)}>+10 min</Button><Button variant="danger" onClick={() => endSession(row)}>End</Button></div> : "Closed" },
      ]} />
    </Panel>
  );
}

function learningConfig(collectionName) {
  const configs = {
    assignments: {
      detailLabel: "Instructions",
      detailPlaceholder: "Write assignment instructions...",
      dateName: "dueDate",
      dateLabel: "Due Date",
      extra: "assignment",
    },
    notes: {
      detailLabel: "Note content",
      detailPlaceholder: "Write the full note content...",
      dateName: "lessonDate",
      dateLabel: "Lesson Date",
      extra: "notes",
    },
    resources: {
      detailLabel: "Description",
      detailPlaceholder: "Describe this resource...",
      dateName: "resourceDate",
      dateLabel: "Resource Date",
      extra: "resources",
    },
    quizzes: {
      detailLabel: "Instructions",
      detailPlaceholder: "Write quiz instructions...",
      dateName: "dueDate",
      dateLabel: "Due Date",
      extra: "quiz",
    },
    announcements: {
      detailLabel: "Details",
      detailPlaceholder: "Write full announcement message...",
      dateName: "",
      dateLabel: "",
      extra: "announcement",
    },
  };
  return configs[collectionName] || configs.assignments;
}

function LearningModule({ collectionName, title, profile, items, classes, terms = [], submissions = [] }) {
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [editing, setEditing] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFilePreviewUrl, setSelectedFilePreviewUrl] = useState("");
  const [activeAssignmentTab, setActiveAssignmentTab] = useState("Overview");
  const [selectedAssignmentId, setSelectedAssignmentId] = useState("");
  const [gradeTarget, setGradeTarget] = useState(null);
  const [workTarget, setWorkTarget] = useState(null);
  const [gradebookHistoryTarget, setGradebookHistoryTarget] = useState(null);
  const [gradeForm, setGradeForm] = useState({ score: "", feedback: "", status: "graded" });
  const [gradeFile, setGradeFile] = useState(null);
  const [gradeFilePreviewUrl, setGradeFilePreviewUrl] = useState("");
  const [gradebookSearch, setGradebookSearch] = useState("");
  const [gradebookClassFilter, setGradebookClassFilter] = useState("");
  useEffect(() => {
    setMessage("");
    setEditing(null);
    setSelectedFile(null);
  }, [collectionName]);
  const displayItems = Array.isArray(items) ? items : [];
  const config = learningConfig(collectionName);
  const messageTone = message.startsWith("✅") ? "success" : message.startsWith("❌") ? "error" : message.startsWith("⚠️") ? "warning" : "loading";
  const isAssignments = collectionName === "assignments";
  const assignmentTabs = ["Overview", "Create Assignment", "My Assignments", "Submissions", "Gradebook", "Analytics"];
  const visibleClassIds = new Set(classes.map((item) => item.id).filter(Boolean));
  const assignmentById = new Map(displayItems.map((assignment) => [assignment.id, assignment]));
  const selectedAssignment = displayItems.find((item) => item.id === selectedAssignmentId);
  const allAssignmentSubmissions = isAssignments
    ? submissions
      .filter((item) => {
        const assignment = assignmentById.get(item.assignmentId);
        return Boolean(
          profile.role === "schoolAdmin" ||
          item.teacherId === profile.uid ||
          visibleClassIds.has(item.classId) ||
          (assignment?.classId && visibleClassIds.has(assignment.classId))
        );
      })
    : [];
  const assignmentSubmissions = allAssignmentSubmissions
      .filter((item) => !selectedAssignmentId || item.assignmentId === selectedAssignmentId)
  ;
  const analytics = assignmentAnalytics(displayItems, submissions, classes);
  const isMarkedSubmission = (row) => {
    const status = String(row.status || "").toLowerCase();
    const hasScore = row.score !== undefined && row.score !== null && row.score !== "" && Number.isFinite(Number(row.score));
    const hasGrade = row.grade !== undefined && row.grade !== null && String(row.grade).trim() !== "";
    return status === "graded" || hasScore || hasGrade;
  };
  const markedSubmissions = allAssignmentSubmissions.filter(isMarkedSubmission);
  const gradebookRows = markedSubmissions
    .filter((row) => !gradebookClassFilter || row.classId === gradebookClassFilter)
    .filter((row) => {
      const term = gradebookSearch.trim().toLowerCase();
      if (!term) return true;
      return [row.studentName, row.fullName, row.studentId, row.loginEmail, row.assignmentTitle, row.className]
        .some((value) => String(value || "").toLowerCase().includes(term));
    });
  const pendingMarkingCount = allAssignmentSubmissions.filter((row) => !isMarkedSubmission(row)).length;
  const gradebookStudentRows = Array.from(gradebookRows.reduce((map, row) => {
    const key = row.studentUid || row.studentId || row.loginEmail || row.studentName || row.id;
    const current = map.get(key) || { key, studentName: row.studentName || row.fullName || "Student", studentId: row.studentId || "", loginEmail: row.loginEmail || "", className: row.className || "", rows: [] };
    current.rows.push(row);
    if (recordTime(row.gradedAt || row.updatedAt || row.submittedAt || row.createdAt) > recordTime(current.latest?.gradedAt || current.latest?.updatedAt || current.latest?.submittedAt || current.latest?.createdAt)) current.latest = row;
    map.set(key, current);
    return map;
  }, new Map()).values()).map((group) => {
    const scored = group.rows
      .map((row) => {
        const assignment = assignmentById.get(row.assignmentId);
        const max = Number(row.maxMarks || assignment?.maxMarks || 0);
        const score = Number(row.score ?? row.grade);
        if (!Number.isFinite(score)) return null;
        if (max > 0 && score <= max) return (score / max) * 100;
        return Math.min(100, Math.max(0, score));
      })
      .filter((value) => Number.isFinite(value));
    return {
      ...group,
      submittedCount: group.rows.length,
      averageScore: scored.length ? Math.round((scored.reduce((sum, value) => sum + value, 0) / scored.length) * 10) / 10 : "N/A",
      latestFeedback: group.latest?.feedback || "No feedback yet",
    };
  });

  useEffect(() => {
    if (!selectedFile) {
      setSelectedFilePreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(selectedFile);
    setSelectedFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  useEffect(() => {
    if (!gradeFile) {
      setGradeFilePreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(gradeFile);
    setGradeFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [gradeFile]);

  async function submit(event) {
    event.preventDefault();
    const formEl = event.currentTarget;
    if (busy) return;
    setBusy(true);
    setUploading(false);
    setUploadProgress(0);
    setMessage(editing ? `⏳ Saving changes to ${title.toLowerCase()}...` : `⏳ Creating ${title.toLowerCase()}...`);
    const db = getFirebaseDb();
    const saveAsDraft = formEl.dataset.saveMode === "draft";
    formEl.dataset.saveMode = "";
    const rawData = Object.fromEntries(new FormData(formEl).entries());
    const { attachment, file: ignoredFile, ...data } = rawData;
    const file = selectedFile || formEl.elements.attachment?.files?.[0] || null;
    try {
      const classItem = classes.find((item) => item.id === data.classId);
      if (!classItem) throw new Error("Choose a valid class before saving.");
      const fileMeta = file
        ? (setUploading(true), setMessage("⏳ Uploading attachment..."), await uploadAlphaLearningFile(file, {
          classId: data.classId,
          collectionName,
          userUid: profile.uid,
          onProgress: setUploadProgress,
        }))
        : {};
      const payload = {
        ...data,
        className: classItem.name || classItem.className || "",
        subject: classItem.subject || data.subject || "",
        description: data.description || data.body || "",
        body: data.description || data.body || data.instructions || "",
        instructions: isAssignments ? (data.instructions || data.body || "") : (data.instructions || data.body || ""),
        schoolId: SCHOOL_ID,
        teacherId: profile.uid,
        teacherName: profile.fullName || profile.email || "Alpha Teacher",
        createdBy: profile.uid,
        status: saveAsDraft ? "draft" : (data.status || "active"),
        visibility: data.visibility || "students",
        dueTime: data.dueTime || "",
        topic: data.topic || "",
        difficulty: data.difficulty || "Medium",
        submissionType: data.submissionType || "Text Answer",
        requireAttachment: data.requireAttachment === "on",
        allowLateSubmission: data.allowLateSubmission === "on",
        maxMarks: data.maxMarks ? Number(data.maxMarks) : null,
        maxAttempts: data.maxAttempts ? Number(data.maxAttempts) : 1,
        timerMinutes: data.timerMinutes ? Number(data.timerMinutes) : null,
        totalMarks: data.totalMarks ? Number(data.totalMarks) : null,
        attemptsAllowed: data.attemptsAllowed ? Number(data.attemptsAllowed) : null,
        ...fileMeta,
        attachmentUrl: fileMeta.fileUrl || editing?.attachmentUrl || editing?.fileUrl || "",
        attachmentName: fileMeta.fileName || editing?.attachmentName || editing?.fileName || "",
        attachmentType: fileMeta.fileType || editing?.attachmentType || editing?.fileType || "",
        updatedAt: serverTimestamp(),
      };
      delete payload.attachment;
      delete payload.file;
      let savedAssignmentId = editing?.id || "";
      if (editing) {
        setMessage(`⏳ Saving changes to ${title.toLowerCase()}...`);
        await updateDoc(doc(db, "schools", SCHOOL_ID, collectionName, editing.id), payload);
      } else {
        setMessage(`⏳ Creating ${title.toLowerCase()}...`);
        const createdRef = await addDoc(collection(db, "schools", SCHOOL_ID, collectionName), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        savedAssignmentId = createdRef.id;
      }
      if (formEl) formEl.reset();
      setEditing(null);
      setSelectedFile(null);
      if (!saveAsDraft && !["draft", "archived"].includes(payload.status)) {
        const notificationTypes = { assignments: "new_assignment", notes: "new_note", resources: "new_resource", announcements: "new_announcement" };
        await createAlphaNotification({
          type: notificationTypes[collectionName] || `new_${collectionName}`,
          title: payload.title || `New ${title.slice(0, -1)}`,
          message: payload.title || `New ${title.toLowerCase()} published`,
          classId: payload.classId,
          className: payload.className,
          itemId: savedAssignmentId,
          module: collectionName,
          actorUid: profile.uid,
        });
      }
      if (isAssignments) {
        setActiveAssignmentTab("My Assignments");
      }
      setMessage(collectionName === "announcements" ? "✅ Announcement sent to students." : `✅ ${title.slice(0, -1) || title} ${editing ? "updated" : "created"}.`);
    } catch (error) {
      console.error(`[AlphaClassroom] save ${collectionName} failed`, error);
      setMessage(`❌ Save failed: ${readableFirebaseError(error)}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setBusy(false);
    }
  }
  async function remove(row) {
    if (busy) return;
    if (!window.confirm(`Delete this ${title.slice(0, -1).toLowerCase() || "item"}?`)) return;
    setBusy(true);
    setMessage("⏳ Deleting record...");
    try {
      await deleteDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, collectionName, row.id));
      if (editing?.id === row.id) setEditing(null);
      if (previewItem?.id === row.id) setPreviewItem(null);
      if (row.storagePath) {
        try {
          await deleteObject(ref(storage, row.storagePath));
          setMessage("✅ Record and attached file deleted.");
        } catch (storageError) {
          console.warn(`[AlphaClassroom] delete ${collectionName} storage failed`, storageError);
          setMessage(`⚠️ Record deleted, but file cleanup failed: ${readableFirebaseError(storageError)}`);
        }
      } else {
        setMessage("✅ Record deleted.");
      }
    } catch (error) {
      console.error(`[AlphaClassroom] delete ${collectionName} failed`, error);
      setMessage(`❌ Delete failed: ${readableFirebaseError(error)}`);
    } finally {
      setBusy(false);
    }
  }
  function cancelEdit() {
    setEditing(null);
    setSelectedFile(null);
    setMessage("");
  }
  function removeSelectedFile() {
    setSelectedFile(null);
    const input = document.getElementById(`alpha-${collectionName}-attachment`);
    if (input) input.value = "";
  }
  function startEdit(row) {
    setEditing(row);
    setMessage("");
    setActiveAssignmentTab("Create Assignment");
  }
  function viewSubmissions(row) {
    setSelectedAssignmentId(row.id);
    setActiveAssignmentTab("Submissions");
  }
  function openGrade(row) {
    setGradeTarget(row);
    setGradeForm({ score: row.score ?? row.grade ?? "", feedback: row.feedback || "", status: row.status === "returned" || row.status === "needs revision" ? row.status : "graded" });
    setGradeFile(null);
  }
  async function duplicateAssignment(row) {
    if (busy) return;
    setBusy(true);
    setMessage("⏳ Duplicating assignment...");
    try {
      const copy = {
        ...row,
        title: `${row.title || "Untitled assignment"} copy`,
        status: "draft",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: profile.uid,
        teacherId: profile.uid,
        teacherName: profile.fullName || profile.email || "Alpha Teacher",
      };
      delete copy.id;
      await addDoc(collection(getFirebaseDb(), "schools", SCHOOL_ID, "assignments"), copy);
      setMessage("✅ Assignment duplicated as draft.");
    } catch (error) {
      setMessage(`❌ Duplicate failed: ${readableFirebaseError(error)}`);
    } finally {
      setBusy(false);
    }
  }
  async function saveGrade(status = gradeForm.status || "graded") {
    if (!gradeTarget || busy) return;
    setBusy(true);
    const nextStatus = status || gradeForm.status || "graded";
    setMessage(nextStatus === "graded" ? "⏳ Saving grade..." : "⏳ Returning feedback...");
    try {
      const score = gradeForm.score === "" ? null : Number(gradeForm.score);
      if (gradeForm.score !== "" && !Number.isFinite(score)) throw new Error("Score must be a number.");
      const correctedFileMeta = gradeFile
        ? (setUploading(true), setMessage("⏳ Uploading corrected file..."), await uploadAlphaLearningFile(gradeFile, {
          classId: gradeTarget.classId,
          collectionName: "assignments",
          userUid: profile.uid,
          prefix: `corrected-${gradeTarget.studentUid || "student"}-`,
          onProgress: setUploadProgress,
        }))
        : {};
      const correctedPayload = correctedFileMeta.fileUrl ? {
        correctedFileUrl: correctedFileMeta.fileUrl,
        correctedFileName: correctedFileMeta.fileName,
        correctedFileType: correctedFileMeta.fileType,
        correctedFileSize: correctedFileMeta.fileSize,
        correctedStoragePath: correctedFileMeta.storagePath,
      } : {};
      await updateDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "assignmentSubmissions", gradeTarget.id), {
        grade: gradeForm.score === "" ? "" : String(gradeForm.score),
        score,
        feedback: gradeForm.feedback || "",
        status: nextStatus,
        gradedAt: serverTimestamp(),
        gradedBy: profile.uid,
        gradedByName: profile.fullName || profile.email || "Alpha Teacher",
        markedAt: serverTimestamp(),
        markedBy: profile.uid,
        ...correctedPayload,
        updatedAt: serverTimestamp(),
      });
      await createAlphaNotification({
        type: nextStatus === "graded" ? "assignment_marked" : "feedback_received",
        title: nextStatus === "graded" ? "Assignment Marked" : "Feedback Received",
        message: gradeTarget.assignmentTitle || "Assignment feedback updated",
        classId: gradeTarget.classId,
        assignmentId: gradeTarget.assignmentId,
        studentUid: gradeTarget.studentUid,
        actorUid: profile.uid,
      });
      setGradeTarget(null);
      setGradeFile(null);
      setMessage(nextStatus === "graded" ? "✅ Grade saved." : "✅ Feedback returned.");
    } catch (error) {
      setMessage(`❌ Grade save failed: ${readableFirebaseError(error)}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setBusy(false);
    }
  }
  function exportGradebook() {
    const headers = ["Student", "Student ID", "Class", "Assignment", "Score", "Feedback", "Graded At"];
    const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const lines = gradebookRows.map((row) => [
      row.studentName,
      row.studentId,
      row.className,
      row.assignmentTitle,
      row.score ?? row.grade ?? "",
      row.feedback || "",
      formatDate(row.gradedAt),
    ].map(escapeCsv).join(","));
    const blob = new Blob([[headers.map(escapeCsv).join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "alpha-assignment-gradebook.csv";
    link.click();
    URL.revokeObjectURL(url);
  }
  function submissionStatusBadge(row) {
    const status = String(row.status || (isMarkedSubmission(row) ? "graded" : "submitted")).toLowerCase();
    const palette = status === "graded"
      ? { bg: "#ECFDF5", fg: "#065F46", bd: "#BBF7D0" }
      : status === "returned"
        ? { bg: "#EFF6FF", fg: BLUE, bd: "#BFDBFE" }
        : status.includes("revision")
          ? { bg: "#FFFBEB", fg: "#92400E", bd: "#FDE68A" }
          : { bg: "#F8FAFC", fg: "#475569", bd: "#CBD5E1" };
    return <span style={{ display: "inline-flex", alignItems: "center", width: "fit-content", background: palette.bg, color: palette.fg, border: `1px solid ${palette.bd}`, borderRadius: 999, padding: "4px 9px", fontSize: 11, fontWeight: 950, textTransform: "capitalize" }}>{status || "submitted"}</span>;
  }
  function submissionScoreLabel(row) {
    const assignment = assignmentById.get(row.assignmentId);
    const max = row.maxMarks || assignment?.maxMarks || "";
    const value = row.score ?? row.grade;
    return value !== undefined && value !== null && String(value) !== "" ? `${value}${max ? ` / ${max}` : ""}` : `Pending${max ? ` / ${max}` : ""}`;
  }
  function answerText(row) {
    return row.answerText || row.answer || row.response || row.submissionText || "";
  }
  function answerPreview(row) {
    const text = answerText(row).trim();
    if (!text) return "No text answer submitted.";
    return text.length > 120 ? `${text.slice(0, 120)}...` : text;
  }
  function fileChip(row, label = "Open file") {
    const firstFile = Array.isArray(row?.fileUrls) ? row.fileUrls.find(Boolean) : "";
    const url = row?.fileUrl || row?.attachmentUrl || row?.submissionFileUrl || row?.uploadUrl || row?.documentUrl || (typeof firstFile === "string" ? firstFile : firstFile?.url) || "";
    if (!url) return null;
    return <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", width: "fit-content", alignItems: "center", justifyContent: "center", border: "1px solid #BFDBFE", background: "#EFF6FF", color: BLUE, borderRadius: 999, padding: "7px 10px", fontSize: 12, fontWeight: 950, textDecoration: "none" }}>{label}</a>;
  }
  function openWork(row) {
    setWorkTarget(row);
  }
  function submissionActions(row) {
    return (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Button variant="light" disabled={busy} onClick={() => openWork(row)}>View Work</Button>
        <Button variant="gold" disabled={busy} onClick={() => openGrade(row)}>Grade</Button>
        <Button variant="light" disabled={busy} onClick={() => openGrade(row)}>Return Feedback</Button>
      </div>
    );
  }
  const actionLabel = editing ? `Update ${title.slice(0, -1) || title}` : `Create ${title.slice(0, -1) || title}`;
  return (
    <Panel title={title}>
      {isAssignments && (
        <div className="alpha-module-tabs" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {assignmentTabs.map((tab) => (
            <Button key={tab} variant={activeAssignmentTab === tab ? "gold" : "light"} onClick={() => setActiveAssignmentTab(tab)}>{tab}</Button>
          ))}
        </div>
      )}
      {message && <div style={{ background: messageTone === "success" ? "#ECFDF5" : messageTone === "error" ? "#FEF2F2" : messageTone === "warning" ? "#FFFBEB" : "#EFF6FF", border: `1px solid ${messageTone === "success" ? "#BBF7D0" : messageTone === "error" ? "#FECACA" : messageTone === "warning" ? "#FDE68A" : "#BFDBFE"}`, color: messageTone === "success" ? "#065F46" : messageTone === "error" ? "#991B1B" : messageTone === "warning" ? "#92400E" : DARK, borderRadius: 8, padding: 12, fontWeight: 900, overflowWrap: "anywhere" }}>{message}</div>}
      {uploading && <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: DARK, borderRadius: 8, padding: 12, fontWeight: 900 }}>Uploading attachment... {uploadProgress}%</div>}
      {isAssignments && activeAssignmentTab === "Overview" && (
        <div style={{ display: "grid", gap: 14 }}>
          <StatGrid stats={assignmentStats(displayItems, allAssignmentSubmissions)} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12 }}>
            {displayItems.slice(0, 4).map((assignment) => {
              const rows = allAssignmentSubmissions.filter((row) => row.assignmentId === assignment.id);
              return (
                <article key={assignment.id} style={{ border: "1px solid #D8E2F0", borderRadius: 8, padding: 14, background: "#fff", display: "grid", gap: 8 }}>
                  <strong style={{ color: DARK }}>{assignment.title || "Untitled assignment"}</strong>
                  <span style={{ color: "#64748B", fontWeight: 850 }}>{assignment.subject || "Subject"} · {assignment.className || "Class"}</span>
                  <span style={{ color: "#64748B", fontSize: 12 }}>Due: {assignment.dueDate || "No date"} {assignment.dueTime || ""}</span>
                  <span style={{ color: BLUE, fontWeight: 900 }}>Submitted: {rows.length} · Pending: {rows.filter((row) => String(row.status || "").toLowerCase() !== "graded").length}</span>
                </article>
              );
            })}
            {!displayItems.length && <AlphaEmptyState title="No assignments yet" />}
          </div>
        </div>
      )}
      {(!isAssignments || activeAssignmentTab === "Create Assignment") && <form id={`alpha-${collectionName}-form`} key={editing?.id || "new"} onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 12 }}>
        <ClassSelect classes={classes} defaultValue={editing?.classId || ""} />
        {isAssignments && <RawInput name="subject" label="Subject" defaultValue={editing?.subject || ""} />}
        <RawInput name="title" label="Title" required defaultValue={editing?.title || ""} />
        {isAssignments && <RawInput name="description" label="Description" multiline defaultValue={editing?.description || editing?.body || ""} wrapperStyle={{ gridColumn: "1 / -1" }} />}
        <RawInput
          name={isAssignments ? "instructions" : "body"}
          label={config.detailLabel}
          placeholder={config.detailPlaceholder}
          multiline
          className={collectionName === "announcements" ? "alpha-announcement-body" : ""}
          defaultValue={editing?.instructions || editing?.body || editing?.description || ""}
          style={{ minHeight: collectionName === "announcements" ? 120 : 130 }}
          wrapperStyle={{ gridColumn: "1 / -1" }}
        />
        {config.dateName && <RawInput name={config.dateName} label={config.dateLabel} type="date" defaultValue={editing?.[config.dateName] || ""} />}
        {isAssignments && <RawInput name="dueTime" label="Due Time" type="time" defaultValue={editing?.dueTime || ""} />}
        {collectionName === "assignments" && <RawInput name="maxMarks" label="Max Marks" type="number" defaultValue={editing?.maxMarks || ""} />}
        {isAssignments && <RawInput name="topic" label="Topic" defaultValue={editing?.topic || ""} />}
        {isAssignments && (
          <>
            <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Difficulty<select name="difficulty" defaultValue={editing?.difficulty || "Medium"} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700, background: "#fff" }}><option>Easy</option><option>Medium</option><option>Hard</option></select></label>
            <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Submission Type<select name="submissionType" defaultValue={editing?.submissionType || "Text Answer"} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700, background: "#fff" }}><option>Text Answer</option><option>File Upload</option><option>Image Upload</option><option>Link</option></select></label>
            <RawInput name="maxAttempts" label="Maximum Attempts" type="number" defaultValue={editing?.maxAttempts || "1"} />
          </>
        )}
        {collectionName === "notes" && <RawInput name="topic" label="Topic" defaultValue={editing?.topic || ""} />}
        {collectionName === "resources" && (
          <>
            <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Resource Type<select name="resourceType" defaultValue={editing?.resourceType || "PDF"} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700, background: "#fff" }}>{["PDF", "Video", "Link", "Image", "Other"].map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
            <RawInput name="url" label="URL (optional)" defaultValue={editing?.url || ""} />
          </>
        )}
        {collectionName === "quizzes" && (
          <>
            <RawInput name="timerMinutes" label="Timer Minutes" type="number" defaultValue={editing?.timerMinutes || "20"} />
            <RawInput name="totalMarks" label="Total Marks" type="number" defaultValue={editing?.totalMarks || ""} />
            <RawInput name="attemptsAllowed" label="Attempts Allowed" type="number" defaultValue={editing?.attemptsAllowed || "1"} />
          </>
        )}
        <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Term<select name="term" defaultValue={editing?.term || ""} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700, background: "#fff" }}><option value="">Current term</option>{terms.map((term) => <option key={term.id} value={term.name || term.title || term.id}>{term.name || term.title || term.id}</option>)}</select></label>
        <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Visibility<select name="visibility" defaultValue={editing?.visibility || "students"} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700, background: "#fff" }}><option value="students">Students</option><option value="teachers">Teachers only</option><option value="draft">Draft</option></select></label>
        <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Status<select name="status" defaultValue={editing?.status || "active"} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700, background: "#fff" }}><option value="active">Active</option><option value="draft">Draft</option><option value="closed">Closed</option><option value="archived">Archived</option></select></label>
        {collectionName === "assignments" && <label style={{ display: "flex", alignItems: "center", gap: 8, color: DARK, fontSize: 13, fontWeight: 900, alignSelf: "end", minHeight: 42 }}><input name="allowLateSubmission" type="checkbox" defaultChecked={Boolean(editing?.allowLateSubmission)} /> Allow late submission</label>}
        {collectionName === "assignments" && <label style={{ display: "flex", alignItems: "center", gap: 8, color: DARK, fontSize: 13, fontWeight: 900, alignSelf: "end", minHeight: 42 }}><input name="requireAttachment" type="checkbox" defaultChecked={Boolean(editing?.requireAttachment)} /> Require attachment</label>}
        <DeviceUploadField id={`alpha-${collectionName}-attachment`} label={collectionName === "assignments" ? "Upload assignment file" : collectionName === "notes" ? "Upload lesson note" : collectionName === "resources" ? "Upload learning resource" : collectionName === "announcements" ? "Upload announcement attachment" : "Upload attachment"} file={selectedFile} onFileChange={setSelectedFile} disabled={busy} />
        {(editing?.fileUrl || editing?.attachmentUrl) && <AttachmentLink item={editing} label="Current attachment" />}
        {selectedFile && (
          <div style={{ gridColumn: "1 / -1", border: "1px solid #D8E2F0", borderRadius: 8, padding: 12, background: "#F8FAFC", display: "grid", gap: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ color: DARK, fontWeight: 900, overflowWrap: "anywhere" }}>Selected file: {selectedFile.name} · {selectedFile.type || "unknown type"} · {formatFileSize(selectedFile.size)}</span>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {selectedFilePreviewUrl && <a href={selectedFilePreviewUrl} target="_blank" rel="noopener noreferrer" style={{ background: "#fff", color: DARK, border: "1px solid #D8E2F0", borderRadius: 8, padding: "9px 12px", fontWeight: 900, textDecoration: "none" }}>Preview Selected File</a>}
                <Button variant="light" disabled={busy} onClick={removeSelectedFile}>Remove File</Button>
              </div>
            </div>
            {selectedFilePreviewUrl && selectedFile.type?.startsWith("image/") && <img src={selectedFilePreviewUrl} alt={selectedFile.name} style={{ maxWidth: 260, maxHeight: 180, objectFit: "contain", border: "1px solid #E2E8F0", borderRadius: 8, background: "#fff" }} />}
            {selectedFilePreviewUrl && selectedFile.type === "application/pdf" && <iframe title="Selected assignment file preview" src={selectedFilePreviewUrl} style={{ width: "100%", height: 260, border: "1px solid #E2E8F0", borderRadius: 8, background: "#fff" }} />}
          </div>
        )}
        <div style={{ alignSelf: "end", display: "flex", gap: 8, flexWrap: "wrap", gridColumn: "1 / -1" }}>
          <Button type="submit" variant="gold" disabled={busy}>{busy ? (uploading ? "Uploading..." : "Saving...") : (isAssignments ? (editing ? "Save Changes" : "Create Assignment") : actionLabel)}</Button>
          {isAssignments && <Button type="button" variant="light" disabled={busy} onClick={() => { const form = document.getElementById(`alpha-${collectionName}-form`); if (form) { form.dataset.saveMode = "draft"; const status = form.querySelector("select[name='status']"); if (status) status.value = "draft"; form.requestSubmit(); } }}>Save Draft</Button>}
          {editing && <Button variant="light" disabled={busy} onClick={cancelEdit}>Cancel Edit</Button>}
          {!editing && isAssignments && <Button variant="light" disabled={busy} onClick={() => { removeSelectedFile(); setMessage(""); }}>Cancel</Button>}
        </div>
      </form>}
      {previewItem && (
        <div style={{ border: `1px solid ${BLUE}`, borderRadius: 8, padding: 16, background: "#F8FAFC", display: "grid", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0, color: DARK }}>{previewItem.title || `${title.slice(0, -1)} Preview`}</h3>
            <Button variant="light" onClick={() => setPreviewItem(null)}>Close Preview</Button>
          </div>
          <p style={{ margin: 0, color: "#64748B", fontWeight: 850 }}>
            {previewItem.className || "Class"} · {previewItem.subject || "Subject"} · Due/Date: {previewItem.dueDate || previewItem.lessonDate || previewItem.resourceDate || "No date"} · Marks: {previewItem.maxMarks || previewItem.totalMarks || "Not set"} · Status: {previewItem.status || "active"}
          </p>
          <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, color: DARK }}>{previewItem.body || previewItem.instructions || previewItem.description || previewItem.url || "No details written."}</div>
          {(previewItem.fileUrl || previewItem.attachmentUrl) && <AttachmentLink item={previewItem} label="Open File" />}
        </div>
      )}
      {!isAssignments && <Table rows={displayItems} empty={`No ${title.toLowerCase()} yet`} columns={[
        { key: "title", label: "Title" },
        { key: "className", label: "Class" },
        { key: "date", label: "Due / Date", render: (row) => row.dueDate || row.lessonDate || row.resourceDate || "" },
        { key: "file", label: "Attachment", render: (row) => (row.fileUrl || row.attachmentUrl) ? <AttachmentLink item={row} /> : "None" },
        { key: "status", label: "Status" },
        { key: "actions", label: "Actions", render: (row) => <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button variant="light" disabled={busy} onClick={() => setPreviewItem(row)}>Preview</Button>
          <Button variant="light" disabled={busy} onClick={() => startEdit(row)}>Edit</Button>
          {(row.fileUrl || row.attachmentUrl) && <AttachmentLink item={row} label="Open File" />}
          {isAssignments && <Button variant="light" disabled={busy} onClick={() => viewSubmissions(row)}>View Submissions</Button>}
          <Button variant="danger" disabled={busy} onClick={() => remove(row)}>Delete</Button>
        </div> },
      ]} />}
      {isAssignments && activeAssignmentTab === "My Assignments" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 12 }}>
          {displayItems.length ? displayItems.map((row) => {
            const rows = allAssignmentSubmissions.filter((item) => item.assignmentId === row.id);
            const classItem = classes.find((item) => item.id === row.classId);
            const studentCount = classStudentIds(classItem).length;
            const pending = Math.max(0, rows.filter((item) => String(item.status || "").toLowerCase() !== "graded").length);
            return (
              <article key={row.id} style={{ border: "1px solid #D8E2F0", borderRadius: 8, padding: 16, background: "#fff", display: "grid", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <strong style={{ color: DARK, fontSize: 17 }}>{row.title || "Untitled assignment"}</strong>
                    <p style={{ margin: "4px 0 0", color: "#64748B", fontWeight: 850 }}>{row.subject || "Subject"} · {row.className || "Class"}</p>
                  </div>
                  <span style={{ color: row.status === "draft" ? "#92400E" : BLUE, fontWeight: 950 }}>{row.status || "active"}</span>
                </div>
                <span style={{ color: "#64748B", fontSize: 13 }}>Due: {row.dueDate || "No date"} {row.dueTime || ""}</span>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                  <span style={{ background: LIGHT, borderRadius: 8, padding: 8, color: DARK, fontWeight: 900 }}>Students<br />{studentCount || "N/A"}</span>
                  <span style={{ background: LIGHT, borderRadius: 8, padding: 8, color: DARK, fontWeight: 900 }}>Submitted<br />{rows.length}</span>
                  <span style={{ background: LIGHT, borderRadius: 8, padding: 8, color: DARK, fontWeight: 900 }}>Pending<br />{pending}</span>
                </div>
                {(row.fileUrl || row.attachmentUrl) && <AttachmentLink item={row} label="Open File" />}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button variant="light" disabled={busy} onClick={() => setPreviewItem(row)}>Preview</Button>
                  <Button variant="light" disabled={busy} onClick={() => startEdit(row)}>Edit</Button>
                  <Button variant="light" disabled={busy} onClick={() => viewSubmissions(row)}>Submissions</Button>
                  <Button variant="light" disabled={busy} onClick={() => { setSelectedAssignmentId(row.id); setActiveAssignmentTab("Analytics"); }}>Analytics</Button>
                  <Button variant="light" disabled={busy} onClick={() => duplicateAssignment(row)}>Duplicate</Button>
                  <Button variant="danger" disabled={busy} onClick={() => remove(row)}>Delete</Button>
                </div>
                {(row.fileUrl || row.attachmentUrl) && <details style={{ border: "1px solid #D8E2F0", borderRadius: 10, padding: 10, background: "#F8FAFC" }}><summary style={{ color: BLUE, fontWeight: 950, cursor: "pointer" }}>Preview assignment attachment</summary><iframe title={`${row.title || title} preview`} src={row.fileUrl || row.attachmentUrl} style={{ width: "100%", minHeight: 420, marginTop: 10, border: "1px solid #CBD5E1", borderRadius: 8, background: "#fff" }} /><p style={{ margin: "8px 0 0", color: "#64748B", fontSize: 12 }}>If this file type cannot preview in your browser, use Open File above.</p></details>}
              </article>
            );
          }) : <AlphaEmptyState title="No assignments yet" />}
        </div>
      )}
      {isAssignments && activeAssignmentTab === "Submissions" && (
        <div style={{ display: "grid", gap: 12 }}>
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900, maxWidth: 360 }}>
            Filter assignment
            <select value={selectedAssignmentId} onChange={(event) => setSelectedAssignmentId(event.target.value)} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700, background: "#fff" }}>
              <option value="">All assignments</option>
              {displayItems.map((assignment) => <option key={assignment.id} value={assignment.id}>{assignment.title || assignment.id}</option>)}
            </select>
          </label>
          {assignmentSubmissions.length ? (
            <>
              <div className="alpha-submissions-desktop" style={{ overflowX: "auto", border: "1px solid #E2E8F0", borderRadius: 12 }}>
                <table style={{ width: "100%", minWidth: 980, borderCollapse: "collapse", fontSize: 13 }}>
                  <thead style={{ background: LIGHT, color: DARK }}>
                    <tr>
                      {["Assignment", "Student", "Class", "Submitted", "Status", "Score", "Answer", "File", "Actions"].map((label) => <th key={label} style={{ textAlign: "left", padding: 12, borderBottom: "1px solid #E2E8F0" }}>{label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {assignmentSubmissions.map((row) => (
                      <tr key={row.id} style={{ borderBottom: "1px solid #EEF2F7" }}>
                        <td style={{ padding: 12, verticalAlign: "top", color: DARK, fontWeight: 900 }}>{row.assignmentTitle || assignmentById.get(row.assignmentId)?.title || "Assignment"}</td>
                        <td style={{ padding: 12, verticalAlign: "top" }}><strong style={{ color: DARK }}>{row.studentName || row.fullName || "Student"}</strong><div style={{ color: "#64748B", fontSize: 12 }}>{row.studentId || row.loginEmail || "No ID"}</div></td>
                        <td style={{ padding: 12, verticalAlign: "top" }}>{row.className || "Class"}</td>
                        <td style={{ padding: 12, verticalAlign: "top" }}>{formatDate(row.submittedAt || row.createdAt)}</td>
                        <td style={{ padding: 12, verticalAlign: "top" }}>{submissionStatusBadge(row)}</td>
                        <td style={{ padding: 12, verticalAlign: "top", fontWeight: 950, color: BLUE }}>{submissionScoreLabel(row)}</td>
                        <td style={{ padding: 12, verticalAlign: "top", maxWidth: 260, whiteSpace: "normal", color: "#475569" }}>{answerPreview(row)}</td>
                        <td style={{ padding: 12, verticalAlign: "top" }}>{fileChip(row) || "None"}</td>
                        <td style={{ padding: 12, verticalAlign: "top" }}>{submissionActions(row)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="alpha-submissions-cards" style={{ display: "none", gap: 10 }}>
                {assignmentSubmissions.map((row) => (
                  <article key={row.id} style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 12, background: "#fff", display: "grid", gap: 10, boxShadow: "0 10px 24px rgba(15,23,42,.05)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                      <div>
                        <strong style={{ color: DARK, fontSize: 15 }}>{row.assignmentTitle || assignmentById.get(row.assignmentId)?.title || "Assignment"}</strong>
                        <div style={{ color: "#64748B", fontWeight: 850, fontSize: 12 }}>{row.studentName || row.fullName || "Student"} · {row.studentId || row.loginEmail || "No ID"}</div>
                      </div>
                      {submissionStatusBadge(row)}
                    </div>
                    <div style={{ color: "#64748B", fontSize: 12, fontWeight: 850 }}>{row.className || "Class"} · {formatDate(row.submittedAt || row.createdAt)}</div>
                    <div style={{ background: LIGHT, border: "1px solid #D8E2F0", borderRadius: 10, padding: 10 }}>
                      <span style={{ display: "block", color: "#64748B", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>Score</span>
                      <strong style={{ color: BLUE, fontSize: 18 }}>{submissionScoreLabel(row)}</strong>
                    </div>
                    <p style={{ margin: 0, color: "#475569", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>{answerPreview(row)}</p>
                    {fileChip(row)}
                    {submissionActions(row)}
                  </article>
                ))}
              </div>
            </>
          ) : <AlphaEmptyState title={selectedAssignment ? `No submissions for ${selectedAssignment.title || "this assignment"}` : "No assignment submissions yet"} />}
        </div>
      )}
      {isAssignments && workTarget && (
        <div style={{ border: `1px solid ${BLUE}`, borderRadius: 12, padding: 16, background: "#F8FAFC", display: "grid", gap: 12, boxShadow: "0 18px 42px rgba(15,23,42,.08)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "start" }}>
            <div>
              <strong style={{ color: DARK, fontSize: 18 }}>Submitted Work</strong>
              <p style={{ margin: "4px 0 0", color: "#64748B", fontWeight: 850 }}>{workTarget.studentName || workTarget.fullName || "Student"} · {workTarget.studentId || workTarget.loginEmail || "No ID"}</p>
            </div>
            <Button variant="light" disabled={busy} onClick={() => setWorkTarget(null)}>Close</Button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
            <div style={{ background: "#fff", border: "1px solid #D8E2F0", borderRadius: 10, padding: 10 }}><span style={{ color: "#64748B", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>Assignment</span><strong style={{ display: "block", color: DARK }}>{workTarget.assignmentTitle || assignmentById.get(workTarget.assignmentId)?.title || "Assignment"}</strong></div>
            <div style={{ background: "#fff", border: "1px solid #D8E2F0", borderRadius: 10, padding: 10 }}><span style={{ color: "#64748B", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>Class</span><strong style={{ display: "block", color: DARK }}>{workTarget.className || "Class"}</strong></div>
            <div style={{ background: "#fff", border: "1px solid #D8E2F0", borderRadius: 10, padding: 10 }}><span style={{ color: "#64748B", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>Submitted</span><strong style={{ display: "block", color: DARK }}>{formatDate(workTarget.submittedAt || workTarget.createdAt)}</strong></div>
            <div style={{ background: "#fff", border: "1px solid #D8E2F0", borderRadius: 10, padding: 10 }}><span style={{ color: "#64748B", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>Current score</span><strong style={{ display: "block", color: BLUE }}>{submissionScoreLabel(workTarget)}</strong></div>
          </div>
          <div style={{ background: "#fff", border: "1px solid #D8E2F0", borderRadius: 10, padding: 12, display: "grid", gap: 8 }}>
            <strong style={{ color: DARK }}>Full text answer</strong>
            <div style={{ color: "#1F2937", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{answerText(workTarget) || "No text answer submitted."}</div>
          </div>
          {fileChip(workTarget, "Open submitted file")}
          <div style={{ background: "#fff", border: "1px solid #D8E2F0", borderRadius: 10, padding: 12 }}>
            <strong style={{ color: DARK }}>Current feedback</strong>
            <p style={{ margin: "6px 0 0", color: "#475569", whiteSpace: "pre-wrap" }}>{workTarget.feedback || "No feedback yet."}</p>
          </div>
          {submissionActions(workTarget)}
        </div>
      )}
      {isAssignments && gradeTarget && (
        <div style={{ border: `1px solid ${BLUE}`, borderRadius: 8, padding: 16, background: "#F8FAFC", display: "grid", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <strong style={{ color: DARK, fontSize: 18 }}>Grade Submission · {gradeTarget.studentName || "Student"}</strong>
            <Button variant="light" disabled={busy} onClick={() => { setGradeTarget(null); setGradeFile(null); }}>Close</Button>
          </div>
          <div style={{ color: DARK, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{answerText(gradeTarget) || "No text answer submitted."}</div>
          {fileChip(gradeTarget, "Open submitted file")}
          <input value={gradeForm.score} onChange={(event) => setGradeForm((current) => ({ ...current, score: event.target.value }))} type="number" placeholder="Score" style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700 }} />
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>
            Status
            <select value={gradeForm.status} onChange={(event) => setGradeForm((current) => ({ ...current, status: event.target.value }))} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700, background: "#fff" }}>
              <option value="graded">Graded</option>
              <option value="returned">Returned</option>
              <option value="needs revision">Needs revision</option>
            </select>
          </label>
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>
            Teacher feedback
            <textarea value={gradeForm.feedback} onChange={(event) => setGradeForm((current) => ({ ...current, feedback: event.target.value }))} style={{ minHeight: 110, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700, resize: "vertical" }} />
          </label>
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>
            Corrected file (optional)
            <input type="file" onChange={(event) => setGradeFile(event.target.files?.[0] || null)} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: 8, background: "#fff" }} />
          </label>
          {gradeFile && (
            <div style={{ border: "1px solid #D8E2F0", borderRadius: 8, padding: 10, background: "#fff", display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ color: DARK, fontWeight: 900, overflowWrap: "anywhere" }}>{gradeFile.name} · {formatFileSize(gradeFile.size)}</span>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {gradeFilePreviewUrl && <a href={gradeFilePreviewUrl} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, fontWeight: 950 }}>Preview corrected file</a>}
                <Button variant="light" disabled={busy} onClick={() => setGradeFile(null)}>Remove corrected file</Button>
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button variant="gold" busy={busy} onClick={() => saveGrade(gradeForm.status || "graded")}>Save Grade</Button>
            <Button variant="light" busy={busy} onClick={() => saveGrade("returned")}>Return Feedback</Button>
          </div>
        </div>
      )}
      {isAssignments && activeAssignmentTab === "Gradebook" && (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
            <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900, minWidth: 220, flex: 1 }}>
              Search Gradebook
              <input value={gradebookSearch} onChange={(event) => setGradebookSearch(event.target.value)} placeholder="Student, ID, assignment..." style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700, background: "#fff" }} />
            </label>
            <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900, minWidth: 220 }}>
              Filter Class
              <select value={gradebookClassFilter} onChange={(event) => setGradebookClassFilter(event.target.value)} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700, background: "#fff" }}>
                <option value="">All classes</option>
                {classes.map((classItem) => <option key={classItem.id} value={classItem.id}>{classItem.name || classItem.className || classItem.id}</option>)}
              </select>
            </label>
            <Button variant="light" onClick={exportGradebook} disabled={!gradebookRows.length}>Export CSV</Button>
          </div>
          {gradebookStudentRows.length ? (
            <div style={{ display: "grid", gap: 10 }}>
              {gradebookStudentRows.map((row) => (
                <article key={row.key} style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 14, background: "#fff", display: "grid", gap: 10, boxShadow: "0 10px 24px rgba(15,23,42,.05)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "start" }}>
                    <div>
                      <strong style={{ color: DARK, fontSize: 17 }}>{row.studentName}</strong>
                      <p style={{ margin: "4px 0 0", color: "#64748B", fontWeight: 850 }}>{row.studentId || row.loginEmail || "No ID"} · {row.className || "Class"}</p>
                    </div>
                    <Button variant="light" onClick={() => setGradebookHistoryTarget(gradebookHistoryTarget?.key === row.key ? null : row)}>View history</Button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>
                    <div style={{ background: LIGHT, border: "1px solid #D8E2F0", borderRadius: 10, padding: 10 }}><span style={{ color: "#64748B", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>Assignments submitted</span><strong style={{ display: "block", color: DARK, fontSize: 22 }}>{row.submittedCount}</strong></div>
                    <div style={{ background: LIGHT, border: "1px solid #D8E2F0", borderRadius: 10, padding: 10 }}><span style={{ color: "#64748B", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>Average score</span><strong style={{ display: "block", color: BLUE, fontSize: 22 }}>{row.averageScore}{typeof row.averageScore === "number" ? "%" : ""}</strong></div>
                    <div style={{ background: LIGHT, border: "1px solid #D8E2F0", borderRadius: 10, padding: 10 }}><span style={{ color: "#64748B", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>Latest feedback</span><strong style={{ display: "block", color: DARK, fontSize: 13, whiteSpace: "pre-wrap" }}>{row.latestFeedback}</strong></div>
                  </div>
                  {gradebookHistoryTarget?.key === row.key && (
                    <div style={{ borderTop: "1px solid #E2E8F0", paddingTop: 10, display: "grid", gap: 8 }}>
                      {row.rows.map((history) => (
                        <div key={history.id} style={{ border: "1px solid #E2E8F0", borderRadius: 10, padding: 10, display: "grid", gap: 6 }}>
                          <strong style={{ color: DARK }}>{history.assignmentTitle || assignmentById.get(history.assignmentId)?.title || "Assignment"}</strong>
                          <span style={{ color: "#64748B", fontSize: 12 }}>{formatDate(history.gradedAt || history.updatedAt || history.submittedAt || history.createdAt)} · {submissionScoreLabel(history)}</span>
                          {history.feedback && <span style={{ color: "#475569", whiteSpace: "pre-wrap" }}>{history.feedback}</span>}
                          <div>{submissionActions(history)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <div style={{ border: "1px dashed #BFD0E8", borderRadius: 12, padding: 18, background: "#fff", display: "grid", gap: 10, justifyItems: "start" }}>
              <strong style={{ color: DARK }}>No graded submissions yet. Grade student submissions first.</strong>
              {pendingMarkingCount > 0 && <Button variant="gold" onClick={() => setActiveAssignmentTab("Submissions")}>Go to Submissions to mark pending work</Button>}
            </div>
          )}
        </div>
      )}
      {isAssignments && activeAssignmentTab === "Analytics" && <StatGrid stats={[
        { label: "Total Assigned Students", value: analytics.totalAssignedStudents },
        { label: "Submitted", value: analytics.submitted },
        { label: "Not Submitted", value: analytics.notSubmitted },
        { label: "Late Submissions", value: analytics.late },
        { label: "Graded", value: analytics.graded },
        { label: "Pending Marking", value: analytics.pending },
        { label: "Average Score", value: analytics.averageScore },
        { label: "Highest Score", value: analytics.highestScore },
        { label: "Lowest Score", value: analytics.lowestScore },
      ]} />}
    </Panel>
  );
}

function ClassSelect({ classes, defaultValue = "" }) {
  return (
    <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Class<select name="classId" required defaultValue={defaultValue} style={{ minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontWeight: 700 }}>{classes.map((item) => <option key={item.id} value={item.id}>{item.name || item.className}</option>)}</select></label>
  );
}

function AttachmentLink({ item, label = "Open attachment" }) {
  const url = item?.fileUrl || item?.attachmentUrl || "";
  if (!url) return null;
  const name = item.fileName || item.attachmentName || "";
  const size = item.fileSize || item.attachmentSize || 0;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, fontWeight: 950, overflowWrap: "anywhere" }}>
      {label}{name ? `: ${name}` : ""}{size ? ` (${formatFileSize(size)})` : ""}
    </a>
  );
}

function DeviceUploadField({ id, label = "Upload File", help = "PDF, document, image or video", file, onFileChange, accept = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.gif,.mp4,.webm,.txt", disabled = false }) {
  return (
    <label htmlFor={id} className="alpha-upload-field" style={{ gridColumn: "1 / -1", border: `1.5px dashed ${file ? BLUE : "#AFC0D5"}`, borderRadius: 12, padding: 14, background: file ? "#EFF6FF" : "#F8FAFC", display: "grid", gap: 7, cursor: disabled ? "not-allowed" : "pointer" }}>
      <input id={id} type="file" accept={accept} disabled={disabled} onChange={(event) => onFileChange(event.target.files?.[0] || null)} style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }} />
      <span style={{ color: DARK, fontWeight: 950 }}>📎 {file ? "Replace selected file" : label}</span>
      <span style={{ color: "#64748B", fontSize: 12 }}>{file ? `${file.name} · ${formatFileSize(file.size)}` : help}</span>
      <span style={{ width: "fit-content", background: file ? "#fff" : BLUE, color: file ? BLUE : "#fff", border: `1px solid ${BLUE}`, borderRadius: 8, padding: "8px 11px", fontSize: 12, fontWeight: 950 }}>{file ? "Choose another file" : "Choose from device"}</span>
    </label>
  );
}

const quizTypes = [
  { id: "multipleChoice", label: "Multiple Choice" },
  { id: "trueFalse", label: "True / False" },
  { id: "shortAnswer", label: "Short Answer" },
  { id: "essay", label: "Essay" },
  { id: "fillBlank", label: "Fill in blank" },
];

const quizInputStyle = { minHeight: 42, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", font: "inherit", fontWeight: 700, boxSizing: "border-box", width: "100%", background: "#fff" };
const quizTextareaStyle = { ...quizInputStyle, minHeight: 118, resize: "vertical", whiteSpace: "pre-wrap", lineHeight: 1.5 };

function quizQuestionId() {
  return `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function makeQuestion(type = "multipleChoice") {
  return {
    id: quizQuestionId(),
    type,
    question: "",
    options: type === "multipleChoice" ? ["", "", "", ""] : [],
    correctAnswer: type === "multipleChoice" ? "A" : type === "trueFalse" ? "true" : "",
    expectedAnswer: "",
    keywords: "",
    marks: 1,
  };
}

function blankQuiz(classes = []) {
  return {
    classId: classes[0]?.id || "",
    subject: "",
    title: "",
    description: "",
    instructions: "",
    startDate: "",
    dueDate: "",
    startTime: "",
    endTime: "",
    timerMinutes: 20,
    attemptsAllowed: 1,
    randomizeQuestions: false,
    randomizeAnswers: false,
    status: "draft",
    questions: [makeQuestion()],
  };
}

function cloneQuizForForm(quiz, classes = []) {
  if (!quiz) return blankQuiz(classes);
  const questions = Array.isArray(quiz.questions) && quiz.questions.length ? quiz.questions : [makeQuestion()];
  return {
    classId: quiz.classId || classes[0]?.id || "",
    subject: quiz.subject || "",
    title: quiz.title || "",
    description: quiz.description || "",
    instructions: quiz.instructions || quiz.body || "",
    startDate: quiz.startDate || "",
    dueDate: quiz.dueDate || "",
    startTime: quiz.startTime || "",
    endTime: quiz.endTime || "",
    timerMinutes: Number(quiz.timerMinutes || 20),
    attemptsAllowed: Number(quiz.attemptsAllowed || 1),
    randomizeQuestions: Boolean(quiz.randomizeQuestions),
    randomizeAnswers: Boolean(quiz.randomizeAnswers),
    status: quiz.status || "draft",
    questions: questions.map((item) => ({
      id: item.id || quizQuestionId(),
      type: item.type || "multipleChoice",
      question: item.question || "",
      options: item.type === "multipleChoice"
        ? (Array.isArray(item.options) && item.options.length ? item.options : ["", "", "", ""]).slice(0, 6)
        : [],
      correctAnswer: item.correctAnswer ?? "",
      expectedAnswer: item.expectedAnswer || "",
      keywords: Array.isArray(item.keywords) ? item.keywords.join(", ") : (item.keywords || ""),
      marks: Number(item.marks || 1),
    })),
  };
}

function quizTotalMarks(questions) {
  return questions.reduce((sum, item) => sum + Math.max(0, Number(item.marks || 0)), 0);
}

function cleanQuizQuestions(questions) {
  return questions.map((item) => {
    const type = item.type || "multipleChoice";
    const options = type === "multipleChoice" ? (item.options || []).map((option) => String(option || "").trim()).filter(Boolean).slice(0, 6) : [];
    const letterAnswer = String(item.correctAnswer || "").trim().toUpperCase();
    const expectedAnswer = String(item.expectedAnswer || item.correctAnswer || "").trim();
    return {
      id: item.id || quizQuestionId(),
      type,
      question: String(item.question || "").trim(),
      options,
      correctAnswer: type === "essay" ? "" : type === "multipleChoice" ? letterAnswer : type === "fillBlank" ? expectedAnswer : String(item.correctAnswer ?? "").trim(),
      expectedAnswer,
      keywords: String(item.keywords || "").split(",").map((keyword) => keyword.trim()).filter(Boolean),
      marks: Math.max(0, Number(item.marks || 0)),
    };
  });
}

function validateQuizDraft(form, status) {
  if (!form.classId) throw new Error("Choose a class.");
  if (!String(form.title || "").trim()) throw new Error("Quiz title is required.");
  const questions = cleanQuizQuestions(form.questions || []);
  if (!questions.length) throw new Error("Add at least one question.");
  questions.forEach((item, index) => {
    const label = `Question ${index + 1}`;
    if (!item.question) throw new Error(`${label} needs question text.`);
    if (item.marks <= 0) throw new Error(`${label} marks must be greater than zero.`);
    if (item.type === "multipleChoice") {
      if (item.options.length < 2) throw new Error(`${label} needs at least two options.`);
      if (!/^[A-Z]$/.test(item.correctAnswer) || item.correctAnswer.charCodeAt(0) - 65 >= item.options.length) throw new Error(`${label} needs a selected correct option.`);
    }
    if (item.type === "trueFalse" && !["true", "false"].includes(item.correctAnswer)) throw new Error(`${label} needs true or false as the correct answer.`);
    if (status === "published" && item.type === "shortAnswer" && !item.correctAnswer) throw new Error(`${label} needs an exact-match answer before publishing.`);
    if (status === "published" && item.type === "fillBlank" && !item.correctAnswer) throw new Error(`${label} needs an expected answer before publishing.`);
  });
  return questions;
}

function quizOverviewStats(quizzes = [], attempts = [], users = []) {
  const quizIds = new Set(quizzes.map((quiz) => quiz.id).filter(Boolean));
  const rows = attempts.filter((attempt) => quizIds.has(attempt.quizId));
  const scores = rows.map((attempt) => Number(attempt.percentage || 0)).filter((score) => Number.isFinite(score));
  const assigned = quizzes.reduce((sum, quiz) => {
    const classStudents = users.filter((user) => user.role === "student" && ((user.classIds || []).includes(quiz.classId) || user.classId === quiz.classId));
    return sum + classStudents.length;
  }, 0);
  return [
    { label: "Total Quizzes", value: quizzes.length },
    { label: "Draft Quizzes", value: quizzes.filter((quiz) => quiz.status === "draft").length },
    { label: "Published Quizzes", value: quizzes.filter((quiz) => quiz.status === "published").length },
    { label: "Active Quizzes", value: quizzes.filter((quiz) => ["published", "active"].includes(quiz.status || "")).length },
    { label: "Total Attempts", value: rows.length },
    { label: "Pending Marking", value: rows.filter((attempt) => attempt.status === "pending-marking").length },
    { label: "Average Score", value: scores.length ? `${Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length)}%` : "0%" },
    { label: "Completion Rate", value: assigned ? `${Math.round((rows.length / assigned) * 100)}%` : "0%" },
  ];
}

function gradeFromPercentage(percentage) {
  const score = Number(percentage || 0);
  if (score >= 80) return "A";
  if (score >= 70) return "B";
  if (score >= 60) return "C";
  if (score >= 50) return "D";
  return "F";
}

async function uploadAlphaQuizAttachment(file, { quizId, onProgress } = {}) {
  if (!file || !quizId) return {};
  const path = `schools/${SCHOOL_ID}/quizzes/${quizId}/attachments/${Date.now()}-${safeFileName(file.name)}`;
  const storageRef = ref(storage, path);
  const snapshot = await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, { contentType: file.type || "application/octet-stream" });
    task.on("state_changed", (state) => {
      if (onProgress) onProgress(Math.round((state.bytesTransferred / state.totalBytes) * 100));
    }, reject, () => resolve(task.snapshot));
  });
  const fileUrl = await getDownloadURL(snapshot.ref);
  return {
    fileUrl,
    fileName: file.name,
    fileType: file.type || "application/octet-stream",
    fileSize: file.size || 0,
    storagePath: path,
    attachmentUrl: fileUrl,
    attachmentName: file.name,
    attachmentType: file.type || "application/octet-stream",
    attachment: {
      fileName: file.name,
      fileUrl,
      storagePath: path,
      fileType: file.type || "application/octet-stream",
      uploadedAt: new Date().toISOString(),
    },
  };
}

function quizAttemptStats(quiz, attempts = [], users = []) {
  const rows = attempts.filter((item) => item.quizId === quiz.id);
  const scores = rows.map((item) => Number(item.percentage || 0)).filter((n) => Number.isFinite(n));
  const classStudents = users.filter((user) => user.role === "student" && ((user.classIds || []).includes(quiz.classId) || user.classId === quiz.classId));
  const submitted = rows.length;
  const assigned = classStudents.length || 0;
  const avg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
  const highest = scores.length ? Math.round(Math.max(...scores)) : 0;
  const lowest = scores.length ? Math.round(Math.min(...scores)) : 0;
  const pending = rows.filter((item) => item.status === "pending-marking").length;
  const passed = rows.filter((item) => Number(item.percentage || 0) >= 50).length;
  const failed = rows.filter((item) => item.status !== "pending-marking" && Number(item.percentage || 0) < 50).length;
  return { rows, assigned, submitted, notStarted: Math.max(0, assigned - submitted), avg, highest, lowest, pending, passed, failed, completion: assigned ? Math.round((submitted / assigned) * 100) : 0 };
}

function QuizBuilderModule({ profile, quizzes, classes, attempts = [], users = [] }) {
  const [form, setForm] = useState(() => blankQuiz(classes));
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [activeTab, setActiveTab] = useState("Overview");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState("");
  const [quizFile, setQuizFile] = useState(null);
  const [quizFilePreviewUrl, setQuizFilePreviewUrl] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [quizTemplate, setQuizTemplate] = useState("");

  useEffect(() => {
    setForm((current) => current.classId ? current : { ...current, classId: classes[0]?.id || "" });
  }, [classes]);

  useEffect(() => {
    if (!quizFile) {
      setQuizFilePreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(quizFile);
    setQuizFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [quizFile]);

  function updateField(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function updateQuestion(index, updates) {
    setForm((current) => ({
      ...current,
      questions: current.questions.map((item, itemIndex) => itemIndex === index ? { ...item, ...updates } : item),
    }));
  }

  function setQuestionType(index, type) {
    updateQuestion(index, {
      type,
      options: type === "multipleChoice" ? ["", "", "", ""] : [],
      correctAnswer: type === "multipleChoice" ? "A" : type === "trueFalse" ? "true" : "",
      expectedAnswer: "",
      keywords: "",
    });
  }

  function addQuestion(type = "multipleChoice") {
    setForm((current) => ({ ...current, questions: [...current.questions, makeQuestion(type)] }));
  }

  function chooseQuizTemplate(type) {
    setQuizTemplate(type);
    if (type === "upload") {
      setForm((current) => ({ ...current, questions: [makeQuestion("essay")], instructions: current.instructions || "Download or open the attached quiz, then upload your completed answers." }));
      return;
    }
    setForm((current) => ({ ...current, questions: [makeQuestion(type)] }));
  }

  function removeQuestion(index) {
    setForm((current) => ({ ...current, questions: current.questions.length > 1 ? current.questions.filter((_, itemIndex) => itemIndex !== index) : current.questions }));
  }

  function duplicateQuestion(index) {
    setForm((current) => {
      const copy = { ...current.questions[index], id: quizQuestionId(), options: [...(current.questions[index].options || [])] };
      const questions = [...current.questions];
      questions.splice(index + 1, 0, copy);
      return { ...current, questions };
    });
  }

  function moveQuestion(index, direction) {
    setForm((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.questions.length) return current;
      const questions = [...current.questions];
      [questions[index], questions[target]] = [questions[target], questions[index]];
      return { ...current, questions };
    });
  }

  function updateOption(questionIndex, optionIndex, value) {
    setForm((current) => {
      const questions = current.questions.map((item, itemIndex) => {
        if (itemIndex !== questionIndex) return item;
        const options = [...(item.options || [])];
        options[optionIndex] = value;
        return { ...item, options };
      });
      return { ...current, questions };
    });
  }

  function addOption(questionIndex) {
    setForm((current) => ({ ...current, questions: current.questions.map((item, index) => index === questionIndex ? { ...item, options: [...(item.options || []), ""] } : item) }));
  }

  function removeOption(questionIndex, optionIndex) {
    setForm((current) => ({ ...current, questions: current.questions.map((item, index) => {
      if (index !== questionIndex || (item.options || []).length <= 2) return item;
      const options = item.options.filter((_, itemIndex) => itemIndex !== optionIndex);
      const answerIndex = String(item.correctAnswer || "A").charCodeAt(0) - 65;
      return { ...item, options, correctAnswer: answerIndex >= options.length ? "A" : item.correctAnswer };
    }) }));
  }

  async function saveQuiz(status) {
    if (busy) return;
    setBusy(status);
    setUploadProgress(0);
    setMessage(status === "published" ? "⏳ Publishing quiz..." : "⏳ Saving draft...");
    try {
      const questions = validateQuizDraft(form, status);
      const classItem = classes.find((item) => item.id === form.classId);
      if (!classItem) throw new Error("Choose a valid class before saving.");
      const quizRef = editing?.id
        ? doc(getFirebaseDb(), "schools", SCHOOL_ID, "quizzes", editing.id)
        : doc(collection(getFirebaseDb(), "schools", SCHOOL_ID, "quizzes"));
      const fileMeta = quizFile
        ? await uploadAlphaQuizAttachment(quizFile, { quizId: quizRef.id, onProgress: setUploadProgress })
        : {};
      const payload = {
        id: quizRef.id,
        schoolId: SCHOOL_ID,
        classId: form.classId,
        className: classItem?.name || classItem?.className || "",
        subject: form.subject || classItem?.subject || "",
        title: String(form.title || "").trim(),
        description: String(form.description || "").trim(),
        instructions: String(form.instructions || "").trim(),
        startDate: form.startDate || "",
        dueDate: form.dueDate || "",
        startTime: form.startTime || "",
        endTime: form.endTime || "",
        timerMinutes: Math.max(0, Number(form.timerMinutes || 0)),
        attemptsAllowed: Math.max(1, Number(form.attemptsAllowed || 1)),
        randomizeQuestions: Boolean(form.randomizeQuestions),
        randomizeAnswers: Boolean(form.randomizeAnswers),
        status,
        totalMarks: quizTotalMarks(questions),
        questions,
        teacherId: profile.uid,
        teacherName: profile.fullName || profile.email || "Alpha Teacher",
        ...fileMeta,
        updatedAt: serverTimestamp(),
        updatedBy: profile.uid,
      };
      if (editing?.id) {
        await updateDoc(quizRef, payload);
      } else {
        await setDoc(quizRef, { ...payload, createdAt: serverTimestamp(), createdBy: profile.uid });
      }
      if (status === "published" && editing?.status !== "published") await createAlphaNotification({ type: "new_quiz", title: payload.title || "New Quiz", message: payload.description || "A new quiz is ready", classId: payload.classId, className: payload.className, itemId: quizRef.id, module: "quizzes", actorUid: profile.uid });
      setEditing(null);
      setForm(blankQuiz(classes));
      setQuizFile(null);
      const quizInput = document.getElementById("alpha-quizzes-attachment");
      if (quizInput) quizInput.value = "";
      setMessage(status === "published" ? "✅ Quiz published." : "✅ Quiz draft saved.");
      setActiveTab("My Quizzes");
    } catch (error) {
      console.error("[AlphaClassroom] save quiz failed", error);
      setMessage(`❌ ${readableFirebaseError(error)}`);
    } finally {
      setUploadProgress(0);
      setBusy("");
    }
  }


  async function duplicateQuiz(quiz) {
    if (busy) return;
    setBusy(`duplicate-${quiz.id}`);
    setMessage("⏳ Duplicating quiz...");
    try {
      const copy = {
        ...quiz,
        title: `${quiz.title || "Untitled quiz"} copy`,
        status: "draft",
        createdAt: serverTimestamp(),
        createdBy: profile.uid,
        updatedAt: serverTimestamp(),
        updatedBy: profile.uid,
      };
      delete copy.id;
      await addDoc(collection(getFirebaseDb(), "schools", SCHOOL_ID, "quizzes"), copy);
      setMessage("✅ Quiz duplicated as draft.");
    } catch (error) {
      setMessage(`❌ Duplicate failed: ${error.message}`);
    } finally {
      setBusy("");
    }
  }

  async function deleteQuiz(quiz) {
    if (!window.confirm(`Delete quiz "${quiz.title || "Untitled"}"? This cannot be undone.`)) return;
    setBusy(`delete-${quiz.id}`);
    setMessage("⏳ Deleting quiz...");
    try {
      await deleteDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "quizzes", quiz.id));
      if (editing?.id === quiz.id) {
        setEditing(null);
        setForm(blankQuiz(classes));
        setQuizFile(null);
      }
      if (quiz.storagePath) {
        try {
          await deleteObject(ref(storage, quiz.storagePath));
          setMessage("✅ Quiz and attached file deleted.");
        } catch (storageError) {
          setMessage(`⚠️ Quiz deleted, but file cleanup failed: ${readableFirebaseError(storageError)}`);
        }
      } else {
        setMessage("✅ Quiz deleted.");
      }
    } catch (error) {
      setMessage(`❌ Delete failed: ${readableFirebaseError(error)}`);
    } finally {
      setBusy("");
    }
  }

  async function setQuizStatus(quiz, status) {
    if (busy) return;
    setBusy(`${status}-${quiz.id}`);
    setMessage(`⏳ Updating quiz status to ${status}...`);
    try {
      await updateDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "quizzes", quiz.id), {
        status,
        updatedAt: serverTimestamp(),
        updatedBy: profile.uid,
      });
      if (status === "published" && quiz.status !== "published") await createAlphaNotification({ type: "new_quiz", title: quiz.title || "New Quiz", message: quiz.description || "A new quiz is ready", classId: quiz.classId, className: quiz.className, itemId: quiz.id, module: "quizzes", actorUid: profile.uid });
      setMessage(`✅ Quiz ${status}.`);
    } catch (error) {
      setMessage(`❌ Status update failed: ${readableFirebaseError(error)}`);
    } finally {
      setBusy("");
    }
  }

  function startEdit(quiz) {
    setEditing(quiz);
    setForm(cloneQuizForForm(quiz, classes));
    setQuizFile(null);
    setMessage("");
    setActiveTab("Create Quiz");
    setQuizTemplate(quiz.questions?.[0]?.type || "multipleChoice");
  }

  function removeQuizFile() {
    setQuizFile(null);
    const input = document.getElementById("alpha-quizzes-attachment");
    if (input) input.value = "";
  }

  const totalMarks = quizTotalMarks(form.questions);
  const quizTabs = ["Overview", "Create Quiz", "My Quizzes", "Live Quizzes", "Submissions", "Results", "Analytics"];
  const displayedQuizzes = activeTab === "Live Quizzes" ? quizzes.filter((quiz) => ["published", "active"].includes(quiz.status || "")) : quizzes;
  const selectedAttempts = selectedQuiz ? attempts.filter((item) => item.quizId === selectedQuiz.id) : attempts;


  if (selectedQuiz && activeTab === "Results") {
    return <QuizResultsDashboard quiz={selectedQuiz} attempts={attempts.filter((item) => item.quizId === selectedQuiz.id)} users={users} onBack={() => setSelectedQuiz(null)} />;
  }

  return (
    <Panel title="Quizzes">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {quizTabs.map((tab) => <Button key={tab} variant={activeTab === tab ? "gold" : "light"} onClick={() => setActiveTab(tab)}>{tab}</Button>)}
      </div>
      {message && <div style={{ background: message.startsWith("✅") ? "#ECFDF5" : message.startsWith("❌") ? "#FEF2F2" : "#EFF6FF", border: `1px solid ${message.startsWith("✅") ? "#BBF7D0" : message.startsWith("❌") ? "#FECACA" : "#BFDBFE"}`, color: message.startsWith("❌") ? "#991B1B" : DARK, borderRadius: 8, padding: 12, fontWeight: 900 }}>{message}</div>}
      {activeTab === "Overview" && <StatGrid stats={quizOverviewStats(quizzes, attempts, users)} />}
      {activeTab === "Create Quiz" && !quizTemplate && <section style={{ border: `1px solid ${GOLD}`, borderRadius: 12, padding: 18, background: "#FFFBEB", display: "grid", gap: 14 }}><div><h2 style={{ margin: 0, color: DARK }}>What kind of quiz do you want to create?</h2><p style={{ color: "#64748B", marginBottom: 0 }}>Choose one format to start. You can add other question types later.</p></div><div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>{[
        ["multipleChoice", "Multiple Choice", "Automatic marking and instant result"],
        ["trueFalse", "True or False", "Automatic marking and instant result"],
        ["shortAnswer", "Short Answer", "Exact-answer automatic marking"],
        ["essay", "Long Answer / Essay", "Teacher marks after submission"],
        ["fillBlank", "Fill in the Blank", "Expected-answer automatic marking"],
        ["upload", "Upload Quiz Work", "Attach a worksheet; students upload answers"],
      ].map(([type, label, help]) => <button key={type} type="button" onClick={() => chooseQuizTemplate(type)} style={{ border: "1px solid #D8E2F0", borderRadius: 10, padding: 16, background: "#fff", color: DARK, textAlign: "left", cursor: "pointer", display: "grid", gap: 6 }}><strong style={{ fontSize: 16 }}>{label}</strong><span style={{ color: "#64748B", fontSize: 12 }}>{help}</span></button>)}</div></section>}
      {activeTab === "Create Quiz" && quizTemplate && <div style={{ border: `1px solid ${GOLD}`, borderRadius: 8, padding: 14, background: "#FFFBEB", display: "grid", gap: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <strong style={{ color: DARK, fontSize: 17 }}>{editing ? "Edit Quiz" : "Create Quiz"}</strong>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}><Button variant="light" onClick={() => setQuizTemplate("")}>Change Quiz Type</Button><span style={{ color: "#92400E", fontWeight: 950 }}>Total marks: {totalMarks}</span></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 12 }}>
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Class<select value={form.classId} onChange={(event) => updateField("classId", event.target.value)} style={quizInputStyle}>{classes.map((item) => <option key={item.id} value={item.id}>{item.name || item.className}</option>)}</select></label>
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Subject<input value={form.subject} onChange={(event) => updateField("subject", event.target.value)} style={quizInputStyle} /></label>
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Quiz title<input value={form.title} onChange={(event) => updateField("title", event.target.value)} style={quizInputStyle} /></label>
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Start date<input type="date" value={form.startDate} onChange={(event) => updateField("startDate", event.target.value)} style={quizInputStyle} /></label>
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Due date<input type="date" value={form.dueDate} onChange={(event) => updateField("dueDate", event.target.value)} style={quizInputStyle} /></label>
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Start time<input type="time" value={form.startTime} onChange={(event) => updateField("startTime", event.target.value)} style={quizInputStyle} /></label>
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>End time<input type="time" value={form.endTime} onChange={(event) => updateField("endTime", event.target.value)} style={quizInputStyle} /></label>
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Timer minutes<input type="number" min="0" value={form.timerMinutes} onChange={(event) => updateField("timerMinutes", event.target.value)} style={quizInputStyle} /></label>
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Attempts allowed<input type="number" min="1" value={form.attemptsAllowed} onChange={(event) => updateField("attemptsAllowed", event.target.value)} style={quizInputStyle} /></label>
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Status<select value={form.status} onChange={(event) => updateField("status", event.target.value)} style={quizInputStyle}><option value="draft">Draft</option><option value="published">Published</option><option value="closed">Closed</option><option value="archived">Archived</option></select></label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, color: DARK, fontSize: 13, fontWeight: 900, alignSelf: "end", minHeight: 42 }}><input type="checkbox" checked={form.randomizeQuestions} onChange={(event) => updateField("randomizeQuestions", event.target.checked)} /> Randomize questions</label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, color: DARK, fontSize: 13, fontWeight: 900, alignSelf: "end", minHeight: 42 }}><input type="checkbox" checked={form.randomizeAnswers} onChange={(event) => updateField("randomizeAnswers", event.target.checked)} /> Randomize answers</label>
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900, gridColumn: "1 / -1" }}>Description<textarea value={form.description} onChange={(event) => updateField("description", event.target.value)} style={quizTextareaStyle} /></label>
          <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900, gridColumn: "1 / -1" }}>Instructions<textarea value={form.instructions} onChange={(event) => updateField("instructions", event.target.value)} style={{ ...quizTextareaStyle, minHeight: 140 }} /></label>
          <DeviceUploadField id="alpha-quizzes-attachment" label={quizTemplate === "upload" ? "Upload quiz worksheet" : "Upload optional quiz attachment"} file={quizFile} onFileChange={setQuizFile} disabled={Boolean(busy)} />
          {(editing?.fileUrl || editing?.attachmentUrl) && <AttachmentLink item={editing} label="Current attachment" />}
          {busy && uploadProgress > 0 && <span style={{ color: DARK, fontSize: 12, gridColumn: "1 / -1" }}>Uploading attachment... {uploadProgress}%</span>}
          {quizFile && (
            <div style={{ gridColumn: "1 / -1", border: "1px solid #D8E2F0", borderRadius: 8, padding: 12, background: "#F8FAFC", display: "grid", gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ color: DARK, fontWeight: 900, overflowWrap: "anywhere" }}>Selected file: {quizFile.name} · {quizFile.type || "unknown type"} · {formatFileSize(quizFile.size)}</span>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {quizFilePreviewUrl && <a href={quizFilePreviewUrl} target="_blank" rel="noopener noreferrer" style={{ background: "#fff", color: DARK, border: "1px solid #D8E2F0", borderRadius: 8, padding: "9px 12px", fontWeight: 900, textDecoration: "none" }}>Preview Selected File</a>}
                  <Button variant="light" disabled={Boolean(busy)} onClick={removeQuizFile}>Remove File</Button>
                </div>
              </div>
              {quizFilePreviewUrl && quizFile.type?.startsWith("image/") && <img src={quizFilePreviewUrl} alt={quizFile.name} style={{ maxWidth: 260, maxHeight: 180, objectFit: "contain", border: "1px solid #E2E8F0", borderRadius: 8, background: "#fff" }} />}
              {quizFilePreviewUrl && quizFile.type === "application/pdf" && <iframe title="Selected quiz file preview" src={quizFilePreviewUrl} style={{ width: "100%", height: 260, border: "1px solid #E2E8F0", borderRadius: 8, background: "#fff" }} />}
            </div>
          )}
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          {form.questions.map((question, index) => <QuizQuestionEditor key={question.id} question={question} index={index} count={form.questions.length} onType={setQuestionType} onUpdate={updateQuestion} onOption={updateOption} onAddOption={addOption} onRemoveOption={removeOption} onRemove={removeQuestion} onDuplicate={duplicateQuestion} onMove={moveQuestion} />)}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button variant="light" onClick={() => addQuestion()}>Add Question</Button>
          <Button variant="light" onClick={() => setPreview({ ...editing, ...form, totalMarks, questions: cleanQuizQuestions(form.questions) })}>Preview Quiz as Student</Button>
          <Button variant="gold" busy={busy === "draft"} disabled={Boolean(busy)} onClick={() => saveQuiz("draft")}>Save Draft</Button>
          <Button busy={busy === "published"} disabled={Boolean(busy)} onClick={() => saveQuiz("published")}>Publish Quiz</Button>
          {editing && <Button variant="light" disabled={Boolean(busy)} onClick={() => { setEditing(null); setForm(blankQuiz(classes)); removeQuizFile(); }}>Cancel Edit</Button>}
        </div>
      </div>}
      {preview && <QuizPreview quiz={preview} onClose={() => setPreview(null)} />}
      {["My Quizzes", "Live Quizzes"].includes(activeTab) && <Table rows={displayedQuizzes} empty="No quizzes yet" columns={[
        { key: "title", label: "Quiz" },
        { key: "className", label: "Class" },
        { key: "questions", label: "Questions", render: (row) => row.questions?.length || 0 },
        { key: "status", label: "Status" },
        { key: "totalMarks", label: "Marks" },
        { key: "file", label: "Attachment", render: (row) => (row.fileUrl || row.attachmentUrl) ? <AttachmentLink item={row} /> : "None" },
        { key: "submissions", label: "Submissions", render: (row) => quizAttemptStats(row, attempts, users).submitted },
        { key: "average", label: "Average", render: (row) => `${quizAttemptStats(row, attempts, users).avg}%` },
        { key: "pending", label: "Pending", render: (row) => quizAttemptStats(row, attempts, users).pending },
        { key: "actions", label: "Actions", render: (row) => <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><Button variant="light" onClick={() => setPreview(row)}>Preview</Button><Button variant="light" onClick={() => startEdit(row)}>Edit</Button>{(row.fileUrl || row.attachmentUrl) && <AttachmentLink item={row} label="Open File" />}<Button variant="light" busy={busy === `duplicate-${row.id}`} onClick={() => duplicateQuiz(row)}>Duplicate</Button><Button variant="light" busy={busy === `published-${row.id}`} onClick={() => setQuizStatus(row, "published")}>Publish</Button><Button variant="light" busy={busy === `closed-${row.id}`} onClick={() => setQuizStatus(row, "closed")}>Close</Button><Button onClick={() => { setSelectedQuiz(row); setActiveTab("Results"); }}>Results</Button><Button variant="light" onClick={() => { setSelectedQuiz(row); setActiveTab("Analytics"); }}>Analytics</Button><Button variant="danger" busy={busy === `delete-${row.id}`} onClick={() => deleteQuiz(row)}>Delete</Button></div> },
      ]} />}
      {["Submissions", "Results", "Analytics"].includes(activeTab) && (
        <QuizResultsDashboard quiz={selectedQuiz || quizzes[0]} attempts={selectedQuiz ? selectedAttempts : attempts} users={users} onBack={() => setSelectedQuiz(null)} />
      )}
    </Panel>
  );
}

function QuizResultsDashboard({ quiz, attempts = [], users = [], onBack }) {
  const [openAttempt, setOpenAttempt] = useState(null);
  const [markingAttempt, setMarkingAttempt] = useState(null);
  const [manualMarks, setManualMarks] = useState({});
  const [feedback, setFeedback] = useState("");
  if (!quiz) {
    return (
      <Panel title="Quiz Results" action={<Button variant="light" onClick={onBack}>Back</Button>}>
        <AlphaEmptyState title="No quiz selected" />
      </Panel>
    );
  }
  const stats = quizAttemptStats(quiz, attempts, users);

  function openMarking(row) {
    setMarkingAttempt(row);
    const values = {};
    Object.values(row.answers || {}).forEach((answer) => {
      if (answer.type === "essay") values[answer.questionId] = answer.awarded ?? "";
    });
    setManualMarks(values);
    setFeedback(row.feedback || "");
  }

  async function saveManualMarks() {
    if (!markingAttempt) return;
    const answers = { ...(markingAttempt.answers || {}) };
    let score = Number(markingAttempt.score || 0);
    Object.values(answers).forEach((answer) => {
      if (answer.type !== "essay") return;
      const max = Number(answer.marks || 0);
      const previous = Number(answer.awarded || 0);
      const awarded = Math.min(max, Math.max(0, Number(manualMarks[answer.questionId] || 0)));
      score = score - previous + awarded;
      answers[answer.questionId] = { ...answer, awarded, marksEarned: awarded, status: "manual-marked", comment: feedback };
    });
    const totalMarks = Number(markingAttempt.totalMarks || quiz.totalMarks || 0);
    await updateDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "quizAttempts", markingAttempt.id), {
      answers,
      score,
      totalMarks,
      percentage: totalMarks ? (score / totalMarks) * 100 : 0,
      grade: gradeFromPercentage(totalMarks ? (score / totalMarks) * 100 : 0),
      feedback,
      status: "graded",
      gradedAt: serverTimestamp(),
      gradedBy: "teacher",
      updatedAt: serverTimestamp(),
    });
    setMarkingAttempt(null);
  }

  return (
    <Panel title="Quiz Results" action={<Button variant="light" onClick={onBack}>Back to Quiz Builder</Button>}>
      <div style={{ border: `1px solid ${BLUE}`, background: LIGHT, borderRadius: 8, padding: 16, display: "grid", gap: 10 }}>
        <h2 style={{ margin: 0, color: DARK }}>{quiz.title || "Untitled quiz"}</h2>
        <p style={{ margin: 0, color: "#64748B", fontWeight: 800 }}>{quiz.className || "Class"} · {quiz.status || "draft"} · Due: {quiz.dueDate || "No due date"} · {quiz.totalMarks || 0} marks · {quiz.questions?.length || 0} questions</p>
      </div>

      <StatGrid stats={[
        { label: "Students Assigned", value: stats.assigned },
        { label: "Submitted", value: stats.submitted },
        { label: "Not Started", value: stats.notStarted },
        { label: "Average", value: `${stats.avg}%` },
        { label: "Highest", value: `${stats.highest}%` },
        { label: "Lowest", value: `${stats.lowest}%` },
        { label: "Completion", value: `${stats.completion}%` },
        { label: "Pending Marking", value: stats.pending },
      ]} />

      {openAttempt && (
        <div style={{ border: "1px solid #D8E2F0", borderRadius: 8, padding: 14, background: "#fff", display: "grid", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <strong style={{ color: DARK, fontSize: 18 }}>Answers · {openAttempt.studentName || "Student"}</strong>
            <Button variant="light" onClick={() => setOpenAttempt(null)}>Close</Button>
          </div>
          {Object.values(openAttempt.answers || {}).map((answer, index) => (
            <div key={answer.questionId || index} style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: 12, display: "grid", gap: 6 }}>
              <strong style={{ color: DARK }}>Question {index + 1}: {answer.question || "Question"}</strong>
              <span><b>Student answer:</b> {String(answer.answer || "No answer")}</span>
              <span><b>Correct answer:</b> {String(answer.correctAnswer || "Manual marking")}</span>
              <span><b>Marks:</b> {answer.awarded ?? answer.marksEarned ?? 0} / {answer.marks || 0}</span>
            </div>
          ))}
        </div>
      )}

      {markingAttempt && (
        <div style={{ border: `1px solid ${GOLD}`, borderRadius: 8, padding: 14, background: "#FFFBEB", display: "grid", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <strong style={{ color: DARK, fontSize: 18 }}>Manual Marking · {markingAttempt.studentName || "Student"}</strong>
            <Button variant="light" onClick={() => setMarkingAttempt(null)}>Close</Button>
          </div>
          {Object.values(markingAttempt.answers || {}).filter((answer) => answer.type === "essay").map((answer) => (
            <div key={answer.questionId} style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: 12, background: "#fff", display: "grid", gap: 8 }}>
              <strong style={{ color: DARK }}>{answer.question}</strong>
              <span style={{ color: "#475569", whiteSpace: "pre-wrap" }}>{answer.answer || "No answer"}</span>
              <label style={{ display: "grid", gap: 6, color: DARK, fontWeight: 900 }}>Award marks / {answer.marks || 0}<input type="number" min="0" max={answer.marks || 0} value={manualMarks[answer.questionId] ?? ""} onChange={(event) => setManualMarks((current) => ({ ...current, [answer.questionId]: event.target.value }))} style={quizInputStyle} /></label>
            </div>
          ))}
          <label style={{ display: "grid", gap: 6, color: DARK, fontWeight: 900 }}>Teacher feedback<textarea value={feedback} onChange={(event) => setFeedback(event.target.value)} style={quizTextareaStyle} /></label>
          <Button variant="gold" onClick={saveManualMarks}>Save Manual Marks</Button>
        </div>
      )}

      <Table rows={attempts} empty="No quiz submissions yet" columns={[
        { key: "studentName", label: "Student" },
        { key: "studentId", label: "Student ID" },
        { key: "attemptNumber", label: "Attempt" },
        { key: "score", label: "Score", render: (row) => `${row.score || 0}/${row.totalMarks || quiz.totalMarks || 0}` },
        { key: "percentage", label: "Percentage", render: (row) => `${Math.round(Number(row.percentage || 0))}%` },
        { key: "status", label: "Status" },
        { key: "submittedAt", label: "Submitted", render: (row) => formatDate(row.submittedAt) },
        { key: "feedback", label: "Feedback", wrap: true },
        { key: "actions", label: "Actions", render: (row) => <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><Button variant="light" onClick={() => setOpenAttempt(row)}>View submission</Button><Button variant="gold" onClick={() => openMarking(row)}>Mark</Button><Button variant="light" onClick={() => openMarking(row)}>Comment</Button><Button variant="light" onClick={() => openMarking(row)}>Return feedback</Button></div> },
      ]} />
    </Panel>
  );
}


function QuizQuestionEditor({ question, index, count, onType, onUpdate, onOption, onAddOption, onRemoveOption, onRemove, onDuplicate, onMove }) {
  const options = question.options || [];
  return (
    <article style={{ border: "1px solid #D8E2F0", borderRadius: 8, padding: 12, background: "#fff", display: "grid", gap: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <strong style={{ color: DARK }}>Question {index + 1}</strong>
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
          <Button variant="light" disabled={index === 0} onClick={() => onMove(index, -1)}>Move Up</Button>
          <Button variant="light" disabled={index === count - 1} onClick={() => onMove(index, 1)}>Move Down</Button>
          <Button variant="light" onClick={() => onDuplicate(index)}>Duplicate</Button>
          <Button variant="danger" disabled={count <= 1} onClick={() => onRemove(index)}>Remove</Button>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
        <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Type<select value={question.type} onChange={(event) => onType(index, event.target.value)} style={quizInputStyle}>{quizTypes.map((type) => <option key={type.id} value={type.id}>{type.label}</option>)}</select></label>
        <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Marks<input type="number" min="1" value={question.marks} onChange={(event) => onUpdate(index, { marks: event.target.value })} style={quizInputStyle} /></label>
        <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900, gridColumn: "1 / -1" }}>Question text<textarea value={question.question} onChange={(event) => onUpdate(index, { question: event.target.value })} style={quizTextareaStyle} /></label>
      </div>
      {question.type === "multipleChoice" && <div style={{ display: "grid", gap: 8 }}>
        {options.map((option, optionIndex) => {
          const letter = String.fromCharCode(65 + optionIndex);
          return (
            <div key={optionIndex} style={{ display: "grid", gridTemplateColumns: "auto auto minmax(0,1fr) auto", gap: 8, alignItems: "center" }}>
              <input type="radio" name={`correct-${question.id}`} checked={question.correctAnswer === letter} onChange={() => onUpdate(index, { correctAnswer: letter })} />
              <strong style={{ width: 28, height: 28, borderRadius: 999, display: "grid", placeItems: "center", background: "#EFF6FF", color: DARK, border: "1px solid #BFDBFE" }}>{letter}</strong>
              <input value={option} onChange={(event) => onOption(index, optionIndex, event.target.value)} placeholder={`Option ${letter}`} style={quizInputStyle} />
              <Button variant="light" disabled={options.length <= 2} onClick={() => onRemoveOption(index, optionIndex)}>Remove</Button>
            </div>
          );
        })}
        <Button variant="light" disabled={options.length >= 6} onClick={() => onAddOption(index)}>Add Option</Button>
      </div>}
      {question.type === "trueFalse" && <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Correct answer<select value={question.correctAnswer || "true"} onChange={(event) => onUpdate(index, { correctAnswer: event.target.value })} style={quizInputStyle}><option value="true">True</option><option value="false">False</option></select></label>}
      {question.type === "shortAnswer" && <div style={{ display: "grid", gap: 8 }}>
        <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Expected answer<textarea value={question.correctAnswer || ""} onChange={(event) => onUpdate(index, { correctAnswer: event.target.value, expectedAnswer: event.target.value })} placeholder="Basic auto grading uses exact match, case-insensitive." style={quizTextareaStyle} /></label>
        <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Keywords<textarea value={question.keywords || ""} onChange={(event) => onUpdate(index, { keywords: event.target.value })} placeholder="Comma-separated keywords for teacher reference." style={quizTextareaStyle} /></label>
      </div>}
      {question.type === "fillBlank" && <label style={{ display: "grid", gap: 6, color: DARK, fontSize: 13, fontWeight: 900 }}>Expected answer<textarea value={question.correctAnswer || ""} onChange={(event) => onUpdate(index, { correctAnswer: event.target.value, expectedAnswer: event.target.value })} placeholder="Answer for the blank." style={quizTextareaStyle} /></label>}
      {question.type === "essay" && <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: DARK, borderRadius: 8, padding: 10, fontWeight: 900 }}>Manual marking required.</div>}
    </article>
  );
}

function QuizPreview({ quiz, onClose }) {
  return (
    <div style={{ border: `1px solid ${BLUE}`, background: LIGHT, borderRadius: 8, padding: 14, display: "grid", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div><strong style={{ color: DARK, fontSize: 18 }}>{quiz.title || "Untitled quiz"}</strong><div style={{ color: "#64748B", fontWeight: 800 }}>{quiz.className || "Selected class"} · {quiz.totalMarks || quizTotalMarks(quiz.questions || [])} marks · {quiz.timerMinutes || 0} minutes</div></div>
        <Button variant="light" onClick={onClose}>Close Preview</Button>
      </div>
      <p style={{ margin: 0, color: "#475569", whiteSpace: "pre-wrap" }}>{quiz.instructions || "No instructions."}</p>
      {quiz.fileUrl && <AttachmentLink item={quiz} label="Open File" />}
      {(quiz.questions || []).map((question, index) => <StudentQuizQuestion key={question.id || index} question={question} index={index} value="" onChange={() => {}} disabled />)}
    </div>
  );
}

function StudentQuizModule({ profile, quizzes, attempts }) {
  const [, refreshReadState] = useAlphaReadVersion();
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [activeTab, setActiveTab] = useState("Available");
  const unreadItems = quizzes.filter((item) => isUnreadForStudent(profile.uid, "quizzes", item));
  function markAllRead() {
    const latest = Math.max(Date.now(), ...quizzes.map(itemCreatedTime));
    setReadState(profile.uid, "quizzes", latest);
    refreshReadState();
  }
  if (activeQuiz) return <StudentQuizTaking quiz={activeQuiz} profile={profile} attempts={attempts.filter((item) => item.quizId === activeQuiz.id)} onBack={() => setActiveQuiz(null)} />;
  const completedQuizIds = new Set(attempts.filter((attempt) => ["submitted", "graded", "pending-marking"].includes(attempt.status)).map((attempt) => attempt.quizId));
  const inProgressAttempts = attempts.filter((attempt) => attempt.status === "in-progress");
  const visibleQuizzes = activeTab === "Completed" || activeTab === "Results"
    ? quizzes.filter((quiz) => completedQuizIds.has(quiz.id))
    : activeTab === "In Progress"
      ? quizzes.filter((quiz) => inProgressAttempts.some((attempt) => attempt.quizId === quiz.id))
      : quizzes.filter((quiz) => !completedQuizIds.has(quiz.id));
  return (
    <Panel title="Quizzes" action={unreadItems.length > 0 && <Button variant="light" onClick={markAllRead}>Mark all as read</Button>}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {["Available", "In Progress", "Completed", "Results"].map((tab) => <Button key={tab} variant={activeTab === tab ? "gold" : "light"} onClick={() => setActiveTab(tab)}>{tab}</Button>)}
      </div>
      {unreadItems.length > 0 && <NewUpdateNotice count={unreadItems.length} />}
      <div style={{ display: "grid", gap: 12 }}>
        {visibleQuizzes.length ? visibleQuizzes.map((quiz) => {
          const quizAttempts = attempts.filter((item) => item.quizId === quiz.id);
          const attemptsAllowed = Number(quiz.attemptsAllowed || 1);
          const remaining = Math.max(0, attemptsAllowed - quizAttempts.length);
          const latest = quizAttempts[0];
          const unread = isUnreadForStudent(profile.uid, "quizzes", quiz);
          return (
            <article key={quiz.id} style={{ border: `1px solid ${unread ? GOLD : "#E2E8F0"}`, borderRadius: 8, padding: 14, background: unread ? "#FFFBEB" : "#fff", boxShadow: unread ? "0 0 0 3px rgba(247,201,72,.14)" : "none", display: "grid", gap: 9 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <strong style={{ color: DARK, fontSize: 16 }}>{quiz.title}</strong>
                <StudentNewBadge unread={unread} status={quiz.status} />
              </div>
              <p style={{ margin: 0, color: "#64748B", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{quiz.instructions || "No instructions."}</p>
              <span style={{ color: "#64748B", fontSize: 12, fontWeight: 850 }}>{quiz.subject || "Subject"} · {quiz.teacherName || "Teacher"} · {quiz.className || "Class"} · Questions: {quiz.questions?.length || 0} · Duration: {quiz.timerMinutes || 0} min · Due: {quiz.dueDate || "No due date"} · Attempts: {quizAttempts.length}/{attemptsAllowed}</span>
              {(quiz.fileUrl || quiz.attachmentUrl) && <AttachmentLink item={quiz} label="Open quiz file" />}
              {latest && <span style={{ color: latest.status === "pending-marking" ? "#92400E" : BLUE, fontWeight: 900 }}>Latest: {latest.score}/{latest.totalMarks} ({Math.round(Number(latest.percentage || 0))}%) · {latest.grade || ""} · {latest.status}</span>}
              {latest?.feedback && <span style={{ color: DARK, whiteSpace: "pre-wrap" }}>Teacher feedback: {latest.feedback}</span>}
              {activeTab === "Available" && <Button disabled={remaining <= 0} onClick={() => setActiveQuiz(quiz)}>{remaining <= 0 ? "Attempts Used" : "Start Quiz"}</Button>}
            </article>
          );
        }) : <AlphaEmptyState title={`No ${activeTab.toLowerCase()} quizzes`} />}
      </div>
    </Panel>
  );
}

function StudentQuizTaking({ quiz, profile, attempts, onBack }) {
  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(Math.max(0, Number(quiz.timerMinutes || 0)) * 60);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submitLock = useRef(false);
  const attemptNumber = attempts.length + 1;
  const questions = quiz.randomizeQuestions ? [...(quiz.questions || [])].sort((a, b) => String(a.id).localeCompare(String(b.id))) : (quiz.questions || []);
  const currentQuestion = questions[currentIndex];
  const submitted = message.startsWith("✅");
  useEffect(() => {
    if (!timeLeft || submitted) return undefined;
    const timer = window.setInterval(() => setTimeLeft((current) => Math.max(0, current - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [timeLeft, submitted]);
  useEffect(() => {
    if (timeLeft === 0 && !submitted && !busy) submitQuiz(true);
  }, [timeLeft, submitted, busy]);
  function formatTimer(seconds) {
    const safe = Math.max(0, Number(seconds || 0));
    const minutes = Math.floor(safe / 60);
    const rest = safe % 60;
    return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
  }
  async function submitQuiz() {
    if (submitLock.current || busy || submitted) return;
    submitLock.current = true;
    setBusy(true);
    setMessage("⏳ Submitting quiz...");
    try {
      const graded = gradeQuiz(quiz, answers);
      const attemptId = `${quiz.id}_${profile.uid}_${attemptNumber}`;
      await setDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "quizAttempts", attemptId), {
        schoolId: SCHOOL_ID,
        quizId: quiz.id,
        quizTitle: quiz.title || "",
        classId: quiz.classId || "",
        className: quiz.className || "",
        studentUid: profile.uid,
        studentName: profile.fullName || profile.email || "Student",
        attemptNumber,
        score: graded.score,
        automaticScore: graded.score,
        totalMarks: graded.totalMarks,
        percentage: graded.percentage,
        grade: gradeFromPercentage(graded.percentage),
        status: graded.needsManual ? "pending-marking" : "submitted",
        manualMarkingPending: graded.needsManual,
        answers: graded.answers,
        submittedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: false });
      setMessage(graded.needsManual ? "✅ Quiz submitted. Essay answers are pending teacher marking." : "✅ Quiz submitted and graded.");
    } catch (error) {
      console.error("[AlphaClassroom] submit quiz failed", error);
      setMessage(`❌ Submit failed: ${error.message}`);
      submitLock.current = false;
      setBusy(false);
      return;
    }
  }
  return (
    <Panel title={quiz.title || "Quiz"}>
      {message && <div style={{ background: message.startsWith("✅") ? "#ECFDF5" : message.startsWith("❌") ? "#FEF2F2" : "#EFF6FF", border: `1px solid ${message.startsWith("✅") ? "#BBF7D0" : message.startsWith("❌") ? "#FECACA" : "#BFDBFE"}`, color: message.startsWith("❌") ? "#991B1B" : DARK, borderRadius: 8, padding: 12, fontWeight: 900 }}>{message}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <span style={{ color: "#64748B", fontWeight: 900 }}>{quiz.className} · Due: {quiz.dueDate || "No due date"} · Timer: {quiz.timerMinutes || 0} min · Total: {quiz.totalMarks || 0} marks · Attempt {attemptNumber}/{quiz.attemptsAllowed || 1}</span>
        <span style={{ color: timeLeft < 60 ? "#991B1B" : BLUE, fontWeight: 950, fontSize: 18 }}>{formatTimer(timeLeft)}</span>
        <Button variant="light" onClick={onBack}>Back to Quizzes</Button>
      </div>
      <p style={{ margin: 0, color: "#475569", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{quiz.instructions || "Answer all questions."}</p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {questions.map((question, index) => {
          const isAnswered = answers[question.id] !== undefined && String(answers[question.id] || "").trim() !== "";
          return <button key={question.id || index} type="button" onClick={() => setCurrentIndex(index)} disabled={busy || submitted} style={{ minWidth: 42, minHeight: 42, borderRadius: 8, border: `1px solid ${currentIndex === index ? GOLD : isAnswered ? "#86EFAC" : "#CBD5E1"}`, background: currentIndex === index ? GOLD : isAnswered ? "#DCFCE7" : "#F8FAFC", color: DARK, fontWeight: 950, cursor: busy || submitted ? "not-allowed" : "pointer" }}>{index + 1}</button>;
        })}
      </div>
      <div style={{ display: "grid", gap: 12 }}>
        {currentQuestion && <StudentQuizQuestion key={currentQuestion.id || currentIndex} question={currentQuestion} index={currentIndex} value={answers[currentQuestion.id] || ""} onChange={(value) => setAnswers((current) => ({ ...current, [currentQuestion.id]: value }))} disabled={busy || submitted || timeLeft === 0} />}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Button variant="light" disabled={currentIndex <= 0 || busy || submitted} onClick={() => setCurrentIndex((index) => Math.max(0, index - 1))}>Previous</Button>
        <Button variant="light" disabled={currentIndex >= questions.length - 1 || busy || submitted} onClick={() => setCurrentIndex((index) => Math.min(questions.length - 1, index + 1))}>Next</Button>
        <Button variant="gold" busy={busy && !submitted} disabled={busy || submitted} onClick={() => submitQuiz(false)}>Submit Quiz</Button>
      </div>
    </Panel>
  );
}

function StudentQuizQuestion({ question, index, value, onChange, disabled = false }) {
  return (
    <article style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: 12, background: "#fff", display: "grid", gap: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
        <strong style={{ color: DARK }}>Question {index + 1}</strong>
        <span style={{ color: BLUE, fontWeight: 950 }}>{question.marks || 0} marks</span>
      </div>
      <p style={{ margin: 0, color: "#111827", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{question.question}</p>
      {question.type === "multipleChoice" && <div style={{ display: "grid", gap: 8 }}>{(question.options || []).map((option, optionIndex) => {
        const letter = String.fromCharCode(65 + optionIndex);
        return <label key={`${letter}-${option}`} style={{ display: "flex", gap: 8, alignItems: "center", color: DARK, fontWeight: 800 }}><input disabled={disabled} type="radio" name={`student-${question.id}`} checked={value === letter} onChange={() => onChange(letter)} /><b>{letter}.</b> {option}</label>;
      })}</div>}
      {question.type === "trueFalse" && <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>{["true", "false"].map((option) => <label key={option} style={{ display: "flex", gap: 8, alignItems: "center", color: DARK, fontWeight: 800 }}><input disabled={disabled} type="radio" name={`student-${question.id}`} checked={value === option} onChange={() => onChange(option)} />{option === "true" ? "True" : "False"}</label>)}</div>}
      {question.type === "shortAnswer" && <input disabled={disabled} value={value} onChange={(event) => onChange(event.target.value)} placeholder="Short answer" style={quizInputStyle} />}
      {question.type === "fillBlank" && <input disabled={disabled} value={value} onChange={(event) => onChange(event.target.value)} placeholder="Fill in the blank" style={quizInputStyle} />}
      {question.type === "essay" && <textarea disabled={disabled} value={value} onChange={(event) => onChange(event.target.value)} placeholder="Write your essay answer..." style={{ ...quizTextareaStyle, minHeight: 160 }} />}
    </article>
  );
}

function gradeQuiz(quiz, rawAnswers) {
  let score = 0;
  let needsManual = false;
  const totalMarks = Number(quiz.totalMarks || quizTotalMarks(quiz.questions || []));
  const answers = {};
  (quiz.questions || []).forEach((question) => {
    const value = rawAnswers[question.id] ?? "";
    const marks = Number(question.marks || 0);
    let awarded = 0;
    let status = "auto-graded";
    if (question.type === "multipleChoice" && String(value).toUpperCase() === String(question.correctAnswer).toUpperCase()) awarded = marks;
    if (question.type === "trueFalse" && String(value).toLowerCase() === String(question.correctAnswer).toLowerCase()) awarded = marks;
    if (question.type === "shortAnswer" && String(value).trim().toLowerCase() === String(question.correctAnswer || "").trim().toLowerCase()) awarded = marks;
    if (question.type === "fillBlank" && String(value).trim().toLowerCase() === String(question.correctAnswer || question.expectedAnswer || "").trim().toLowerCase()) awarded = marks;
    if (question.type === "essay") {
      needsManual = true;
      status = "pending-marking";
    }
    score += awarded;
    answers[question.id] = {
      questionId: question.id,
      type: question.type,
      question: question.question,
      answer: value,
      correctAnswer: question.type === "essay" ? "" : question.correctAnswer,
      marks,
      awarded,
      marksEarned: awarded,
      status,
    };
  });
  return { score, totalMarks, percentage: totalMarks ? (score / totalMarks) * 100 : 0, needsManual, answers };
}

function TenantCollectionModule({ collectionName, title, profile, fields = ["title", "value"] }) {
  const data = useTenantCollection(collectionName);
  async function submit(event) {
    event.preventDefault();
    const formEl = event.currentTarget;
    await addDoc(collection(getFirebaseDb(), "schools", SCHOOL_ID, collectionName), {
      ...Object.fromEntries(new FormData(formEl).entries()),
      schoolId: SCHOOL_ID,
      createdBy: profile.uid,
      createdAt: serverTimestamp(),
    });
    if (formEl) formEl.reset();
  }
  return (
    <Panel title={title}>
      <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 12 }}>
        {fields.map((field) => <RawInput key={field} name={field} label={field.replace(/([A-Z])/g, " $1")} />)}
        <div style={{ alignSelf: "end" }}><Button type="submit" variant="gold">Save</Button></div>
      </form>
      <Table rows={data.items} columns={[...fields.map((field) => ({ key: field, label: field })), { key: "createdAt", label: "Created", render: (row) => formatDate(row.createdAt) }]} />
    </Panel>
  );
}

function StudentAssignments({ profile, assignments, submissions }) {
  const [, refreshReadState] = useAlphaReadVersion();
  const unreadItems = assignments.filter((item) => isUnreadForStudent(profile.uid, "assignments", item));
  function markAllRead() {
    const latest = Math.max(Date.now(), ...assignments.map(itemCreatedTime));
    setReadState(profile.uid, "assignments", latest);
    refreshReadState();
  }
  async function submitAssignment(assignment, text, file, existingSubmission = null) {
    const fileMeta = file
      ? await uploadAlphaLearningFile(file, { classId: assignment.classId, collectionName: "submissions", userUid: profile.uid, prefix: `${profile.uid}-` })
      : {};
    const dueTime = assignment.dueDate ? new Date(`${assignment.dueDate}T23:59:59`).getTime() : 0;
    const isLate = Boolean(dueTime && Date.now() > dueTime);
    const payload = {
      assignmentId: assignment.id,
      assignmentTitle: assignment.title,
      classId: assignment.classId || "",
      className: assignment.className || "",
      studentUid: profile.uid,
      studentName: profile.fullName || profile.email,
      studentId: profile.studentId || "",
      answer: text,
      answerText: text,
      ...fileMeta,
      isLate,
      status: isLate ? "late" : "submitted",
      attemptCount: (Number(existingSubmission?.attemptCount) || 0) + 1,
      schoolId: SCHOOL_ID,
      submittedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    if (existingSubmission?.id) {
      await updateDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "assignmentSubmissions", existingSubmission.id), payload);
    } else {
      await addDoc(collection(getFirebaseDb(), "schools", SCHOOL_ID, "assignmentSubmissions"), {
        ...payload,
        createdAt: serverTimestamp(),
      });
    }
    await createAlphaNotification({
      type: "assignment_submitted",
      title: "Assignment Submitted",
      message: assignment.title || "Assignment submitted",
      classId: assignment.classId,
      className: assignment.className || "",
      itemId: assignment.id,
      module: "Gradebook",
      actionUrl: `/portal/teacher`,
      studentUid: profile.uid,
      actorUid: profile.uid,
    });
  }
  return (
    <Panel title="Assignments" action={unreadItems.length > 0 && <Button variant="light" onClick={markAllRead}>Mark all as read</Button>}>
      {unreadItems.length > 0 && <NewUpdateNotice count={unreadItems.length} />}
      {assignments.length ? assignments.map((assignment) => <StudentAssignmentRow key={assignment.id} assignment={assignment} unread={isUnreadForStudent(profile.uid, "assignments", assignment)} submission={submissions.find((item) => item.assignmentId === assignment.id && item.studentUid === profile.uid)} onSubmit={submitAssignment} />) : <AlphaEmptyState title="No assignments" />}
    </Panel>
  );
}

function StudentAssignmentRow({ assignment, submission, onSubmit, unread = false }) {
  const [answer, setAnswer] = useState("");
  const [file, setFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const maxAttempts = Math.max(1, Number(assignment.maxAttempts || 1));
  const attemptsUsed = Number(submission?.attemptCount || (submission ? 1 : 0));
  const canResubmit = Boolean(submission && assignment.allowLateSubmission && attemptsUsed < maxAttempts);
  useEffect(() => {
    if (!file) {
      setFilePreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  async function submit() {
    if (busy || (!answer.trim() && !file)) return;
    if (assignment.requireAttachment && !file && !submission?.fileUrl) {
      setMessage("❌ This assignment requires a file attachment.");
      return;
    }
    setBusy(true);
    setMessage("⏳ Submitting assignment...");
    try {
      await onSubmit(assignment, answer, file, submission);
      setAnswer("");
      setFile(null);
      setMessage("✅ Assignment submitted.");
    } catch (error) {
      setMessage(`❌ Submit failed: ${readableFirebaseError(error)}`);
    } finally {
      setBusy(false);
    }
  }
  return (
    <article style={{ border: `1px solid ${unread ? GOLD : "#E2E8F0"}`, borderRadius: 8, padding: 12, display: "grid", gap: 8, background: unread ? "#FFFBEB" : "#fff", boxShadow: unread ? "0 0 0 3px rgba(247,201,72,.14)" : "none" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <strong style={{ color: DARK }}>{assignment.title}</strong>
        <StudentNewBadge unread={unread} status={assignment.status} />
      </div>
      <span style={{ color: "#64748B", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{assignment.instructions || assignment.body}</span>
      <span style={{ color: "#64748B", fontSize: 12, fontWeight: 800 }}>{assignment.className || "Class"} · Due: {assignment.dueDate || "No due date"} · {assignment.maxMarks ? `${assignment.maxMarks} marks` : "Marks not set"}</span>
      {(assignment.fileUrl || assignment.attachmentUrl) && <AttachmentLink item={assignment} label="Open assignment file" />}
      {submission && (
        <div style={{ border: "1px solid #D8E2F0", borderRadius: 8, padding: 10, background: "#F8FAFC", display: "grid", gap: 6 }}>
          <strong style={{ color: BLUE }}>Submission status: {submission.status || "submitted"}</strong>
          <span style={{ color: DARK, fontWeight: 850 }}>Score/Grade: {submission.score ?? submission.grade ?? "Pending"}{assignment.maxMarks ? ` / ${assignment.maxMarks}` : ""}</span>
          {submission.feedback && <span style={{ color: DARK, whiteSpace: "pre-wrap" }}>Teacher feedback: {submission.feedback}</span>}
          {(submission.answerText || submission.answer) && <span style={{ color: "#64748B", whiteSpace: "pre-wrap" }}>Your answer: {submission.answerText || submission.answer}</span>}
          {(submission.fileUrl || submission.attachmentUrl) && <AttachmentLink item={submission} label="Submitted file" />}
          <span style={{ color: "#64748B", fontSize: 12, fontWeight: 800 }}>Attempts: {attemptsUsed} / {maxAttempts}</span>
        </div>
      )}
      {message && <div style={{ border: `1px solid ${message.startsWith("❌") ? "#FECACA" : message.startsWith("✅") ? "#BBF7D0" : "#BFDBFE"}`, background: message.startsWith("❌") ? "#FEF2F2" : message.startsWith("✅") ? "#ECFDF5" : "#EFF6FF", color: message.startsWith("❌") ? "#991B1B" : DARK, borderRadius: 8, padding: 10, fontWeight: 900 }}>{message}</div>}
      {(!submission || canResubmit) ? <div style={{ display: "grid", gap: 8 }}>
        <textarea value={answer} onChange={(event) => setAnswer(event.target.value)} placeholder="Write your answer..." style={{ minHeight: 100, border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", resize: "vertical" }} />
        <label style={{ display: "grid", gap: 6, color: DARK, fontWeight: 900 }}><span>Upload Answer</span><span style={{ display: "inline-flex", alignItems: "center", width: "fit-content", minHeight: 42, borderRadius: 8, padding: "9px 14px", background: BLUE, color: "#fff", cursor: "pointer" }}>📎 Upload answer file<input type="file" onChange={(event) => setFile(event.target.files?.[0] || null)} style={{ position: "absolute", width: 1, height: 1, opacity: 0 }} /></span></label>
        {file && (
          <div style={{ border: "1px solid #D8E2F0", borderRadius: 8, padding: 10, background: "#F8FAFC", display: "grid", gap: 8 }}>
            <span style={{ color: DARK, fontWeight: 900, overflowWrap: "anywhere" }}>{file.name} · {file.type || "unknown type"} · {formatFileSize(file.size)}</span>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {filePreviewUrl && <a href={filePreviewUrl} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, fontWeight: 950 }}>Preview selected file</a>}
              <Button variant="light" disabled={busy} onClick={() => setFile(null)}>Remove selected file</Button>
            </div>
            {filePreviewUrl && file.type?.startsWith("image/") && <img src={filePreviewUrl} alt={file.name} style={{ maxWidth: 240, maxHeight: 160, objectFit: "contain", border: "1px solid #E2E8F0", borderRadius: 8, background: "#fff" }} />}
          </div>
        )}
        <Button onClick={submit} disabled={busy || (!answer.trim() && !file)}>{busy ? "Submitting..." : submission ? "Resubmit Assignment" : "Submit Assignment"}</Button>
      </div> : <span style={{ color: "#64748B", fontWeight: 850 }}>Resubmission is closed for this assignment.</span>}
    </article>
  );
}

function StudentAttendance({ profile, sessions }) {
  const [, refreshReadState] = useAlphaReadVersion();
  const [message, setMessage] = useState("");
  const [markingId, setMarkingId] = useState("");
  const [locallyMarkedIds, setLocallyMarkedIds] = useState(() => new Set());
  const unreadItems = sessions.filter((item) => isUnreadForStudent(profile.uid, "attendance", item));
  function markAllRead() {
    const latest = Math.max(Date.now(), ...sessions.map(itemCreatedTime));
    setReadState(profile.uid, "attendance", latest);
    refreshReadState();
  }
  async function mark(session) {
    if (locallyMarkedIds.has(session.id) || session?.marks?.[profile.uid] || (session.presentUids || []).includes(profile.uid)) {
      setMessage("✅ Attendance was already marked once. This session is closed for you.");
      return;
    }
    if (markingId) return;
    if (sessionState(session) !== "Live now") {
      setMessage("❌ This attendance session is not open.");
      return;
    }
    const code = window.prompt("Enter the unique attendance code from your teacher:", "");
    if (code === null) return;
    if (!code.trim()) return setMessage("❌ Enter the attendance session code.");
    if (!navigator.geolocation) return setMessage("❌ This device does not support GPS location.");
    setMarkingId(session.id);
    setMessage("📍 Checking your classroom location...");
    try {
      const position = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }));
      const token = await getFirebaseAuth().currentUser?.getIdToken();
      if (!token) throw new Error("Sign in again before marking attendance.");
      const response = await fetch(`${ALPHA_API_BASE}/api/alpha/attendance/mark`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sessionId: session.id, sessionCode: code.trim(), latitude: position.coords.latitude, longitude: position.coords.longitude, accuracyMeters: position.coords.accuracy }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || "Attendance could not be marked.");
      setLocallyMarkedIds((current) => new Set([...current, session.id]));
      setReadState(profile.uid, "attendance", Math.max(Date.now(), itemCreatedTime(session)));
      refreshReadState();
      setMessage(result.duplicate
        ? "✅ Attendance was already recorded. This session is closed for you."
        : `✅ Attendance marked once. GPS confirmed you are ${Math.round(result.distanceMeters || 0)}m from the classroom point. This session is now closed for you.`);
    } catch (error) {
      console.error("[AlphaClassroom] student attendance mark failed", { module: "Attendance", operation: "student-mark", collection: "attendance", documentId: session.id, userUid: profile.uid, role: profile.role, schoolId: SCHOOL_ID, code: error.code, message: error.message });
      const denied = error?.code === 1;
      setMessage(`❌ ${denied ? "Location permission is required. Allow GPS and try again." : (error.message || readableFirebaseError(error))}`);
    } finally {
      setMarkingId("");
    }
  }
  function sessionState(session) {
    const created = itemCreatedTime(session);
    const duration = Number(session.durationMinutes || 0);
    const expired = session.status === "open" && duration > 0 && created && Date.now() > created + duration * 60 * 1000;
    if (expired) return "Expired";
    if (session.status === "open") return "Live now";
    return "Closed";
  }
  return (
    <Panel title="Attendance" action={unreadItems.length > 0 && <Button variant="light" onClick={markAllRead}>Mark all as read</Button>}>
      {message && <div style={{ background: message.startsWith("✅") ? "#ECFDF5" : "#FEF2F2", border: `1px solid ${message.startsWith("✅") ? "#BBF7D0" : "#FECACA"}`, color: message.startsWith("✅") ? "#065F46" : "#991B1B", borderRadius: 8, padding: 12, fontWeight: 900 }}>{message}</div>}
      {unreadItems.length > 0 && <NewUpdateNotice count={unreadItems.length} />}
      <div style={{ display: "grid", gap: 12 }}>
        {sessions.length ? sessions.map((session) => {
          const state = sessionState(session);
          const alreadyMarked = Boolean(locallyMarkedIds.has(session.id) || session?.marks?.[profile.uid] || (session.presentUids || []).includes(profile.uid));
          const live = state === "Live now";
          const unread = isUnreadForStudent(profile.uid, "attendance", session);
          return (
            <article key={session.id} style={{ border: `1px solid ${live ? "#38BDF8" : unread ? GOLD : "#E2E8F0"}`, borderRadius: 8, padding: 14, display: "grid", gap: 10, background: live ? "#EFF6FF" : unread ? "#FFFBEB" : state === "Closed" ? "#F8FAFC" : "#fff", opacity: state === "Closed" ? 0.72 : 1, boxShadow: live ? "0 0 0 3px rgba(14,165,233,.16), 0 0 26px rgba(34,197,94,.18)" : unread ? "0 0 0 3px rgba(247,201,72,.14)" : "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <strong style={{ color: DARK }}>{session.className || "Class attendance"}</strong>
                <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {live && <span style={{ background: "#DBEAFE", color: BLUE, border: "1px solid #93C5FD", borderRadius: 999, padding: "3px 8px", fontSize: 11, fontWeight: 950 }}>Live now</span>}
                  {state === "Expired" && <span style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A", borderRadius: 999, padding: "3px 8px", fontSize: 11, fontWeight: 950 }}>Expired</span>}
                  {state === "Closed" && <span style={{ background: "#E2E8F0", color: "#475569", borderRadius: 999, padding: "3px 8px", fontSize: 11, fontWeight: 950 }}>Closed</span>}
                  {alreadyMarked && <span style={{ background: "#DCFCE7", color: "#166534", border: "1px solid #86EFAC", borderRadius: 999, padding: "3px 8px", fontSize: 11, fontWeight: 950 }}>✓ Marked · Closed for you</span>}
                  <StudentNewBadge unread={unread} status={session.status} />
                </span>
              </div>
              <span style={{ color: "#64748B", fontWeight: 800 }}>{session.subject || "Attendance"} · Code {session.code || "N/A"} · {formatDate(session.createdAt || session.openedAt)}</span>
              <Button disabled={!live || alreadyMarked || markingId === session.id} onClick={() => mark(session)}>{alreadyMarked ? "Attendance complete — marked once" : state === "Expired" ? "Expired" : markingId === session.id ? "Detecting GPS & marking..." : "Enter Code · GPS Marks Automatically"}</Button>
            </article>
          );
        }) : <AlphaEmptyState title="No attendance sessions" />}
      </div>
    </Panel>
  );
}

function NewUpdateNotice({ count }) {
  return (
    <div style={{ background: "#FFFBEB", border: `1px solid ${GOLD}`, color: "#92400E", borderRadius: 8, padding: 12, fontWeight: 950 }}>
      New update{count === 1 ? "" : "s"} available
    </div>
  );
}

function StudentNewBadge({ unread, status }) {
  if (unread) return <span style={{ display: "inline-block", background: GOLD, color: DARK, borderRadius: 999, padding: "3px 8px", fontSize: 11, fontWeight: 950 }}>NEW</span>;
  return <span style={{ display: "inline-block", background: "#E2E8F0", color: "#475569", borderRadius: 999, padding: "3px 8px", fontSize: 11, fontWeight: 900 }}>{status || "Read"}</span>;
}

function StudentReadOnly({ title, items, columns, profile, collectionName }) {
  const [, refreshReadState] = useAlphaReadVersion();
  const readable = Boolean(profile?.uid && collectionName);
  const unreadItems = readable ? items.filter((item) => isUnreadForStudent(profile.uid, collectionName, item)) : [];
  function markAllRead() {
    const latest = Math.max(Date.now(), ...items.map(itemCreatedTime));
    setReadState(profile.uid, collectionName, latest);
    refreshReadState();
  }
  const finalColumns = readable && collectionName !== "announcements"
    ? [...columns, { key: "newStatus", label: "Status/New", render: (row) => <StudentNewBadge unread={isUnreadForStudent(profile.uid, collectionName, row)} status={row.status} /> }]
    : columns;
  if (readable && collectionName !== "announcements") {
    return (
      <Panel title={title} action={unreadItems.length > 0 && <Button variant="light" onClick={markAllRead}>Mark all as read</Button>}>
        {unreadItems.length > 0 && <NewUpdateNotice count={unreadItems.length} />}
        <div style={{ display: "grid", gap: 12 }}>
          {items.length ? items.map((item) => {
            const unread = isUnreadForStudent(profile.uid, collectionName, item);
            const date = item.dueDate || item.lessonDate || item.resourceDate || formatDate(item.createdAt);
            return (
              <article key={item.id} style={{ border: `1px solid ${unread ? GOLD : "#E2E8F0"}`, borderRadius: 8, padding: 14, background: unread ? "#FFFBEB" : "#fff", boxShadow: unread ? "0 0 0 3px rgba(247,201,72,.14)" : "none", display: "grid", gap: 9 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <strong style={{ color: DARK, fontSize: 16 }}>{item.title}</strong>
                  <StudentNewBadge unread={unread} status={item.status} />
                </div>
                <p style={{ margin: 0, color: "#64748B", whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.55 }}>{item.body || item.description || item.instructions || item.url || "No details added."}</p>
                <span style={{ color: "#64748B", fontSize: 12, fontWeight: 850 }}>{item.className || "Class"} · {item.subject || "Subject"} · {date}</span>
                {item.topic && <span style={{ color: BLUE, fontSize: 12, fontWeight: 900 }}>Topic: {item.topic}</span>}
                {item.resourceType && <span style={{ color: BLUE, fontSize: 12, fontWeight: 900 }}>Type: {item.resourceType}</span>}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {item.url && <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ background: BLUE, color: "#fff", borderRadius: 8, padding: "9px 12px", fontWeight: 950, textDecoration: "none" }}>Open Link</a>}
                  {(item.fileUrl || item.attachmentUrl) && <a href={item.fileUrl || item.attachmentUrl} target="_blank" rel="noopener noreferrer" onClick={() => { setReadState(profile.uid, collectionName, Math.max(itemCreatedTime(item), getReadState(profile.uid, collectionName))); refreshReadState(); }} style={{ background: "#fff", color: DARK, border: "1px solid #D8E2F0", borderRadius: 8, padding: "9px 12px", fontWeight: 950, textDecoration: "none" }}>Preview / Download</a>}
                  {collectionName === "quizzes" && <button disabled style={{ background: "#E2E8F0", color: "#475569", border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 12px", fontWeight: 950, cursor: "not-allowed" }}>Start Quiz - coming soon</button>}
                </div>
                {collectionName === "quizzes" && <span style={{ color: "#92400E", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, padding: 10, fontWeight: 900 }}>Question builder coming soon. Quiz details are visible for planning.</span>}
              </article>
            );
          }) : <AlphaEmptyState title={`No ${title.toLowerCase()} found`} />}
        </div>
      </Panel>
    );
  }
  return (
    <Panel title={title} action={unreadItems.length > 0 && <Button variant="light" onClick={markAllRead}>Mark all as read</Button>}>
      {unreadItems.length > 0 && <NewUpdateNotice count={unreadItems.length} />}
      <Table
        rows={items}
        columns={finalColumns}
        empty={`No ${title.toLowerCase()} found`}
        rowStyle={(row) => readable && isUnreadForStudent(profile.uid, collectionName, row) ? { background: "#FFFBEB", boxShadow: "inset 4px 0 0 #F7C948" } : {}}
      />
    </Panel>
  );
}

function StudentAnalytics({ profile, collections }) {
  const submissions = (collections.submissions?.items || []).filter((item) => item.studentUid === profile.uid);
  const attempts = (collections.quizAttempts?.items || []).filter((item) => item.studentUid === profile.uid);
  const assignments = (collections.assignments?.items || []).filter((item) => !["draft", "archived"].includes(item.status));
  const quizzes = (collections.quizzes?.items || []).filter((item) => item.status === "published" || item.status === "closed");
  const sessions = collections.attendance?.items || [];
  const percent = (value) => Math.max(0, Math.min(100, Math.round(Number(value || 0))));
  const present = sessions.filter((session) => session.presentUids?.includes(profile.uid) || session.marks?.[profile.uid]?.status === "present").length;
  const attendance = sessions.length ? percent((present / sessions.length) * 100) : 0;
  const assignmentScores = submissions.map((row) => {
    const assignment = assignments.find((item) => item.id === row.assignmentId);
    const score = Number(row.score ?? row.grade);
    const maximum = Number(row.maxMarks || assignment?.maxMarks || 0);
    return Number.isFinite(score) ? percent(maximum > 0 && score <= maximum ? score / maximum * 100 : score) : null;
  }).filter(Number.isFinite);
  const quizScores = attempts.filter((row) => row.status !== "in-progress").map((row) => Number(row.percentage)).filter(Number.isFinite);
  const assignmentAverage = assignmentScores.length ? percent(assignmentScores.reduce((a, b) => a + b, 0) / assignmentScores.length) : 0;
  const quizAverage = quizScores.length ? percent(quizScores.reduce((a, b) => a + b, 0) / quizScores.length) : 0;
  const assignmentCompletion = assignments.length ? percent(submissions.length / assignments.length * 100) : 0;
  const quizCompletion = quizzes.length ? percent(attempts.filter((row) => row.status !== "in-progress").length / quizzes.length * 100) : 0;
  const development = percent((attendance + assignmentAverage + quizAverage + assignmentCompletion + quizCompletion) / 5);
  const metrics = [["Attendance", attendance, BLUE], ["Assignment average", assignmentAverage, GOLD], ["Quiz average", quizAverage, "#16A34A"], ["Assignment completion", assignmentCompletion, "#7C3AED"], ["Quiz completion", quizCompletion, "#0EA5E9"]];
  const strong = metrics.filter(([, value]) => value >= 70).map(([name]) => name);
  const improve = metrics.filter(([, value]) => value < 50).map(([name]) => name);
  const history = [
    ...submissions.map((row) => ({ title: row.assignmentTitle || "Assignment", type: "Assignment", score: row.grade ?? row.score ?? "Pending", feedback: row.feedback || "No feedback yet", date: row.gradedAt || row.updatedAt || row.createdAt })),
    ...attempts.map((row) => ({ title: row.quizTitle || "Quiz", type: "Quiz", score: ["pending-manual", "pending-marking"].includes(row.status) ? `Automatic: ${row.automaticScore ?? row.score ?? 0}/${row.totalMarks || 0} · essay marks pending` : `${Math.round(Number(row.percentage || 0))}%`, feedback: row.feedback || "No feedback yet", date: row.gradedAt || row.submittedAt || row.updatedAt })),
  ].sort((a, b) => itemCreatedTime(b) - itemCreatedTime(a));
  return <Panel title="My Development Analytics"><StatGrid stats={[{ label: "Attendance", value: `${attendance}%` }, { label: "Assignment Average", value: `${assignmentAverage}%` }, { label: "Quiz Average", value: `${quizAverage}%` }, { label: "Assignment Completion", value: `${assignmentCompletion}%` }, { label: "Quiz Completion", value: `${quizCompletion}%` }, { label: "Development Score", value: `${development}%`, glow: development >= 70 }]} /><div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 14 }}><section style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 16, display: "grid", gap: 13 }}><h3 style={{ margin: 0, color: DARK }}>Performance</h3>{metrics.map(([name, value, color]) => <div key={name}><div style={{ display: "flex", justifyContent: "space-between", color: DARK, fontWeight: 900 }}><span>{name}</span><span>{value}%</span></div><div style={{ height: 12, marginTop: 6, borderRadius: 999, background: "#E2E8F0", overflow: "hidden" }}><div style={{ width: `${value}%`, height: "100%", background: color }} /></div></div>)}</section><section style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 16, display: "grid", placeItems: "center", gap: 12 }}><h3 style={{ margin: 0, color: DARK }}>Overall Development</h3><div style={{ width: 180, aspectRatio: "1", borderRadius: "50%", background: `conic-gradient(${BLUE} 0 ${development}%,#E2E8F0 ${development}% 100%)`, display: "grid", placeItems: "center" }}><div style={{ width: 118, aspectRatio: "1", borderRadius: "50%", background: "#fff", display: "grid", placeItems: "center", color: DARK, fontSize: 28, fontWeight: 950 }}>{development}%</div></div><p style={{ margin: 0, color: "#64748B", textAlign: "center" }}><strong>Strong:</strong> {strong.join(", ") || "Keep developing"}<br /><strong>Improve:</strong> {improve.join(", ") || "No weak area detected"}</p></section></div><Panel title="Recent Grades & Teacher Feedback"><Table rows={history} empty="No graded work yet" columns={[{ key: "title", label: "Work" }, { key: "type", label: "Type" }, { key: "score", label: "Result" }, { key: "feedback", label: "Teacher Feedback", wrap: true }, { key: "date", label: "Date", render: (row) => formatDate(row.date) }]} /></Panel></Panel>;
}

function Gradebook({ submissions, quizAttempts = [], quizzes = [] }) {
  async function grade(row) {
    const gradeValue = window.prompt("Grade", row.grade || "");
    if (gradeValue === null) return;
    await updateDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "assignmentSubmissions", row.id), {
      grade: gradeValue,
      status: "graded",
      gradedAt: serverTimestamp(),
    });
  }
  async function markEssayAttempt(row) {
    const quiz = quizzes.find((item) => item.id === row.quizId);
    const essayQuestions = (quiz?.questions || []).filter((item) => item.type === "essay");
    const answers = { ...(row.answers || {}) };
    let score = Number(row.score || 0);
    for (const question of essayQuestions) {
      const current = answers[question.id] || {};
      const raw = window.prompt(`Marks for essay: ${question.question}\nMax: ${question.marks}`, current.awarded ?? "");
      if (raw === null) return;
      const awarded = Math.min(Number(question.marks || 0), Math.max(0, Number(raw || 0)));
      const previous = Number(current.awarded || 0);
      score = score - previous + awarded;
      answers[question.id] = { ...current, questionId: question.id, type: "essay", question: question.question, answer: current.answer || "", marks: Number(question.marks || 0), awarded, status: "manual-marked" };
    }
    const feedback = window.prompt("Feedback for student", row.feedback || "");
    const totalMarks = Number(row.totalMarks || quiz?.totalMarks || 0);
    await updateDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "quizAttempts", row.id), {
      answers,
      score,
      totalMarks,
      percentage: totalMarks ? (score / totalMarks) * 100 : 0,
      feedback: feedback || "",
      status: "submitted",
      manualMarkingPending: false,
      gradedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Panel title="Quiz Attempts">
        <Table rows={quizAttempts} empty="No quiz attempts yet" columns={[
          { key: "studentName", label: "Student" },
          { key: "quizTitle", label: "Quiz" },
          { key: "className", label: "Class" },
          { key: "score", label: "Score", render: (row) => `${row.score || 0}/${row.totalMarks || 0}` },
          { key: "percentage", label: "Percent", render: (row) => `${Math.round(Number(row.percentage || 0))}%` },
          { key: "status", label: "Status" },
          { key: "feedback", label: "Feedback", wrap: true },
          { key: "actions", label: "Actions", render: (row) => <Button onClick={() => markEssayAttempt(row)}>Mark Essay / Feedback</Button> },
        ]} />
      </Panel>
      <Panel title="Assignment Submissions">
        <Table rows={submissions} empty="No assignment submissions yet" columns={[{ key: "studentName", label: "Student" }, { key: "assignmentTitle", label: "Assignment" }, { key: "answer", label: "Submission" }, { key: "grade", label: "Grade" }, { key: "actions", label: "Actions", render: (row) => <Button onClick={() => grade(row)}>Grade</Button> }]} />
      </Panel>
    </div>
  );
}

function Reports({ collections }) {
  const classes = collections.classes?.items || [];
  const [classFilter, setClassFilter] = useState("");
  const allUsers = collections.users?.items || [];
  const students = allUsers.filter((item) => normalizeRole(item.role) === "student" && !["suspended", "duplicate"].includes(item.status) && (!classFilter || item.classId === classFilter || (item.classIds || []).includes(classFilter)));
  const assignments = (collections.assignments?.items || []).filter((item) => !classFilter || item.classId === classFilter);
  const assignmentIds = new Set(assignments.map((item) => item.id));
  const submissions = (collections.submissions?.items || []).filter((item) => (!classFilter || item.classId === classFilter) && (!assignmentIds.size || assignmentIds.has(item.assignmentId)));
  const quizzes = (collections.quizzes?.items || []).filter((item) => !classFilter || item.classId === classFilter);
  const quizIds = new Set(quizzes.map((item) => item.id));
  const attempts = (collections.quizAttempts?.items || []).filter((item) => (!classFilter || item.classId === classFilter) && (!quizIds.size || quizIds.has(item.quizId)));
  const sessions = (collections.attendance?.items || []).filter((item) => !classFilter || item.classId === classFilter);
  const percent = (value) => Math.max(0, Math.min(100, Math.round(Number(value || 0))));
  const assignmentCompletion = students.length && assignments.length ? percent((submissions.length / (students.length * assignments.length)) * 100) : 0;
  const quizCompletion = students.length && quizzes.length ? percent((attempts.length / (students.length * quizzes.length)) * 100) : 0;
  const attendancePossible = students.length * sessions.length;
  const attendancePresent = sessions.reduce((sum, session) => sum + students.filter((student) => session.presentUids?.includes(student.uid || student.id) || session.marks?.[student.uid || student.id]?.status === "present").length, 0);
  const attendanceRate = attendancePossible ? percent((attendancePresent / attendancePossible) * 100) : 0;
  const assignmentPercentages = submissions.map((row) => {
    const assignment = assignments.find((item) => item.id === row.assignmentId);
    const score = Number(row.score ?? row.grade);
    const max = Number(row.maxMarks || assignment?.maxMarks || 0);
    if (!Number.isFinite(score)) return null;
    return percent(max > 0 && score <= max ? (score / max) * 100 : score);
  }).filter((value) => Number.isFinite(value));
  const assignmentAverage = assignmentPercentages.length ? percent(assignmentPercentages.reduce((sum, value) => sum + value, 0) / assignmentPercentages.length) : 0;
  const quizScores = attempts.map((item) => Number(item.percentage)).filter(Number.isFinite);
  const quizAverage = quizScores.length ? percent(quizScores.reduce((sum, value) => sum + value, 0) / quizScores.length) : 0;
  const studentRows = students.map((student) => {
    const uid = student.uid || student.id;
    const ownSubmissions = submissions.filter((item) => item.studentUid === uid);
    const ownAttempts = attempts.filter((item) => item.studentUid === uid);
    const ownSessions = sessions.filter((session) => !session.classId || session.classId === student.classId || (student.classIds || []).includes(session.classId));
    const present = ownSessions.filter((session) => session.presentUids?.includes(uid) || session.marks?.[uid]?.status === "present").length;
    const ownAssignmentScores = ownSubmissions.map((row) => {
      const assignment = assignments.find((item) => item.id === row.assignmentId);
      const score = Number(row.score ?? row.grade);
      const max = Number(row.maxMarks || assignment?.maxMarks || 0);
      return Number.isFinite(score) ? percent(max > 0 && score <= max ? (score / max) * 100 : score) : null;
    }).filter(Number.isFinite);
    const ownQuizScores = ownAttempts.map((item) => Number(item.percentage)).filter(Number.isFinite);
    const assignmentScore = ownAssignmentScores.length ? percent(ownAssignmentScores.reduce((a, b) => a + b, 0) / ownAssignmentScores.length) : 0;
    const quizScore = ownQuizScores.length ? percent(ownQuizScores.reduce((a, b) => a + b, 0) / ownQuizScores.length) : 0;
    const attendance = ownSessions.length ? percent((present / ownSessions.length) * 100) : 0;
    const development = percent((assignmentScore + quizScore + attendance) / 3);
    return { uid, name: student.fullName || student.email || student.studentId || "Student", studentId: student.studentId || "", className: student.className || classes.find((item) => item.id === student.classId)?.name || "Class", assignmentScore, quizScore, attendance, development, submissions: ownSubmissions.length };
  }).sort((a, b) => b.development - a.development);
  const atRisk = studentRows.filter((row) => row.development < 50 || row.attendance < 60);
  const chartRows = [
    ["Attendance", attendanceRate, "#0B4EA2"],
    ["Assignments", assignmentAverage, "#F7C948"],
    ["Quiz results", quizAverage, "#16A34A"],
    ["Assignment completion", assignmentCompletion, "#7C3AED"],
  ];
  return (
    <Panel title="Student Development Analytics">
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}><label style={{ display: "grid", gap: 6, minWidth: 240, color: DARK, fontWeight: 900 }}>Class<select value={classFilter} onChange={(event) => setClassFilter(event.target.value)} style={{ minHeight: 44, border: "1px solid #C9D6E5", borderRadius: 10, padding: "9px 11px", background: "#fff", fontWeight: 800 }}><option value="">All my classes</option>{classes.map((item) => <option key={item.id} value={item.id}>{item.name || item.className || item.id}</option>)}</select></label><span style={{ color: "#64748B", fontSize: 12, fontWeight: 800 }}>Charts and student development update automatically for the selected class.</span></div>
      <StatGrid stats={[
        { label: "Students", value: students.length },
        { label: "Attendance", value: `${attendanceRate}%` },
        { label: "Assignment Average", value: `${assignmentAverage}%` },
        { label: "Quiz Average", value: `${quizAverage}%` },
        { label: "Assignment Completion", value: `${assignmentCompletion}%` },
        { label: "Quiz Completion", value: `${quizCompletion}%` },
        { label: "Students Needing Support", value: atRisk.length, glow: atRisk.length > 0 },
      ]} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 14 }}>
        <section style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 16, background: "#fff", display: "grid", gap: 14 }}><h3 style={{ margin: 0, color: DARK }}>Performance Overview</h3>{chartRows.map(([label, value, color]) => <div key={label} style={{ display: "grid", gap: 6 }}><div style={{ display: "flex", justifyContent: "space-between", fontWeight: 900, color: DARK }}><span>{label}</span><span>{value}%</span></div><div style={{ height: 14, background: "#E2E8F0", borderRadius: 999, overflow: "hidden" }}><div style={{ height: "100%", width: `${value}%`, background: color, borderRadius: 999 }} /></div></div>)}</section>
        <section style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 16, background: "#fff", display: "grid", placeItems: "center", gap: 12 }}><h3 style={{ margin: 0, color: DARK }}>Assignment Completion</h3><div style={{ width: 190, aspectRatio: "1", borderRadius: "50%", background: `conic-gradient(${BLUE} 0 ${assignmentCompletion}%, #E2E8F0 ${assignmentCompletion}% 100%)`, display: "grid", placeItems: "center" }}><div style={{ width: 126, aspectRatio: "1", borderRadius: "50%", background: "#fff", display: "grid", placeItems: "center", color: DARK, fontSize: 28, fontWeight: 950 }}>{assignmentCompletion}%</div></div><div style={{ display: "flex", gap: 14, color: "#64748B", fontWeight: 850 }}><span>● Completed {assignmentCompletion}%</span><span>○ Pending {100 - assignmentCompletion}%</span></div></section>
      </div>
      <section style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 16, background: "#fff", display: "grid", gap: 12 }}><h3 style={{ margin: 0, color: DARK }}>Individual Student Development</h3><Table empty="No student performance data yet" rows={studentRows} columns={[{ key: "name", label: "Student" }, { key: "studentId", label: "ID" }, { key: "className", label: "Class" }, { key: "attendance", label: "Attendance", render: (row) => `${row.attendance}%` }, { key: "assignmentScore", label: "Assignments", render: (row) => `${row.assignmentScore}%` }, { key: "quizScore", label: "Quizzes", render: (row) => `${row.quizScore}%` }, { key: "development", label: "Development", render: (row) => <span style={{ color: row.development < 50 ? "#B91C1C" : row.development < 70 ? "#92400E" : "#166534", fontWeight: 950 }}>{row.development}%</span> }, { key: "status", label: "Support Status", render: (row) => row.development < 50 || row.attendance < 60 ? "Needs support" : row.development >= 75 ? "Strong progress" : "Developing" }]} /></section>
      {atRisk.length > 0 && <section style={{ border: "1px solid #FECACA", borderRadius: 12, padding: 16, background: "#FEF2F2" }}><h3 style={{ margin: "0 0 10px", color: "#991B1B" }}>Students Requiring Attention</h3><p style={{ margin: 0, color: "#7F1D1D", lineHeight: 1.6 }}>{atRisk.map((row) => `${row.name} (${row.development}% development, ${row.attendance}% attendance)`).join(" · ")}</p></section>}
    </Panel>
  );
}

function LoadingScreen() {
  return (
    <div className="alpha-splash" style={{ minHeight: "100svh", display: "grid", placeItems: "center", background: `radial-gradient(circle at 50% 38%, #0F5DB5 0, ${BLUE} 24%, ${DARK} 72%)`, color: "#fff", fontWeight: 950, padding: 24, overflow: "hidden" }}>
      <div style={{ display: "grid", justifyItems: "center", gap: 14, textAlign: "center" }}>
        <div className="alpha-splash-mark"><img src={alphaAssets.logo} alt="Alpha Schools" width="144" height="144" style={{ width: 144, height: 144, objectFit: "contain", filter: "drop-shadow(0 18px 32px rgba(0,0,0,.3))" }} /></div>
        <div style={{ fontSize: "clamp(23px,5vw,31px)", letterSpacing: "-.02em" }}>Alpha Schools Portal</div>
        <div style={{ color: "rgba(255,255,255,.72)", fontSize: 12, letterSpacing: ".16em", textTransform: "uppercase" }}>Your Education Is Our Priority</div>
        <div style={{ width: 180, height: 3, marginTop: 8, borderRadius: 999, overflow: "hidden", background: "rgba(255,255,255,.18)" }}>
          <div style={{ width: "46%", height: "100%", background: `linear-gradient(90deg, transparent, ${GOLD}, #fff, ${GOLD}, transparent)`, borderRadius: "inherit", animation: "alphaLoad 1.25s ease-in-out infinite" }} />
        </div>
        <style>{`@keyframes alphaSplashIn{0%{opacity:0;transform:scale(.78) translateY(14px)}65%{opacity:1;transform:scale(1.035) translateY(0)}100%{opacity:1;transform:scale(1)}}@keyframes alphaHalo{0%,100%{box-shadow:0 0 0 0 rgba(247,201,72,.36)}50%{box-shadow:0 0 0 16px rgba(247,201,72,0)}}@keyframes alphaLoad{0%{transform:translateX(-110%)}100%{transform:translateX(240%)}}.alpha-splash-mark{width:164px;height:164px;display:grid;place-items:center;border-radius:50%;animation:alphaSplashIn .7s cubic-bezier(.2,.8,.2,1) both,alphaHalo 1.8s ease-out .7s infinite}@media(prefers-reduced-motion:reduce){.alpha-splash-mark,.alpha-splash-mark+div,.alpha-splash *{animation-duration:.01ms!important;animation-iteration-count:1!important}}@media(max-width:780px){aside{position:static}body{overflow-x:hidden}.alpha-splash-mark{width:142px;height:142px}.alpha-splash-mark img{width:128px!important;height:128px!important}}`}</style>
      </div>
    </div>
  );
}

function BlockedScreen({ reason, authUser, checks = [], pending = false, retryable = false, onRetry }) {
  return (
    <main style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#F8FAFC", padding: 18 }}>
      <section style={{ width: "min(520px,100%)", background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: 24, textAlign: "center" }}>
        <AlphaMark />
        <h1 style={{ color: DARK, margin: "18px 0 8px", fontSize: 24, fontWeight: 950 }}>{pending ? "Account Review Pending" : "Access unavailable"}</h1>
        <p style={{ color: "#64748B", lineHeight: 1.55 }}>{reason}</p>
        {authUser && (
          <div style={{ margin: "14px 0", textAlign: "left", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: 12, color: DARK, fontSize: 12, lineHeight: 1.5, overflowWrap: "anywhere" }}>
            <strong>Login debug</strong>
            <div>{isSyntheticStudentEmail(authUser.email) ? "Student ID" : "Email"}: {isSyntheticStudentEmail(authUser.email) ? visibleLoginIdentifier(authUser.email) : (authUser.email || "missing")}</div>
            <div>UID: {authUser.uid || "missing"}</div>
            {checks.length > 0 && <div>Checks: {sanitizeStudentLoginText(checks.join(" | "))}</div>}
          </div>
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginTop: 8 }}>
          {retryable && onRetry && <Button variant="gold" onClick={onRetry}>Try again</Button>}
          <Button onClick={() => signOut(getFirebaseAuth())}>Back to login</Button>
        </div>
      </section>
    </main>
  );
}

// ── LOGIN RESOLUTION STATES (STEP 2) ───────────────────────────────
// Authentication (Firebase) and authorization (Alpha approval) are
// separate. These states distinguish every outcome so the UI never
// mistakenly shows "pending" for a network error or "not assigned" for
// a genuinely pending account.
const LOGIN_STATE = {
  BOOT: "BOOT",
  AUTHENTICATING: "AUTHENTICATING",
  RESOLVING_PROFILE: "RESOLVING_PROFILE",
  APPROVED: "APPROVED",
  PENDING: "PENDING",
  REJECTED: "REJECTED",
  INACTIVE: "INACTIVE",
  PROFILE_INCOMPLETE: "PROFILE_INCOMPLETE",
  NETWORK_ERROR: "NETWORK_ERROR",
  PERMISSION_ERROR: "PERMISSION_ERROR",
  AUTH_ERROR: "AUTH_ERROR",
};

// STEP 5 — Explicit negative status MUST win. These are checked first
// across every identity source so legacy compatibility never reactivates
// a removed/suspended user.
const REJECTED_STATUSES = ["rejected"];
const INACTIVE_STATUSES = ["suspended", "deactivated", "revoked", "disabled"];
const PENDING_STATUSES = ["pending", "pending_approval"];

// Roles supported by the emergency login patch (STEP 9 — admin/teacher/
// student only). Parent/accountant/bursar dashboards are out of scope for
// this surgical fix.
const LOGIN_VALID_ROLES = ["schoolAdmin", "teacher", "student"];

function isPermissionOrNetworkError(error) {
  const code = String(error?.code || error?.message || "").toLowerCase();
  return code.includes("permission-denied") || code.includes("unavailable") ||
    code.includes("deadline-exceeded") || code.includes("network") || code.includes("failed-precondition");
}

// ErrorBoundary — catches any rendering crash inside dashboards (TenantDashboard,
// SuperAdminDashboard) and shows a friendly recovery screen instead of a white
// screen. This is the last line of defence against TDZ crashes, undefined
// props, or any other rendering error that would otherwise blank the page.
class DashboardErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error("[DashboardErrorBoundary] caught rendering error", {
      message: error.message,
      componentStack: info?.componentStack,
    });
  }
  handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== "undefined") window.location.reload();
  };
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f7fa", padding: 24 }}>
          <div style={{ maxWidth: 480, background: "white", borderRadius: 16, padding: 32, boxShadow: "0 8px 32px rgba(0,0,0,0.08)", textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ margin: "0 0 12px", fontSize: 22, fontWeight: 700, color: "#1a1a2e" }}>Dashboard encountered an issue</h2>
            <p style={{ margin: "0 0 24px", color: "#6b7280", lineHeight: 1.6 }}>
              Your account was authenticated successfully, but the dashboard couldn't be loaded. This is usually a temporary issue.
            </p>
            <button
              onClick={this.handleReset}
              style={{
                background: "#0B4EA2", color: "white", border: "none", borderRadius: 10,
                padding: "12px 32px", fontSize: 15, fontWeight: 600, cursor: "pointer",
              }}
            >
              Reload Dashboard
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function classifyStatus(status, activeFlag) {
  const s = String(status || "active").toLowerCase();
  if (REJECTED_STATUSES.includes(s)) return LOGIN_STATE.REJECTED;
  if (INACTIVE_STATUSES.includes(s) || activeFlag === false) return LOGIN_STATE.INACTIVE;
  if (PENDING_STATUSES.includes(s)) return LOGIN_STATE.PENDING;
  return null; // not a negative state — proceed to approval checks
}

function messageForState(state) {
  switch (state) {
    case LOGIN_STATE.PENDING: return "Your registration has been received and is awaiting school approval.";
    case LOGIN_STATE.REJECTED: return "Your registration was not approved. Contact the school office for details.";
    case LOGIN_STATE.INACTIVE: return "This account is currently inactive. Contact the school administrator.";
    case LOGIN_STATE.NETWORK_ERROR:
    case LOGIN_STATE.PERMISSION_ERROR: return "We couldn't connect to the school portal. Check your connection and try again.";
    case LOGIN_STATE.PROFILE_INCOMPLETE: return "Your account is not assigned to Alpha Schools. Ask an Alpha school admin to approve your profile.";
    case LOGIN_STATE.AUTH_ERROR: return "Email or password is incorrect.";
    default: return "";
  }
}

// ── BACKWARD-COMPATIBLE LOGIN RESOLVER (STEPS 3, 4, 5) ─────────────
// Reads existing compatible identity sources in precedence order. NEVER
// migrates records during login. Defaults schoolId to "alpha" for any doc
// found inside schools/alpha/users (legacy compatibility — the doc is
// physically in the alpha school collection, so it logically belongs to
// alpha). Firebase Auth existence alone is NOT school approval.
//
// PERFORMANCE FIX: All 5 identity sources are read in PARALLEL (via
// Promise.allSettled). Only the slowest read determines latency, not
// the sum. Previously 5 sequential reads took 1.5-4s; now ~300-800ms.
async function attachProfileForAuthUser(user, debugChecks = []) {
  const db = getFirebaseDb();
  const email = String(user.email || "").toLowerCase();
  const uid = user.uid;
  const compatibilitySources = [];
  const isSyntheticStudent = email.endsWith("@alpha.stea.africa");
  const studentId = isSyntheticStudent ? email.replace("@alpha.stea.africa", "") : "";

  // Helper: persist a normalized profile doc to the two legacy mirror
  // locations so subsequent logins resolve from SOURCE 1 directly.
  async function writeAttachedProfile(profileData) {
    await setDoc(doc(db, "schools", SCHOOL_ID, "users", uid), profileData, { merge: true });
    await setDoc(doc(db, "users", uid), profileData, { merge: true }).catch(() => {});
  }

  // Helper: best-effort multi-role merge. This MUST NOT block login — any
  // permission/network failure is caught and skipped.
  async function mergeAdditionalRoles(profile) {
    const roles = normalizeRoles(profile);
    let mergedRoles = [...roles];
    if (email) {
      try {
        const accessSnap = await getDoc(doc(db, "schools", SCHOOL_ID, "emailAccess", emailDocId(email)));
        if (accessSnap.exists()) {
          mergedRoles = Array.from(new Set([...mergedRoles, ...normalizeRoles(accessSnap.data())]));
          compatibilitySources.push(`emailAccess/${emailDocId(email)}`);
        }
      } catch (e) { /* non-blocking */ }
      try {
        const sameEmailSnap = await getDocs(collection(db, "schools", SCHOOL_ID, "users"));
        const matchingProfiles = sameEmailSnap.docs.filter((item) => String(item.data().email || "").trim().toLowerCase() === email);
        matchingProfiles.forEach((item) => {
          mergedRoles = Array.from(new Set([...mergedRoles, ...normalizeRoles(item.data())]));
          compatibilitySources.push(`schools/alpha/users/${item.id} (email-merge)`);
        });
        if (normalizeRole(profile.role) === "student") {
          const rosterDoc = matchingProfiles.find((item) => item.id.startsWith("student-") && item.id !== uid);
          const roster = rosterDoc?.data();
          if (roster) {
            const mergedClassIds = Array.from(new Set([profile.classId, roster.classId, ...(profile.classIds || []), ...(profile.assignedClassIds || []), ...(roster.classIds || []), ...(roster.assignedClassIds || [])].filter(Boolean)));
            profile.classId = roster.classId || profile.classId || mergedClassIds[0] || "";
            profile.classIds = mergedClassIds;
            profile.assignedClassIds = mergedClassIds;
            profile.className = roster.className || profile.className || "";
            profile.form = roster.form || profile.form || "";
            profile.stream = roster.stream || profile.stream || "";
            profile.attachedFrom = rosterDoc.id || profile.attachedFrom;
            setDoc(doc(db, "schools", SCHOOL_ID, "users", uid), { classId: profile.classId, classIds: mergedClassIds, className: profile.className, form: profile.form, stream: profile.stream, updatedAt: serverTimestamp() }, { merge: true }).catch(() => {});
          }
        }
      } catch (e) { /* non-blocking */ }
    }
    try {
      const classSnap = await getDocs(collection(db, "schools", SCHOOL_ID, "classes"));
      const assignedAsTeacher = classSnap.docs.some((item) => {
        const row = item.data();
        const ownerUids = [row.assignedTeacherUid, row.classTeacherUid, row.createdByUid, row.createdBy, row.ownerId, row.teacherId, row.classTeacherId, ...(Array.isArray(row.teacherIds) ? row.teacherIds : []), ...(Array.isArray(row.assignedTeacherIds) ? row.assignedTeacherIds : []), ...(Array.isArray(row.activeTeacherUids) ? row.activeTeacherUids : []), ...(Array.isArray(row.classTeacherIds) ? row.classTeacherIds : [])].filter(Boolean);
        return ownerUids.includes(uid);
      });
      if (assignedAsTeacher) mergedRoles = Array.from(new Set([...mergedRoles, "teacher"]));
    } catch (e) { /* non-blocking */ }
    if (mergedRoles.length > roles.length) {
      profile.roles = mergedRoles;
      const rememberedRole = normalizeRole(localStorage.getItem("alpha_active_role"));
      if (mergedRoles.includes(rememberedRole)) profile.role = rememberedRole;
      setDoc(doc(db, "schools", SCHOOL_ID, "users", uid), { roles: mergedRoles, updatedAt: serverTimestamp() }, { merge: true }).catch(() => {});
      setDoc(doc(db, "users", uid), { roles: mergedRoles, updatedAt: serverTimestamp() }, { merge: true }).catch(() => {});
    }
  }

  // ── PARALLEL READ: fetch all 5 identity sources simultaneously ──
  // Each read is wrapped so one failure (permission/network) doesn't
  // block the others. We evaluate results in precedence order below.
  const sourcePromises = [
    // SOURCE 1: schools/alpha/users/{uid}
    getDoc(doc(db, "schools", SCHOOL_ID, "users", uid)).then(
      (snap) => ({ ok: snap.exists(), data: snap.exists() ? snap.data() : null, source: `schools/alpha/users/${uid}`, error: null }),
      (err) => ({ ok: false, data: null, source: `schools/alpha/users/${uid}`, error: err })
    ),
    // SOURCE 2: schools/alpha/emailAccess/{email}
    email ? getDoc(doc(db, "schools", SCHOOL_ID, "emailAccess", emailDocId(email))).then(
      (snap) => ({ ok: snap.exists(), data: snap.exists() ? snap.data() : null, source: `schools/alpha/emailAccess/${emailDocId(email)}`, error: null }),
      (err) => ({ ok: false, data: null, source: `schools/alpha/emailAccess/${emailDocId(email)}`, error: err })
    ) : Promise.resolve({ ok: false, data: null, source: "emailAccess (no email)", error: null }),
    // SOURCE 3: userProfiles/{uid}
    getDoc(doc(db, "userProfiles", uid)).then(
      (snap) => ({ ok: snap.exists(), data: snap.exists() ? snap.data() : null, source: `userProfiles/${uid}`, error: null }),
      (err) => ({ ok: false, data: null, source: `userProfiles/${uid}`, error: err })
    ),
    // SOURCE 4: users/{uid}
    getDoc(doc(db, "users", uid)).then(
      (snap) => ({ ok: snap.exists(), data: snap.exists() ? snap.data() : null, source: `users/${uid}`, error: null }),
      (err) => ({ ok: false, data: null, source: `users/${uid}`, error: err })
    ),
    // SOURCE 5: student roster (synthetic email only)
    isSyntheticStudent ? getDoc(doc(db, "schools", SCHOOL_ID, "users", `student-${cleanStudentId(studentId)}`)).then(
      (snap) => ({ ok: snap.exists(), data: snap.exists() ? snap.data() : null, source: snap.exists() ? `schools/alpha/users/${snap.id}` : `student-${cleanStudentId(studentId)}`, error: null }),
      (err) => ({ ok: false, data: null, source: `student-${cleanStudentId(studentId)}`, error: err })
    ) : Promise.resolve({ ok: false, data: null, source: "student roster (not synthetic)", error: null }),
  ];

  const results = await Promise.allSettled(sourcePromises);
  const parsed = results.map((r) => r.status === "fulfilled" ? r.value : { ok: false, data: null, source: "unknown", error: r.reason });

  // Check for permission/network errors first — if all reads failed with
  // permission/network, return ERROR state, not PROFILE_INCOMPLETE.
  const allPermissionOrNetwork = parsed.every((r) => r.error && isPermissionOrNetworkError(r.error));
  if (allPermissionOrNetwork) {
    debugChecks.push("All identity reads failed with permission/network errors");
    return { profile: null, state: LOGIN_STATE.PERMISSION_ERROR, source: null, compatibilitySources, checks: debugChecks };
  }

  // Check for explicit negative statuses first (STEP 5 — highest precedence).
  for (const r of parsed) {
    if (!r.ok || !r.data) continue;
    const status = String(r.data.status || "active").toLowerCase();
    const negative = classifyStatus(status, r.data.active);
    if (negative === LOGIN_STATE.REJECTED) {
      debugChecks.push(`${r.source} status=REJECTED`);
      return { profile: null, state: LOGIN_STATE.REJECTED, source: r.source, compatibilitySources, checks: debugChecks };
    }
    if (negative === LOGIN_STATE.INACTIVE) {
      debugChecks.push(`${r.source} status=INACTIVE`);
      return { profile: null, state: LOGIN_STATE.INACTIVE, source: r.source, compatibilitySources, checks: debugChecks };
    }
    // Also check userProfiles-specific rejection fields
    if (r.source.startsWith("userProfiles/")) {
      const up = r.data;
      const accountStatus = String(up.accountStatus || "").toLowerCase();
      const approvalStatus = String(up.approvalStatus || "").toLowerCase();
      if (accountStatus === "rejected" || approvalStatus === "rejected") {
        return { profile: null, state: LOGIN_STATE.REJECTED, source: r.source, compatibilitySources, checks: debugChecks };
      }
      if (up.active === false || accountStatus === "suspended" || approvalStatus === "suspended") {
        return { profile: null, state: LOGIN_STATE.INACTIVE, source: r.source, compatibilitySources, checks: debugChecks };
      }
    }
  }

  // Evaluate in precedence order for APPROVED resolution.
  // Priority: SOURCE 1 (schools/alpha/users/{uid}) > SOURCE 2 (emailAccess)
  // > SOURCE 3 (userProfiles) > SOURCE 4 (users/{uid}) > SOURCE 5 (roster)
  for (let i = 0; i < parsed.length; i++) {
    const r = parsed[i];
    if (!r.ok || !r.data) {
      if (r.error) debugChecks.push(`${r.source} read failed: ${r.error.code || r.error.message}`);
      else debugChecks.push(`${r.source} not found`);
      continue;
    }
    const data = r.data;
    const status = String(data.status || "active").toLowerCase();
    compatibilitySources.push(r.source);

    // SOURCE 1: schools/alpha/users/{uid} — primary tenant profile
    if (i === 0) {
      const schoolId = data.schoolId || SCHOOL_ID;
      const roles = normalizeRoles(data);
      const rememberedRole = normalizeRole(localStorage.getItem("alpha_active_role"));
      const role = roles.includes(rememberedRole) ? rememberedRole : normalizeRole(data.role || roles[0]);
      if (LOGIN_VALID_ROLES.includes(role) && status !== "pending" && status !== "pending_approval") {
        const normalized = { uid, ...data, email, role, roles: roles.length ? roles : [role], status: "active", schoolId };
        await mergeAdditionalRoles(normalized).catch(() => {});
        if (!data.status) setDoc(doc(db, "schools", SCHOOL_ID, "users", uid), { status: "active", updatedAt: serverTimestamp() }, { merge: true }).catch(() => {});
        if (!data.schoolId) setDoc(doc(db, "schools", SCHOOL_ID, "users", uid), { schoolId: SCHOOL_ID, updatedAt: serverTimestamp() }, { merge: true }).catch(() => {});
        return { profile: normalized, state: LOGIN_STATE.APPROVED, source: r.source, compatibilitySources, checks: debugChecks };
      }
      if (status === "pending" || status === "pending_approval") {
        debugChecks.push(`${r.source} status=pending — checking other sources for approved evidence`);
        // Fall through to check other sources for approved evidence
        continue;
      }
      debugChecks.push(`${r.source} found but role invalid: role=${data.role || "missing"}`);
      continue;
    }

    // SOURCE 2: schools/alpha/emailAccess/{email}
    if (i === 1 && ["schoolAdmin", "teacher"].includes(normalizeRole(data.role))) {
      if (status === "active") {
        const profileData = { ...data, uid, email, role: normalizeRole(data.role), roles: [normalizeRole(data.role)], schoolId: data.schoolId || SCHOOL_ID, status: "active", passwordCreated: true, attachedFrom: r.source };
        await writeAttachedProfile({ ...profileData, attachedAt: serverTimestamp(), updatedAt: serverTimestamp() });
        return { profile: profileData, state: LOGIN_STATE.APPROVED, source: r.source, compatibilitySources, checks: debugChecks };
      }
      continue;
    }

    // SOURCE 3: userProfiles/{uid} — Identity V2
    if (i === 2) {
      const up = data;
      const accountStatus = String(up.accountStatus || "").toLowerCase();
      const approvalStatus = String(up.approvalStatus || "").toLowerCase();
      const approved = accountStatus === "active" || accountStatus === "approved" || accountStatus === "legacy_approved" || approvalStatus === "approved";
      if (approved) {
        const roleIds = Array.isArray(up.roleIds) ? up.roleIds : [];
        const primaryRole = normalizeRole(up.legacyPrimaryRole || roleIds[0] || "student");
        if (LOGIN_VALID_ROLES.includes(primaryRole)) {
          const profileData = { uid, email, role: primaryRole, roles: roleIds.length ? roleIds : [primaryRole], schoolId: up.schoolId || SCHOOL_ID, status: "active", accountStatus, approvalStatus, attachedFrom: r.source, fullName: up.fullName || up.displayName || "" };
          await writeAttachedProfile({ ...profileData, attachedAt: serverTimestamp(), updatedAt: serverTimestamp() });
          return { profile: profileData, state: LOGIN_STATE.APPROVED, source: r.source, compatibilitySources, checks: debugChecks };
        }
      }
      continue;
    }

    // SOURCE 4: users/{uid} — legacy root
    if (i === 3) {
      const role = normalizeRole(data.role);
      if (status === "active" && LOGIN_VALID_ROLES.includes(role)) {
        const profileData = { ...data, uid, email, role, roles: [role], schoolId: data.schoolId || SCHOOL_ID, status: "active", attachedFrom: r.source };
        await writeAttachedProfile({ ...profileData, attachedAt: serverTimestamp(), updatedAt: serverTimestamp() });
        return { profile: profileData, state: LOGIN_STATE.APPROVED, source: r.source, compatibilitySources, checks: debugChecks };
      }
      continue;
    }

    // SOURCE 5: student roster
    if (i === 4 && data.role === "student") {
      if (status !== "suspended") {
        const profileData = { ...data, uid, email, role: "student", schoolId: SCHOOL_ID, status: "active", passwordCreated: true, attachedFrom: r.source };
        await writeAttachedProfile({ ...profileData, attachedAt: serverTimestamp(), updatedAt: serverTimestamp() });
        if (r.source.startsWith("student-") && r.source !== uid) {
          setDoc(doc(db, "schools", SCHOOL_ID, "users", r.source), { claimedUid: uid, claimedAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true }).catch(() => {});
        }
        return { profile: profileData, state: LOGIN_STATE.APPROVED, source: r.source, compatibilitySources, checks: debugChecks };
      }
      continue;
    }
  }

  // Also check for explicit pending in userProfiles (after exhausting approved sources)
  for (const r of parsed) {
    if (!r.ok || !r.data) continue;
    if (r.source.startsWith("userProfiles/")) {
      const up = r.data;
      const accountStatus = String(up.accountStatus || "").toLowerCase();
      const approvalStatus = String(up.approvalStatus || "").toLowerCase();
      if (accountStatus === "pending" || approvalStatus === "pending") {
        return { profile: null, state: LOGIN_STATE.PENDING, source: r.source, compatibilitySources, checks: debugChecks };
      }
    }
  }

  // STEP 4 — Firebase Auth existence alone is NOT school approval.
  return { profile: null, state: LOGIN_STATE.PROFILE_INCOMPLETE, source: null, compatibilitySources, checks: debugChecks };
}

export default function AlphaClassroomSaaS() {
  const location = useLocation();
  const navigate = useNavigate();
  const [authUser, setAuthUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessChecks, setAccessChecks] = useState([]);
  const [loginState, setLoginState] = useState(LOGIN_STATE.BOOT);
  const profileStatusRef = useRef("");
  const profileIsNullRef = useRef(true);
  const resolvingRef = useRef(false);

  function switchActiveRole(nextRole) {
    const role = normalizeRole(nextRole);
    setProfile((current) => {
      if (!current || !normalizeRoles(current).includes(role)) return current;
      localStorage.setItem("alpha_active_role", role);
      return { ...current, role };
    });
    navigate(roleHome[role] || "/portal", { replace: true });
  }

  useEffect(() => {
    document.title = isPortalPath(location.pathname) ? "Alpha School Portal" : "Alpha Schools";
    document.querySelector("link[rel='manifest']")?.setAttribute("href", "/alpha.webmanifest");
    document.querySelector("meta[name='theme-color']")?.setAttribute("content", BLUE);
    document.querySelector("link[rel='icon']")?.setAttribute("href", alphaAssets.favicon);
    const faviconLink = document.querySelector("link[rel='icon']");
    faviconLink?.setAttribute("type", "image/png");
    document.querySelector("link[rel='apple-touch-icon']")?.setAttribute("href", `${alphaAssets.appleTouchIcon}?v=alpha-logo-20260713-1`);
  }, [location.pathname]);

  // ── STEP 7 — Deterministic boot sequence ──────────────────────────
  // Single resolution entry-point. Guards against concurrent resolutions
  // (race between onAuthStateChanged, the real-time watcher, and retries).
  // Never logs passwords, tokens, or sensitive personal data — only safe
  // stage markers per STEP 1.
  async function resolveAndApply(user) {
    if (!user) {
      setProfile(null);
      setSchool(null);
      setAccessChecks([]);
      setLoginState(LOGIN_STATE.BOOT);
      profileIsNullRef.current = true;
      setLoading(false);
      return;
    }
    if (resolvingRef.current) return;
    resolvingRef.current = true;
    setLoading(true);
    try {
      const checks = [];
      if (isSuperAdminEmail(user.email)) {
        console.log("[AUTH] super-admin email — direct dashboard");
        setProfile({ uid: user.uid, email: user.email, fullName: user.displayName || "STEA Super Admin", role: "superAdmin", roles: ["superAdmin"], status: "active", schoolId: SCHOOL_ID });
        setSchool({ schoolId: SCHOOL_ID, schoolName: alphaInfo.schoolName });
        setLoginState(LOGIN_STATE.APPROVED);
        setAccessChecks([]);
        profileIsNullRef.current = false;
        profileStatusRef.current = "active";
        return;
      }
      console.log("[AUTH] Firebase authentication: success", { uid: user.uid });
      // PERFORMANCE FIX: 15-second timeout on identity resolution so we never
      // hang forever on a slow/failed Firestore read. The parallel reads above
      // should normally complete in <1s, but if the network is dead we want
      // to show the NETWORK_ERROR state, not freeze on LoadingScreen.
      const result = await Promise.race([
        attachProfileForAuthUser(user, checks),
        new Promise((_, reject) => setTimeout(() => reject(new Error("RESOLUTION_TIMEOUT")), 15000)),
      ]);
      // STEP 1 — safe stage logging (dev only; no PII)
      if (import.meta.env?.DEV) {
        console.log("[AUTH] resolution", {
          uid: user.uid,
          state: result.state,
          source: result.source,
          compatibilitySources: result.compatibilitySources,
          effectiveStatus: result.profile ? "approved" : result.state.toLowerCase(),
          effectiveRoles: result.profile ? normalizeRoles(result.profile) : [],
          selectedDashboard: result.profile ? result.profile.role : "none",
        });
      }
      let schoolData = { schoolId: SCHOOL_ID, schoolName: alphaInfo.schoolName };
      try {
        const db = getFirebaseDb();
        const schoolSnap = await getDoc(doc(db, "schools", SCHOOL_ID));
        schoolData = schoolSnap.exists() ? schoolSnap.data() : schoolData;
      } catch (error) {
        checks.push(`school profile read skipped: ${error.code || error.message}`);
      }
      setSchool(schoolData);
      setLoginState(result.state);
      if (result.profile) {
        setProfile({ ...result.profile, roles: normalizeRoles(result.profile), status: result.profile.status || "active" });
        setAccessChecks([]);
        profileIsNullRef.current = false;
        profileStatusRef.current = String(result.profile.status || "active").toLowerCase();
        getFirebaseDb() && setDoc(doc(getFirebaseDb(), "schools", SCHOOL_ID, "users", user.uid), { lastLogin: serverTimestamp() }, { merge: true }).catch(() => {});
      } else {
        setProfile(null);
        setAccessChecks(result.checks || checks);
        profileIsNullRef.current = true;
      }
    } catch (error) {
      console.error("[AUTH] profile load failed", { code: error.code, message: error.message });
      setProfile(null);
      const isTimeout = error.message === "RESOLUTION_TIMEOUT";
      const recoverable = isTimeout || isPermissionOrNetworkError(error);
      setLoginState(isTimeout ? LOGIN_STATE.NETWORK_ERROR : recoverable ? LOGIN_STATE.NETWORK_ERROR : LOGIN_STATE.PROFILE_INCOMPLETE);
      setAccessChecks([isTimeout ? "Identity resolution timed out (15s). Check your connection." : `profile load exception: ${error.code || error.message}`]);
      profileIsNullRef.current = true;
    } finally {
      resolvingRef.current = false;
      setLoading(false);
    }
  }

  function retryLoginResolution() {
    if (authUser) resolveAndApply(authUser);
  }

  useEffect(() => onAuthStateChanged(getFirebaseAuth(), (user) => {
    setAuthUser(user);
    resolveAndApply(user);
  }), [location.pathname]);

  // Real-time profile watcher — STEP 6 & STEP 7 fix.
  // Re-resolves identity when (a) the user's status changes OR (b) the
  // profile doc appears for the first time while the user is blocked.
  // This eliminates the "stuck on pending" loop: when an admin approves a
  // pending account, the watcher fires and auto-transitions to the
  // dashboard WITHOUT requiring a manual refresh.
  useEffect(() => {
    if (!authUser) return undefined;
    const db = getFirebaseDb();
    const profileRef = doc(db, "schools", SCHOOL_ID, "users", authUser.uid);
    return onSnapshot(profileRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() || {};
      const newStatus = String(data.status || "active").toLowerCase();
      const prevStatus = profileStatusRef.current;
      const currentlyBlocked = profileIsNullRef.current;
      // Re-resolve on: status change, OR doc newly appeared while blocked,
      // OR role changes while blocked. Skip when status unchanged AND not
      // blocked (avoids loops on lastLogin writes).
      if (newStatus === prevStatus && !currentlyBlocked) return;
      profileStatusRef.current = newStatus;
      resolveAndApply(authUser);
    }, (err) => {
      console.warn("[AUTH] profile watcher error", err.code || err.message);
    });
  }, [authUser]);

  useEffect(() => {
    if (!location.pathname || location.pathname === "/") return;
    const redirect = legacyAlphaRedirect(location.pathname);
    if (redirect) {
      navigate(redirect, { replace: true });
      return;
    }
    if (location.pathname !== "/portal" && location.pathname !== "/portal/" && !location.pathname.startsWith("/portal/")) return;
    if (loading || !authUser || !profile) return;
    if (location.pathname === "/portal" || location.pathname === "/portal/") {
      navigate(roleHome[profile.role], { replace: true });
      return;
    }
    const requested = routeRole(location.pathname);
    if (profile.role === "superAdmin" && requested !== "superAdmin") navigate("/portal/super-admin", { replace: true });
    if (profile.role === "schoolAdmin" && requested === "superAdmin") navigate("/portal/admin", { replace: true });
    if (profile.role === "teacher" && (requested === "superAdmin" || requested === "schoolAdmin")) navigate("/portal/teacher", { replace: true });
    if (profile.role === "student" && requested !== "student") navigate("/portal/student", { replace: true });
  }, [authUser, loading, location.pathname, navigate, profile]);

  const requestedRole = useMemo(() => routeRole(location.pathname), [location.pathname]);

  if (!isPortalPath(location.pathname)) return <PublicAlphaWebsite />;
  if (loading) return <LoadingScreen />;
  if (!authUser || location.pathname.startsWith("/portal/auth")) return <AuthPage onSignedIn={() => navigate("/portal", { replace: true })} />;
  if (location.pathname === "/portal" || location.pathname === "/portal/") return <LoadingScreen />;

  // STEP 2 & STEP 8 — Explicit login states drive the screen, NOT a
  // generic "pending" fallback. Each state has an accurate message.
  if (loginState === LOGIN_STATE.PENDING) {
    return <BlockedScreen authUser={authUser} checks={accessChecks} reason={messageForState(LOGIN_STATE.PENDING)} pending />;
  }
  if (loginState === LOGIN_STATE.REJECTED) {
    return <BlockedScreen authUser={authUser} checks={accessChecks} reason={messageForState(LOGIN_STATE.REJECTED)} />;
  }
  if (loginState === LOGIN_STATE.INACTIVE) {
    return <BlockedScreen authUser={authUser} checks={accessChecks} reason={messageForState(LOGIN_STATE.INACTIVE)} />;
  }
  if (loginState === LOGIN_STATE.NETWORK_ERROR || loginState === LOGIN_STATE.PERMISSION_ERROR) {
    return <BlockedScreen authUser={authUser} checks={accessChecks} reason={messageForState(loginState)} onRetry={retryLoginResolution} retryable />;
  }
  if (!profile) {
    return <BlockedScreen authUser={authUser} checks={accessChecks} reason={messageForState(LOGIN_STATE.PROFILE_INCOMPLETE)} onRetry={retryLoginResolution} retryable />;
  }
  if (profile.status !== "active") {
    // Defensive: profile resolved but status field says otherwise — classify it.
    const fallback = classifyStatus(profile.status, profile.active);
    return <BlockedScreen authUser={authUser} checks={accessChecks} reason={messageForState(fallback || LOGIN_STATE.PROFILE_INCOMPLETE)} />;
  }
  if (profile.role !== "superAdmin" && profile.schoolId !== SCHOOL_ID) {
    return <BlockedScreen authUser={authUser} checks={accessChecks} reason="Your account belongs to another school and cannot access Alpha Schools." />;
  }
  if (profile.mustChangePassword) return <ChangeTemporaryPassword authUser={authUser} profile={profile} onComplete={() => setProfile((current) => ({ ...current, mustChangePassword: false }))} />;

  if (profile.role === "superAdmin") return <DashboardErrorBoundary><SuperAdminDashboard authUser={authUser} profile={profile} /></DashboardErrorBoundary>;
  return <DashboardErrorBoundary><TenantDashboard authUser={authUser} profile={{ ...profile, onSwitchRole: switchActiveRole }} school={school} routeRole={requestedRole} /></DashboardErrorBoundary>;
}
